// Static interface copy in every supported app language. User and model content is not translated here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  kindWrite: 'App change to approve',
  kindGrant: 'App access to approve',
  kindBusiness: 'Approval for an app',
  approve: 'Approve',
  reject: 'Reject',
  working: 'Working…',
  checkStatus: 'Check status',
  statusStillPending:
    'Poneva has not settled this yet. It may still be going through, so nothing else can be decided until it is.',
  statusStillUnknown:
    'We could not get an answer. Nothing else can be decided until this is settled. Try again in a moment.',
  loading: 'Loading…',
  unavailable: 'This is not available right now.',
  loadFailed: 'We could not load this. Try again in a moment.',
  requestedBy: 'Asked by {name}',
  originApp: 'From the app',
  originAgent: 'From an Agent',
  originClient: 'Via {client}',
  originFor: 'for {name}',
  changedUnderYou:
    'This changed while you were reading it. What you see now is the current version. Nothing was decided.',
  notAllowed: 'You cannot decide this.',
  cannotDecideNow: 'This can’t be decided right now. Nothing was sent.',
  expired: 'This request has expired. Nothing changed.',
  stale: 'The data changed since this was asked. Ask again to get a fresh request.',
  approved: 'Approved',
  rejected: 'Rejected',
  needsYou: 'Needs approval',
  waitingOn: 'Waiting for {name}',
  unknownTitle: 'We cannot confirm what happened',
  unknownBody:
    'The change may or may not have been made. Do not repeat it. Check the status, or ask the app owner to look.',
  blockedApproval: 'Waiting for this approval before it continues.',
  blockedBudget: 'Paused: the AI budget for this app is used up.',
  blockedProvider: 'Paused: a connected service is not responding.',
  blockedViewer:
    "Paused: this needs to run as a signed-in viewer, and no viewer is attached. Nothing runs on the owner's access instead.",
  writeTitle: 'Approve changes to {target}',
  writeSummaryAll: '{count} changes in {app}',
  rowCreate: 'New',
  rowUpdate: 'Changed',
  rowDelete: 'Removed',
  rowInclude: 'Include this change',
  rowExcluded: 'Left out',
  fieldBefore: 'Before',
  fieldAfter: 'After',
  fieldEmpty: 'Empty',
  approveSome: 'Approve {included} of {total}',
  approveAll: 'Approve all {total}',
  allLeftOut: 'Every change is left out. Reject to stop, or include a change to approve it.',
  appliedSome: '{count} changes made.',
  rejectedBody: 'Nothing was changed.',
  noRows: 'There is nothing to approve here.',
  grantTitle: 'Let {app} use {source}',
  grantRead: 'Read only',
  grantWrite: 'Read and change',
  audienceOwner: 'Only you will see this app’s data from {source}.',
  audiencePeople: 'The {count} people this app is shared with will see data from {source}.',
  audienceCompany: 'Everyone in the company who opens the app will see data from {source}.',
  grantRouted: '{owner} decides this, so it was sent to them.',
  grantRoutedUnknown: 'The owner of this connection decides this, so it was sent to them.',
  grantWhat: 'Access',
  businessStep: 'Step {order}',
  stepWaiting: 'Waiting',
  stepPending: 'Needs a decision',
  stepApproved: 'Approved',
  stepRejected: 'Rejected',
  stepYou: 'You',
  businessNote: 'Approving here does not allow any new change outside Poneva.',
  businessYourTurn: 'It is your turn to decide.',
  businessNotYourTurn: 'Waiting for {name} to decide first.',
  policyTitle: 'How apps use this connection',
  policyCreator: 'Creator’s only',
  policyCreatorBody: 'Everyone sees the same data, through the app creator’s connection.',
  policyViewer: 'Viewer’s own only',
  policyViewerBody: 'Each person sees their own data, through their own connection.',
  policyBoth: 'Both',
  policyBothBody: 'The app creator chooses for each app.',
  policyRevoked: 'Apps cannot use this connection',
  policyRevokedBody: 'Choose an option to allow it again. Nothing is chosen for you.',
  policySave: 'Save',
  policySaving: 'Saving…',
  policySaved: 'Saved.',
  policyReadOnly: 'Only a company admin can change this.',
  policyConflict: 'Someone else changed this first. This is the current setting.',
  policyRefused: 'This could not be saved.',
  policyFailed:
    'We could not confirm this was saved. Reload to see the current setting before you try again.',
  policyWidened:
    'Apps that are already published keep how they connect today. Their creators are asked to republish to use the new option.',
  policyNarrowed: 'Apps check the current setting each time they connect.',
  policyAffected: '{count} published apps use this connection (as of {when}).',
  policyAffectedOne: '1 published app uses this connection (as of {when}).',
  policyAffectsNames: 'Affects {count} published apps: {names}.',
  policyAffectsOneName: 'Affects 1 published app: {names}.',
  policyGenericWiden:
    'Published apps that use this connection keep how they connect today. Their creators are asked to republish to use the new option.',
  policyGenericNarrow:
    'Published apps that use this connection check the current setting each time they connect.',
  policyAffectedGeneric:
    'Published apps that use this connection are not changed automatically. Their creators are asked to republish.',
  policyUnavailable: 'This setting is not available right now.',
  // An App's outside services (approve_app_outside_access): the card says what the App may do and where.
  outsideRead: 'Let {app} read from {hosts}',
  outsideReadSend: 'Let {app} read from and send data to {hosts}',
  outsideNone: 'Stop {app} from reaching {hosts}',
  outsideReadDone: '{app} can read from {hosts}',
  outsideReadSendDone: '{app} can read from and send data to {hosts}',
  outsideNoneDone: '{app} no longer reaches {hosts}',
  outsideMore: '{count} more',
  // An App event (set_app_event): what starts the App's function, in one sentence.
  eventWebhookOn: 'Let {app} receive “{event}” at its webhook',
  eventWebhookOnDone: '{app} receives “{event}” at its webhook',
  eventWebhookOff: 'Stop {app} from receiving “{event}” at its webhook',
  eventWebhookOffDone: '{app} no longer receives “{event}”',
  eventRowsOn: 'Let {app} run {runs} when rows in {collection} are {changes}',
  eventRowsOnDone: '{app} runs {runs} when rows in {collection} are {changes}',
  eventRowsOff: 'Stop {app} from running {runs} when rows in {collection} change',
  eventRowsOffDone: '{app} no longer runs {runs} when rows in {collection} change',
  eventInsert: 'added',
  eventUpdate: 'changed',
  eventDelete: 'deleted',
  // An App schedule (set_app_schedule): what changes, then the cadence in the schedule
  // panel's own words ({cadence}); a decided one adds its next runs ({runs}).
  scheduleTurnOn: 'Turn on “{job}” in {app}. {cadence} ({timezone}).',
  scheduleTurnOnDone: '“{job}” in {app} is on. {cadence} ({timezone}).',
  scheduleResume: 'Resume “{job}” in {app}. {cadence} ({timezone}).',
  scheduleResumeDone: '“{job}” in {app} is on again. {cadence} ({timezone}).',
  scheduleReschedule: 'Change when “{job}” runs in {app}. {cadence} ({timezone}).',
  scheduleRescheduleDone: '“{job}” in {app} has a new schedule. {cadence} ({timezone}).',
  schedulePause: 'Pause “{job}” in {app}.',
  schedulePauseDone: '“{job}” in {app} is paused.',
  scheduleNext: 'Next: {runs}.',
  scheduleResumeSaved:
    'Resume “{job}” in {app} on the latest saved version. {cadence} ({timezone}).',
  scheduleResumeSavedDone:
    '“{job}” in {app} is on again and runs the latest saved version. {cadence} ({timezone}).',
  scheduleResumePublished:
    'Resume “{job}” in {app} on the published version. {cadence} ({timezone}).',
  scheduleResumePublishedDone:
    '“{job}” in {app} is on again and runs the published version. {cadence} ({timezone}).',
  scheduleReschedulePaused:
    'Change when “{job}” runs in {app}. {cadence} ({timezone}). It will be paused until you resume it.',
  scheduleReschedulePausedDone:
    '“{job}” in {app} has a new schedule. {cadence} ({timezone}). It is paused until you resume it.',
  scheduleSwitchSaved:
    'Switch “{job}” in {app} to the latest saved version. {cadence} ({timezone}).',
  scheduleSwitchSavedDone:
    '“{job}” in {app} now runs the latest saved version. {cadence} ({timezone}).',
  scheduleSwitchSavedPaused:
    'Switch “{job}” in {app} to the latest saved version. {cadence} ({timezone}). It will be paused until you resume it.',
  scheduleSwitchSavedPausedDone:
    '“{job}” in {app} now uses the latest saved version. {cadence} ({timezone}). It is paused until you resume it.',
  scheduleSwitchPublished:
    'Switch “{job}” in {app} to the published version. {cadence} ({timezone}).',
  scheduleSwitchPublishedDone:
    '“{job}” in {app} now runs the published version. {cadence} ({timezone}).',
  scheduleSwitchPublishedPaused:
    'Switch “{job}” in {app} to the published version. {cadence} ({timezone}). It will be paused until you resume it.',
  scheduleSwitchPublishedPausedDone:
    '“{job}” in {app} now uses the published version. {cadence} ({timezone}). It is paused until you resume it.',
  // An App function's own-row writes (set_app_data_writes).
  dataWritesOn: 'Let {app}’s “{function}” {verbs} its own rows',
  dataWritesOnDone: '{app}’s “{function}” can {verbs} its own rows',
  dataWritesOff: 'Stop {app}’s “{function}” from changing its own rows',
  dataWritesOffDone: '{app}’s “{function}” no longer changes its own rows',
  dataWriteInsert: 'add',
  dataWriteUpdate: 'change',
  dataWriteDelete: 'delete',
  // A destructive change to an App's tables (apply_app_table_change).
  tableDropField: 'Let {app} drop the field “{field}” from {table} ({rows})',
  tableDropFieldDone: '{app} dropped the field “{field}” from {table} ({rows})',
  tableDropTable: 'Let {app} drop the table {table} ({rows})',
  tableDropTableDone: '{app} dropped the table {table} ({rows})',
  tableClear: 'Let {app} clear “{field}” in {table} where it breaks the new rule ({rows})',
  tableClearDone: '{app} cleared “{field}” in {table} where it broke the new rule ({rows})',
  tableRestoreField: 'Let {app} put back “{field}” in {table} from its copy ({rows})',
  tableRestoreFieldDone: '{app} put back “{field}” in {table} ({rows})',
  tableRestoreTable: 'Let {app} restore the table {table} from its copy ({rows})',
  tableRestoreTableDone: '{app} restored the table {table} ({rows})',
  tableRowsLoseOne: '1 row loses a value',
  tableRowsLoseMany: '{count} rows lose a value',
  tableRowsLostOne: '1 row lost a value',
  tableRowsLostMany: '{count} rows lost a value',
  tableRowsOne: '1 row',
  tableRowsMany: '{count} rows',
  // An App function's connected write (set_app_connected_write).
  connectedWriteOn: 'Let {app} {action} in {destination}',
  connectedWriteOnDone: '{app} can {action} in {destination}',
  connectedWriteOff: 'Stop {app} from writing to {destination}',
  connectedWriteOffDone: '{app} no longer writes to {destination}',
  // Code someone else changed, confirmed by the owner (confirm_app_code).
  codeConfirm: 'Let {app} use your approvals with the changes by {names}',
  codeConfirmDone: '{app} uses your approvals with the changes by {names}',
  // The same, when no one else is named or the history is too long to list.
  codeConfirmCurrent: 'Let {app} use your approvals with its current code',
  codeConfirmCurrentDone: '{app} uses your approvals with its current code',
  // A saved App function that can change data, run from chat after a tap.
  runChanges: 'Run “{function}” in {app} now',
  runChangesDone: '“{function}” ran in {app}',
  // The chat continues on its own once the owner has decided the App builder's cards.
  continueApproved: 'Approved. Continue: run it and check that it works.',
  continueDeclined: 'Declined. Continue without it.',
  continueMixed: 'I’ve decided on the requests. Continue.',
  // Titles, Details labels and value words of the App cards (settings approval cards).
  typeOutsideAccess: 'Let an App reach outside services',
  typeEvent: 'Turn an App event on or off',
  typeSchedule: 'Turn an App schedule on, pause it or change it',
  typeDataWrites: 'Let an App function change its own rows',
  typeTableChange: 'Drop or restore data in an App’s tables',
  typeConnectedWrite: 'Let an App write to a company system',
  typeConfirmCode: 'Let changed App code use your approvals',
  typeRunChanges: 'Run an App function that changes data',
  typePublicLink: 'Change an App’s public link',
  typeUnpublish: 'Take down an App’s public link',
  labelHosts: 'Services',
  labelAccess: 'Access',
  labelEvent: 'Event',
  labelKind: 'Starts on',
  labelCollection: 'Collection',
  labelChanges: 'Row changes',
  labelRuns: 'Runs',
  labelFunction: 'Function',
  labelCollections: 'Tables',
  labelWho: 'For',
  labelAllowed: 'Allowed',
  labelEnabled: 'State',
  labelJob: 'Scheduled job',
  labelSchedule: 'Schedule',
  labelTimezone: 'Time zone',
  labelChange: 'Change',
  labelTable: 'Table',
  labelField: 'Field',
  labelRows: 'Rows',
  labelKeptDays: 'Copy kept (days)',
  labelRestores: 'Restores',
  labelAction: 'Action',
  labelDestination: 'Destination',
  labelChangedBy: 'Changed by',
  labelInput: 'Input',
  labelPublicTables: 'Copied tables',
  labelPublicActions: 'Visitors can send',
  labelPublicRuns: 'Each submission starts',
  labelPublicRunsOff: 'Would also start once turned on',
  labelEmbedSites: 'Sites that may embed it',
  labelPublicLink: 'Public link',
  labelPublishedBy: 'Published by',
  wordRead: 'Read only',
  wordReadSend: 'Read and send data',
  wordNoAccess: 'No access',
  publicPublish: 'Publish to a public link',
  publicRefresh: 'Copy its data again',
  publicUnpublish: 'Unpublish',
  eventWebhook: 'Its webhook',
  eventRows: 'Changes to its rows',
  switchOn: 'On',
  switchOff: 'Off',
  switchPaused: 'Paused',
  changeInsert: 'Added',
  changeUpdate: 'Changed',
  changeDelete: 'Deleted',
  wordDropField: 'Drop a field’s values',
  wordDropTable: 'Drop a table',
  tableClearValues: 'Clear values that break a stricter rule',
  tableRestore: 'Restore from its copy',
  whoOwner: 'Your own runs',
  whoEveryone: 'Everyone who uses it',
  answerYes: 'Yes',
  answerNo: 'No',
};

export type AppApprovalsCopyKey = keyof typeof en;
export type AppApprovalsCopy = Readonly<Record<AppApprovalsCopyKey, string>>;

const copy: NamespaceCatalog<keyof typeof en> = {
  bg: {
    kindWrite: 'Промяна в приложението за одобрение',
    kindGrant: 'Достъп до приложението за одобрение',
    kindBusiness: 'Одобрение за приложение',
    approve: 'Одобряване',
    reject: 'Отхвърляне',
    working: 'Изпълнение…',
    checkStatus: 'Проверка на състоянието',
    statusStillPending:
      'Poneva все още не е приключил с това. Възможно е процесът да продължава, затова дотогава не може да се вземе друго решение.',
    statusStillUnknown:
      'Не успяхме да получим отговор. Докато това не бъде изяснено, не може да се вземе друго решение. Опитайте отново след малко.',
    loading: 'Зареждане…',
    unavailable: 'Това не е достъпно в момента.',
    loadFailed: 'Не успяхме да заредим това. Опитайте отново след малко.',
    requestedBy: 'Поискано от {name}',
    originApp: 'От приложението',
    originAgent: 'От Агент',
    originClient: 'Чрез {client}',
    originFor: 'за {name}',
    changedUnderYou:
      'Това се промени, докато го преглеждахте. Това, което виждате сега, е текущата версия. Не е взето решение.',
    notAllowed: 'Не можете да вземете решение за това.',
    cannotDecideNow: 'В момента не може да се вземе решение за това. Нищо не е изпратено.',
    expired: 'Тази заявка е изтекла. Нищо не е променено.',
    stale:
      'Данните са се променили, откакто беше отправена тази заявка. Поискайте отново, за да получите нова заявка.',
    approved: 'Одобрено',
    rejected: 'Отхвърлено',
    needsYou: 'Изисква одобрение',
    waitingOn: 'Изчаква {name}',
    unknownTitle: 'Не можем да потвърдим какво се е случило',
    unknownBody:
      'Промяната може да е извършена, а може и да не е. Не я повтаряйте. Проверете състоянието или помолете собственика на приложението да провери.',
    blockedApproval: 'Изчаква това одобрение, преди да продължи.',
    blockedBudget: 'На пауза: бюджетът за AI на това приложение е изчерпан.',
    blockedProvider: 'На пауза: свързана услуга не отговаря.',
    blockedViewer:
      'На пауза: това трябва да се изпълни като влязъл зрител, но няма свързан зрител. Вместо това няма да се използва достъпът на собственика.',
    writeTitle: 'Одобряване на промените по {target}',
    writeSummaryAll: '{count} промени в {app}',
    rowCreate: 'Ново',
    rowUpdate: 'Променено',
    rowDelete: 'Премахнато',
    rowInclude: 'Включване на тази промяна',
    rowExcluded: 'Пропуснато',
    fieldBefore: 'Преди',
    fieldAfter: 'След',
    fieldEmpty: 'Празно',
    approveSome: 'Одобряване на {included} от {total}',
    approveAll: 'Одобряване на всичките {total}',
    allLeftOut:
      'Всички промени са пропуснати. Отхвърлете, за да спрете, или включете промяна, за да я одобрите.',
    appliedSome: 'Извършени са {count} промени.',
    rejectedBody: 'Нищо не е променено.',
    noRows: 'Тук няма нищо за одобряване.',
    grantTitle: 'Разрешаване на {app} да използва {source}',
    grantRead: 'Само за четене',
    grantWrite: 'Четене и промяна',
    audienceOwner: 'Само вие ще виждате данните на това приложение от {source}.',
    audiencePeople:
      'Хората на брой {count}, с които е споделено това приложение, ще виждат данни от {source}.',
    audienceCompany: 'Всеки в компанията, който отвори приложението, ще вижда данни от {source}.',
    grantRouted: '{owner} взема това решение, затова беше изпратено до него.',
    grantRoutedUnknown:
      'Собственикът на тази връзка взема това решение, затова беше изпратено до него.',
    grantWhat: 'Достъп',
    businessStep: 'Стъпка {order}',
    stepWaiting: 'Изчакване',
    stepPending: 'Изисква решение',
    stepApproved: 'Одобрено',
    stepRejected: 'Отхвърлено',
    stepYou: 'Вие',
    businessNote: 'Одобряването тук не разрешава нови промени извън Poneva.',
    businessYourTurn: 'Ваш ред е да вземете решение.',
    businessNotYourTurn: 'Изчаква първо {name} да вземе решение.',
    policyTitle: 'Как приложенията използват тази връзка',
    policyCreator: 'Само връзката на създателя',
    policyCreatorBody:
      'Всички виждат едни и същи данни чрез връзката на създателя на приложението.',
    policyViewer: 'Само личната връзка на зрителя',
    policyViewerBody: 'Всеки вижда собствените си данни чрез собствената си връзка.',
    policyBoth: 'И двете',
    policyBothBody: 'Създателят на приложението избира за всяко приложение.',
    policyRevoked: 'Приложенията не могат да използват тази връзка',
    policyRevokedBody:
      'Изберете опция, за да разрешите използването ѝ отново. Нищо няма да бъде избрано вместо вас.',
    policySave: 'Запазване',
    policySaving: 'Запазване…',
    policySaved: 'Запазено.',
    policyReadOnly: 'Само администратор на компанията може да промени това.',
    policyConflict: 'Някой друг промени това първи. Това е текущата настройка.',
    policyRefused: 'Това не можа да бъде запазено.',
    policyFailed:
      'Не успяхме да потвърдим, че е запазено. Презаредете, за да видите текущата настройка, преди да опитате отново.',
    policyWidened:
      'Вече публикуваните приложения запазват начина, по който се свързват днес. Създателите им трябва да ги публикуват отново, за да използват новата опция.',
    policyNarrowed: 'Приложенията проверяват текущата настройка при всяко свързване.',
    policyAffected: '{count} публикувани приложения използват тази връзка (към {when}).',
    policyAffectedOne: '1 публикувано приложение използва тази връзка (към {when}).',
    policyAffectsNames: 'Засяга {count} публикувани приложения: {names}.',
    policyAffectsOneName: 'Засяга 1 публикувано приложение: {names}.',
    policyGenericWiden:
      'Публикуваните приложения, които използват тази връзка, запазват начина, по който се свързват днес. Създателите им трябва да ги публикуват отново, за да използват новата опция.',
    policyGenericNarrow:
      'Публикуваните приложения, които използват тази връзка, проверяват текущата настройка при всяко свързване.',
    policyAffectedGeneric:
      'Публикуваните приложения, които използват тази връзка, не се променят автоматично. Създателите им трябва да ги публикуват отново.',
    policyUnavailable: 'Тази настройка не е налична в момента.',
    outsideRead: 'Разрешаване на {app} да чете от {hosts}',
    outsideReadSend: 'Разрешаване на {app} да чете от {hosts} и да изпраща данни към тях',
    outsideNone: 'Забраняване на {app} да достига {hosts}',
    outsideReadDone: '{app} може да чете от {hosts}',
    outsideReadSendDone: '{app} може да чете от {hosts} и да изпраща данни към тях',
    outsideNoneDone: '{app} вече не достига {hosts}',
    outsideMore: 'още {count}',
    eventWebhookOn: 'Разрешаване на {app} да получава „{event}“ на своя webhook',
    eventWebhookOnDone: '{app} получава „{event}“ на своя webhook',
    eventWebhookOff: 'Спиране на {app} да получава „{event}“ на своя webhook',
    eventWebhookOffDone: '{app} вече не получава „{event}“',
    eventRowsOn:
      'Разрешаване на {app} да изпълнява {runs}, когато редове в {collection} бъдат {changes}',
    eventRowsOnDone: '{app} изпълнява {runs}, когато редове в {collection} бъдат {changes}',
    eventRowsOff: 'Спиране на {app} да изпълнява {runs}, когато редове в {collection} се променят',
    eventRowsOffDone: '{app} вече не изпълнява {runs}, когато редове в {collection} се променят',
    eventInsert: 'добавени',
    eventUpdate: 'променени',
    eventDelete: 'изтрити',
    scheduleTurnOn: 'Включване на графика на „{job}“ в {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Графикът на „{job}“ в {app} е включен. {cadence} ({timezone}).',
    scheduleResume: 'Възобновяване на графика на „{job}“ в {app}. {cadence} ({timezone}).',
    scheduleResumeDone: 'Графикът на „{job}“ в {app} отново е включен. {cadence} ({timezone}).',
    scheduleReschedule: 'Промяна на графика на „{job}“ в {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone: 'Графикът на „{job}“ в {app} е променен. {cadence} ({timezone}).',
    schedulePause: 'Поставяне на графика на „{job}“ в {app} на пауза.',
    schedulePauseDone: 'Графикът на „{job}“ в {app} е на пауза.',
    scheduleNext: 'Следващи изпълнения: {runs}.',
    scheduleResumeSaved:
      'Възобновяване на графика на „{job}“ в {app} с последната запазена версия. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Графикът на „{job}“ в {app} отново е включен и изпълнява последната запазена версия. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Възобновяване на графика на „{job}“ в {app} с публикуваната версия. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Графикът на „{job}“ в {app} отново е включен и изпълнява публикуваната версия. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Промяна на графика на „{job}“ в {app}. {cadence} ({timezone}). Той ще бъде на пауза, докато не го възобновите.',
    scheduleReschedulePausedDone:
      'Графикът на „{job}“ в {app} е променен. {cadence} ({timezone}). Той е на пауза, докато не го възобновите.',
    scheduleSwitchSaved:
      'Превключване на графика на „{job}“ в {app} към последната запазена версия. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Графикът на „{job}“ в {app} вече изпълнява последната запазена версия. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Превключване на графика на „{job}“ в {app} към последната запазена версия. {cadence} ({timezone}). Той ще бъде на пауза, докато не го възобновите.',
    scheduleSwitchSavedPausedDone:
      'Графикът на „{job}“ в {app} вече използва последната запазена версия. {cadence} ({timezone}). Той е на пауза, докато не го възобновите.',
    scheduleSwitchPublished:
      'Превключване на графика на „{job}“ в {app} към публикуваната версия. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Графикът на „{job}“ в {app} вече изпълнява публикуваната версия. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Превключване на графика на „{job}“ в {app} към публикуваната версия. {cadence} ({timezone}). Той ще бъде на пауза, докато не го възобновите.',
    scheduleSwitchPublishedPausedDone:
      'Графикът на „{job}“ в {app} вече използва публикуваната версия. {cadence} ({timezone}). Той е на пауза, докато не го възобновите.',
    dataWritesOn:
      'Разрешаване на „{function}“ в {app} да {verbs} собствените редове на приложението',
    dataWritesOnDone: '„{function}“ в {app} може да {verbs} собствените редове на приложението',
    dataWritesOff: 'Спиране на „{function}“ в {app} да променя собствените редове на приложението',
    dataWritesOffDone: '„{function}“ в {app} вече не променя собствените редове на приложението',
    dataWriteInsert: 'добавя',
    dataWriteUpdate: 'променя',
    dataWriteDelete: 'изтрива',
    tableDropField: 'Разрешаване на {app} да премахне полето „{field}“ от {table} ({rows})',
    tableDropFieldDone: '{app} премахна полето „{field}“ от {table} ({rows})',
    tableDropTable: 'Разрешаване на {app} да премахне таблицата {table} ({rows})',
    tableDropTableDone: '{app} премахна таблицата {table} ({rows})',
    tableClear:
      'Разрешаване на {app} да изчисти „{field}“ в {table}, където нарушава новото правило ({rows})',
    tableClearDone: '{app} изчисти „{field}“ в {table}, където нарушаваше новото правило ({rows})',
    tableRestoreField: 'Разрешаване на {app} да върне „{field}“ в {table} от копието ({rows})',
    tableRestoreFieldDone: '{app} върна „{field}“ в {table} ({rows})',
    tableRestoreTable: 'Разрешаване на {app} да възстанови таблицата {table} от копието ({rows})',
    tableRestoreTableDone: '{app} възстанови таблицата {table} ({rows})',
    tableRowsLoseOne: '1 ред губи стойност',
    tableRowsLoseMany: '{count} реда губят стойност',
    tableRowsLostOne: '1 ред загуби стойност',
    tableRowsLostMany: '{count} реда загубиха стойност',
    tableRowsOne: '1 ред',
    tableRowsMany: '{count} реда',
    connectedWriteOn: 'Разрешаване на {app} да {action} в {destination}',
    connectedWriteOnDone: '{app} може да {action} в {destination}',
    connectedWriteOff: 'Спиране на {app} да записва в {destination}',
    connectedWriteOffDone: '{app} вече не записва в {destination}',
    codeConfirm: 'Разрешаване на {app} да използва вашите одобрения с промените от {names}',
    codeConfirmDone: '{app} използва вашите одобрения с промените от {names}',
    codeConfirmCurrent: 'Разрешаване на {app} да използва вашите одобрения с текущия си код',
    codeConfirmCurrentDone: '{app} използва вашите одобрения с текущия си код',
    runChanges: 'Изпълнение на „{function}“ в {app} сега',
    runChangesDone: '„{function}“ беше изпълнена в {app}',
    continueApproved: 'Одобрено. Продължи: изпълни го и провери дали работи.',
    continueDeclined: 'Отхвърлено. Продължи без него.',
    continueMixed: 'Взех решение по заявките. Продължи.',
    typeOutsideAccess: 'Разрешаване на достъп на приложение до външни услуги',
    typeEvent: 'Включване или изключване на събитие на приложение',
    typeSchedule: 'Включване, пауза или промяна на график на приложение',
    typeDataWrites: 'Разрешаване на функция на приложение да променя собствените си редове',
    typeTableChange: 'Изтриване или възстановяване на данни в таблиците на приложение',
    typeConnectedWrite: 'Разрешаване на приложение да записва в система на фирмата',
    typeConfirmCode: 'Разрешаване на променения код на приложението да използва вашите одобрения',
    typeRunChanges: 'Изпълнение на функция на приложение, която променя данни',
    typePublicLink: 'Промяна на публичната връзка на приложение',
    typeUnpublish: 'Премахване на публичната връзка на приложение',
    labelHosts: 'Услуги',
    labelAccess: 'Достъп',
    labelEvent: 'Събитие',
    labelKind: 'Стартира при',
    labelCollection: 'Колекция',
    labelChanges: 'Промени в редовете',
    labelRuns: 'Изпълнения',
    labelFunction: 'Функция',
    labelCollections: 'Таблици',
    labelWho: 'За',
    labelAllowed: 'Разрешено',
    labelEnabled: 'Състояние',
    labelJob: 'Планирана задача',
    labelSchedule: 'График',
    labelTimezone: 'Часова зона',
    labelChange: 'Промяна',
    labelTable: 'Таблица',
    labelField: 'Поле',
    labelRows: 'Редове',
    labelKeptDays: 'Копието се пази (дни)',
    labelRestores: 'Възстановява',
    labelAction: 'Действие',
    labelDestination: 'Цел',
    labelChangedBy: 'Променено от',
    labelInput: 'Вход',
    labelPublicTables: 'Копирани таблици',
    labelPublicActions: 'Посетителите могат да изпращат',
    labelPublicRuns: 'Всяко изпращане стартира',
    labelPublicRunsOff: 'Би стартирало и след включване',
    labelEmbedSites: 'Сайтове, които могат да го вграждат',
    labelPublicLink: 'Публична връзка',
    labelPublishedBy: 'Публикувано от',
    wordRead: 'Само за четене',
    wordReadSend: 'Четене и изпращане на данни',
    wordNoAccess: 'Без достъп',
    publicPublish: 'Публикуване с публична връзка',
    publicRefresh: 'Копиране на данните му отново',
    publicUnpublish: 'Отмяна на публикуването',
    eventWebhook: 'Неговият уебхук',
    eventRows: 'Промени в неговите редове',
    switchOn: 'Включено',
    switchOff: 'Изключено',
    switchPaused: 'На пауза',
    changeInsert: 'Добавено',
    changeUpdate: 'Променено',
    changeDelete: 'Изтрито',
    wordDropField: 'Изтриване на стойностите на поле',
    wordDropTable: 'Изтриване на таблица',
    tableClearValues: 'Изчистване на стойности, които нарушават по-строго правило',
    tableRestore: 'Възстановяване от копието му',
    whoOwner: 'Вашите собствени изпълнения',
    whoEveryone: 'Всеки, който го използва',
    answerYes: 'Да',
    answerNo: 'Не',
  },
  hr: {
    kindWrite: 'Promjena aplikacije za odobrenje',
    kindGrant: 'Pristup aplikacije za odobrenje',
    kindBusiness: 'Odobrenje za aplikaciju',
    approve: 'Odobri',
    reject: 'Odbij',
    working: 'Obrada…',
    checkStatus: 'Provjeri status',
    statusStillPending:
      'Poneva ovo još nije riješio. Možda je još u obradi, pa se ništa drugo ne može odlučiti dok se ovo ne riješi.',
    statusStillUnknown:
      'Nismo mogli dobiti odgovor. Ništa se drugo ne može odlučiti dok se ovo ne riješi. Pokušajte ponovno za trenutak.',
    loading: 'Učitavanje…',
    unavailable: 'Ovo trenutačno nije dostupno.',
    loadFailed: 'Nismo mogli učitati ovo. Pokušajte ponovno za trenutak.',
    requestedBy: 'Zatražio/la {name}',
    originApp: 'Iz aplikacije',
    originAgent: 'Od agenta',
    originClient: 'Putem {client}',
    originFor: 'za {name}',
    changedUnderYou:
      'Ovo se promijenilo dok ste čitali. Sada vidite trenutačnu verziju. Ništa nije odlučeno.',
    notAllowed: 'Ne možete odlučiti o ovome.',
    cannotDecideNow: 'O ovome se trenutačno ne može odlučiti. Ništa nije poslano.',
    expired: 'Ovaj je zahtjev istekao. Ništa se nije promijenilo.',
    stale:
      'Podaci su se promijenili otkad je ovo zatraženo. Zatražite ponovno da biste dobili novi zahtjev.',
    approved: 'Odobreno',
    rejected: 'Odbijeno',
    needsYou: 'Potrebno je vaše odobrenje',
    waitingOn: 'Čeka se na {name}',
    unknownTitle: 'Ne možemo potvrditi što se dogodilo',
    unknownBody:
      'Promjena je možda izvršena, a možda i nije. Nemojte je ponavljati. Provjerite status ili zamolite vlasnika aplikacije da provjeri.',
    blockedApproval: 'Čeka se ovo odobrenje prije nastavka.',
    blockedBudget: 'Pauzirano: AI proračun za ovu aplikaciju je potrošen.',
    blockedProvider: 'Pauzirano: povezana usluga ne odgovara.',
    blockedViewer:
      'Pauzirano: ovo se mora pokrenuti kao prijavljeni gledatelj, a nijedan gledatelj nije povezan. Ništa se ne pokreće s pristupom vlasnika.',
    writeTitle: 'Odobri promjene za {target}',
    writeSummaryAll: '{count} promjena u aplikaciji {app}',
    rowCreate: 'Novo',
    rowUpdate: 'Promijenjeno',
    rowDelete: 'Uklonjeno',
    rowInclude: 'Uključi ovu promjenu',
    rowExcluded: 'Izostavljeno',
    fieldBefore: 'Prije',
    fieldAfter: 'Poslije',
    fieldEmpty: 'Prazno',
    approveSome: 'Odobri {included} od {total}',
    approveAll: 'Odobri svih {total}',
    allLeftOut:
      'Sve su promjene izostavljene. Odbijte ih da biste zaustavili postupak ili uključite promjenu da biste je odobrili.',
    appliedSome: 'Izvršeno je {count} promjena.',
    rejectedBody: 'Ništa se nije promijenilo.',
    noRows: 'Ovdje nema ničega za odobriti.',
    grantTitle: 'Dopusti aplikaciji {app} upotrebu izvora {source}',
    grantRead: 'Samo za čitanje',
    grantWrite: 'Čitanje i izmjene',
    audienceOwner: 'Samo ćete vi vidjeti podatke ove aplikacije iz izvora {source}.',
    audiencePeople:
      '{count} osoba s kojima se ova aplikacija dijeli vidjet će podatke iz izvora {source}.',
    audienceCompany: 'Svi u tvrtki koji otvore aplikaciju vidjet će podatke iz izvora {source}.',
    grantRouted: 'O ovome odlučuje {owner}, pa je poslano toj osobi.',
    grantRoutedUnknown: 'O ovome odlučuje vlasnik ove veze, pa je poslano toj osobi.',
    grantWhat: 'Pristup',
    businessStep: 'Korak {order}',
    stepWaiting: 'Čeka se',
    stepPending: 'Potrebna je odluka',
    stepApproved: 'Odobreno',
    stepRejected: 'Odbijeno',
    stepYou: 'Vi',
    businessNote: 'Odobrenje ovdje ne dopušta nove promjene izvan Poneve.',
    businessYourTurn: 'Sada ste vi na redu za odluku.',
    businessNotYourTurn: 'Čeka se da {name} prvi odluči.',
    policyTitle: 'Kako aplikacije upotrebljavaju ovu vezu',
    policyCreator: 'Samo veza kreatora',
    policyCreatorBody: 'Svi vide iste podatke putem veze kreatora aplikacije.',
    policyViewer: 'Samo vlastita veza gledatelja',
    policyViewerBody: 'Svaka osoba vidi vlastite podatke putem vlastite veze.',
    policyBoth: 'Oboje',
    policyBothBody: 'Kreator aplikacije odabire za svaku aplikaciju.',
    policyRevoked: 'Aplikacije ne mogu upotrebljavati ovu vezu',
    policyRevokedBody:
      'Odaberite opciju da biste ponovno omogućili upotrebu. Ništa nije odabrano umjesto vas.',
    policySave: 'Spremi',
    policySaving: 'Spremanje…',
    policySaved: 'Spremljeno.',
    policyReadOnly: 'Samo administrator tvrtke može ovo promijeniti.',
    policyConflict: 'Netko drugi ovo je prvi promijenio. Ovo je trenutačna postavka.',
    policyRefused: 'Ovo nije moguće spremiti.',
    policyFailed:
      'Nismo mogli potvrditi da je spremljeno. Ponovno učitajte stranicu da biste vidjeli trenutačnu postavku prije nego što pokušate ponovno.',
    policyWidened:
      'Već objavljene aplikacije i dalje se povezuju na dosadašnji način. Njihovi autori trebaju ponovno objaviti aplikacije kako bi upotrebljavali novu opciju.',
    policyNarrowed: 'Aplikacije provjeravaju trenutačnu postavku svaki put kad se povežu.',
    policyAffected: '{count} objavljenih aplikacija upotrebljava ovu vezu (na dan {when}).',
    policyAffectedOne: '1 objavljena aplikacija upotrebljava ovu vezu (na dan {when}).',
    policyAffectsNames: 'Utječe na {count} objavljenih aplikacija: {names}.',
    policyAffectsOneName: 'Utječe na 1 objavljenu aplikaciju: {names}.',
    policyGenericWiden:
      'Objavljene aplikacije koje upotrebljavaju ovu vezu i dalje se povezuju na dosadašnji način. Njihovi autori trebaju ponovno objaviti aplikacije kako bi upotrebljavali novu opciju.',
    policyGenericNarrow:
      'Objavljene aplikacije koje upotrebljavaju ovu vezu provjeravaju trenutačnu postavku svaki put kad se povežu.',
    policyAffectedGeneric:
      'Objavljene aplikacije koje upotrebljavaju ovu vezu neće se automatski promijeniti. Njihovi autori trebaju ponovno objaviti aplikacije.',
    policyUnavailable: 'Ova postavka trenutačno nije dostupna.',
    outsideRead: 'Dopusti aplikaciji {app} čitanje s: {hosts}',
    outsideReadSend: 'Dopusti aplikaciji {app} čitanje s i slanje podataka na: {hosts}',
    outsideNone: 'Spriječi aplikaciju {app} u pristupu: {hosts}',
    outsideReadDone: 'Aplikacija {app} može čitati s: {hosts}',
    outsideReadSendDone: 'Aplikacija {app} može čitati s i slati podatke na: {hosts}',
    outsideNoneDone: 'Aplikacija {app} više nema pristup: {hosts}',
    outsideMore: 'još {count}',
    eventWebhookOn: 'Dopusti aplikaciji {app} primanje „{event}“ na njezinu webhooku',
    eventWebhookOnDone: 'Aplikacija {app} prima „{event}“ na svojem webhooku',
    eventWebhookOff: 'Spriječi aplikaciju {app} u primanju „{event}“ na njezinu webhooku',
    eventWebhookOffDone: 'Aplikacija {app} više ne prima „{event}“',
    eventRowsOn:
      'Dopusti aplikaciji {app} pokretanje: {runs}, kad su redci u tablici {collection} {changes}',
    eventRowsOnDone:
      'Aplikacija {app} pokreće: {runs}, kad su redci u tablici {collection} {changes}',
    eventRowsOff:
      'Spriječi aplikaciju {app} u pokretanju: {runs}, kad se redci u tablici {collection} promijene',
    eventRowsOffDone:
      'Aplikacija {app} više ne pokreće: {runs}, kad se redci u tablici {collection} promijene',
    eventInsert: 'dodani',
    eventUpdate: 'promijenjeni',
    eventDelete: 'izbrisani',
    scheduleTurnOn: 'Uključi raspored za „{job}“ u aplikaciji {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone:
      'Raspored za „{job}“ u aplikaciji {app} je uključen. {cadence} ({timezone}).',
    scheduleResume: 'Nastavi raspored za „{job}“ u aplikaciji {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Raspored za „{job}“ u aplikaciji {app} ponovno je uključen. {cadence} ({timezone}).',
    scheduleReschedule: 'Promijeni raspored za „{job}“ u aplikaciji {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Raspored za „{job}“ u aplikaciji {app} je promijenjen. {cadence} ({timezone}).',
    schedulePause: 'Pauziraj raspored za „{job}“ u aplikaciji {app}.',
    schedulePauseDone: 'Raspored za „{job}“ u aplikaciji {app} je pauziran.',
    scheduleNext: 'Sljedeća pokretanja: {runs}.',
    scheduleResumeSaved:
      'Nastavi raspored za „{job}“ u aplikaciji {app} s najnovijom spremljenom verzijom. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Raspored za „{job}“ u aplikaciji {app} ponovno je uključen i pokreće najnoviju spremljenu verziju. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Nastavi raspored za „{job}“ u aplikaciji {app} s objavljenom verzijom. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Raspored za „{job}“ u aplikaciji {app} ponovno je uključen i pokreće objavljenu verziju. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Promijeni raspored za „{job}“ u aplikaciji {app}. {cadence} ({timezone}). Bit će pauziran dok ga ne nastavite.',
    scheduleReschedulePausedDone:
      'Raspored za „{job}“ u aplikaciji {app} je promijenjen. {cadence} ({timezone}). Pauziran je dok ga ne nastavite.',
    scheduleSwitchSaved:
      'Prebaci raspored za „{job}“ u aplikaciji {app} na najnoviju spremljenu verziju. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Raspored za „{job}“ u aplikaciji {app} sada pokreće najnoviju spremljenu verziju. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Prebaci raspored za „{job}“ u aplikaciji {app} na najnoviju spremljenu verziju. {cadence} ({timezone}). Bit će pauziran dok ga ne nastavite.',
    scheduleSwitchSavedPausedDone:
      'Raspored za „{job}“ u aplikaciji {app} sada koristi najnoviju spremljenu verziju. {cadence} ({timezone}). Pauziran je dok ga ne nastavite.',
    scheduleSwitchPublished:
      'Prebaci raspored za „{job}“ u aplikaciji {app} na objavljenu verziju. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Raspored za „{job}“ u aplikaciji {app} sada pokreće objavljenu verziju. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Prebaci raspored za „{job}“ u aplikaciji {app} na objavljenu verziju. {cadence} ({timezone}). Bit će pauziran dok ga ne nastavite.',
    scheduleSwitchPublishedPausedDone:
      'Raspored za „{job}“ u aplikaciji {app} sada koristi objavljenu verziju. {cadence} ({timezone}). Pauziran je dok ga ne nastavite.',
    dataWritesOn: 'Dopusti funkciji „{function}“ aplikacije {app} {verbs} vlastite retke',
    dataWritesOnDone: 'Funkcija „{function}“ aplikacije {app} može {verbs} vlastite retke',
    dataWritesOff: 'Spriječi funkciju „{function}“ aplikacije {app} u mijenjanju vlastitih redaka',
    dataWritesOffDone: 'Funkcija „{function}“ aplikacije {app} više ne mijenja vlastite retke',
    dataWriteInsert: 'dodavati',
    dataWriteUpdate: 'mijenjati',
    dataWriteDelete: 'brisati',
    tableDropField:
      'Dopusti aplikaciji {app} uklanjanje polja „{field}“ iz tablice {table} ({rows})',
    tableDropFieldDone: 'Aplikacija {app} uklonila je polje „{field}“ iz tablice {table} ({rows})',
    tableDropTable: 'Dopusti aplikaciji {app} uklanjanje tablice {table} ({rows})',
    tableDropTableDone: 'Aplikacija {app} uklonila je tablicu {table} ({rows})',
    tableClear:
      'Dopusti aplikaciji {app} pražnjenje polja „{field}“ u tablici {table} gdje vrijednost krši novo pravilo ({rows})',
    tableClearDone:
      'Aplikacija {app} ispraznila je polje „{field}“ u tablici {table} gdje je vrijednost kršila novo pravilo ({rows})',
    tableRestoreField:
      'Dopusti aplikaciji {app} vraćanje polja „{field}“ u tablici {table} iz kopije ({rows})',
    tableRestoreFieldDone: 'Aplikacija {app} vratila je polje „{field}“ u tablici {table} ({rows})',
    tableRestoreTable: 'Dopusti aplikaciji {app} vraćanje tablice {table} iz kopije ({rows})',
    tableRestoreTableDone: 'Aplikacija {app} vratila je tablicu {table} ({rows})',
    tableRowsLoseOne: '1 redak gubi vrijednost',
    tableRowsLoseMany: 'Redaka koji gube vrijednost: {count}',
    tableRowsLostOne: '1 redak izgubio je vrijednost',
    tableRowsLostMany: 'Redaka koji su izgubili vrijednost: {count}',
    tableRowsOne: '1 redak',
    tableRowsMany: 'Redaka: {count}',
    connectedWriteOn: 'Dopusti aplikaciji {app} {action} u: {destination}',
    connectedWriteOnDone: 'Aplikacija {app} može {action} u: {destination}',
    connectedWriteOff: 'Spriječi aplikaciju {app} u pisanju u: {destination}',
    connectedWriteOffDone: 'Aplikacija {app} više ne piše u: {destination}',
    codeConfirm:
      'Dopusti aplikaciji {app} upotrebu vaših odobrenja uz promjene koje su napravili: {names}',
    codeConfirmDone:
      'Aplikacija {app} upotrebljava vaša odobrenja uz promjene koje su napravili: {names}',
    codeConfirmCurrent:
      'Dopusti aplikaciji {app} upotrebu vaših odobrenja s njezinim trenutačnim kodom',
    codeConfirmCurrentDone:
      'Aplikacija {app} upotrebljava vaša odobrenja sa svojim trenutačnim kodom',
    runChanges: 'Pokreni sada funkciju „{function}“ u aplikaciji {app}',
    runChangesDone: 'Funkcija „{function}“ pokrenuta je u aplikaciji {app}',
    continueApproved: 'Odobreno. Nastavi: pokreni to i provjeri radi li.',
    continueDeclined: 'Odbijeno. Nastavi bez toga.',
    continueMixed: 'O zahtjevima je odlučeno. Nastavi.',
    typeOutsideAccess: 'Dopusti aplikaciji pristup vanjskim uslugama',
    typeEvent: 'Uključi ili isključi događaj aplikacije',
    typeSchedule: 'Uključi, pauziraj ili promijeni raspored aplikacije',
    typeDataWrites: 'Dopusti funkciji aplikacije da mijenja vlastite retke',
    typeTableChange: 'Izbriši ili vrati podatke u tablicama aplikacije',
    typeConnectedWrite: 'Dopusti aplikaciji pisanje u sustav tvrtke',
    typeConfirmCode: 'Dopusti izmijenjenom kodu aplikacije da koristi vaša odobrenja',
    typeRunChanges: 'Pokreni funkciju aplikacije koja mijenja podatke',
    typePublicLink: 'Promijeni javnu poveznicu aplikacije',
    typeUnpublish: 'Ukloni javnu poveznicu aplikacije',
    labelHosts: 'Usluge',
    labelAccess: 'Pristup',
    labelEvent: 'Događaj',
    labelKind: 'Pokreće se pri',
    labelCollection: 'Zbirka',
    labelChanges: 'Promjene redaka',
    labelRuns: 'Pokretanja',
    labelFunction: 'Funkcija',
    labelCollections: 'Tablice',
    labelWho: 'Za',
    labelAllowed: 'Dopušteno',
    labelEnabled: 'Stanje',
    labelJob: 'Zakazani zadatak',
    labelSchedule: 'Raspored',
    labelTimezone: 'Vremenska zona',
    labelChange: 'Promjena',
    labelTable: 'Tablica',
    labelField: 'Polje',
    labelRows: 'Reci',
    labelKeptDays: 'Kopija se čuva (dana)',
    labelRestores: 'Vraća',
    labelAction: 'Radnja',
    labelDestination: 'Odredište',
    labelChangedBy: 'Promijenio',
    labelInput: 'Ulaz',
    labelPublicTables: 'Kopirane tablice',
    labelPublicActions: 'Posjetitelji mogu slati',
    labelPublicRuns: 'Svako slanje pokreće',
    labelPublicRunsOff: 'Pokrenulo bi se i nakon uključivanja',
    labelEmbedSites: 'Web-stranice koje je smiju ugraditi',
    labelPublicLink: 'Javna poveznica',
    labelPublishedBy: 'Objavio',
    wordRead: 'Samo čitanje',
    wordReadSend: 'Čitanje i slanje podataka',
    wordNoAccess: 'Bez pristupa',
    publicPublish: 'Objavi na javnoj poveznici',
    publicRefresh: 'Ponovno kopiraj njezine podatke',
    publicUnpublish: 'Povuci objavu',
    eventWebhook: 'Njezin webhook',
    eventRows: 'Promjene njezinih redaka',
    switchOn: 'Uključeno',
    switchOff: 'Isključeno',
    switchPaused: 'Pauzirano',
    changeInsert: 'Dodano',
    changeUpdate: 'Promijenjeno',
    changeDelete: 'Izbrisano',
    wordDropField: 'Izbriši vrijednosti polja',
    wordDropTable: 'Izbriši tablicu',
    tableClearValues: 'Očisti vrijednosti koje krše strože pravilo',
    tableRestore: 'Vrati iz njezine kopije',
    whoOwner: 'Vaša vlastita pokretanja',
    whoEveryone: 'Svi koji je koriste',
    answerYes: 'Da',
    answerNo: 'Ne',
  },
  cs: {
    kindWrite: 'Změna aplikace ke schválení',
    kindGrant: 'Přístup aplikace ke schválení',
    kindBusiness: 'Schválení aplikace',
    approve: 'Schválit',
    reject: 'Zamítnout',
    working: 'Pracuji…',
    checkStatus: 'Zkontrolovat stav',
    statusStillPending:
      'Poneva to zatím nevyřešila. Stále se to může zpracovávat, takže dokud se to nevyřeší, nelze rozhodnout o ničem dalším.',
    statusStillUnknown:
      'Nepodařilo se získat odpověď. Dokud se to nevyřeší, nelze rozhodnout o ničem dalším. Zkuste to za chvíli znovu.',
    loading: 'Načítání…',
    unavailable: 'Tato možnost momentálně není dostupná.',
    loadFailed: 'Toto se nepodařilo načíst. Zkuste to za chvíli znovu.',
    requestedBy: 'Požaduje {name}',
    originApp: 'Z aplikace',
    originAgent: 'Od agenta',
    originClient: 'Přes {client}',
    originFor: 'pro {name}',
    changedUnderYou:
      'Toto se během vašeho čtení změnilo. Nyní vidíte aktuální verzi. O ničem nebylo rozhodnuto.',
    notAllowed: 'O tomto nemůžete rozhodnout.',
    cannotDecideNow: 'O tomto nyní nelze rozhodnout. Nic nebylo odesláno.',
    expired: 'Platnost této žádosti vypršela. Nic se nezměnilo.',
    stale: 'Data se od vytvoření této žádosti změnila. Požádejte znovu o aktuální žádost.',
    approved: 'Schváleno',
    rejected: 'Zamítnuto',
    needsYou: 'Vyžaduje schválení',
    waitingOn: 'Čeká se na {name}',
    unknownTitle: 'Nemůžeme potvrdit, co se stalo',
    unknownBody:
      'Změna mohla, ale nemusela být provedena. Neopakujte ji. Zkontrolujte stav nebo požádejte vlastníka aplikace, aby ho ověřil.',
    blockedApproval: 'Před pokračováním se čeká na toto schválení.',
    blockedBudget: 'Pozastaveno: rozpočet AI pro tuto aplikaci je vyčerpán.',
    blockedProvider: 'Pozastaveno: připojená služba neodpovídá.',
    blockedViewer:
      'Pozastaveno: tato akce musí běžet jako přihlášený prohlížející a žádný není připojen. Místo toho se nic nespustí s přístupem vlastníka.',
    writeTitle: 'Schválit změny v {target}',
    writeSummaryAll: '{count} změn v {app}',
    rowCreate: 'Nové',
    rowUpdate: 'Změněné',
    rowDelete: 'Odstraněné',
    rowInclude: 'Zahrnout tuto změnu',
    rowExcluded: 'Vynecháno',
    fieldBefore: 'Předtím',
    fieldAfter: 'Potom',
    fieldEmpty: 'Prázdné',
    approveSome: 'Schválit {included} z {total}',
    approveAll: 'Schválit všech {total}',
    allLeftOut:
      'Všechny změny jsou vynechány. Zamítnutím akci zastavíte, nebo zahrňte změnu a schvalte ji.',
    appliedSome: 'Provedeno změn: {count}.',
    rejectedBody: 'Nic se nezměnilo.',
    noRows: 'Není zde nic ke schválení.',
    grantTitle: 'Povolit aplikaci {app} používat {source}',
    grantRead: 'Pouze číst',
    grantWrite: 'Číst a měnit',
    audienceOwner: 'Data z {source} v této aplikaci uvidíte pouze vy.',
    audiencePeople: 'Data z {source} uvidí {count} lidí, se kterými je tato aplikace sdílená.',
    audienceCompany: 'Data z {source} uvidí každý ve firmě, kdo tuto aplikaci otevře.',
    grantRouted: 'Rozhoduje o tom {owner}, proto byla žádost odeslána této osobě.',
    grantRoutedUnknown: 'Rozhoduje o tom vlastník tohoto připojení, proto mu byla žádost odeslána.',
    grantWhat: 'Přístup',
    businessStep: 'Krok {order}',
    stepWaiting: 'Čeká se',
    stepPending: 'Vyžaduje rozhodnutí',
    stepApproved: 'Schváleno',
    stepRejected: 'Zamítnuto',
    stepYou: 'Vy',
    businessNote: 'Schválení zde nepovoluje žádnou novou změnu mimo Ponevu.',
    businessYourTurn: 'Nyní jste na řadě s rozhodnutím.',
    businessNotYourTurn: 'Nejprve se čeká na rozhodnutí osoby {name}.',
    policyTitle: 'Jak aplikace používají toto připojení',
    policyCreator: 'Pouze připojení tvůrce',
    policyCreatorBody: 'Všichni vidí stejná data prostřednictvím připojení tvůrce aplikace.',
    policyViewer: 'Pouze vlastní připojení prohlížejícího',
    policyViewerBody: 'Každý vidí svá vlastní data prostřednictvím svého připojení.',
    policyBoth: 'Obojí',
    policyBothBody: 'Tvůrce aplikace vybírá pro každou aplikaci.',
    policyRevoked: 'Aplikace nemohou toto připojení používat',
    policyRevokedBody: 'Vyberte možnost a znovu to povolte. Nic se nevybere za vás.',
    policySave: 'Uložit',
    policySaving: 'Ukládání…',
    policySaved: 'Uloženo.',
    policyReadOnly: 'Toto může změnit pouze správce firmy.',
    policyConflict: 'Někdo jiný to změnil jako první. Toto je aktuální nastavení.',
    policyRefused: 'Toto se nepodařilo uložit.',
    policyFailed:
      'Nepodařilo se potvrdit, že bylo nastavení uloženo. Před dalším pokusem stránku znovu načtěte, abyste viděli aktuální nastavení.',
    policyWidened:
      'Aplikace, které jsou už publikované, si zachovají způsob připojení jako dosud. Jejich tvůrci budou požádáni o opětovné publikování, aby mohli použít novou možnost.',
    policyNarrowed: 'Aplikace při každém připojení kontrolují aktuální nastavení.',
    policyAffected: 'Toto připojení používá {count} publikovaných aplikací (k {when}).',
    policyAffectedOne: 'Toto připojení používá 1 publikovaná aplikace (k {when}).',
    policyAffectsNames: 'Týká se {count} publikovaných aplikací: {names}.',
    policyAffectsOneName: 'Týká se 1 publikované aplikace: {names}.',
    policyGenericWiden:
      'Publikované aplikace, které toto připojení používají, si zachovají způsob připojení jako dosud. Jejich tvůrci budou požádáni o opětovné publikování, aby mohli použít novou možnost.',
    policyGenericNarrow:
      'Publikované aplikace, které toto připojení používají, při každém připojení kontrolují aktuální nastavení.',
    policyAffectedGeneric:
      'Publikované aplikace, které toto připojení používají, se automaticky nezmění. Jejich tvůrci budou požádáni o opětovné publikování.',
    policyUnavailable: 'Toto nastavení momentálně není k dispozici.',
    outsideRead: 'Povolit aplikaci {app} číst z: {hosts}',
    outsideReadSend: 'Povolit aplikaci {app} číst z a odesílat data na: {hosts}',
    outsideNone: 'Zabránit aplikaci {app} v přístupu k: {hosts}',
    outsideReadDone: 'Aplikace {app} může číst z: {hosts}',
    outsideReadSendDone: 'Aplikace {app} může číst z a odesílat data na: {hosts}',
    outsideNoneDone: 'Aplikace {app} už nemá přístup k: {hosts}',
    outsideMore: 'ještě {count}',
    eventWebhookOn: 'Povolit aplikaci {app} přijímat „{event}“ na svém webhooku',
    eventWebhookOnDone: 'Aplikace {app} přijímá „{event}“ na svém webhooku',
    eventWebhookOff: 'Zabránit aplikaci {app} v přijímání „{event}“ na svém webhooku',
    eventWebhookOffDone: 'Aplikace {app} už nepřijímá „{event}“',
    eventRowsOn:
      'Povolit aplikaci {app} spouštět: {runs}, když jsou řádky v tabulce {collection} {changes}',
    eventRowsOnDone:
      'Aplikace {app} spouští: {runs}, když jsou řádky v tabulce {collection} {changes}',
    eventRowsOff:
      'Zabránit aplikaci {app} ve spouštění: {runs}, když se řádky v tabulce {collection} mění',
    eventRowsOffDone:
      'Aplikace {app} už nespouští: {runs}, když se řádky v tabulce {collection} mění',
    eventInsert: 'přidány',
    eventUpdate: 'změněny',
    eventDelete: 'odstraněny',
    scheduleTurnOn: 'Zapnout plán pro „{job}“ v aplikaci {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Plán pro „{job}“ v aplikaci {app} je zapnutý. {cadence} ({timezone}).',
    scheduleResume: 'Obnovit plán pro „{job}“ v aplikaci {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Plán pro „{job}“ v aplikaci {app} je znovu zapnutý. {cadence} ({timezone}).',
    scheduleReschedule: 'Změnit plán pro „{job}“ v aplikaci {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone: 'Plán pro „{job}“ v aplikaci {app} byl změněn. {cadence} ({timezone}).',
    schedulePause: 'Pozastavit plán pro „{job}“ v aplikaci {app}.',
    schedulePauseDone: 'Plán pro „{job}“ v aplikaci {app} je pozastavený.',
    scheduleNext: 'Další spuštění: {runs}.',
    scheduleResumeSaved:
      'Obnovit plán pro „{job}“ v aplikaci {app} s nejnovější uloženou verzí. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Plán pro „{job}“ v aplikaci {app} je znovu zapnutý a spouští nejnovější uloženou verzi. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Obnovit plán pro „{job}“ v aplikaci {app} s publikovanou verzí. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Plán pro „{job}“ v aplikaci {app} je znovu zapnutý a spouští publikovanou verzi. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Změnit plán pro „{job}“ v aplikaci {app}. {cadence} ({timezone}). Bude pozastavený, dokud ho neobnovíte.',
    scheduleReschedulePausedDone:
      'Plán pro „{job}“ v aplikaci {app} byl změněn. {cadence} ({timezone}). Je pozastavený, dokud ho neobnovíte.',
    scheduleSwitchSaved:
      'Přepnout plán pro „{job}“ v aplikaci {app} na nejnovější uloženou verzi. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Plán pro „{job}“ v aplikaci {app} teď spouští nejnovější uloženou verzi. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Přepnout plán pro „{job}“ v aplikaci {app} na nejnovější uloženou verzi. {cadence} ({timezone}). Bude pozastavený, dokud ho neobnovíte.',
    scheduleSwitchSavedPausedDone:
      'Plán pro „{job}“ v aplikaci {app} teď používá nejnovější uloženou verzi. {cadence} ({timezone}). Je pozastavený, dokud ho neobnovíte.',
    scheduleSwitchPublished:
      'Přepnout plán pro „{job}“ v aplikaci {app} na publikovanou verzi. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Plán pro „{job}“ v aplikaci {app} teď spouští publikovanou verzi. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Přepnout plán pro „{job}“ v aplikaci {app} na publikovanou verzi. {cadence} ({timezone}). Bude pozastavený, dokud ho neobnovíte.',
    scheduleSwitchPublishedPausedDone:
      'Plán pro „{job}“ v aplikaci {app} teď používá publikovanou verzi. {cadence} ({timezone}). Je pozastavený, dokud ho neobnovíte.',
    dataWritesOn: 'Povolit funkci „{function}“ aplikace {app} {verbs} vlastní řádky',
    dataWritesOnDone: 'Funkce „{function}“ aplikace {app} může {verbs} vlastní řádky',
    dataWritesOff: 'Zabránit funkci „{function}“ aplikace {app} ve změnách vlastních řádků',
    dataWritesOffDone: 'Funkce „{function}“ aplikace {app} už nemění vlastní řádky',
    dataWriteInsert: 'přidávat',
    dataWriteUpdate: 'měnit',
    dataWriteDelete: 'mazat',
    tableDropField: 'Povolit aplikaci {app} odstranit pole „{field}“ z tabulky {table} ({rows})',
    tableDropFieldDone: 'Aplikace {app} odstranila pole „{field}“ z tabulky {table} ({rows})',
    tableDropTable: 'Povolit aplikaci {app} odstranit tabulku {table} ({rows})',
    tableDropTableDone: 'Aplikace {app} odstranila tabulku {table} ({rows})',
    tableClear:
      'Povolit aplikaci {app} vymazat „{field}“ v tabulce {table} tam, kde hodnota porušuje nové pravidlo ({rows})',
    tableClearDone:
      'Aplikace {app} vymazala „{field}“ v tabulce {table} tam, kde hodnota porušovala nové pravidlo ({rows})',
    tableRestoreField:
      'Povolit aplikaci {app} obnovit „{field}“ v tabulce {table} z kopie ({rows})',
    tableRestoreFieldDone: 'Aplikace {app} obnovila „{field}“ v tabulce {table} ({rows})',
    tableRestoreTable: 'Povolit aplikaci {app} obnovit tabulku {table} z kopie ({rows})',
    tableRestoreTableDone: 'Aplikace {app} obnovila tabulku {table} ({rows})',
    tableRowsLoseOne: '1 řádek ztratí hodnotu',
    tableRowsLoseMany: 'Řádky, ve kterých se hodnota odstraní: {count}',
    tableRowsLostOne: '1 řádek přišel o hodnotu',
    tableRowsLostMany: 'Řádky, ve kterých se hodnota odstranila: {count}',
    tableRowsOne: '1 řádek',
    tableRowsMany: 'Řádky: {count}',
    connectedWriteOn: 'Povolit aplikaci {app} {action} v: {destination}',
    connectedWriteOnDone: 'Aplikace {app} může {action} v: {destination}',
    connectedWriteOff: 'Zabránit aplikaci {app} v zápisu do: {destination}',
    connectedWriteOffDone: 'Aplikace {app} už nezapisuje do: {destination}',
    codeConfirm: 'Povolit aplikaci {app} používat vaše schválení se změnami od: {names}',
    codeConfirmDone: 'Aplikace {app} používá vaše schválení se změnami od: {names}',
    codeConfirmCurrent: 'Povolit aplikaci {app} používat vaše schválení s jejím aktuálním kódem',
    codeConfirmCurrentDone: 'Aplikace {app} používá vaše schválení se svým aktuálním kódem',
    runChanges: 'Spustit nyní funkci „{function}“ v aplikaci {app}',
    runChangesDone: 'Funkce „{function}“ byla spuštěna v aplikaci {app}',
    continueApproved: 'Schváleno. Pokračuj: spusť to a zkontroluj, že to funguje.',
    continueDeclined: 'Zamítnuto. Pokračuj bez toho.',
    continueMixed: 'O žádostech je rozhodnuto. Pokračuj.',
    typeOutsideAccess: 'Povolit aplikaci přístup k externím službám',
    typeEvent: 'Zapnout nebo vypnout událost aplikace',
    typeSchedule: 'Zapnout, pozastavit nebo změnit plán aplikace',
    typeDataWrites: 'Povolit funkci aplikace měnit vlastní řádky',
    typeTableChange: 'Smazat nebo obnovit data v tabulkách aplikace',
    typeConnectedWrite: 'Povolit aplikaci zapisovat do firemního systému',
    typeConfirmCode: 'Nechat změněný kód aplikace používat vaše schválení',
    typeRunChanges: 'Spustit funkci aplikace, která mění data',
    typePublicLink: 'Změnit veřejný odkaz aplikace',
    typeUnpublish: 'Stáhnout veřejný odkaz aplikace',
    labelHosts: 'Služby',
    labelAccess: 'Přístup',
    labelEvent: 'Událost',
    labelKind: 'Spustí se při',
    labelCollection: 'Kolekce',
    labelChanges: 'Změny řádků',
    labelRuns: 'Spuštění',
    labelFunction: 'Funkce',
    labelCollections: 'Tabulky',
    labelWho: 'Pro',
    labelAllowed: 'Povoleno',
    labelEnabled: 'Stav',
    labelJob: 'Naplánovaná úloha',
    labelSchedule: 'Plán',
    labelTimezone: 'Časové pásmo',
    labelChange: 'Změna',
    labelTable: 'Tabulka',
    labelField: 'Pole',
    labelRows: 'Řádky',
    labelKeptDays: 'Kopie uchována (dny)',
    labelRestores: 'Obnoví',
    labelAction: 'Akce',
    labelDestination: 'Cíl',
    labelChangedBy: 'Změnil(a)',
    labelInput: 'Vstup',
    labelPublicTables: 'Zkopírované tabulky',
    labelPublicActions: 'Návštěvníci mohou odeslat',
    labelPublicRuns: 'Každé odeslání spustí',
    labelPublicRunsOff: 'Spustilo by se také po zapnutí',
    labelEmbedSites: 'Weby, které ji smějí vložit',
    labelPublicLink: 'Veřejný odkaz',
    labelPublishedBy: 'Publikoval(a)',
    wordRead: 'Pouze čtení',
    wordReadSend: 'Čtení a odesílání dat',
    wordNoAccess: 'Bez přístupu',
    publicPublish: 'Publikovat na veřejný odkaz',
    publicRefresh: 'Znovu zkopírovat její data',
    publicUnpublish: 'Zrušit publikování',
    eventWebhook: 'Její webhook',
    eventRows: 'Změny jejích řádků',
    switchOn: 'Zapnuto',
    switchOff: 'Vypnuto',
    switchPaused: 'Pozastaveno',
    changeInsert: 'Přidáno',
    changeUpdate: 'Změněno',
    changeDelete: 'Smazáno',
    wordDropField: 'Smazat hodnoty pole',
    wordDropTable: 'Smazat tabulku',
    tableClearValues: 'Vymazat hodnoty, které porušují přísnější pravidlo',
    tableRestore: 'Obnovit z její kopie',
    whoOwner: 'Vaše vlastní spuštění',
    whoEveryone: 'Všichni, kdo ji používají',
    answerYes: 'Ano',
    answerNo: 'Ne',
  },
  da: {
    kindWrite: 'Appændring til godkendelse',
    kindGrant: 'Appadgang til godkendelse',
    kindBusiness: 'Godkendelse af en app',
    approve: 'Godkend',
    reject: 'Afvis',
    working: 'Arbejder…',
    checkStatus: 'Tjek status',
    statusStillPending:
      'Poneva har ikke afgjort dette endnu. Det er muligvis stadig under behandling, så der kan ikke træffes andre beslutninger, før det er afgjort.',
    statusStillUnknown:
      'Vi kunne ikke få et svar. Der kan ikke træffes andre beslutninger, før dette er afgjort. Prøv igen om et øjeblik.',
    loading: 'Indlæser…',
    unavailable: 'Dette er ikke tilgængeligt lige nu.',
    loadFailed: 'Vi kunne ikke indlæse dette. Prøv igen om et øjeblik.',
    requestedBy: 'Anmodet af {name}',
    originApp: 'Fra appen',
    originAgent: 'Fra en agent',
    originClient: 'Via {client}',
    originFor: 'for {name}',
    changedUnderYou:
      'Dette blev ændret, mens du læste det. Det, du ser nu, er den aktuelle version. Der blev ikke truffet nogen beslutning.',
    notAllowed: 'Du kan ikke træffe beslutning om dette.',
    cannotDecideNow: 'Der kan ikke træffes beslutning om dette lige nu. Intet blev sendt.',
    expired: 'Denne anmodning er udløbet. Intet blev ændret.',
    stale: 'Dataene blev ændret, siden dette blev anmodet. Anmod igen for at få en ny anmodning.',
    approved: 'Godkendt',
    rejected: 'Afvist',
    needsYou: 'Afventer godkendelse',
    waitingOn: 'Afventer {name}',
    unknownTitle: 'Vi kan ikke bekræfte, hvad der skete',
    unknownBody:
      'Ændringen er muligvis blevet foretaget eller ikke. Gentag den ikke. Tjek status, eller bed appens ejer om at undersøge det.',
    blockedApproval: 'Afventer denne godkendelse, før den fortsætter.',
    blockedBudget: 'Sat på pause: AI-budgettet for denne app er brugt op.',
    blockedProvider: 'Sat på pause: En tilsluttet tjeneste svarer ikke.',
    blockedViewer:
      'Sat på pause: Dette skal køres som en logget ind bruger, og ingen bruger er tilknyttet. Det køres ikke med ejerens adgang i stedet.',
    writeTitle: 'Godkend ændringer af {target}',
    writeSummaryAll: '{count} ændringer i {app}',
    rowCreate: 'Ny',
    rowUpdate: 'Ændret',
    rowDelete: 'Fjernet',
    rowInclude: 'Medtag denne ændring',
    rowExcluded: 'Udeladt',
    fieldBefore: 'Før',
    fieldAfter: 'Efter',
    fieldEmpty: 'Tom',
    approveSome: 'Godkend {included} af {total}',
    approveAll: 'Godkend alle {total}',
    allLeftOut:
      'Alle ændringer er udeladt. Afvis for at stoppe, eller medtag en ændring for at godkende den.',
    appliedSome: '{count} ændringer foretaget.',
    rejectedBody: 'Intet blev ændret.',
    noRows: 'Der er intet at godkende her.',
    grantTitle: 'Giv {app} adgang til {source}',
    grantRead: 'Kun læsning',
    grantWrite: 'Læs og rediger',
    audienceOwner: 'Det er kun dig, der kan se denne apps data fra {source}.',
    audiencePeople: 'De {count} personer, som denne app er delt med, kan se data fra {source}.',
    audienceCompany: 'Alle i virksomheden, der åbner appen, kan se data fra {source}.',
    grantRouted: '{owner} træffer beslutning om dette, så det blev sendt til dem.',
    grantRoutedUnknown:
      'Ejeren af denne forbindelse træffer beslutning om dette, så det blev sendt til dem.',
    grantWhat: 'Adgang',
    businessStep: 'Trin {order}',
    stepWaiting: 'Afventer',
    stepPending: 'Afventer beslutning',
    stepApproved: 'Godkendt',
    stepRejected: 'Afvist',
    stepYou: 'Dig',
    businessNote: 'Godkendelse her giver ikke tilladelse til nye ændringer uden for Poneva.',
    businessYourTurn: 'Det er din tur til at træffe beslutning.',
    businessNotYourTurn: 'Afventer først en beslutning fra {name}.',
    policyTitle: 'Sådan bruger apps denne forbindelse',
    policyCreator: 'Kun opretterens',
    policyCreatorBody: 'Alle ser de samme data via appens opretters forbindelse.',
    policyViewer: 'Kun brugerens egen',
    policyViewerBody: 'Hver person ser sine egne data via sin egen forbindelse.',
    policyBoth: 'Begge',
    policyBothBody: 'Appens opretter vælger for hver app.',
    policyRevoked: 'Apps kan ikke bruge denne forbindelse',
    policyRevokedBody: 'Vælg en mulighed for at tillade det igen. Der vælges ikke noget for dig.',
    policySave: 'Gem',
    policySaving: 'Gemmer…',
    policySaved: 'Gemt.',
    policyReadOnly: 'Kun en virksomhedsadministrator kan ændre dette.',
    policyConflict: 'En anden ændrede dette først. Dette er den aktuelle indstilling.',
    policyRefused: 'Dette kunne ikke gemmes.',
    policyFailed:
      'Vi kunne ikke bekræfte, at dette blev gemt. Genindlæs for at se den aktuelle indstilling, før du prøver igen.',
    policyWidened:
      'Apps, der allerede er udgivet, bevarer den måde, de opretter forbindelse på i dag. Deres skabere bliver bedt om at udgive dem igen for at bruge den nye mulighed.',
    policyNarrowed:
      'Apps kontrollerer den aktuelle indstilling, hver gang de opretter forbindelse.',
    policyAffected: '{count} udgivne apps bruger denne forbindelse (pr. {when}).',
    policyAffectedOne: '1 udgivet app bruger denne forbindelse (pr. {when}).',
    policyAffectsNames: 'Påvirker {count} udgivne apps: {names}.',
    policyAffectsOneName: 'Påvirker 1 udgivet app: {names}.',
    policyGenericWiden:
      'Udgivne apps, der bruger denne forbindelse, bevarer den måde, de opretter forbindelse på i dag. Deres skabere bliver bedt om at udgive dem igen for at bruge den nye mulighed.',
    policyGenericNarrow:
      'Udgivne apps, der bruger denne forbindelse, kontrollerer den aktuelle indstilling, hver gang de opretter forbindelse.',
    policyAffectedGeneric:
      'Udgivne apps, der bruger denne forbindelse, ændres ikke automatisk. Deres skabere bliver bedt om at udgive dem igen.',
    policyUnavailable: 'Denne indstilling er ikke tilgængelig lige nu.',
    outsideRead: 'Lad {app} læse fra {hosts}',
    outsideReadSend: 'Lad {app} læse fra og sende data til {hosts}',
    outsideNone: 'Stop {app} i at nå {hosts}',
    outsideReadDone: '{app} kan læse fra {hosts}',
    outsideReadSendDone: '{app} kan læse fra og sende data til {hosts}',
    outsideNoneDone: '{app} når ikke længere {hosts}',
    outsideMore: '{count} flere',
    eventWebhookOn: 'Lad {app} modtage “{event}” på sin webhook',
    eventWebhookOnDone: '{app} modtager “{event}” på sin webhook',
    eventWebhookOff: 'Stop {app} i at modtage “{event}” på sin webhook',
    eventWebhookOffDone: '{app} modtager ikke længere “{event}”',
    eventRowsOn: 'Lad {app} køre {runs}, når rækker i {collection} bliver {changes}',
    eventRowsOnDone: '{app} kører {runs}, når rækker i {collection} bliver {changes}',
    eventRowsOff: 'Stop {app} i at køre {runs}, når rækker i {collection} ændres',
    eventRowsOffDone: '{app} kører ikke længere {runs}, når rækker i {collection} ændres',
    eventInsert: 'tilføjet',
    eventUpdate: 'ændret',
    eventDelete: 'slettet',
    scheduleTurnOn: 'Slå tidsplanen for “{job}” i {app} til. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Tidsplanen for “{job}” i {app} er slået til. {cadence} ({timezone}).',
    scheduleResume: 'Genoptag tidsplanen for “{job}” i {app}. {cadence} ({timezone}).',
    scheduleResumeDone: 'Tidsplanen for “{job}” i {app} er slået til igen. {cadence} ({timezone}).',
    scheduleReschedule: 'Ændr tidsplanen for “{job}” i {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone: 'Tidsplanen for “{job}” i {app} er ændret. {cadence} ({timezone}).',
    schedulePause: 'Sæt tidsplanen for “{job}” i {app} på pause.',
    schedulePauseDone: 'Tidsplanen for “{job}” i {app} er sat på pause.',
    scheduleNext: 'Næste kørsler: {runs}.',
    scheduleResumeSaved:
      'Genoptag tidsplanen for “{job}” i {app} med den senest gemte version. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Tidsplanen for “{job}” i {app} er slået til igen og kører den senest gemte version. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Genoptag tidsplanen for “{job}” i {app} med den offentliggjorte version. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Tidsplanen for “{job}” i {app} er slået til igen og kører den offentliggjorte version. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Ændr tidsplanen for “{job}” i {app}. {cadence} ({timezone}). Den bliver sat på pause, indtil du genoptager den.',
    scheduleReschedulePausedDone:
      'Tidsplanen for “{job}” i {app} er ændret. {cadence} ({timezone}). Den er sat på pause, indtil du genoptager den.',
    scheduleSwitchSaved:
      'Skift tidsplanen for “{job}” i {app} til den senest gemte version. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Tidsplanen for “{job}” i {app} kører nu den senest gemte version. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Skift tidsplanen for “{job}” i {app} til den senest gemte version. {cadence} ({timezone}). Den bliver sat på pause, indtil du genoptager den.',
    scheduleSwitchSavedPausedDone:
      'Tidsplanen for “{job}” i {app} bruger nu den senest gemte version. {cadence} ({timezone}). Den er sat på pause, indtil du genoptager den.',
    scheduleSwitchPublished:
      'Skift tidsplanen for “{job}” i {app} til den offentliggjorte version. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Tidsplanen for “{job}” i {app} kører nu den offentliggjorte version. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Skift tidsplanen for “{job}” i {app} til den offentliggjorte version. {cadence} ({timezone}). Den bliver sat på pause, indtil du genoptager den.',
    scheduleSwitchPublishedPausedDone:
      'Tidsplanen for “{job}” i {app} bruger nu den offentliggjorte version. {cadence} ({timezone}). Den er sat på pause, indtil du genoptager den.',
    dataWritesOn: 'Lad “{function}” i {app} {verbs} sine egne rækker',
    dataWritesOnDone: '“{function}” i {app} kan {verbs} sine egne rækker',
    dataWritesOff: 'Stop “{function}” i {app} i at ændre sine egne rækker',
    dataWritesOffDone: '“{function}” i {app} ændrer ikke længere sine egne rækker',
    dataWriteInsert: 'tilføje',
    dataWriteUpdate: 'ændre',
    dataWriteDelete: 'slette',
    tableDropField: 'Lad {app} fjerne feltet “{field}” fra {table} ({rows})',
    tableDropFieldDone: '{app} fjernede feltet “{field}” fra {table} ({rows})',
    tableDropTable: 'Lad {app} slette tabellen {table} ({rows})',
    tableDropTableDone: '{app} slettede tabellen {table} ({rows})',
    tableClear: 'Lad {app} rydde “{field}” i {table}, hvor det bryder den nye regel ({rows})',
    tableClearDone: '{app} ryddede “{field}” i {table}, hvor det brød den nye regel ({rows})',
    tableRestoreField: 'Lad {app} sætte “{field}” i {table} tilbage fra kopien ({rows})',
    tableRestoreFieldDone: '{app} satte “{field}” i {table} tilbage ({rows})',
    tableRestoreTable: 'Lad {app} gendanne tabellen {table} fra kopien ({rows})',
    tableRestoreTableDone: '{app} gendannede tabellen {table} ({rows})',
    tableRowsLoseOne: '1 række mister en værdi',
    tableRowsLoseMany: '{count} rækker mister en værdi',
    tableRowsLostOne: '1 række mistede en værdi',
    tableRowsLostMany: '{count} rækker mistede en værdi',
    tableRowsOne: '1 række',
    tableRowsMany: '{count} rækker',
    connectedWriteOn: 'Lad {app} {action} i {destination}',
    connectedWriteOnDone: '{app} kan {action} i {destination}',
    connectedWriteOff: 'Stop {app} i at skrive til {destination}',
    connectedWriteOffDone: '{app} skriver ikke længere til {destination}',
    codeConfirm: 'Lad {app} bruge dine godkendelser med ændringerne fra {names}',
    codeConfirmDone: '{app} bruger dine godkendelser med ændringerne fra {names}',
    codeConfirmCurrent: 'Lad {app} bruge dine godkendelser med dens nuværende kode',
    codeConfirmCurrentDone: '{app} bruger dine godkendelser med dens nuværende kode',
    runChanges: 'Kør “{function}” i {app} nu',
    runChangesDone: '“{function}” blev kørt i {app}',
    continueApproved: 'Godkendt. Fortsæt: kør det, og tjek at det virker.',
    continueDeclined: 'Afvist. Fortsæt uden det.',
    continueMixed: 'Jeg har truffet beslutning om anmodningerne. Fortsæt.',
    typeOutsideAccess: 'Lad en app nå eksterne tjenester',
    typeEvent: 'Slå en app-hændelse til eller fra',
    typeSchedule: 'Slå en apps tidsplan til, sæt den på pause eller ændr den',
    typeDataWrites: 'Lad en app-funktion ændre sine egne rækker',
    typeTableChange: 'Slet eller gendan data i en apps tabeller',
    typeConnectedWrite: 'Lad en app skrive til et virksomhedssystem',
    typeConfirmCode: 'Lad ændret app-kode bruge dine godkendelser',
    typeRunChanges: 'Kør en app-funktion, der ændrer data',
    typePublicLink: 'Ændr en apps offentlige link',
    typeUnpublish: 'Tag en apps offentlige link ned',
    labelHosts: 'Tjenester',
    labelAccess: 'Adgang',
    labelEvent: 'Hændelse',
    labelKind: 'Starter ved',
    labelCollection: 'Samling',
    labelChanges: 'Rækkeændringer',
    labelRuns: 'Kørsler',
    labelFunction: 'Funktion',
    labelCollections: 'Tabeller',
    labelWho: 'Til',
    labelAllowed: 'Tilladt',
    labelEnabled: 'Status',
    labelJob: 'Planlagt job',
    labelSchedule: 'Tidsplan',
    labelTimezone: 'Tidszone',
    labelChange: 'Ændring',
    labelTable: 'Tabel',
    labelField: 'Felt',
    labelRows: 'Rækker',
    labelKeptDays: 'Kopi gemt (dage)',
    labelRestores: 'Gendanner',
    labelAction: 'Handling',
    labelDestination: 'Destination',
    labelChangedBy: 'Ændret af',
    labelInput: 'Input',
    labelPublicTables: 'Kopierede tabeller',
    labelPublicActions: 'Besøgende kan sende',
    labelPublicRuns: 'Hver indsendelse starter',
    labelPublicRunsOff: 'Ville også starte, når den slås til',
    labelEmbedSites: 'Sider, der må indlejre den',
    labelPublicLink: 'Offentligt link',
    labelPublishedBy: 'Udgivet af',
    wordRead: 'Kun læsning',
    wordReadSend: 'Læs og send data',
    wordNoAccess: 'Ingen adgang',
    publicPublish: 'Udgiv på et offentligt link',
    publicRefresh: 'Kopiér dens data igen',
    publicUnpublish: 'Træk tilbage',
    eventWebhook: 'Appens webhook',
    eventRows: 'Ændringer i appens rækker',
    switchOn: 'Til',
    switchOff: 'Fra',
    switchPaused: 'Sat på pause',
    changeInsert: 'Tilføjet',
    changeUpdate: 'Ændret',
    changeDelete: 'Slettet',
    wordDropField: 'Slet et felts værdier',
    wordDropTable: 'Slet en tabel',
    tableClearValues: 'Ryd værdier, der bryder en strengere regel',
    tableRestore: 'Gendan fra kopien',
    whoOwner: 'Dine egne kørsler',
    whoEveryone: 'Alle, der bruger den',
    answerYes: 'Ja',
    answerNo: 'Nej',
  },
  nl: {
    kindWrite: 'App-wijziging ter goedkeuring',
    kindGrant: 'App-toegang ter goedkeuring',
    kindBusiness: 'Goedkeuring voor een app',
    approve: 'Goedkeuren',
    reject: 'Afwijzen',
    working: 'Bezig…',
    checkStatus: 'Status controleren',
    statusStillPending:
      'Poneva heeft dit nog niet afgehandeld. Het wordt mogelijk nog verwerkt, dus er kan niets anders worden beslist totdat dit is afgehandeld.',
    statusStillUnknown:
      'We konden geen antwoord krijgen. Er kan niets anders worden beslist totdat dit is afgehandeld. Probeer het over een moment opnieuw.',
    loading: 'Laden…',
    unavailable: 'Dit is momenteel niet beschikbaar.',
    loadFailed: 'We konden dit niet laden. Probeer het over een moment opnieuw.',
    requestedBy: 'Aangevraagd door {name}',
    originApp: 'Vanuit de app',
    originAgent: 'Vanuit een agent',
    originClient: 'Via {client}',
    originFor: 'voor {name}',
    changedUnderYou:
      'Dit is gewijzigd terwijl je het las. Wat je nu ziet, is de huidige versie. Er is niets besloten.',
    notAllowed: 'Je kunt hierover niet beslissen.',
    cannotDecideNow: 'Hierover kan nu niet worden beslist. Er is niets verzonden.',
    expired: 'Dit verzoek is verlopen. Er is niets gewijzigd.',
    stale:
      'De gegevens zijn gewijzigd sinds dit werd aangevraagd. Vraag het opnieuw aan voor een nieuw verzoek.',
    approved: 'Goedgekeurd',
    rejected: 'Afgewezen',
    needsYou: 'Goedkeuring vereist',
    waitingOn: 'Wachten op {name}',
    unknownTitle: 'We kunnen niet bevestigen wat er is gebeurd',
    unknownBody:
      'De wijziging is mogelijk wel of niet uitgevoerd. Herhaal deze niet. Controleer de status of vraag de eigenaar van de app om dit te controleren.',
    blockedApproval: 'Wachten op deze goedkeuring voordat het proces doorgaat.',
    blockedBudget: 'Gepauzeerd: het AI-budget voor deze app is op.',
    blockedProvider: 'Gepauzeerd: een verbonden service reageert niet.',
    blockedViewer:
      'Gepauzeerd: dit moet worden uitgevoerd als een ingelogde kijker en er is geen kijker gekoppeld. Er wordt niets uitgevoerd met de toegang van de eigenaar.',
    writeTitle: 'Wijzigingen in {target} goedkeuren',
    writeSummaryAll: '{count} wijzigingen in {app}',
    rowCreate: 'Nieuw',
    rowUpdate: 'Gewijzigd',
    rowDelete: 'Verwijderd',
    rowInclude: 'Deze wijziging opnemen',
    rowExcluded: 'Niet opgenomen',
    fieldBefore: 'Voor',
    fieldAfter: 'Na',
    fieldEmpty: 'Leeg',
    approveSome: '{included} van {total} goedkeuren',
    approveAll: 'Alle {total} goedkeuren',
    allLeftOut:
      'Elke wijziging is uitgesloten. Wijs af om te stoppen of neem een wijziging op om deze goed te keuren.',
    appliedSome: '{count} wijzigingen uitgevoerd.',
    rejectedBody: 'Er is niets gewijzigd.',
    noRows: 'Er is hier niets om goed te keuren.',
    grantTitle: '{app} toegang geven tot {source}',
    grantRead: 'Alleen lezen',
    grantWrite: 'Lezen en wijzigen',
    audienceOwner: 'Alleen jij ziet de gegevens van deze app uit {source}.',
    audiencePeople: 'De {count} personen met wie deze app is gedeeld, zien gegevens uit {source}.',
    audienceCompany: 'Iedereen binnen het bedrijf die de app opent, ziet gegevens uit {source}.',
    grantRouted: '{owner} beslist hierover, dus het is naar hen doorgestuurd.',
    grantRoutedUnknown:
      'De eigenaar van deze verbinding beslist hierover, dus het is naar hen doorgestuurd.',
    grantWhat: 'Toegang',
    businessStep: 'Stap {order}',
    stepWaiting: 'In afwachting',
    stepPending: 'Beslissing vereist',
    stepApproved: 'Goedgekeurd',
    stepRejected: 'Afgewezen',
    stepYou: 'Jij',
    businessNote: 'Goedkeuring hier staat geen nieuwe wijziging buiten Poneva toe.',
    businessYourTurn: 'Jij bent aan de beurt om te beslissen.',
    businessNotYourTurn: 'Wachten tot {name} eerst beslist.',
    policyTitle: 'Hoe apps deze verbinding gebruiken',
    policyCreator: 'Alleen van de maker',
    policyCreatorBody: 'Iedereen ziet dezelfde gegevens via de verbinding van de maker van de app.',
    policyViewer: 'Alleen van de kijker zelf',
    policyViewerBody: 'Iedereen ziet zijn eigen gegevens via zijn eigen verbinding.',
    policyBoth: 'Beide',
    policyBothBody: 'De maker van de app kiest dit per app.',
    policyRevoked: 'Apps kunnen deze verbinding niet gebruiken',
    policyRevokedBody: 'Kies een optie om dit weer toe te staan. Er wordt niets voor je gekozen.',
    policySave: 'Opslaan',
    policySaving: 'Opslaan…',
    policySaved: 'Opgeslagen.',
    policyReadOnly: 'Alleen een bedrijfsbeheerder kan dit wijzigen.',
    policyConflict: 'Iemand anders heeft dit eerst gewijzigd. Dit is de huidige instelling.',
    policyRefused: 'Dit kon niet worden opgeslagen.',
    policyFailed:
      'We konden niet bevestigen dat dit is opgeslagen. Laad de pagina opnieuw om de huidige instelling te bekijken voordat je het opnieuw probeert.',
    policyWidened:
      'Apps die al gepubliceerd zijn, blijven zich voorlopig op dezelfde manier verbinden. Hun makers wordt gevraagd de apps opnieuw te publiceren om de nieuwe optie te gebruiken.',
    policyNarrowed: 'Apps controleren telkens wanneer ze verbinding maken de huidige instelling.',
    policyAffected: '{count} gepubliceerde apps gebruiken deze verbinding (op {when}).',
    policyAffectedOne: '1 gepubliceerde app gebruikt deze verbinding (op {when}).',
    policyAffectsNames: 'Heeft gevolgen voor {count} gepubliceerde apps: {names}.',
    policyAffectsOneName: 'Heeft gevolgen voor 1 gepubliceerde app: {names}.',
    policyGenericWiden:
      'Gepubliceerde apps die deze verbinding gebruiken, blijven zich voorlopig op dezelfde manier verbinden. Hun makers wordt gevraagd de apps opnieuw te publiceren om de nieuwe optie te gebruiken.',
    policyGenericNarrow:
      'Gepubliceerde apps die deze verbinding gebruiken, controleren telkens wanneer ze verbinding maken de huidige instelling.',
    policyAffectedGeneric:
      'Gepubliceerde apps die deze verbinding gebruiken, worden niet automatisch gewijzigd. Hun makers wordt gevraagd de apps opnieuw te publiceren.',
    policyUnavailable: 'Deze instelling is momenteel niet beschikbaar.',
    outsideRead: '{app} toestaan te lezen van {hosts}',
    outsideReadSend: '{app} toestaan te lezen van {hosts} en er gegevens naartoe te sturen',
    outsideNone: 'Voorkomen dat {app} {hosts} bereikt',
    outsideReadDone: '{app} kan lezen van {hosts}',
    outsideReadSendDone: '{app} kan lezen van {hosts} en er gegevens naartoe sturen',
    outsideNoneDone: '{app} bereikt {hosts} niet meer',
    outsideMore: 'nog {count}',
    eventWebhookOn: '{app} “{event}” laten ontvangen op zijn webhook',
    eventWebhookOnDone: '{app} ontvangt “{event}” op zijn webhook',
    eventWebhookOff: 'Voorkomen dat {app} “{event}” ontvangt op zijn webhook',
    eventWebhookOffDone: '{app} ontvangt “{event}” niet meer',
    eventRowsOn: '{app} {runs} laten uitvoeren wanneer rijen in {collection} zijn {changes}',
    eventRowsOnDone: '{app} voert {runs} uit wanneer rijen in {collection} zijn {changes}',
    eventRowsOff: 'Voorkomen dat {app} {runs} uitvoert wanneer rijen in {collection} wijzigen',
    eventRowsOffDone: '{app} voert {runs} niet meer uit wanneer rijen in {collection} wijzigen',
    eventInsert: 'toegevoegd',
    eventUpdate: 'gewijzigd',
    eventDelete: 'verwijderd',
    scheduleTurnOn: 'De planning van “{job}” in {app} inschakelen. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'De planning van “{job}” in {app} staat aan. {cadence} ({timezone}).',
    scheduleResume: 'De planning van “{job}” in {app} hervatten. {cadence} ({timezone}).',
    scheduleResumeDone: 'De planning van “{job}” in {app} staat weer aan. {cadence} ({timezone}).',
    scheduleReschedule: 'De planning van “{job}” in {app} wijzigen. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'De planning van “{job}” in {app} is gewijzigd. {cadence} ({timezone}).',
    schedulePause: 'De planning van “{job}” in {app} pauzeren.',
    schedulePauseDone: 'De planning van “{job}” in {app} is gepauzeerd.',
    scheduleNext: 'Volgende uitvoeringen: {runs}.',
    scheduleResumeSaved:
      'De planning van “{job}” in {app} hervatten met de laatst opgeslagen versie. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'De planning van “{job}” in {app} staat weer aan en voert de laatst opgeslagen versie uit. {cadence} ({timezone}).',
    scheduleResumePublished:
      'De planning van “{job}” in {app} hervatten met de gepubliceerde versie. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'De planning van “{job}” in {app} staat weer aan en voert de gepubliceerde versie uit. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'De planning van “{job}” in {app} wijzigen. {cadence} ({timezone}). De planning is daarna gepauzeerd tot je die hervat.',
    scheduleReschedulePausedDone:
      'De planning van “{job}” in {app} is gewijzigd. {cadence} ({timezone}). De planning is gepauzeerd tot je die hervat.',
    scheduleSwitchSaved:
      'De planning van “{job}” in {app} overzetten naar de laatst opgeslagen versie. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'De planning van “{job}” in {app} voert nu de laatst opgeslagen versie uit. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'De planning van “{job}” in {app} overzetten naar de laatst opgeslagen versie. {cadence} ({timezone}). De planning is daarna gepauzeerd tot je die hervat.',
    scheduleSwitchSavedPausedDone:
      'De planning van “{job}” in {app} gebruikt nu de laatst opgeslagen versie. {cadence} ({timezone}). De planning is gepauzeerd tot je die hervat.',
    scheduleSwitchPublished:
      'De planning van “{job}” in {app} overzetten naar de gepubliceerde versie. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'De planning van “{job}” in {app} voert nu de gepubliceerde versie uit. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'De planning van “{job}” in {app} overzetten naar de gepubliceerde versie. {cadence} ({timezone}). De planning is daarna gepauzeerd tot je die hervat.',
    scheduleSwitchPublishedPausedDone:
      'De planning van “{job}” in {app} gebruikt nu de gepubliceerde versie. {cadence} ({timezone}). De planning is gepauzeerd tot je die hervat.',
    dataWritesOn: '“{function}” van {app} toestaan om eigen rijen te {verbs}',
    dataWritesOnDone: '“{function}” van {app} kan eigen rijen {verbs}',
    dataWritesOff: 'Voorkomen dat “{function}” van {app} eigen rijen wijzigt',
    dataWritesOffDone: '“{function}” van {app} wijzigt eigen rijen niet meer',
    dataWriteInsert: 'toevoegen',
    dataWriteUpdate: 'wijzigen',
    dataWriteDelete: 'verwijderen',
    tableDropField: '{app} toestaan het veld “{field}” uit {table} te verwijderen ({rows})',
    tableDropFieldDone: '{app} heeft het veld “{field}” uit {table} verwijderd ({rows})',
    tableDropTable: '{app} toestaan de tabel {table} te verwijderen ({rows})',
    tableDropTableDone: '{app} heeft de tabel {table} verwijderd ({rows})',
    tableClear:
      '{app} toestaan “{field}” in {table} te wissen waar het de nieuwe regel overtreedt ({rows})',
    tableClearDone:
      '{app} heeft “{field}” in {table} gewist waar het de nieuwe regel overtrad ({rows})',
    tableRestoreField:
      '{app} toestaan “{field}” in {table} terug te zetten vanuit de kopie ({rows})',
    tableRestoreFieldDone: '{app} heeft “{field}” in {table} teruggezet ({rows})',
    tableRestoreTable: '{app} toestaan de tabel {table} te herstellen vanuit de kopie ({rows})',
    tableRestoreTableDone: '{app} heeft de tabel {table} hersteld ({rows})',
    tableRowsLoseOne: '1 rij verliest een waarde',
    tableRowsLoseMany: '{count} rijen verliezen een waarde',
    tableRowsLostOne: '1 rij verloor een waarde',
    tableRowsLostMany: '{count} rijen verloren een waarde',
    tableRowsOne: '1 rij',
    tableRowsMany: '{count} rijen',
    connectedWriteOn: '{app} toestaan om {action} in {destination}',
    connectedWriteOnDone: '{app} kan {action} in {destination}',
    connectedWriteOff: 'Voorkomen dat {app} schrijft naar {destination}',
    connectedWriteOffDone: '{app} schrijft niet meer naar {destination}',
    codeConfirm: '{app} jouw goedkeuringen laten gebruiken met de wijzigingen van {names}',
    codeConfirmDone: '{app} gebruikt jouw goedkeuringen met de wijzigingen van {names}',
    codeConfirmCurrent: '{app} jouw goedkeuringen laten gebruiken met de huidige code',
    codeConfirmCurrentDone: '{app} gebruikt jouw goedkeuringen met de huidige code',
    runChanges: '“{function}” nu uitvoeren in {app}',
    runChangesDone: '“{function}” is uitgevoerd in {app}',
    continueApproved: 'Goedgekeurd. Ga verder: voer het uit en controleer of het werkt.',
    continueDeclined: 'Afgewezen. Ga verder zonder.',
    continueMixed: 'Ik heb over de verzoeken beslist. Ga verder.',
    typeOutsideAccess: 'Een app toegang geven tot externe diensten',
    typeEvent: 'Een app-gebeurtenis in- of uitschakelen',
    typeSchedule: 'De planning van een app inschakelen, pauzeren of wijzigen',
    typeDataWrites: 'Een app-functie haar eigen rijen laten wijzigen',
    typeTableChange: 'Gegevens in de tabellen van een app verwijderen of terugzetten',
    typeConnectedWrite: 'Een app laten schrijven naar een bedrijfssysteem',
    typeConfirmCode: 'Gewijzigde app-code jouw goedkeuringen laten gebruiken',
    typeRunChanges: 'Een app-functie uitvoeren die gegevens wijzigt',
    typePublicLink: 'De openbare link van een app wijzigen',
    typeUnpublish: 'De openbare link van een app offline halen',
    labelHosts: 'Diensten',
    labelAccess: 'Toegang',
    labelEvent: 'Gebeurtenis',
    labelKind: 'Start bij',
    labelCollection: 'Verzameling',
    labelChanges: 'Rijwijzigingen',
    labelRuns: 'Uitvoeringen',
    labelFunction: 'Functie',
    labelCollections: 'Tabellen',
    labelWho: 'Voor',
    labelAllowed: 'Toegestaan',
    labelEnabled: 'Status',
    labelJob: 'Geplande taak',
    labelSchedule: 'Planning',
    labelTimezone: 'Tijdzone',
    labelChange: 'Wijziging',
    labelTable: 'Tabel',
    labelField: 'Veld',
    labelRows: 'Rijen',
    labelKeptDays: 'Kopie bewaard (dagen)',
    labelRestores: 'Zet terug',
    labelAction: 'Actie',
    labelDestination: 'Bestemming',
    labelChangedBy: 'Gewijzigd door',
    labelInput: 'Invoer',
    labelPublicTables: 'Gekopieerde tabellen',
    labelPublicActions: 'Bezoekers kunnen sturen',
    labelPublicRuns: 'Elke inzending start',
    labelPublicRunsOff: 'Zou ook starten zodra het is ingeschakeld',
    labelEmbedSites: 'Sites die de app mogen insluiten',
    labelPublicLink: 'Openbare link',
    labelPublishedBy: 'Gepubliceerd door',
    wordRead: 'Alleen lezen',
    wordReadSend: 'Lezen en gegevens versturen',
    wordNoAccess: 'Geen toegang',
    publicPublish: 'Publiceren naar een openbare link',
    publicRefresh: 'De gegevens opnieuw kopiëren',
    publicUnpublish: 'Publicatie ongedaan maken',
    eventWebhook: 'De webhook van de app',
    eventRows: 'Wijzigingen in de rijen van de app',
    switchOn: 'Aan',
    switchOff: 'Uit',
    switchPaused: 'Gepauzeerd',
    changeInsert: 'Toegevoegd',
    changeUpdate: 'Gewijzigd',
    changeDelete: 'Verwijderd',
    wordDropField: 'De waarden van een veld verwijderen',
    wordDropTable: 'Een tabel verwijderen',
    tableClearValues: 'Waarden wissen die een strengere regel overtreden',
    tableRestore: 'Terugzetten vanaf de kopie',
    whoOwner: 'Jouw eigen uitvoeringen',
    whoEveryone: 'Iedereen die de app gebruikt',
    answerYes: 'Ja',
    answerNo: 'Nee',
  },
  en: {
    kindWrite: 'App change to approve',
    kindGrant: 'App access to approve',
    kindBusiness: 'Approval for an app',
    approve: 'Approve',
    reject: 'Reject',
    working: 'Working…',
    checkStatus: 'Check status',
    statusStillPending:
      'Poneva has not settled this yet. It may still be going through, so nothing else can be decided until it is.',
    statusStillUnknown:
      'We could not get an answer. Nothing else can be decided until this is settled. Try again in a moment.',
    loading: 'Loading…',
    unavailable: 'This is not available right now.',
    loadFailed: 'We could not load this. Try again in a moment.',
    requestedBy: 'Asked by {name}',
    originApp: 'From the app',
    originAgent: 'From an Agent',
    originClient: 'Via {client}',
    originFor: 'for {name}',
    changedUnderYou:
      'This changed while you were reading it. What you see now is the current version. Nothing was decided.',
    notAllowed: 'You cannot decide this.',
    cannotDecideNow: 'This can’t be decided right now. Nothing was sent.',
    expired: 'This request has expired. Nothing changed.',
    stale: 'The data changed since this was asked. Ask again to get a fresh request.',
    approved: 'Approved',
    rejected: 'Rejected',
    needsYou: 'Needs approval',
    waitingOn: 'Waiting for {name}',
    unknownTitle: 'We cannot confirm what happened',
    unknownBody:
      'The change may or may not have been made. Do not repeat it. Check the status, or ask the app owner to look.',
    blockedApproval: 'Waiting for this approval before it continues.',
    blockedBudget: 'Paused: the AI budget for this app is used up.',
    blockedProvider: 'Paused: a connected service is not responding.',
    blockedViewer:
      "Paused: this needs to run as a signed-in viewer, and no viewer is attached. Nothing runs on the owner's access instead.",
    writeTitle: 'Approve changes to {target}',
    writeSummaryAll: '{count} changes in {app}',
    rowCreate: 'New',
    rowUpdate: 'Changed',
    rowDelete: 'Removed',
    rowInclude: 'Include this change',
    rowExcluded: 'Left out',
    fieldBefore: 'Before',
    fieldAfter: 'After',
    fieldEmpty: 'Empty',
    approveSome: 'Approve {included} of {total}',
    approveAll: 'Approve all {total}',
    allLeftOut: 'Every change is left out. Reject to stop, or include a change to approve it.',
    appliedSome: '{count} changes made.',
    rejectedBody: 'Nothing was changed.',
    noRows: 'There is nothing to approve here.',
    grantTitle: 'Let {app} use {source}',
    grantRead: 'Read only',
    grantWrite: 'Read and change',
    audienceOwner: 'Only you will see this app’s data from {source}.',
    audiencePeople: 'The {count} people this app is shared with will see data from {source}.',
    audienceCompany: 'Everyone in the company who opens the app will see data from {source}.',
    grantRouted: '{owner} decides this, so it was sent to them.',
    grantRoutedUnknown: 'The owner of this connection decides this, so it was sent to them.',
    grantWhat: 'Access',
    businessStep: 'Step {order}',
    stepWaiting: 'Waiting',
    stepPending: 'Needs a decision',
    stepApproved: 'Approved',
    stepRejected: 'Rejected',
    stepYou: 'You',
    businessNote: 'Approving here does not allow any new change outside Poneva.',
    businessYourTurn: 'It is your turn to decide.',
    businessNotYourTurn: 'Waiting for {name} to decide first.',
    policyTitle: 'How apps use this connection',
    policyCreator: 'Creator’s only',
    policyCreatorBody: 'Everyone sees the same data, through the app creator’s connection.',
    policyViewer: 'Viewer’s own only',
    policyViewerBody: 'Each person sees their own data, through their own connection.',
    policyBoth: 'Both',
    policyBothBody: 'The app creator chooses for each app.',
    policyRevoked: 'Apps cannot use this connection',
    policyRevokedBody: 'Choose an option to allow it again. Nothing is chosen for you.',
    policySave: 'Save',
    policySaving: 'Saving…',
    policySaved: 'Saved.',
    policyReadOnly: 'Only a company admin can change this.',
    policyConflict: 'Someone else changed this first. This is the current setting.',
    policyRefused: 'This could not be saved.',
    policyFailed:
      'We could not confirm this was saved. Reload to see the current setting before you try again.',
    policyWidened:
      'Apps that are already published keep how they connect today. Their creators are asked to republish to use the new option.',
    policyNarrowed: 'Apps check the current setting each time they connect.',
    policyAffected: '{count} published apps use this connection (as of {when}).',
    policyAffectedOne: '1 published app uses this connection (as of {when}).',
    policyAffectsNames: 'Affects {count} published apps: {names}.',
    policyAffectsOneName: 'Affects 1 published app: {names}.',
    policyGenericWiden:
      'Published apps that use this connection keep how they connect today. Their creators are asked to republish to use the new option.',
    policyGenericNarrow:
      'Published apps that use this connection check the current setting each time they connect.',
    policyAffectedGeneric:
      'Published apps that use this connection are not changed automatically. Their creators are asked to republish.',
    policyUnavailable: 'This setting is not available right now.',
    outsideRead: 'Let {app} read from {hosts}',
    outsideReadSend: 'Let {app} read from and send data to {hosts}',
    outsideNone: 'Stop {app} from reaching {hosts}',
    outsideReadDone: '{app} can read from {hosts}',
    outsideReadSendDone: '{app} can read from and send data to {hosts}',
    outsideNoneDone: '{app} no longer reaches {hosts}',
    outsideMore: '{count} more',
    eventWebhookOn: 'Let {app} receive “{event}” at its webhook',
    eventWebhookOnDone: '{app} receives “{event}” at its webhook',
    eventWebhookOff: 'Stop {app} from receiving “{event}” at its webhook',
    eventWebhookOffDone: '{app} no longer receives “{event}”',
    eventRowsOn: 'Let {app} run {runs} when rows in {collection} are {changes}',
    eventRowsOnDone: '{app} runs {runs} when rows in {collection} are {changes}',
    eventRowsOff: 'Stop {app} from running {runs} when rows in {collection} change',
    eventRowsOffDone: '{app} no longer runs {runs} when rows in {collection} change',
    eventInsert: 'added',
    eventUpdate: 'changed',
    eventDelete: 'deleted',
    scheduleTurnOn: 'Turn on “{job}” in {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: '“{job}” in {app} is on. {cadence} ({timezone}).',
    scheduleResume: 'Resume “{job}” in {app}. {cadence} ({timezone}).',
    scheduleResumeDone: '“{job}” in {app} is on again. {cadence} ({timezone}).',
    scheduleReschedule: 'Change when “{job}” runs in {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone: '“{job}” in {app} has a new schedule. {cadence} ({timezone}).',
    schedulePause: 'Pause “{job}” in {app}.',
    schedulePauseDone: '“{job}” in {app} is paused.',
    scheduleNext: 'Next: {runs}.',
    scheduleResumeSaved:
      'Resume “{job}” in {app} on the latest saved version. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      '“{job}” in {app} is on again and runs the latest saved version. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Resume “{job}” in {app} on the published version. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      '“{job}” in {app} is on again and runs the published version. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Change when “{job}” runs in {app}. {cadence} ({timezone}). It will be paused until you resume it.',
    scheduleReschedulePausedDone:
      '“{job}” in {app} has a new schedule. {cadence} ({timezone}). It is paused until you resume it.',
    scheduleSwitchSaved:
      'Switch “{job}” in {app} to the latest saved version. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      '“{job}” in {app} now runs the latest saved version. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Switch “{job}” in {app} to the latest saved version. {cadence} ({timezone}). It will be paused until you resume it.',
    scheduleSwitchSavedPausedDone:
      '“{job}” in {app} now uses the latest saved version. {cadence} ({timezone}). It is paused until you resume it.',
    scheduleSwitchPublished:
      'Switch “{job}” in {app} to the published version. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      '“{job}” in {app} now runs the published version. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Switch “{job}” in {app} to the published version. {cadence} ({timezone}). It will be paused until you resume it.',
    scheduleSwitchPublishedPausedDone:
      '“{job}” in {app} now uses the published version. {cadence} ({timezone}). It is paused until you resume it.',
    dataWritesOn: 'Let {app}’s “{function}” {verbs} its own rows',
    dataWritesOnDone: '{app}’s “{function}” can {verbs} its own rows',
    dataWritesOff: 'Stop {app}’s “{function}” from changing its own rows',
    dataWritesOffDone: '{app}’s “{function}” no longer changes its own rows',
    dataWriteInsert: 'add',
    dataWriteUpdate: 'change',
    dataWriteDelete: 'delete',
    tableDropField: 'Let {app} drop the field “{field}” from {table} ({rows})',
    tableDropFieldDone: '{app} dropped the field “{field}” from {table} ({rows})',
    tableDropTable: 'Let {app} drop the table {table} ({rows})',
    tableDropTableDone: '{app} dropped the table {table} ({rows})',
    tableClear: 'Let {app} clear “{field}” in {table} where it breaks the new rule ({rows})',
    tableClearDone: '{app} cleared “{field}” in {table} where it broke the new rule ({rows})',
    tableRestoreField: 'Let {app} put back “{field}” in {table} from its copy ({rows})',
    tableRestoreFieldDone: '{app} put back “{field}” in {table} ({rows})',
    tableRestoreTable: 'Let {app} restore the table {table} from its copy ({rows})',
    tableRestoreTableDone: '{app} restored the table {table} ({rows})',
    tableRowsLoseOne: '1 row loses a value',
    tableRowsLoseMany: '{count} rows lose a value',
    tableRowsLostOne: '1 row lost a value',
    tableRowsLostMany: '{count} rows lost a value',
    tableRowsOne: '1 row',
    tableRowsMany: '{count} rows',
    connectedWriteOn: 'Let {app} {action} in {destination}',
    connectedWriteOnDone: '{app} can {action} in {destination}',
    connectedWriteOff: 'Stop {app} from writing to {destination}',
    connectedWriteOffDone: '{app} no longer writes to {destination}',
    codeConfirm: 'Let {app} use your approvals with the changes by {names}',
    codeConfirmDone: '{app} uses your approvals with the changes by {names}',
    codeConfirmCurrent: 'Let {app} use your approvals with its current code',
    codeConfirmCurrentDone: '{app} uses your approvals with its current code',
    runChanges: 'Run “{function}” in {app} now',
    runChangesDone: '“{function}” ran in {app}',
    continueApproved: 'Approved. Continue: run it and check that it works.',
    continueDeclined: 'Declined. Continue without it.',
    continueMixed: 'I’ve decided on the requests. Continue.',
    typeOutsideAccess: 'Let an App reach outside services',
    typeEvent: 'Turn an App event on or off',
    typeSchedule: 'Turn an App schedule on, pause it or change it',
    typeDataWrites: 'Let an App function change its own rows',
    typeTableChange: 'Drop or restore data in an App’s tables',
    typeConnectedWrite: 'Let an App write to a company system',
    typeConfirmCode: 'Let changed App code use your approvals',
    typeRunChanges: 'Run an App function that changes data',
    typePublicLink: 'Change an App’s public link',
    typeUnpublish: 'Take down an App’s public link',
    labelHosts: 'Services',
    labelAccess: 'Access',
    labelEvent: 'Event',
    labelKind: 'Starts on',
    labelCollection: 'Collection',
    labelChanges: 'Row changes',
    labelRuns: 'Runs',
    labelFunction: 'Function',
    labelCollections: 'Tables',
    labelWho: 'For',
    labelAllowed: 'Allowed',
    labelEnabled: 'State',
    labelJob: 'Scheduled job',
    labelSchedule: 'Schedule',
    labelTimezone: 'Time zone',
    labelChange: 'Change',
    labelTable: 'Table',
    labelField: 'Field',
    labelRows: 'Rows',
    labelKeptDays: 'Copy kept (days)',
    labelRestores: 'Restores',
    labelAction: 'Action',
    labelDestination: 'Destination',
    labelChangedBy: 'Changed by',
    labelInput: 'Input',
    labelPublicTables: 'Copied tables',
    labelPublicActions: 'Visitors can send',
    labelPublicRuns: 'Each submission starts',
    labelPublicRunsOff: 'Would also start once turned on',
    labelEmbedSites: 'Sites that may embed it',
    labelPublicLink: 'Public link',
    labelPublishedBy: 'Published by',
    wordRead: 'Read only',
    wordReadSend: 'Read and send data',
    wordNoAccess: 'No access',
    publicPublish: 'Publish to a public link',
    publicRefresh: 'Copy its data again',
    publicUnpublish: 'Unpublish',
    eventWebhook: 'Its webhook',
    eventRows: 'Changes to its rows',
    switchOn: 'On',
    switchOff: 'Off',
    switchPaused: 'Paused',
    changeInsert: 'Added',
    changeUpdate: 'Changed',
    changeDelete: 'Deleted',
    wordDropField: 'Drop a field’s values',
    wordDropTable: 'Drop a table',
    tableClearValues: 'Clear values that break a stricter rule',
    tableRestore: 'Restore from its copy',
    whoOwner: 'Your own runs',
    whoEveryone: 'Everyone who uses it',
    answerYes: 'Yes',
    answerNo: 'No',
  },
  et: {
    kindWrite: 'Kinnitamist vajav rakenduse muudatus',
    kindGrant: 'Kinnitamist vajav rakenduse juurdepääs',
    kindBusiness: 'Rakenduse kinnitamine',
    approve: 'Kinnita',
    reject: 'Lükka tagasi',
    working: 'Töötlemine…',
    checkStatus: 'Kontrolli olekut',
    statusStillPending:
      'Poneva pole seda veel lahendanud. Seda võidakse veel töödelda, seega ei saa enne midagi muud otsustada.',
    statusStillUnknown:
      'Me ei saanud vastust. Enne selle lahendamist ei saa midagi muud otsustada. Proovi hetke pärast uuesti.',
    loading: 'Laadimine…',
    unavailable: 'See pole praegu saadaval.',
    loadFailed: 'Me ei saanud seda laadida. Proovi hetke pärast uuesti.',
    requestedBy: 'Küsija: {name}',
    originApp: 'Rakendusest',
    originAgent: 'Agendilt',
    originClient: 'Kliendi {client} kaudu',
    originFor: 'kasutaja {name} jaoks',
    changedUnderYou:
      'See muutus lugemise ajal. Praegu näed uusimat versiooni. Midagi ei otsustatud.',
    notAllowed: 'Sa ei saa seda otsustada.',
    cannotDecideNow: 'Seda ei saa praegu otsustada. Midagi ei saadetud.',
    expired: 'See taotlus on aegunud. Midagi ei muutunud.',
    stale: 'Andmed muutusid pärast selle taotlemist. Värske taotluse saamiseks küsi uuesti.',
    approved: 'Kinnitatud',
    rejected: 'Tagasi lükatud',
    needsYou: 'Vajab kinnitamist',
    waitingOn: 'Ootab kasutaja {name} otsust',
    unknownTitle: 'Me ei saa toimunut kinnitada',
    unknownBody:
      'Muudatus võidi teha või mitte. Ära korda seda. Kontrolli olekut või palu rakenduse omanikul järele vaadata.',
    blockedApproval: 'Ootab enne jätkumist selle kinnitamist.',
    blockedBudget: 'Peatatud: selle rakenduse tehisintellekti eelarve on otsas.',
    blockedProvider: 'Peatatud: ühendatud teenus ei vasta.',
    blockedViewer:
      'Peatatud: see peab käivituma sisseloginud vaatajana, kuid ühtegi vaatajat pole ühendatud. Omaniku juurdepääsu selle asemel ei kasutata.',
    writeTitle: 'Kinnita muudatused sihtkohas {target}',
    writeSummaryAll: '{count} muudatust rakenduses {app}',
    rowCreate: 'Uus',
    rowUpdate: 'Muudetud',
    rowDelete: 'Eemaldatud',
    rowInclude: 'Kaasa see muudatus',
    rowExcluded: 'Välja jäetud',
    fieldBefore: 'Enne',
    fieldAfter: 'Pärast',
    fieldEmpty: 'Tühi',
    approveSome: 'Kinnita {included} muudatust {total}-st',
    approveAll: 'Kinnita kõik {total} muudatust',
    allLeftOut:
      'Kõik muudatused on välja jäetud. Peatamiseks lükka tagasi või lisa kinnitamiseks mõni muudatus.',
    appliedSome: 'Rakendati {count} muudatust.',
    rejectedBody: 'Midagi ei muudetud.',
    noRows: 'Siin pole midagi kinnitada.',
    grantTitle: 'Luba rakendusel {app} kasutada ühendust {source}',
    grantRead: 'Ainult lugemine',
    grantWrite: 'Lugemine ja muutmine',
    audienceOwner: 'Ainult sina näed selle rakenduse andmeid allikast {source}.',
    audiencePeople:
      'Andmeid allikast {source} näevad {count} inimest, kellega seda rakendust jagatakse.',
    audienceCompany:
      'Andmeid allikast {source} näevad kõik ettevõtte töötajad, kes rakenduse avavad.',
    grantRouted: 'Seda otsustab {owner}, seega saadeti see talle.',
    grantRoutedUnknown: 'Seda otsustab selle ühenduse omanik, seega saadeti see talle.',
    grantWhat: 'Juurdepääs',
    businessStep: 'Samm {order}',
    stepWaiting: 'Ootel',
    stepPending: 'Vajab otsust',
    stepApproved: 'Kinnitatud',
    stepRejected: 'Tagasi lükatud',
    stepYou: 'Sina',
    businessNote: 'Siin kinnitamine ei luba Ponevas uusi muudatusi.',
    businessYourTurn: 'On sinu kord otsustada.',
    businessNotYourTurn: 'Ootab esmalt kasutaja {name} otsust.',
    policyTitle: 'Kuidas rakendused seda ühendust kasutavad',
    policyCreator: 'Ainult looja ühendus',
    policyCreatorBody: 'Kõik näevad samu andmeid rakenduse looja ühenduse kaudu.',
    policyViewer: 'Ainult vaataja enda ühendus',
    policyViewerBody: 'Iga inimene näeb oma andmeid oma ühenduse kaudu.',
    policyBoth: 'Mõlemad',
    policyBothBody: 'Rakenduse looja valib selle iga rakenduse jaoks.',
    policyRevoked: 'Rakendused ei saa seda ühendust kasutada',
    policyRevokedBody: 'Selle uuesti lubamiseks vali suvand. Sinu eest ei valita midagi.',
    policySave: 'Salvesta',
    policySaving: 'Salvestamine…',
    policySaved: 'Salvestatud.',
    policyReadOnly: 'Seda saab muuta ainult ettevõtte administraator.',
    policyConflict: 'Keegi teine muutis seda enne sind. See on praegune seade.',
    policyRefused: 'Seda ei saanud salvestada.',
    policyFailed:
      'Me ei saanud kinnitada, et see salvestati. Enne uuesti proovimist laadige praeguse seade nägemiseks leht uuesti.',
    policyWidened:
      'Juba avaldatud rakendused säilitavad praeguse ühendusviisi. Nende loojatel palutakse uue valiku kasutamiseks rakendus uuesti avaldada.',
    policyNarrowed: 'Rakendused kontrollivad ühenduse loomisel iga kord praegust seadet.',
    policyAffected: 'Seda ühendust kasutab {count} avaldatud rakendust ({when} seisuga).',
    policyAffectedOne: 'Seda ühendust kasutab 1 avaldatud rakendus ({when} seisuga).',
    policyAffectsNames: 'Mõjutatud avaldatud rakendused ({count}): {names}.',
    policyAffectsOneName: 'Mõjutatud avaldatud rakendus (1): {names}.',
    policyGenericWiden:
      'Seda ühendust kasutavad avaldatud rakendused säilitavad praeguse ühendusviisi. Nende loojatel palutakse uue valiku kasutamiseks rakendus uuesti avaldada.',
    policyGenericNarrow:
      'Seda ühendust kasutavad avaldatud rakendused kontrollivad ühenduse loomisel iga kord praegust seadet.',
    policyAffectedGeneric:
      'Seda ühendust kasutavaid avaldatud rakendusi ei muudeta automaatselt. Nende loojatel palutakse rakendus uuesti avaldada.',
    policyUnavailable: 'See seade pole praegu saadaval.',
    outsideRead: 'Luba rakendusel {app} lugeda andmeid aadressilt {hosts}',
    outsideReadSend:
      'Luba rakendusel {app} lugeda andmeid aadressilt {hosts} ja saata sinna andmeid',
    outsideNone: 'Keela rakendusel {app} aadressile {hosts} juurde pääseda',
    outsideReadDone: '{app} saab lugeda andmeid aadressilt {hosts}',
    outsideReadSendDone: '{app} saab lugeda andmeid aadressilt {hosts} ja saata sinna andmeid',
    outsideNoneDone: '{app} ei pääse enam aadressile {hosts}',
    outsideMore: 'veel {count}',
    eventWebhookOn: 'Luba rakendusel {app} oma veebikonksu kaudu vastu võtta sündmus „{event}“',
    eventWebhookOnDone: '{app} võtab oma veebikonksu kaudu vastu sündmuse „{event}“',
    eventWebhookOff: 'Keela rakendusel {app} oma veebikonksu kaudu vastu võtta sündmust „{event}“',
    eventWebhookOffDone: '{app} ei võta enam vastu sündmust „{event}“',
    eventRowsOn:
      'Luba rakendusel {app} käivitada {runs}, kui tabeli {collection} ridu on {changes}',
    eventRowsOnDone: '{app} käivitab {runs}, kui tabeli {collection} ridu on {changes}',
    eventRowsOff: 'Keela rakendusel {app} käivitada {runs}, kui tabeli {collection} read muutuvad',
    eventRowsOffDone: '{app} ei käivita enam {runs}, kui tabeli {collection} read muutuvad',
    eventInsert: 'lisatud',
    eventUpdate: 'muudetud',
    eventDelete: 'kustutatud',
    scheduleTurnOn: 'Lülita sisse töö „{job}“ ajakava rakenduses {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Töö „{job}“ ajakava rakenduses {app} on sees. {cadence} ({timezone}).',
    scheduleResume: 'Jätka töö „{job}“ ajakava rakenduses {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Töö „{job}“ ajakava rakenduses {app} on jälle sees. {cadence} ({timezone}).',
    scheduleReschedule: 'Muuda töö „{job}“ ajakava rakenduses {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Töö „{job}“ ajakava rakenduses {app} on muudetud. {cadence} ({timezone}).',
    schedulePause: 'Peata töö „{job}“ ajakava rakenduses {app}.',
    schedulePauseDone: 'Töö „{job}“ ajakava rakenduses {app} on peatatud.',
    scheduleNext: 'Järgmised käivitused: {runs}.',
    scheduleResumeSaved:
      'Jätka töö „{job}“ ajakava rakenduses {app} viimati salvestatud versiooniga. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Töö „{job}“ ajakava rakenduses {app} on jälle sees ja käivitab viimati salvestatud versiooni. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Jätka töö „{job}“ ajakava rakenduses {app} avaldatud versiooniga. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Töö „{job}“ ajakava rakenduses {app} on jälle sees ja käivitab avaldatud versiooni. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Muuda töö „{job}“ ajakava rakenduses {app}. {cadence} ({timezone}). Pärast seda on see peatatud, kuni selle jätkad.',
    scheduleReschedulePausedDone:
      'Töö „{job}“ ajakava rakenduses {app} on muudetud. {cadence} ({timezone}). See on peatatud, kuni selle jätkad.',
    scheduleSwitchSaved:
      'Lülita töö „{job}“ ajakava rakenduses {app} ümber viimati salvestatud versioonile. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Töö „{job}“ ajakava rakenduses {app} käivitab nüüd viimati salvestatud versiooni. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Lülita töö „{job}“ ajakava rakenduses {app} ümber viimati salvestatud versioonile. {cadence} ({timezone}). Pärast seda on see peatatud, kuni selle jätkad.',
    scheduleSwitchSavedPausedDone:
      'Töö „{job}“ ajakava rakenduses {app} kasutab nüüd viimati salvestatud versiooni. {cadence} ({timezone}). See on peatatud, kuni selle jätkad.',
    scheduleSwitchPublished:
      'Lülita töö „{job}“ ajakava rakenduses {app} ümber avaldatud versioonile. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Töö „{job}“ ajakava rakenduses {app} käivitab nüüd avaldatud versiooni. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Lülita töö „{job}“ ajakava rakenduses {app} ümber avaldatud versioonile. {cadence} ({timezone}). Pärast seda on see peatatud, kuni selle jätkad.',
    scheduleSwitchPublishedPausedDone:
      'Töö „{job}“ ajakava rakenduses {app} kasutab nüüd avaldatud versiooni. {cadence} ({timezone}). See on peatatud, kuni selle jätkad.',
    dataWritesOn: 'Luba rakenduse {app} funktsioonil „{function}“ oma ridu {verbs}',
    dataWritesOnDone: 'Rakenduse {app} funktsioon „{function}“ saab oma ridu {verbs}',
    dataWritesOff: 'Keela rakenduse {app} funktsioonil „{function}“ oma ridu muuta',
    dataWritesOffDone: 'Rakenduse {app} funktsioon „{function}“ ei muuda enam oma ridu',
    dataWriteInsert: 'lisada',
    dataWriteUpdate: 'muuta',
    dataWriteDelete: 'kustutada',
    tableDropField: 'Luba rakendusel {app} kustutada väli „{field}“ tabelist {table} ({rows})',
    tableDropFieldDone: '{app} kustutas välja „{field}“ tabelist {table} ({rows})',
    tableDropTable: 'Luba rakendusel {app} kustutada tabel {table} ({rows})',
    tableDropTableDone: '{app} kustutas tabeli {table} ({rows})',
    tableClear:
      'Luba rakendusel {app} tühjendada „{field}“ tabelis {table}, kus see rikub uut reeglit ({rows})',
    tableClearDone:
      '{app} tühjendas „{field}“ tabelis {table}, kus see rikkus uut reeglit ({rows})',
    tableRestoreField: 'Luba rakendusel {app} taastada „{field}“ tabelis {table} koopiast ({rows})',
    tableRestoreFieldDone: '{app} taastas „{field}“ tabelis {table} ({rows})',
    tableRestoreTable: 'Luba rakendusel {app} taastada tabel {table} koopiast ({rows})',
    tableRestoreTableDone: '{app} taastas tabeli {table} ({rows})',
    tableRowsLoseOne: '1 rida kaotab väärtuse',
    tableRowsLoseMany: '{count} rida kaotab väärtuse',
    tableRowsLostOne: '1 rida kaotas väärtuse',
    tableRowsLostMany: '{count} rida kaotas väärtuse',
    tableRowsOne: '1 rida',
    tableRowsMany: '{count} rida',
    connectedWriteOn: 'Luba rakendusel {app} {action} sihtkohas {destination}',
    connectedWriteOnDone: '{app} saab {action} sihtkohas {destination}',
    connectedWriteOff: 'Keela rakendusel {app} sihtkohta {destination} kirjutada',
    connectedWriteOffDone: '{app} ei kirjuta enam sihtkohta {destination}',
    codeConfirm:
      'Luba rakendusel {app} kasutada sinu kinnitusi koos kasutajate {names} tehtud muudatustega',
    codeConfirmDone: '{app} kasutab sinu kinnitusi koos kasutajate {names} tehtud muudatustega',
    codeConfirmCurrent: 'Luba rakendusel {app} kasutada sinu kinnitusi koos selle praeguse koodiga',
    codeConfirmCurrentDone: '{app} kasutab sinu kinnitusi koos oma praeguse koodiga',
    runChanges: 'Käivita „{function}“ rakenduses {app} nüüd',
    runChangesDone: '„{function}“ käivitati rakenduses {app}',
    continueApproved: 'Kinnitatud. Jätka: käivita see ja kontrolli, et see töötab.',
    continueDeclined: 'Tagasi lükatud. Jätka ilma selleta.',
    continueMixed: 'Olen taotlused otsustanud. Jätka.',
    typeOutsideAccess: 'Luba rakendusel pääseda välistele teenustele',
    typeEvent: 'Lülita rakenduse sündmus sisse või välja',
    typeSchedule: 'Lülita rakenduse ajakava sisse, peata see või muuda seda',
    typeDataWrites: 'Luba rakenduse funktsioonil oma ridu muuta',
    typeTableChange: 'Kustuta või taasta andmeid rakenduse tabelites',
    typeConnectedWrite: 'Luba rakendusel kirjutada ettevõtte süsteemi',
    typeConfirmCode: 'Lase muudetud rakenduse koodil kasutada sinu kinnitusi',
    typeRunChanges: 'Käivita rakenduse funktsioon, mis muudab andmeid',
    typePublicLink: 'Muuda rakenduse avalikku linki',
    typeUnpublish: 'Eemalda rakenduse avalik link',
    labelHosts: 'Teenused',
    labelAccess: 'Juurdepääs',
    labelEvent: 'Sündmus',
    labelKind: 'Käivitub, kui',
    labelCollection: 'Kogum',
    labelChanges: 'Ridade muudatused',
    labelRuns: 'Käivitused',
    labelFunction: 'Funktsioon',
    labelCollections: 'Tabelid',
    labelWho: 'Kellele',
    labelAllowed: 'Lubatud',
    labelEnabled: 'Olek',
    labelJob: 'Ajastatud töö',
    labelSchedule: 'Ajakava',
    labelTimezone: 'Ajavöönd',
    labelChange: 'Muudatus',
    labelTable: 'Tabel',
    labelField: 'Väli',
    labelRows: 'Read',
    labelKeptDays: 'Koopiat hoitakse (päeva)',
    labelRestores: 'Taastab',
    labelAction: 'Toiming',
    labelDestination: 'Sihtkoht',
    labelChangedBy: 'Muutja',
    labelInput: 'Sisend',
    labelPublicTables: 'Kopeeritud tabelid',
    labelPublicActions: 'Külastajad saavad saata',
    labelPublicRuns: 'Iga saadetis käivitab',
    labelPublicRunsOff: 'Käivituks ka pärast sisselülitamist',
    labelEmbedSites: 'Saidid, mis võivad seda manustada',
    labelPublicLink: 'Avalik link',
    labelPublishedBy: 'Avaldaja',
    wordRead: 'Ainult lugemine',
    wordReadSend: 'Andmete lugemine ja saatmine',
    wordNoAccess: 'Juurdepääsu pole',
    publicPublish: 'Avalda avalikul lingil',
    publicRefresh: 'Kopeeri selle andmed uuesti',
    publicUnpublish: 'Võta avaldamine tagasi',
    eventWebhook: 'Selle veebikonks',
    eventRows: 'Muudatused selle ridades',
    switchOn: 'Sees',
    switchOff: 'Väljas',
    switchPaused: 'Peatatud',
    changeInsert: 'Lisatud',
    changeUpdate: 'Muudetud',
    changeDelete: 'Kustutatud',
    wordDropField: 'Kustuta välja väärtused',
    wordDropTable: 'Kustuta tabel',
    tableClearValues: 'Tühjenda väärtused, mis rikuvad rangemat reeglit',
    tableRestore: 'Taasta koopiast',
    whoOwner: 'Sinu enda käivitused',
    whoEveryone: 'Kõik, kes seda kasutavad',
    answerYes: 'Jah',
    answerNo: 'Ei',
  },
  fi: {
    kindWrite: 'Hyväksyttävä sovelluksen muutos',
    kindGrant: 'Hyväksyttävä sovelluksen käyttöoikeus',
    kindBusiness: 'Sovelluksen hyväksyntä',
    approve: 'Hyväksy',
    reject: 'Hylkää',
    working: 'Käsitellään…',
    checkStatus: 'Tarkista tila',
    statusStillPending:
      'Poneva ei ole vielä käsitellyt tätä. Käsittely saattaa olla vielä kesken, joten mitään muuta ei voi päättää ennen sitä.',
    statusStillUnknown:
      'Emme saaneet vastausta. Mitään muuta ei voi päättää ennen kuin tämä on ratkaistu. Yritä hetken kuluttua uudelleen.',
    loading: 'Ladataan…',
    unavailable: 'Tämä ei ole juuri nyt käytettävissä.',
    loadFailed: 'Emme voineet ladata tätä. Yritä hetken kuluttua uudelleen.',
    requestedBy: 'Pyytäjä: {name}',
    originApp: 'Sovelluksesta',
    originAgent: 'Agentilta',
    originClient: 'Palvelun {client} kautta',
    originFor: 'henkilölle {name}',
    changedUnderYou:
      'Tämä muuttui, kun luit sitä. Näet nyt ajantasaisen version. Mitään ei päätetty.',
    notAllowed: 'Et voi päättää tästä.',
    cannotDecideNow: 'Tästä ei voi päättää juuri nyt. Mitään ei lähetetty.',
    expired: 'Tämä pyyntö on vanhentunut. Mikään ei muuttunut.',
    stale:
      'Tiedot muuttuivat pyynnön tekemisen jälkeen. Tee uusi pyyntö saadaksesi ajantasaisen pyynnön.',
    approved: 'Hyväksytty',
    rejected: 'Hylätty',
    needsYou: 'Odottaa hyväksyntää',
    waitingOn: 'Odottaa henkilöä {name}',
    unknownTitle: 'Emme voi vahvistaa, mitä tapahtui',
    unknownBody:
      'Muutos on ehkä tehty tai ehkä ei. Älä toista toimintoa. Tarkista tila tai pyydä sovelluksen omistajaa selvittämään asia.',
    blockedApproval: 'Odottaa tätä hyväksyntää ennen jatkamista.',
    blockedBudget: 'Keskeytetty: sovelluksen tekoälybudjetti on käytetty loppuun.',
    blockedProvider: 'Keskeytetty: yhdistetty palvelu ei vastaa.',
    blockedViewer:
      'Keskeytetty: tämä on suoritettava sisäänkirjautuneena katselijana, mutta katselijaa ei ole liitetty. Toimintoa ei suoriteta omistajan käyttöoikeuksilla.',
    writeTitle: 'Hyväksy kohteen {target} muutokset',
    writeSummaryAll: '{count} muutosta sovelluksessa {app}',
    rowCreate: 'Uusi',
    rowUpdate: 'Muutettu',
    rowDelete: 'Poistettu',
    rowInclude: 'Sisällytä tämä muutos',
    rowExcluded: 'Jätetty pois',
    fieldBefore: 'Ennen',
    fieldAfter: 'Jälkeen',
    fieldEmpty: 'Tyhjä',
    approveSome: 'Hyväksy {included}/{total}',
    approveAll: 'Hyväksy kaikki {total}',
    allLeftOut:
      'Kaikki muutokset on jätetty pois. Lopeta hylkäämällä tai sisällytä muutos hyväksyäksesi sen.',
    appliedSome: '{count} muutosta tehty.',
    rejectedBody: 'Mitään ei muutettu.',
    noRows: 'Täällä ei ole mitään hyväksyttävää.',
    grantTitle: 'Salli sovelluksen {app} käyttää lähdettä {source}',
    grantRead: 'Vain luku',
    grantWrite: 'Luku ja muokkaus',
    audienceOwner: 'Vain sinä näet tämän sovelluksen lähteen {source} tiedot.',
    audiencePeople:
      'Ne {count} henkilöä, joiden kanssa sovellus on jaettu, näkevät lähteen {source} tiedot.',
    audienceCompany:
      'Kaikki yrityksessä, jotka avaavat sovelluksen, näkevät lähteen {source} tiedot.',
    grantRouted: '{owner} päättää tästä, joten se lähetettiin hänelle.',
    grantRoutedUnknown: 'Tämän yhteyden omistaja päättää tästä, joten se lähetettiin hänelle.',
    grantWhat: 'Käyttöoikeus',
    businessStep: 'Vaihe {order}',
    stepWaiting: 'Odottaa',
    stepPending: 'Odottaa päätöstä',
    stepApproved: 'Hyväksytty',
    stepRejected: 'Hylätty',
    stepYou: 'Sinä',
    businessNote: 'Tämän hyväksyminen ei salli uusia muutoksia Ponevan ulkopuolella.',
    businessYourTurn: 'Sinun vuorosi päättää.',
    businessNotYourTurn: 'Odotetaan ensin henkilön {name} päätöstä.',
    policyTitle: 'Miten sovellukset käyttävät tätä yhteyttä',
    policyCreator: 'Vain luojan yhteys',
    policyCreatorBody: 'Kaikki näkevät samat tiedot sovelluksen luojan yhteyden kautta.',
    policyViewer: 'Vain katselijan oma yhteys',
    policyViewerBody: 'Jokainen näkee omat tietonsa oman yhteytensä kautta.',
    policyBoth: 'Molemmat',
    policyBothBody: 'Sovelluksen luoja valitsee yhteyden kullekin sovellukselle.',
    policyRevoked: 'Sovellukset eivät voi käyttää tätä yhteyttä',
    policyRevokedBody:
      'Valitse vaihtoehto salliaksesi käytön uudelleen. Mitään ei valita puolestasi.',
    policySave: 'Tallenna',
    policySaving: 'Tallennetaan…',
    policySaved: 'Tallennettu.',
    policyReadOnly: 'Vain yrityksen ylläpitäjä voi muuttaa tätä.',
    policyConflict: 'Joku muu muutti tätä ensin. Tämä on nykyinen asetus.',
    policyRefused: 'Tätä ei voitu tallentaa.',
    policyFailed:
      'Emme voineet vahvistaa, että tämä tallennettiin. Lataa sivu uudelleen nähdäksesi nykyisen asetuksen ennen uutta yritystä.',
    policyWidened:
      'Jo julkaistut sovellukset säilyttävät nykyisen yhteystapansa. Niiden tekijöitä pyydetään julkaisemaan ne uudelleen uuden vaihtoehdon käyttöä varten.',
    policyNarrowed: 'Sovellukset tarkistavat nykyisen asetuksen aina yhdistäessään.',
    policyAffected:
      '{count} julkaistua sovellusta käyttää tätä yhteyttä ({when} tilanteen mukaan).',
    policyAffectedOne: '1 julkaistu sovellus käyttää tätä yhteyttä ({when} tilanteen mukaan).',
    policyAffectsNames: 'Vaikuttaa seuraaviin julkaistuihin sovelluksiin ({count}): {names}.',
    policyAffectsOneName: 'Vaikuttaa yhteen julkaistuun sovellukseen: {names}.',
    policyGenericWiden:
      'Tätä yhteyttä käyttävät julkaistut sovellukset säilyttävät nykyisen yhteystapansa. Niiden tekijöitä pyydetään julkaisemaan ne uudelleen uuden vaihtoehdon käyttöä varten.',
    policyGenericNarrow:
      'Tätä yhteyttä käyttävät julkaistut sovellukset tarkistavat nykyisen asetuksen aina yhdistäessään.',
    policyAffectedGeneric:
      'Tätä yhteyttä käyttävien julkaistujen sovellusten asetuksia ei muuteta automaattisesti. Niiden tekijöitä pyydetään julkaisemaan sovellukset uudelleen.',
    policyUnavailable: 'Tämä asetus ei ole tällä hetkellä käytettävissä.',
    outsideRead: 'Salli sovelluksen {app} lukea tietoja palvelusta {hosts}',
    outsideReadSend:
      'Salli sovelluksen {app} lukea tietoja palvelusta {hosts} ja lähettää sinne tietoja',
    outsideNone: 'Estä sovellusta {app} tavoittamasta palvelua {hosts}',
    outsideReadDone: '{app} voi lukea tietoja palvelusta {hosts}',
    outsideReadSendDone: '{app} voi lukea tietoja palvelusta {hosts} ja lähettää sinne tietoja',
    outsideNoneDone: '{app} ei enää tavoita palvelua {hosts}',
    outsideMore: '{count} lisää',
    eventWebhookOn: 'Salli sovelluksen {app} vastaanottaa tapahtuma “{event}” webhookiinsa',
    eventWebhookOnDone: '{app} vastaanottaa tapahtuman “{event}” webhookiinsa',
    eventWebhookOff: 'Estä sovellusta {app} vastaanottamasta tapahtumaa “{event}” webhookiinsa',
    eventWebhookOffDone: '{app} ei enää vastaanota tapahtumaa “{event}”',
    eventRowsOn:
      'Salli sovelluksen {app} suorittaa {runs}, kun taulukon {collection} rivejä on {changes}',
    eventRowsOnDone: '{app} suorittaa {runs}, kun taulukon {collection} rivejä on {changes}',
    eventRowsOff:
      'Estä sovellusta {app} suorittamasta {runs}, kun taulukon {collection} rivit muuttuvat',
    eventRowsOffDone: '{app} ei enää suorita {runs}, kun taulukon {collection} rivit muuttuvat',
    eventInsert: 'lisätty',
    eventUpdate: 'muutettu',
    eventDelete: 'poistettu',
    scheduleTurnOn:
      'Ota käyttöön työn “{job}” aikataulu sovelluksessa {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone:
      'Työn “{job}” aikataulu on käytössä sovelluksessa {app}. {cadence} ({timezone}).',
    scheduleResume: 'Jatka työn “{job}” aikataulua sovelluksessa {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Työn “{job}” aikataulu on taas käytössä sovelluksessa {app}. {cadence} ({timezone}).',
    scheduleReschedule:
      'Muuta työn “{job}” aikataulua sovelluksessa {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Työn “{job}” aikataulu sovelluksessa {app} on muutettu. {cadence} ({timezone}).',
    schedulePause: 'Keskeytä työn “{job}” aikataulu sovelluksessa {app}.',
    schedulePauseDone: 'Työn “{job}” aikataulu sovelluksessa {app} on keskeytetty.',
    scheduleNext: 'Seuraavat suoritukset: {runs}.',
    scheduleResumeSaved:
      'Jatka työn “{job}” aikataulua sovelluksessa {app} viimeksi tallennetulla versiolla. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Työn “{job}” aikataulu on taas käytössä sovelluksessa {app} ja suorittaa viimeksi tallennetun version. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Jatka työn “{job}” aikataulua sovelluksessa {app} julkaistulla versiolla. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Työn “{job}” aikataulu on taas käytössä sovelluksessa {app} ja suorittaa julkaistun version. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Muuta työn “{job}” aikataulua sovelluksessa {app}. {cadence} ({timezone}). Sen jälkeen se on keskeytetty, kunnes jatkat sitä.',
    scheduleReschedulePausedDone:
      'Työn “{job}” aikataulu sovelluksessa {app} on muutettu. {cadence} ({timezone}). Se on keskeytetty, kunnes jatkat sitä.',
    scheduleSwitchSaved:
      'Vaihda työn “{job}” aikataulu sovelluksessa {app} viimeksi tallennettuun versioon. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Työn “{job}” aikataulu sovelluksessa {app} suorittaa nyt viimeksi tallennetun version. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Vaihda työn “{job}” aikataulu sovelluksessa {app} viimeksi tallennettuun versioon. {cadence} ({timezone}). Sen jälkeen se on keskeytetty, kunnes jatkat sitä.',
    scheduleSwitchSavedPausedDone:
      'Työn “{job}” aikataulu sovelluksessa {app} käyttää nyt viimeksi tallennettua versiota. {cadence} ({timezone}). Se on keskeytetty, kunnes jatkat sitä.',
    scheduleSwitchPublished:
      'Vaihda työn “{job}” aikataulu sovelluksessa {app} julkaistuun versioon. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Työn “{job}” aikataulu sovelluksessa {app} suorittaa nyt julkaistun version. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Vaihda työn “{job}” aikataulu sovelluksessa {app} julkaistuun versioon. {cadence} ({timezone}). Sen jälkeen se on keskeytetty, kunnes jatkat sitä.',
    scheduleSwitchPublishedPausedDone:
      'Työn “{job}” aikataulu sovelluksessa {app} käyttää nyt julkaistua versiota. {cadence} ({timezone}). Se on keskeytetty, kunnes jatkat sitä.',
    dataWritesOn: 'Salli sovelluksen {app} funktion “{function}” {verbs} omia rivejään',
    dataWritesOnDone: 'Sovelluksen {app} funktio “{function}” voi {verbs} omia rivejään',
    dataWritesOff: 'Estä sovelluksen {app} funktiota “{function}” muuttamasta omia rivejään',
    dataWritesOffDone: 'Sovelluksen {app} funktio “{function}” ei enää muuta omia rivejään',
    dataWriteInsert: 'lisätä',
    dataWriteUpdate: 'muuttaa',
    dataWriteDelete: 'poistaa',
    tableDropField: 'Salli sovelluksen {app} poistaa kenttä “{field}” taulukosta {table} ({rows})',
    tableDropFieldDone: '{app} poisti kentän “{field}” taulukosta {table} ({rows})',
    tableDropTable: 'Salli sovelluksen {app} poistaa taulukko {table} ({rows})',
    tableDropTableDone: '{app} poisti taulukon {table} ({rows})',
    tableClear:
      'Salli sovelluksen {app} tyhjentää “{field}” taulukossa {table}, missä se rikkoo uutta sääntöä ({rows})',
    tableClearDone:
      '{app} tyhjensi “{field}” taulukossa {table}, missä se rikkoi uutta sääntöä ({rows})',
    tableRestoreField:
      'Salli sovelluksen {app} palauttaa “{field}” taulukkoon {table} kopiosta ({rows})',
    tableRestoreFieldDone: '{app} palautti “{field}” taulukkoon {table} ({rows})',
    tableRestoreTable: 'Salli sovelluksen {app} palauttaa taulukko {table} kopiosta ({rows})',
    tableRestoreTableDone: '{app} palautti taulukon {table} ({rows})',
    tableRowsLoseOne: '1 rivi menettää arvon',
    tableRowsLoseMany: '{count} riviä menettää arvon',
    tableRowsLostOne: '1 rivi menetti arvon',
    tableRowsLostMany: '{count} riviä menetti arvon',
    tableRowsOne: '1 rivi',
    tableRowsMany: '{count} riviä',
    connectedWriteOn: 'Salli sovelluksen {app} {action} kohteessa {destination}',
    connectedWriteOnDone: '{app} voi {action} kohteessa {destination}',
    connectedWriteOff: 'Estä sovellusta {app} kirjoittamasta kohteeseen {destination}',
    connectedWriteOffDone: '{app} ei enää kirjoita kohteeseen {destination}',
    codeConfirm:
      'Salli sovelluksen {app} käyttää hyväksyntöjäsi käyttäjien {names} muutosten kanssa',
    codeConfirmDone: '{app} käyttää hyväksyntöjäsi käyttäjien {names} muutosten kanssa',
    codeConfirmCurrent: 'Salli sovelluksen {app} käyttää hyväksyntöjäsi sen nykyisellä koodilla',
    codeConfirmCurrentDone: '{app} käyttää hyväksyntöjäsi nykyisellä koodillaan',
    runChanges: 'Suorita “{function}” sovelluksessa {app} nyt',
    runChangesDone: '“{function}” suoritettiin sovelluksessa {app}',
    continueApproved: 'Hyväksytty. Jatka: suorita se ja tarkista, että se toimii.',
    continueDeclined: 'Hylätty. Jatka ilman sitä.',
    continueMixed: 'Olen päättänyt pyynnöistä. Jatka.',
    typeOutsideAccess: 'Salli sovelluksen käyttää ulkoisia palveluita',
    typeEvent: 'Ota sovelluksen tapahtuma käyttöön tai pois käytöstä',
    typeSchedule: 'Ota sovelluksen aikataulu käyttöön, keskeytä se tai muuta sitä',
    typeDataWrites: 'Salli sovelluksen toiminnon muuttaa omia rivejään',
    typeTableChange: 'Poista tai palauta tietoja sovelluksen taulukoista',
    typeConnectedWrite: 'Salli sovelluksen kirjoittaa yrityksen järjestelmään',
    typeConfirmCode: 'Anna muutetun sovelluskoodin käyttää hyväksyntiäsi',
    typeRunChanges: 'Suorita sovelluksen toiminto, joka muuttaa tietoja',
    typePublicLink: 'Muuta sovelluksen julkista linkkiä',
    typeUnpublish: 'Poista sovelluksen julkinen linkki',
    labelHosts: 'Palvelut',
    labelAccess: 'Käyttöoikeus',
    labelEvent: 'Tapahtuma',
    labelKind: 'Käynnistyy, kun',
    labelCollection: 'Kokoelma',
    labelChanges: 'Rivimuutokset',
    labelRuns: 'Suoritukset',
    labelFunction: 'Toiminto',
    labelCollections: 'Taulukot',
    labelWho: 'Kenelle',
    labelAllowed: 'Sallittu',
    labelEnabled: 'Tila',
    labelJob: 'Ajastettu työ',
    labelSchedule: 'Aikataulu',
    labelTimezone: 'Aikavyöhyke',
    labelChange: 'Muutos',
    labelTable: 'Taulukko',
    labelField: 'Kenttä',
    labelRows: 'Rivit',
    labelKeptDays: 'Kopio säilytetään (päivää)',
    labelRestores: 'Palauttaa',
    labelAction: 'Toiminto',
    labelDestination: 'Kohde',
    labelChangedBy: 'Muuttaja',
    labelInput: 'Syöte',
    labelPublicTables: 'Kopioidut taulukot',
    labelPublicActions: 'Kävijät voivat lähettää',
    labelPublicRuns: 'Jokainen lähetys käynnistää',
    labelPublicRunsOff: 'Käynnistyisi myös, kun se otetaan käyttöön',
    labelEmbedSites: 'Sivustot, jotka saavat upottaa sen',
    labelPublicLink: 'Julkinen linkki',
    labelPublishedBy: 'Julkaisija',
    wordRead: 'Vain luku',
    wordReadSend: 'Lue ja lähetä tietoja',
    wordNoAccess: 'Ei käyttöoikeutta',
    publicPublish: 'Julkaise julkiseen linkkiin',
    publicRefresh: 'Kopioi sen tiedot uudelleen',
    publicUnpublish: 'Poista julkaisu',
    eventWebhook: 'Sen webhook',
    eventRows: 'Muutokset sen riveihin',
    switchOn: 'Käytössä',
    switchOff: 'Pois käytöstä',
    switchPaused: 'Keskeytetty',
    changeInsert: 'Lisätty',
    changeUpdate: 'Muutettu',
    changeDelete: 'Poistettu',
    wordDropField: 'Poista kentän arvot',
    wordDropTable: 'Poista taulukko',
    tableClearValues: 'Tyhjennä arvot, jotka rikkovat tiukempaa sääntöä',
    tableRestore: 'Palauta kopiosta',
    whoOwner: 'Omat suorituksesi',
    whoEveryone: 'Kaikki, jotka käyttävät sitä',
    answerYes: 'Kyllä',
    answerNo: 'Ei',
  },
  fr: {
    kindWrite: 'Modification d’application à approuver',
    kindGrant: 'Accès à une application à approuver',
    kindBusiness: 'Approbation pour une application',
    approve: 'Approuver',
    reject: 'Refuser',
    working: 'En cours…',
    checkStatus: 'Vérifier l’état',
    statusStillPending:
      'Poneva n’a pas encore statué. La demande est peut-être toujours en cours de traitement : aucune autre décision ne peut être prise avant sa résolution.',
    statusStillUnknown:
      'Nous n’avons pas pu obtenir de réponse. Aucune autre décision ne peut être prise avant la résolution de ce problème. Réessayez dans un instant.',
    loading: 'Chargement…',
    unavailable: 'Cette fonctionnalité est indisponible pour le moment.',
    loadFailed: 'Nous n’avons pas pu charger ces données. Réessayez dans un instant.',
    requestedBy: 'Demandé par {name}',
    originApp: 'Depuis l’application',
    originAgent: 'Depuis un agent',
    originClient: 'Via {client}',
    originFor: 'pour {name}',
    changedUnderYou:
      'Cette demande a changé pendant que vous la consultiez. La version actuelle est affichée. Aucune décision n’a été prise.',
    notAllowed: 'Vous ne pouvez pas prendre cette décision.',
    cannotDecideNow: 'Cette décision ne peut pas être prise pour le moment. Rien n’a été envoyé.',
    expired: 'Cette demande a expiré. Rien n’a changé.',
    stale:
      'Les données ont changé depuis cette demande. Demandez-la à nouveau pour obtenir une demande à jour.',
    approved: 'Approuvée',
    rejected: 'Refusée',
    needsYou: 'Approbation requise',
    waitingOn: 'En attente de {name}',
    unknownTitle: 'Impossible de confirmer ce qui s’est passé',
    unknownBody:
      'La modification a peut-être été effectuée, ou non. Ne la répétez pas. Vérifiez l’état ou demandez au propriétaire de l’application de vérifier.',
    blockedApproval: 'En attente de cette approbation avant de continuer.',
    blockedBudget: 'En pause : le budget d’IA de cette application est épuisé.',
    blockedProvider: 'En pause : un service connecté ne répond pas.',
    blockedViewer:
      'En pause : cette opération doit être exécutée par un utilisateur connecté, mais aucun utilisateur n’est associé. L’accès du propriétaire ne sera pas utilisé à la place.',
    writeTitle: 'Approuver les modifications apportées à {target}',
    writeSummaryAll: '{count} modifications dans {app}',
    rowCreate: 'Nouvelle',
    rowUpdate: 'Modifiée',
    rowDelete: 'Supprimée',
    rowInclude: 'Inclure cette modification',
    rowExcluded: 'Exclue',
    fieldBefore: 'Avant',
    fieldAfter: 'Après',
    fieldEmpty: 'Vide',
    approveSome: 'Approuver {included} sur {total}',
    approveAll: 'Approuver les {total}',
    allLeftOut:
      'Toutes les modifications sont exclues. Refusez pour arrêter ou incluez une modification pour l’approuver.',
    appliedSome: '{count} modifications effectuées.',
    rejectedBody: 'Aucune modification n’a été effectuée.',
    noRows: 'Il n’y a rien à approuver ici.',
    grantTitle: 'Autoriser {app} à utiliser {source}',
    grantRead: 'Lecture seule',
    grantWrite: 'Lecture et modification',
    audienceOwner: 'Vous seul verrez les données de cette application provenant de {source}.',
    audiencePeople:
      'Les {count} personnes avec qui cette application est partagée verront les données provenant de {source}.',
    audienceCompany:
      'Toute personne de l’entreprise qui ouvre l’application verra les données provenant de {source}.',
    grantRouted: '{owner} prend cette décision, la demande lui a donc été envoyée.',
    grantRoutedUnknown:
      'Le propriétaire de cette connexion prend cette décision, la demande lui a donc été envoyée.',
    grantWhat: 'Accès',
    businessStep: 'Étape {order}',
    stepWaiting: 'En attente',
    stepPending: 'Décision requise',
    stepApproved: 'Approuvée',
    stepRejected: 'Refusée',
    stepYou: 'Vous',
    businessNote: 'Cette approbation n’autorise aucune nouvelle modification en dehors de Poneva.',
    businessYourTurn: 'C’est à vous de décider.',
    businessNotYourTurn: 'En attente de la décision de {name}.',
    policyTitle: 'Utilisation de cette connexion par les applications',
    policyCreator: 'Uniquement celle du créateur',
    policyCreatorBody:
      'Tout le monde voit les mêmes données, via la connexion du créateur de l’application.',
    policyViewer: 'Uniquement celle de l’utilisateur',
    policyViewerBody: 'Chaque personne voit ses propres données, via sa propre connexion.',
    policyBoth: 'Les deux',
    policyBothBody: 'Le créateur de l’application choisit pour chaque application.',
    policyRevoked: 'Les applications ne peuvent pas utiliser cette connexion',
    policyRevokedBody:
      'Choisissez une option pour l’autoriser à nouveau. Aucun choix ne sera fait à votre place.',
    policySave: 'Enregistrer',
    policySaving: 'Enregistrement…',
    policySaved: 'Enregistré.',
    policyReadOnly: 'Seul un administrateur de l’entreprise peut modifier ce paramètre.',
    policyConflict:
      'Quelqu’un d’autre a effectué une modification avant vous. Voici le paramètre actuel.',
    policyRefused: 'Impossible d’enregistrer.',
    policyFailed:
      'Impossible de confirmer l’enregistrement. Rechargez la page pour voir le réglage actuel avant de réessayer.',
    policyWidened:
      'Les Apps déjà publiées conservent leur mode de connexion actuel. Leurs créateurs sont invités à les republier pour utiliser la nouvelle option.',
    policyNarrowed: 'Les Apps vérifient le réglage actuel à chaque connexion.',
    policyAffected: '{count} Apps publiées utilisent cette connexion (au {when}).',
    policyAffectedOne: '1 App publiée utilise cette connexion (au {when}).',
    policyAffectsNames: 'Concerne {count} Apps publiées : {names}.',
    policyAffectsOneName: 'Concerne 1 App publiée : {names}.',
    policyGenericWiden:
      'Les Apps publiées qui utilisent cette connexion conservent leur mode de connexion actuel. Leurs créateurs sont invités à les republier pour utiliser la nouvelle option.',
    policyGenericNarrow:
      'Les Apps publiées qui utilisent cette connexion vérifient le réglage actuel à chaque connexion.',
    policyAffectedGeneric:
      'Les Apps publiées qui utilisent cette connexion ne sont pas modifiées automatiquement. Leurs créateurs sont invités à les republier.',
    policyUnavailable: 'Ce réglage n’est pas disponible pour le moment.',
    outsideRead: 'Autoriser {app} à lire depuis {hosts}',
    outsideReadSend: 'Autoriser {app} à lire depuis {hosts} et à y envoyer des données',
    outsideNone: 'Empêcher {app} d’accéder à {hosts}',
    outsideReadDone: '{app} peut lire depuis {hosts}',
    outsideReadSendDone: '{app} peut lire depuis {hosts} et y envoyer des données',
    outsideNoneDone: '{app} n’accède plus à {hosts}',
    outsideMore: '{count} de plus',
    eventWebhookOn: 'Autoriser {app} à recevoir « {event} » sur son webhook',
    eventWebhookOnDone: '{app} reçoit « {event} » sur son webhook',
    eventWebhookOff: 'Empêcher {app} de recevoir « {event} » sur son webhook',
    eventWebhookOffDone: '{app} ne reçoit plus « {event} »',
    eventRowsOn:
      'Autoriser {app} à exécuter {runs} quand des lignes de {collection} sont {changes}',
    eventRowsOnDone: '{app} exécute {runs} quand des lignes de {collection} sont {changes}',
    eventRowsOff: 'Empêcher {app} d’exécuter {runs} quand des lignes de {collection} changent',
    eventRowsOffDone: '{app} n’exécute plus {runs} quand des lignes de {collection} changent',
    eventInsert: 'ajoutées',
    eventUpdate: 'modifiées',
    eventDelete: 'supprimées',
    scheduleTurnOn: 'Activer le planning de « {job} » dans {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Le planning de « {job} » dans {app} est activé. {cadence} ({timezone}).',
    scheduleResume: 'Reprendre le planning de « {job} » dans {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Le planning de « {job} » dans {app} est de nouveau activé. {cadence} ({timezone}).',
    scheduleReschedule: 'Modifier le planning de « {job} » dans {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Le planning de « {job} » dans {app} a été modifié. {cadence} ({timezone}).',
    schedulePause: 'Mettre en pause le planning de « {job} » dans {app}.',
    schedulePauseDone: 'Le planning de « {job} » dans {app} est en pause.',
    scheduleNext: 'Prochaines exécutions : {runs}.',
    scheduleResumeSaved:
      'Reprendre le planning de « {job} » dans {app} avec la dernière version enregistrée. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Le planning de « {job} » dans {app} est de nouveau activé et exécute la dernière version enregistrée. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Reprendre le planning de « {job} » dans {app} avec la version publiée. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Le planning de « {job} » dans {app} est de nouveau activé et exécute la version publiée. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Modifier le planning de « {job} » dans {app}. {cadence} ({timezone}). Il sera en pause jusqu’à ce que vous le repreniez.',
    scheduleReschedulePausedDone:
      'Le planning de « {job} » dans {app} a été modifié. {cadence} ({timezone}). Il est en pause jusqu’à ce que vous le repreniez.',
    scheduleSwitchSaved:
      'Passer le planning de « {job} » dans {app} à la dernière version enregistrée. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Le planning de « {job} » dans {app} exécute désormais la dernière version enregistrée. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Passer le planning de « {job} » dans {app} à la dernière version enregistrée. {cadence} ({timezone}). Il sera en pause jusqu’à ce que vous le repreniez.',
    scheduleSwitchSavedPausedDone:
      'Le planning de « {job} » dans {app} utilise désormais la dernière version enregistrée. {cadence} ({timezone}). Il est en pause jusqu’à ce que vous le repreniez.',
    scheduleSwitchPublished:
      'Passer le planning de « {job} » dans {app} à la version publiée. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Le planning de « {job} » dans {app} exécute désormais la version publiée. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Passer le planning de « {job} » dans {app} à la version publiée. {cadence} ({timezone}). Il sera en pause jusqu’à ce que vous le repreniez.',
    scheduleSwitchPublishedPausedDone:
      'Le planning de « {job} » dans {app} utilise désormais la version publiée. {cadence} ({timezone}). Il est en pause jusqu’à ce que vous le repreniez.',
    dataWritesOn: 'Autoriser « {function} » de {app} à {verbs} ses propres lignes',
    dataWritesOnDone: '« {function} » de {app} peut {verbs} ses propres lignes',
    dataWritesOff: 'Empêcher « {function} » de {app} de modifier ses propres lignes',
    dataWritesOffDone: '« {function} » de {app} ne modifie plus ses propres lignes',
    dataWriteInsert: 'ajouter',
    dataWriteUpdate: 'modifier',
    dataWriteDelete: 'supprimer',
    tableDropField: 'Autoriser {app} à supprimer le champ « {field} » de {table} ({rows})',
    tableDropFieldDone: '{app} a supprimé le champ « {field} » de {table} ({rows})',
    tableDropTable: 'Autoriser {app} à supprimer la table {table} ({rows})',
    tableDropTableDone: '{app} a supprimé la table {table} ({rows})',
    tableClear:
      'Autoriser {app} à vider « {field} » dans {table} là où il enfreint la nouvelle règle ({rows})',
    tableClearDone:
      '{app} a vidé « {field} » dans {table} là où il enfreignait la nouvelle règle ({rows})',
    tableRestoreField:
      'Autoriser {app} à rétablir « {field} » dans {table} depuis sa copie ({rows})',
    tableRestoreFieldDone: '{app} a rétabli « {field} » dans {table} ({rows})',
    tableRestoreTable: 'Autoriser {app} à restaurer la table {table} depuis sa copie ({rows})',
    tableRestoreTableDone: '{app} a restauré la table {table} ({rows})',
    tableRowsLoseOne: '1 ligne perd une valeur',
    tableRowsLoseMany: '{count} lignes perdent une valeur',
    tableRowsLostOne: '1 ligne a perdu une valeur',
    tableRowsLostMany: '{count} lignes ont perdu une valeur',
    tableRowsOne: '1 ligne',
    tableRowsMany: '{count} lignes',
    connectedWriteOn: 'Autoriser {app} à {action} dans {destination}',
    connectedWriteOnDone: '{app} peut {action} dans {destination}',
    connectedWriteOff: 'Empêcher {app} d’écrire dans {destination}',
    connectedWriteOffDone: '{app} n’écrit plus dans {destination}',
    codeConfirm: 'Autoriser {app} à utiliser vos approbations avec les modifications de {names}',
    codeConfirmDone: '{app} utilise vos approbations avec les modifications de {names}',
    codeConfirmCurrent: 'Autoriser {app} à utiliser vos approbations avec son code actuel',
    codeConfirmCurrentDone: '{app} utilise vos approbations avec son code actuel',
    runChanges: 'Exécuter « {function} » dans {app} maintenant',
    runChangesDone: '« {function} » a été exécutée dans {app}',
    continueApproved: 'Approuvé. Continuez : exécutez-le et vérifiez que cela fonctionne.',
    continueDeclined: 'Refusé. Continuez sans.',
    continueMixed: 'J’ai statué sur les demandes. Continuez.',
    typeOutsideAccess: 'Autoriser une app à joindre des services externes',
    typeEvent: 'Activer ou désactiver un événement d’app',
    typeSchedule: 'Activer, mettre en pause ou modifier le planning d’une app',
    typeDataWrites: 'Autoriser une fonction d’app à modifier ses propres lignes',
    typeTableChange: 'Supprimer ou restaurer des données dans les tables d’une app',
    typeConnectedWrite: 'Autoriser une app à écrire dans un système de l’entreprise',
    typeConfirmCode: 'Laisser le code modifié d’une app utiliser vos approbations',
    typeRunChanges: 'Exécuter une fonction d’app qui modifie des données',
    typePublicLink: 'Modifier le lien public d’une app',
    typeUnpublish: 'Retirer le lien public d’une app',
    labelHosts: 'Services',
    labelAccess: 'Accès',
    labelEvent: 'Événement',
    labelKind: 'Se déclenche sur',
    labelCollection: 'Collection',
    labelChanges: 'Modifications de lignes',
    labelRuns: 'Exécutions',
    labelFunction: 'Fonction',
    labelCollections: 'Tables',
    labelWho: 'Pour',
    labelAllowed: 'Autorisé',
    labelEnabled: 'État',
    labelJob: 'Tâche planifiée',
    labelSchedule: 'Planning',
    labelTimezone: 'Fuseau horaire',
    labelChange: 'Modification',
    labelTable: 'Table',
    labelField: 'Champ',
    labelRows: 'Lignes',
    labelKeptDays: 'Copie conservée (jours)',
    labelRestores: 'Restaure',
    labelAction: 'Action',
    labelDestination: 'Destination',
    labelChangedBy: 'Modifié par',
    labelInput: 'Entrée',
    labelPublicTables: 'Tables copiées',
    labelPublicActions: 'Les visiteurs peuvent envoyer',
    labelPublicRuns: 'Chaque envoi déclenche',
    labelPublicRunsOff: 'Se déclencherait aussi une fois activé',
    labelEmbedSites: 'Sites autorisés à l’intégrer',
    labelPublicLink: 'Lien public',
    labelPublishedBy: 'Publié par',
    wordRead: 'Lecture seule',
    wordReadSend: 'Lire et envoyer des données',
    wordNoAccess: 'Aucun accès',
    publicPublish: 'Publier sur un lien public',
    publicRefresh: 'Copier à nouveau ses données',
    publicUnpublish: 'Dépublier',
    eventWebhook: 'Son webhook',
    eventRows: 'Modifications de ses lignes',
    switchOn: 'Activé',
    switchOff: 'Désactivé',
    switchPaused: 'En pause',
    changeInsert: 'Ajouté',
    changeUpdate: 'Modifié',
    changeDelete: 'Supprimé',
    wordDropField: 'Supprimer les valeurs d’un champ',
    wordDropTable: 'Supprimer une table',
    tableClearValues: 'Effacer les valeurs qui enfreignent une règle plus stricte',
    tableRestore: 'Restaurer depuis sa copie',
    whoOwner: 'Vos propres exécutions',
    whoEveryone: 'Toutes les personnes qui l’utilisent',
    answerYes: 'Oui',
    answerNo: 'Non',
  },
  de: {
    kindWrite: 'Zu genehmigende App-Änderung',
    kindGrant: 'Zu genehmigender App-Zugriff',
    kindBusiness: 'Genehmigung für eine App',
    approve: 'Genehmigen',
    reject: 'Ablehnen',
    working: 'Wird bearbeitet…',
    checkStatus: 'Status prüfen',
    statusStillPending:
      'Poneva hat dies noch nicht entschieden. Die Verarbeitung läuft möglicherweise noch. Bis dahin kann nichts anderes entschieden werden.',
    statusStillUnknown:
      'Wir konnten keine Antwort erhalten. Bis dies geklärt ist, kann nichts anderes entschieden werden. Versuchen Sie es gleich noch einmal.',
    loading: 'Wird geladen…',
    unavailable: 'Dies ist derzeit nicht verfügbar.',
    loadFailed: 'Dies konnte nicht geladen werden. Versuchen Sie es gleich noch einmal.',
    requestedBy: 'Angefordert von {name}',
    originApp: 'Aus der App',
    originAgent: 'Von einem Agenten',
    originClient: 'Über {client}',
    originFor: 'für {name}',
    changedUnderYou:
      'Dies wurde geändert, während Sie es gelesen haben. Sie sehen jetzt die aktuelle Version. Es wurde nichts entschieden.',
    notAllowed: 'Sie können dies nicht entscheiden.',
    cannotDecideNow: 'Dies kann derzeit nicht entschieden werden. Es wurde nichts gesendet.',
    expired: 'Diese Anfrage ist abgelaufen. Es wurde nichts geändert.',
    stale:
      'Die Daten wurden geändert, seit dies angefragt wurde. Fordern Sie eine neue Anfrage an.',
    approved: 'Genehmigt',
    rejected: 'Abgelehnt',
    needsYou: 'Genehmigung erforderlich',
    waitingOn: 'Wartet auf {name}',
    unknownTitle: 'Wir können nicht bestätigen, was passiert ist',
    unknownBody:
      'Die Änderung wurde möglicherweise vorgenommen oder auch nicht. Wiederholen Sie den Vorgang nicht. Prüfen Sie den Status oder bitten Sie den App-Eigentümer, dies zu überprüfen.',
    blockedApproval: 'Wartet auf diese Genehmigung, bevor der Vorgang fortgesetzt wird.',
    blockedBudget: 'Pausiert: Das KI-Budget für diese App ist aufgebraucht.',
    blockedProvider: 'Pausiert: Ein verbundener Dienst antwortet nicht.',
    blockedViewer:
      'Pausiert: Dies muss als angemeldeter Betrachter ausgeführt werden, aber es ist kein Betrachter verbunden. Stattdessen wird nichts über den Zugriff des Eigentümers ausgeführt.',
    writeTitle: 'Änderungen an {target} genehmigen',
    writeSummaryAll: '{count} Änderungen in {app}',
    rowCreate: 'Neu',
    rowUpdate: 'Geändert',
    rowDelete: 'Entfernt',
    rowInclude: 'Diese Änderung einschließen',
    rowExcluded: 'Ausgelassen',
    fieldBefore: 'Vorher',
    fieldAfter: 'Nachher',
    fieldEmpty: 'Leer',
    approveSome: '{included} von {total} genehmigen',
    approveAll: 'Alle {total} genehmigen',
    allLeftOut:
      'Alle Änderungen sind ausgelassen. Lehnen Sie sie ab, um den Vorgang zu beenden, oder schließen Sie eine Änderung ein, um sie zu genehmigen.',
    appliedSome: '{count} Änderungen vorgenommen.',
    rejectedBody: 'Es wurde nichts geändert.',
    noRows: 'Hier gibt es nichts zu genehmigen.',
    grantTitle: '{app} die Nutzung von {source} erlauben',
    grantRead: 'Nur lesen',
    grantWrite: 'Lesen und ändern',
    audienceOwner: 'Nur Sie sehen die Daten dieser App aus {source}.',
    audiencePeople:
      'Die {count} Personen, mit denen diese App geteilt wird, sehen Daten aus {source}.',
    audienceCompany: 'Alle Personen im Unternehmen, die die App öffnen, sehen Daten aus {source}.',
    grantRouted: '{owner} entscheidet dies, daher wurde es an diese Person gesendet.',
    grantRoutedUnknown:
      'Der Eigentümer dieser Verbindung entscheidet dies, daher wurde es an ihn gesendet.',
    grantWhat: 'Zugriff',
    businessStep: 'Schritt {order}',
    stepWaiting: 'Wartet',
    stepPending: 'Entscheidung erforderlich',
    stepApproved: 'Genehmigt',
    stepRejected: 'Abgelehnt',
    stepYou: 'Sie',
    businessNote: 'Eine Genehmigung hier erlaubt keine neue Änderung außerhalb von Poneva.',
    businessYourTurn: 'Sie sind an der Reihe zu entscheiden.',
    businessNotYourTurn: 'Wartet darauf, dass {name} zuerst entscheidet.',
    policyTitle: 'Wie Apps diese Verbindung nutzen',
    policyCreator: 'Nur über den Ersteller',
    policyCreatorBody: 'Alle sehen dieselben Daten über die Verbindung des App-Erstellers.',
    policyViewer: 'Nur über den jeweiligen Betrachter',
    policyViewerBody: 'Jede Person sieht ihre eigenen Daten über ihre eigene Verbindung.',
    policyBoth: 'Beide',
    policyBothBody: 'Der App-Ersteller entscheidet dies für jede App.',
    policyRevoked: 'Apps können diese Verbindung nicht nutzen',
    policyRevokedBody:
      'Wählen Sie eine Option, um die Nutzung wieder zu erlauben. Für Sie wird nichts ausgewählt.',
    policySave: 'Speichern',
    policySaving: 'Wird gespeichert…',
    policySaved: 'Gespeichert.',
    policyReadOnly: 'Nur ein Unternehmensadministrator kann dies ändern.',
    policyConflict: 'Jemand anderes hat dies zuerst geändert. Dies ist die aktuelle Einstellung.',
    policyRefused: 'Dies konnte nicht gespeichert werden.',
    policyFailed:
      'Wir konnten nicht bestätigen, dass dies gespeichert wurde. Laden Sie die Seite neu, um die aktuelle Einstellung zu sehen, bevor Sie es erneut versuchen.',
    policyWidened:
      'Bereits veröffentlichte Apps behalten die bisherige Verbindungsart bei. Ihre Ersteller werden aufgefordert, sie erneut zu veröffentlichen, um die neue Option zu verwenden.',
    policyNarrowed: 'Apps prüfen bei jeder Verbindung die aktuelle Einstellung.',
    policyAffected: '{count} veröffentlichte Apps verwenden diese Verbindung (Stand: {when}).',
    policyAffectedOne: '1 veröffentlichte App verwendet diese Verbindung (Stand: {when}).',
    policyAffectsNames: 'Betrifft {count} veröffentlichte Apps: {names}.',
    policyAffectsOneName: 'Betrifft 1 veröffentlichte App: {names}.',
    policyGenericWiden:
      'Veröffentlichte Apps, die diese Verbindung verwenden, behalten die bisherige Verbindungsart bei. Ihre Ersteller werden aufgefordert, sie erneut zu veröffentlichen, um die neue Option zu verwenden.',
    policyGenericNarrow:
      'Veröffentlichte Apps, die diese Verbindung verwenden, prüfen bei jeder Verbindung die aktuelle Einstellung.',
    policyAffectedGeneric:
      'Veröffentlichte Apps, die diese Verbindung verwenden, werden nicht automatisch geändert. Ihre Ersteller werden aufgefordert, sie erneut zu veröffentlichen.',
    policyUnavailable: 'Diese Einstellung ist derzeit nicht verfügbar.',
    outsideRead: '{app} das Lesen von {hosts} erlauben',
    outsideReadSend: '{app} das Lesen von und Senden von Daten an {hosts} erlauben',
    outsideNone: '{app} den Zugriff auf {hosts} entziehen',
    outsideReadDone: '{app} kann von {hosts} lesen',
    outsideReadSendDone: '{app} kann von {hosts} lesen und Daten dorthin senden',
    outsideNoneDone: '{app} greift nicht mehr auf {hosts} zu',
    outsideMore: '{count} weitere',
    eventWebhookOn: '{app} „{event}“ an seinem Webhook empfangen lassen',
    eventWebhookOnDone: '{app} empfängt „{event}“ an seinem Webhook',
    eventWebhookOff: '{app} den Empfang von „{event}“ an seinem Webhook entziehen',
    eventWebhookOffDone: '{app} empfängt „{event}“ nicht mehr',
    eventRowsOn: '{app} {runs} ausführen lassen, wenn Zeilen in {collection} {changes} werden',
    eventRowsOnDone: '{app} führt {runs} aus, wenn Zeilen in {collection} {changes} werden',
    eventRowsOff:
      '{app} nicht mehr {runs} ausführen lassen, wenn sich Zeilen in {collection} ändern',
    eventRowsOffDone: '{app} führt {runs} nicht mehr aus, wenn sich Zeilen in {collection} ändern',
    eventInsert: 'hinzugefügt',
    eventUpdate: 'geändert',
    eventDelete: 'gelöscht',
    scheduleTurnOn: 'Zeitplan von „{job}“ in {app} einschalten. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Der Zeitplan von „{job}“ in {app} ist an. {cadence} ({timezone}).',
    scheduleResume: 'Zeitplan von „{job}“ in {app} fortsetzen. {cadence} ({timezone}).',
    scheduleResumeDone: 'Der Zeitplan von „{job}“ in {app} ist wieder an. {cadence} ({timezone}).',
    scheduleReschedule: 'Zeitplan von „{job}“ in {app} ändern. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Der Zeitplan von „{job}“ in {app} wurde geändert. {cadence} ({timezone}).',
    schedulePause: 'Zeitplan von „{job}“ in {app} pausieren.',
    schedulePauseDone: 'Der Zeitplan von „{job}“ in {app} ist pausiert.',
    scheduleNext: 'Nächste Ausführungen: {runs}.',
    scheduleResumeSaved:
      'Zeitplan von „{job}“ in {app} mit der zuletzt gespeicherten Version fortsetzen. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Der Zeitplan von „{job}“ in {app} ist wieder an und führt die zuletzt gespeicherte Version aus. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Zeitplan von „{job}“ in {app} mit der veröffentlichten Version fortsetzen. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Der Zeitplan von „{job}“ in {app} ist wieder an und führt die veröffentlichte Version aus. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Zeitplan von „{job}“ in {app} ändern. {cadence} ({timezone}). Er ist danach pausiert, bis Sie ihn fortsetzen.',
    scheduleReschedulePausedDone:
      'Der Zeitplan von „{job}“ in {app} wurde geändert. {cadence} ({timezone}). Er ist pausiert, bis Sie ihn fortsetzen.',
    scheduleSwitchSaved:
      'Zeitplan von „{job}“ in {app} auf die zuletzt gespeicherte Version umstellen. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Der Zeitplan von „{job}“ in {app} führt jetzt die zuletzt gespeicherte Version aus. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Zeitplan von „{job}“ in {app} auf die zuletzt gespeicherte Version umstellen. {cadence} ({timezone}). Er ist danach pausiert, bis Sie ihn fortsetzen.',
    scheduleSwitchSavedPausedDone:
      'Der Zeitplan von „{job}“ in {app} verwendet jetzt die zuletzt gespeicherte Version. {cadence} ({timezone}). Er ist pausiert, bis Sie ihn fortsetzen.',
    scheduleSwitchPublished:
      'Zeitplan von „{job}“ in {app} auf die veröffentlichte Version umstellen. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Der Zeitplan von „{job}“ in {app} führt jetzt die veröffentlichte Version aus. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Zeitplan von „{job}“ in {app} auf die veröffentlichte Version umstellen. {cadence} ({timezone}). Er ist danach pausiert, bis Sie ihn fortsetzen.',
    scheduleSwitchPublishedPausedDone:
      'Der Zeitplan von „{job}“ in {app} verwendet jetzt die veröffentlichte Version. {cadence} ({timezone}). Er ist pausiert, bis Sie ihn fortsetzen.',
    dataWritesOn: '„{function}“ von {app} erlauben: eigene Zeilen {verbs}',
    dataWritesOnDone: '„{function}“ von {app} kann die eigenen Zeilen {verbs}',
    dataWritesOff: '„{function}“ von {app} das Ändern der eigenen Zeilen entziehen',
    dataWritesOffDone: '„{function}“ von {app} ändert die eigenen Zeilen nicht mehr',
    dataWriteInsert: 'hinzufügen',
    dataWriteUpdate: 'ändern',
    dataWriteDelete: 'löschen',
    tableDropField: '{app} das Feld „{field}“ aus {table} löschen lassen ({rows})',
    tableDropFieldDone: '{app} hat das Feld „{field}“ aus {table} gelöscht ({rows})',
    tableDropTable: '{app} die Tabelle {table} löschen lassen ({rows})',
    tableDropTableDone: '{app} hat die Tabelle {table} gelöscht ({rows})',
    tableClear:
      '{app} „{field}“ in {table} leeren lassen, wo es gegen die neue Regel verstößt ({rows})',
    tableClearDone:
      '{app} hat „{field}“ in {table} geleert, wo es gegen die neue Regel verstieß ({rows})',
    tableRestoreField: '{app} „{field}“ in {table} aus der Kopie wiederherstellen lassen ({rows})',
    tableRestoreFieldDone: '{app} hat „{field}“ in {table} wiederhergestellt ({rows})',
    tableRestoreTable: '{app} die Tabelle {table} aus der Kopie wiederherstellen lassen ({rows})',
    tableRestoreTableDone: '{app} hat die Tabelle {table} wiederhergestellt ({rows})',
    tableRowsLoseOne: '1 Zeile verliert einen Wert',
    tableRowsLoseMany: '{count} Zeilen verlieren einen Wert',
    tableRowsLostOne: '1 Zeile hat einen Wert verloren',
    tableRowsLostMany: '{count} Zeilen haben einen Wert verloren',
    tableRowsOne: '1 Zeile',
    tableRowsMany: '{count} Zeilen',
    connectedWriteOn: '{app} {action} in {destination} erlauben',
    connectedWriteOnDone: '{app} kann {action} in {destination}',
    connectedWriteOff: '{app} das Schreiben in {destination} entziehen',
    connectedWriteOffDone: '{app} schreibt nicht mehr in {destination}',
    codeConfirm: '{app} Ihre Genehmigungen mit den Änderungen von {names} verwenden lassen',
    codeConfirmDone: '{app} verwendet Ihre Genehmigungen mit den Änderungen von {names}',
    codeConfirmCurrent: '{app} Ihre Genehmigungen mit ihrem aktuellen Code verwenden lassen',
    codeConfirmCurrentDone: '{app} verwendet Ihre Genehmigungen mit ihrem aktuellen Code',
    runChanges: '„{function}“ in {app} jetzt ausführen',
    runChangesDone: '„{function}“ wurde in {app} ausgeführt',
    continueApproved: 'Genehmigt. Weiter: Führen Sie es aus und prüfen Sie, ob es funktioniert.',
    continueDeclined: 'Abgelehnt. Fahren Sie ohne dies fort.',
    continueMixed: 'Ich habe über die Anfragen entschieden. Fahren Sie fort.',
    typeOutsideAccess: 'Einer App den Zugriff auf externe Dienste erlauben',
    typeEvent: 'Ein App-Ereignis ein- oder ausschalten',
    typeSchedule: 'Den Zeitplan einer App einschalten, pausieren oder ändern',
    typeDataWrites: 'Einer App-Funktion erlauben, ihre eigenen Zeilen zu ändern',
    typeTableChange: 'Daten in den Tabellen einer App löschen oder wiederherstellen',
    typeConnectedWrite: 'Einer App erlauben, in ein Firmensystem zu schreiben',
    typeConfirmCode: 'Geänderten App-Code Ihre Genehmigungen nutzen lassen',
    typeRunChanges: 'Eine App-Funktion ausführen, die Daten ändert',
    typePublicLink: 'Den öffentlichen Link einer App ändern',
    typeUnpublish: 'Den öffentlichen Link einer App entfernen',
    labelHosts: 'Dienste',
    labelAccess: 'Zugriff',
    labelEvent: 'Ereignis',
    labelKind: 'Startet bei',
    labelCollection: 'Sammlung',
    labelChanges: 'Zeilenänderungen',
    labelRuns: 'Ausführungen',
    labelFunction: 'Funktion',
    labelCollections: 'Tabellen',
    labelWho: 'Für',
    labelAllowed: 'Erlaubt',
    labelEnabled: 'Status',
    labelJob: 'Geplanter Job',
    labelSchedule: 'Zeitplan',
    labelTimezone: 'Zeitzone',
    labelChange: 'Änderung',
    labelTable: 'Tabelle',
    labelField: 'Feld',
    labelRows: 'Zeilen',
    labelKeptDays: 'Kopie aufbewahrt (Tage)',
    labelRestores: 'Stellt wieder her',
    labelAction: 'Aktion',
    labelDestination: 'Ziel',
    labelChangedBy: 'Geändert von',
    labelInput: 'Eingabe',
    labelPublicTables: 'Kopierte Tabellen',
    labelPublicActions: 'Besucher können senden',
    labelPublicRuns: 'Jede Einsendung startet',
    labelPublicRunsOff: 'Würde nach dem Einschalten ebenfalls starten',
    labelEmbedSites: 'Websites, die sie einbetten dürfen',
    labelPublicLink: 'Öffentlicher Link',
    labelPublishedBy: 'Veröffentlicht von',
    wordRead: 'Nur lesen',
    wordReadSend: 'Lesen und Daten senden',
    wordNoAccess: 'Kein Zugriff',
    publicPublish: 'Unter einem öffentlichen Link veröffentlichen',
    publicRefresh: 'Die Daten der App erneut kopieren',
    publicUnpublish: 'Veröffentlichung zurücknehmen',
    eventWebhook: 'Der Webhook der App',
    eventRows: 'Änderungen an den Zeilen der App',
    switchOn: 'An',
    switchOff: 'Aus',
    switchPaused: 'Pausiert',
    changeInsert: 'Hinzugefügt',
    changeUpdate: 'Geändert',
    changeDelete: 'Gelöscht',
    wordDropField: 'Die Werte eines Felds löschen',
    wordDropTable: 'Eine Tabelle löschen',
    tableClearValues: 'Werte löschen, die gegen eine strengere Regel verstoßen',
    tableRestore: 'Aus der Kopie wiederherstellen',
    whoOwner: 'Ihre eigenen Ausführungen',
    whoEveryone: 'Alle, die sie nutzen',
    answerYes: 'Ja',
    answerNo: 'Nein',
  },
  el: {
    kindWrite: 'Αλλαγή εφαρμογής προς έγκριση',
    kindGrant: 'Πρόσβαση εφαρμογής προς έγκριση',
    kindBusiness: 'Έγκριση για εφαρμογή',
    approve: 'Έγκριση',
    reject: 'Απόρριψη',
    working: 'Σε εξέλιξη…',
    checkStatus: 'Έλεγχος κατάστασης',
    statusStillPending:
      'Η Poneva δεν έχει ολοκληρώσει ακόμη αυτή τη διαδικασία. Ίσως βρίσκεται ακόμη σε εξέλιξη, επομένως δεν μπορεί να ληφθεί άλλη απόφαση μέχρι να ολοκληρωθεί.',
    statusStillUnknown:
      'Δεν μπορέσαμε να λάβουμε απάντηση. Δεν μπορεί να ληφθεί άλλη απόφαση μέχρι να ολοκληρωθεί αυτή η διαδικασία. Δοκιμάστε ξανά σε λίγο.',
    loading: 'Φόρτωση…',
    unavailable: 'Αυτό δεν είναι διαθέσιμο αυτή τη στιγμή.',
    loadFailed: 'Δεν ήταν δυνατή η φόρτωση. Δοκιμάστε ξανά σε λίγο.',
    requestedBy: 'Ζητήθηκε από τον/την {name}',
    originApp: 'Από την εφαρμογή',
    originAgent: 'Από έναν πράκτορα',
    originClient: 'Μέσω του/της {client}',
    originFor: 'για τον/την {name}',
    changedUnderYou:
      'Αυτό άλλαξε ενώ το διαβάζατε. Αυτό που βλέπετε τώρα είναι η τρέχουσα έκδοση. Δεν λήφθηκε καμία απόφαση.',
    notAllowed: 'Δεν μπορείτε να αποφασίσετε για αυτό.',
    cannotDecideNow: 'Δεν μπορεί να ληφθεί απόφαση αυτή τη στιγμή. Δεν στάλθηκε τίποτα.',
    expired: 'Αυτό το αίτημα έληξε. Δεν έγινε καμία αλλαγή.',
    stale:
      'Τα δεδομένα άλλαξαν από τότε που υποβλήθηκε αυτό το αίτημα. Ζητήστε το ξανά για να λάβετε νέο αίτημα.',
    approved: 'Εγκρίθηκε',
    rejected: 'Απορρίφθηκε',
    needsYou: 'Απαιτείται έγκριση',
    waitingOn: 'Αναμονή για τον/την {name}',
    unknownTitle: 'Δεν μπορούμε να επιβεβαιώσουμε τι συνέβη',
    unknownBody:
      'Η αλλαγή μπορεί να έγινε ή να μην έγινε. Μην την επαναλάβετε. Ελέγξτε την κατάσταση ή ζητήστε από τον υπεύθυνο της εφαρμογής να το ελέγξει.',
    blockedApproval: 'Αναμονή για αυτή την έγκριση πριν συνεχιστεί.',
    blockedBudget: 'Σε παύση: ο προϋπολογισμός AI για αυτή την εφαρμογή εξαντλήθηκε.',
    blockedProvider: 'Σε παύση: μια συνδεδεμένη υπηρεσία δεν ανταποκρίνεται.',
    blockedViewer:
      'Σε παύση: αυτό πρέπει να εκτελεστεί ως συνδεδεμένος χρήστης προβολής και δεν έχει συνδεθεί κανένας. Δεν εκτελείται με την πρόσβαση του υπεύθυνου.',
    writeTitle: 'Έγκριση αλλαγών στο {target}',
    writeSummaryAll: '{count} αλλαγές στο {app}',
    rowCreate: 'Νέο',
    rowUpdate: 'Άλλαξε',
    rowDelete: 'Αφαιρέθηκε',
    rowInclude: 'Συμπερίληψη αυτής της αλλαγής',
    rowExcluded: 'Παραλείφθηκε',
    fieldBefore: 'Πριν',
    fieldAfter: 'Μετά',
    fieldEmpty: 'Κενό',
    approveSome: 'Έγκριση {included} από {total}',
    approveAll: 'Έγκριση και των {total}',
    allLeftOut:
      'Όλες οι αλλαγές έχουν παραλειφθεί. Απορρίψτε για διακοπή ή συμπεριλάβετε μια αλλαγή για να την εγκρίνετε.',
    appliedSome: 'Έγιναν {count} αλλαγές.',
    rejectedBody: 'Δεν έγινε καμία αλλαγή.',
    noRows: 'Δεν υπάρχει τίποτα προς έγκριση εδώ.',
    grantTitle: 'Παραχώρηση στο {app} χρήσης του {source}',
    grantRead: 'Μόνο ανάγνωση',
    grantWrite: 'Ανάγνωση και αλλαγές',
    audienceOwner: 'Μόνο εσείς θα βλέπετε τα δεδομένα αυτής της εφαρμογής από το {source}.',
    audiencePeople:
      'Τα {count} άτομα με τα οποία έχει κοινοποιηθεί αυτή η εφαρμογή θα βλέπουν δεδομένα από το {source}.',
    audienceCompany:
      'Όλοι στην εταιρεία που ανοίγουν την εφαρμογή θα βλέπουν δεδομένα από το {source}.',
    grantRouted: 'Ο/Η {owner} αποφασίζει για αυτό, επομένως στάλθηκε σε εκείνον/εκείνη.',
    grantRoutedUnknown:
      'Ο υπεύθυνος αυτής της σύνδεσης αποφασίζει για αυτό, επομένως στάλθηκε σε εκείνον/εκείνη.',
    grantWhat: 'Πρόσβαση',
    businessStep: 'Βήμα {order}',
    stepWaiting: 'Σε αναμονή',
    stepPending: 'Απαιτείται απόφαση',
    stepApproved: 'Εγκρίθηκε',
    stepRejected: 'Απορρίφθηκε',
    stepYou: 'Εσείς',
    businessNote: 'Η έγκριση εδώ δεν επιτρέπει καμία νέα αλλαγή εκτός της Poneva.',
    businessYourTurn: 'Είναι η σειρά σας να αποφασίσετε.',
    businessNotYourTurn: 'Αναμονή μέχρι να αποφασίσει πρώτα ο/η {name}.',
    policyTitle: 'Πώς χρησιμοποιούν οι εφαρμογές αυτή τη σύνδεση',
    policyCreator: 'Μόνο του δημιουργού',
    policyCreatorBody:
      'Όλοι βλέπουν τα ίδια δεδομένα, μέσω της σύνδεσης του δημιουργού της εφαρμογής.',
    policyViewer: 'Μόνο του ίδιου του χρήστη',
    policyViewerBody: 'Κάθε άτομο βλέπει τα δικά του δεδομένα, μέσω της δικής του σύνδεσης.',
    policyBoth: 'Και τα δύο',
    policyBothBody: 'Ο δημιουργός της εφαρμογής επιλέγει για κάθε εφαρμογή.',
    policyRevoked: 'Οι εφαρμογές δεν μπορούν να χρησιμοποιήσουν αυτή τη σύνδεση',
    policyRevokedBody:
      'Επιλέξτε μια ρύθμιση για να την επιτρέψετε ξανά. Δεν έχει επιλεγεί τίποτα για εσάς.',
    policySave: 'Αποθήκευση',
    policySaving: 'Αποθήκευση…',
    policySaved: 'Αποθηκεύτηκε.',
    policyReadOnly: 'Μόνο ένας διαχειριστής της εταιρείας μπορεί να το αλλάξει.',
    policyConflict: 'Κάποιος άλλος το άλλαξε πρώτος. Αυτή είναι η τρέχουσα ρύθμιση.',
    policyRefused: 'Δεν ήταν δυνατή η αποθήκευση.',
    policyFailed:
      'Δεν μπορέσαμε να επιβεβαιώσουμε ότι αποθηκεύτηκε. Φορτώστε ξανά για να δείτε την τρέχουσα ρύθμιση πριν δοκιμάσετε ξανά.',
    policyWidened:
      'Οι εφαρμογές που έχουν ήδη δημοσιευτεί διατηρούν τον τρέχοντα τρόπο σύνδεσής τους. Ζητείται από τους δημιουργούς τους να τις αναδημοσιεύσουν για να χρησιμοποιήσουν τη νέα επιλογή.',
    policyNarrowed: 'Οι εφαρμογές ελέγχουν την τρέχουσα ρύθμιση κάθε φορά που συνδέονται.',
    policyAffected: '{count} δημοσιευμένες εφαρμογές χρησιμοποιούν αυτήν τη σύνδεση (στις {when}).',
    policyAffectedOne: '1 δημοσιευμένη εφαρμογή χρησιμοποιεί αυτήν τη σύνδεση (στις {when}).',
    policyAffectsNames: 'Επηρεάζονται {count} δημοσιευμένες εφαρμογές: {names}.',
    policyAffectsOneName: 'Επηρεάζεται 1 δημοσιευμένη εφαρμογή: {names}.',
    policyGenericWiden:
      'Οι δημοσιευμένες εφαρμογές που χρησιμοποιούν αυτήν τη σύνδεση διατηρούν τον τρέχοντα τρόπο σύνδεσής τους. Ζητείται από τους δημιουργούς τους να τις αναδημοσιεύσουν για να χρησιμοποιήσουν τη νέα επιλογή.',
    policyGenericNarrow:
      'Οι δημοσιευμένες εφαρμογές που χρησιμοποιούν αυτήν τη σύνδεση ελέγχουν την τρέχουσα ρύθμιση κάθε φορά που συνδέονται.',
    policyAffectedGeneric:
      'Οι δημοσιευμένες εφαρμογές που χρησιμοποιούν αυτήν τη σύνδεση δεν αλλάζουν αυτόματα. Ζητείται από τους δημιουργούς τους να τις αναδημοσιεύσουν.',
    policyUnavailable: 'Αυτή η ρύθμιση δεν είναι διαθέσιμη αυτήν τη στιγμή.',
    outsideRead: 'Επιτρέψτε στο {app} να διαβάζει από {hosts}',
    outsideReadSend: 'Επιτρέψτε στο {app} να διαβάζει από {hosts} και να στέλνει δεδομένα σε αυτά',
    outsideNone: 'Διακοπή πρόσβασης του {app} σε {hosts}',
    outsideReadDone: 'Το {app} μπορεί να διαβάζει από {hosts}',
    outsideReadSendDone: 'Το {app} μπορεί να διαβάζει από {hosts} και να στέλνει δεδομένα σε αυτά',
    outsideNoneDone: 'Το {app} δεν έχει πλέον πρόσβαση σε {hosts}',
    outsideMore: 'ακόμη {count}',
    eventWebhookOn: 'Επιτρέψτε στο {app} να λαμβάνει «{event}» στο webhook του',
    eventWebhookOnDone: 'Το {app} λαμβάνει «{event}» στο webhook του',
    eventWebhookOff: 'Διακοπή λήψης «{event}» από το {app} στο webhook του',
    eventWebhookOffDone: 'Το {app} δεν λαμβάνει πλέον «{event}»',
    eventRowsOn: 'Επιτρέψτε στο {app} να εκτελεί {runs} όταν οι γραμμές σε {collection} {changes}',
    eventRowsOnDone: 'Το {app} εκτελεί {runs} όταν οι γραμμές σε {collection} {changes}',
    eventRowsOff: 'Διακοπή εκτέλεσης {runs} από το {app} όταν αλλάζουν οι γραμμές σε {collection}',
    eventRowsOffDone: 'Το {app} δεν εκτελεί πλέον {runs} όταν αλλάζουν οι γραμμές σε {collection}',
    eventInsert: 'προστίθενται',
    eventUpdate: 'αλλάζουν',
    eventDelete: 'διαγράφονται',
    scheduleTurnOn: 'Ενεργοποίηση του προγράμματος του «{job}» στο {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Το πρόγραμμα του «{job}» στο {app} είναι ενεργό. {cadence} ({timezone}).',
    scheduleResume: 'Συνέχιση του προγράμματος του «{job}» στο {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Το πρόγραμμα του «{job}» στο {app} είναι ξανά ενεργό. {cadence} ({timezone}).',
    scheduleReschedule: 'Αλλαγή του προγράμματος του «{job}» στο {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone: 'Το πρόγραμμα του «{job}» στο {app} άλλαξε. {cadence} ({timezone}).',
    schedulePause: 'Παύση του προγράμματος του «{job}» στο {app}.',
    schedulePauseDone: 'Το πρόγραμμα του «{job}» στο {app} είναι σε παύση.',
    scheduleNext: 'Επόμενες εκτελέσεις: {runs}.',
    scheduleResumeSaved:
      'Συνέχιση του προγράμματος του «{job}» στο {app} με την πιο πρόσφατη αποθηκευμένη έκδοση. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Το πρόγραμμα του «{job}» στο {app} είναι ξανά ενεργό και εκτελεί την πιο πρόσφατη αποθηκευμένη έκδοση. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Συνέχιση του προγράμματος του «{job}» στο {app} με τη δημοσιευμένη έκδοση. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Το πρόγραμμα του «{job}» στο {app} είναι ξανά ενεργό και εκτελεί τη δημοσιευμένη έκδοση. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Αλλαγή του προγράμματος του «{job}» στο {app}. {cadence} ({timezone}). Θα είναι σε παύση μέχρι να το συνεχίσετε.',
    scheduleReschedulePausedDone:
      'Το πρόγραμμα του «{job}» στο {app} άλλαξε. {cadence} ({timezone}). Είναι σε παύση μέχρι να το συνεχίσετε.',
    scheduleSwitchSaved:
      'Μετάβαση του προγράμματος του «{job}» στο {app} στην πιο πρόσφατη αποθηκευμένη έκδοση. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Το πρόγραμμα του «{job}» στο {app} εκτελεί πλέον την πιο πρόσφατη αποθηκευμένη έκδοση. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Μετάβαση του προγράμματος του «{job}» στο {app} στην πιο πρόσφατη αποθηκευμένη έκδοση. {cadence} ({timezone}). Θα είναι σε παύση μέχρι να το συνεχίσετε.',
    scheduleSwitchSavedPausedDone:
      'Το πρόγραμμα του «{job}» στο {app} χρησιμοποιεί πλέον την πιο πρόσφατη αποθηκευμένη έκδοση. {cadence} ({timezone}). Είναι σε παύση μέχρι να το συνεχίσετε.',
    scheduleSwitchPublished:
      'Μετάβαση του προγράμματος του «{job}» στο {app} στη δημοσιευμένη έκδοση. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Το πρόγραμμα του «{job}» στο {app} εκτελεί πλέον τη δημοσιευμένη έκδοση. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Μετάβαση του προγράμματος του «{job}» στο {app} στη δημοσιευμένη έκδοση. {cadence} ({timezone}). Θα είναι σε παύση μέχρι να το συνεχίσετε.',
    scheduleSwitchPublishedPausedDone:
      'Το πρόγραμμα του «{job}» στο {app} χρησιμοποιεί πλέον τη δημοσιευμένη έκδοση. {cadence} ({timezone}). Είναι σε παύση μέχρι να το συνεχίσετε.',
    dataWritesOn:
      'Επιτρέψτε στη λειτουργία «{function}» του {app} να {verbs} τις δικές του γραμμές',
    dataWritesOnDone: 'Η λειτουργία «{function}» του {app} μπορεί να {verbs} τις δικές του γραμμές',
    dataWritesOff:
      'Διακοπή αλλαγών στις δικές του γραμμές από τη λειτουργία «{function}» του {app}',
    dataWritesOffDone:
      'Η λειτουργία «{function}» του {app} δεν αλλάζει πλέον τις δικές του γραμμές',
    dataWriteInsert: 'προσθέτει',
    dataWriteUpdate: 'αλλάζει',
    dataWriteDelete: 'διαγράφει',
    tableDropField: 'Επιτρέψτε στο {app} να διαγράψει το πεδίο «{field}» από {table} ({rows})',
    tableDropFieldDone: 'Το {app} διέγραψε το πεδίο «{field}» από {table} ({rows})',
    tableDropTable: 'Επιτρέψτε στο {app} να διαγράψει τον πίνακα {table} ({rows})',
    tableDropTableDone: 'Το {app} διέγραψε τον πίνακα {table} ({rows})',
    tableClear:
      'Επιτρέψτε στο {app} να καθαρίσει το «{field}» σε {table} όπου παραβιάζει τον νέο κανόνα ({rows})',
    tableClearDone:
      'Το {app} καθάρισε το «{field}» σε {table} όπου παραβίαζε τον νέο κανόνα ({rows})',
    tableRestoreField:
      'Επιτρέψτε στο {app} να επαναφέρει το «{field}» σε {table} από το αντίγραφό του ({rows})',
    tableRestoreFieldDone: 'Το {app} επανέφερε το «{field}» σε {table} ({rows})',
    tableRestoreTable:
      'Επιτρέψτε στο {app} να επαναφέρει τον πίνακα {table} από το αντίγραφό του ({rows})',
    tableRestoreTableDone: 'Το {app} επανέφερε τον πίνακα {table} ({rows})',
    tableRowsLoseOne: '1 γραμμή χάνει μια τιμή',
    tableRowsLoseMany: '{count} γραμμές χάνουν μια τιμή',
    tableRowsLostOne: '1 γραμμή έχασε μια τιμή',
    tableRowsLostMany: '{count} γραμμές έχασαν μια τιμή',
    tableRowsOne: '1 γραμμή',
    tableRowsMany: '{count} γραμμές',
    connectedWriteOn: 'Επιτρέψτε στο {app} να {action} σε {destination}',
    connectedWriteOnDone: 'Το {app} μπορεί να {action} σε {destination}',
    connectedWriteOff: 'Διακοπή εγγραφής του {app} σε {destination}',
    connectedWriteOffDone: 'Το {app} δεν γράφει πλέον σε {destination}',
    codeConfirm: 'Επιτρέψτε στο {app} να χρησιμοποιεί τις εγκρίσεις σας με τις αλλαγές από {names}',
    codeConfirmDone: 'Το {app} χρησιμοποιεί τις εγκρίσεις σας με τις αλλαγές από {names}',
    codeConfirmCurrent:
      'Επιτρέψτε στο {app} να χρησιμοποιεί τις εγκρίσεις σας με τον τρέχοντα κώδικά του',
    codeConfirmCurrentDone: 'Το {app} χρησιμοποιεί τις εγκρίσεις σας με τον τρέχοντα κώδικά του',
    runChanges: 'Εκτέλεση της λειτουργίας «{function}» στο {app} τώρα',
    runChangesDone: 'Η λειτουργία «{function}» εκτελέστηκε στο {app}',
    continueApproved: 'Εγκρίθηκε. Συνέχισε: εκτέλεσέ το και έλεγξε ότι λειτουργεί.',
    continueDeclined: 'Απορρίφθηκε. Συνέχισε χωρίς αυτό.',
    continueMixed: 'Αποφάσισα για τα αιτήματα. Συνέχισε.',
    typeOutsideAccess: 'Επιτρέψτε σε μια εφαρμογή να φτάνει σε εξωτερικές υπηρεσίες',
    typeEvent: 'Ενεργοποίηση ή απενεργοποίηση ενός συμβάντος εφαρμογής',
    typeSchedule: 'Ενεργοποίηση, παύση ή αλλαγή του προγράμματος μιας εφαρμογής',
    typeDataWrites: 'Επιτρέψτε σε μια λειτουργία εφαρμογής να αλλάζει τις δικές της γραμμές',
    typeTableChange: 'Διαγραφή ή επαναφορά δεδομένων στους πίνακες μιας εφαρμογής',
    typeConnectedWrite: 'Επιτρέψτε σε μια εφαρμογή να γράφει σε ένα σύστημα της εταιρείας',
    typeConfirmCode:
      'Επιτρέψτε στον τροποποιημένο κώδικα εφαρμογής να χρησιμοποιεί τις εγκρίσεις σας',
    typeRunChanges: 'Εκτέλεση λειτουργίας εφαρμογής που αλλάζει δεδομένα',
    typePublicLink: 'Αλλαγή του δημόσιου συνδέσμου μιας εφαρμογής',
    typeUnpublish: 'Κατέβασμα του δημόσιου συνδέσμου μιας εφαρμογής',
    labelHosts: 'Υπηρεσίες',
    labelAccess: 'Πρόσβαση',
    labelEvent: 'Συμβάν',
    labelKind: 'Ξεκινά με',
    labelCollection: 'Συλλογή',
    labelChanges: 'Αλλαγές γραμμών',
    labelRuns: 'Εκτελέσεις',
    labelFunction: 'Λειτουργία',
    labelCollections: 'Πίνακες',
    labelWho: 'Για',
    labelAllowed: 'Επιτρέπεται',
    labelEnabled: 'Κατάσταση',
    labelJob: 'Προγραμματισμένη εργασία',
    labelSchedule: 'Πρόγραμμα',
    labelTimezone: 'Ζώνη ώρας',
    labelChange: 'Αλλαγή',
    labelTable: 'Πίνακας',
    labelField: 'Πεδίο',
    labelRows: 'Γραμμές',
    labelKeptDays: 'Διατήρηση αντιγράφου (ημέρες)',
    labelRestores: 'Επαναφέρει',
    labelAction: 'Ενέργεια',
    labelDestination: 'Προορισμός',
    labelChangedBy: 'Άλλαξε από',
    labelInput: 'Είσοδος',
    labelPublicTables: 'Αντιγραμμένοι πίνακες',
    labelPublicActions: 'Οι επισκέπτες μπορούν να στείλουν',
    labelPublicRuns: 'Κάθε υποβολή ξεκινά',
    labelPublicRunsOff: 'Θα ξεκινούσε επίσης μόλις ενεργοποιηθεί',
    labelEmbedSites: 'Ιστότοποι που μπορούν να την ενσωματώσουν',
    labelPublicLink: 'Δημόσιος σύνδεσμος',
    labelPublishedBy: 'Δημοσιεύτηκε από',
    wordRead: 'Μόνο ανάγνωση',
    wordReadSend: 'Ανάγνωση και αποστολή δεδομένων',
    wordNoAccess: 'Χωρίς πρόσβαση',
    publicPublish: 'Δημοσίευση σε δημόσιο σύνδεσμο',
    publicRefresh: 'Νέα αντιγραφή των δεδομένων της',
    publicUnpublish: 'Κατάργηση δημοσίευσης',
    eventWebhook: 'Το webhook της',
    eventRows: 'Αλλαγές στις γραμμές της',
    switchOn: 'Ενεργό',
    switchOff: 'Ανενεργό',
    switchPaused: 'Σε παύση',
    changeInsert: 'Προστέθηκε',
    changeUpdate: 'Άλλαξε',
    changeDelete: 'Διαγράφηκε',
    wordDropField: 'Διαγραφή των τιμών ενός πεδίου',
    wordDropTable: 'Διαγραφή ενός πίνακα',
    tableClearValues: 'Εκκαθάριση τιμών που παραβιάζουν αυστηρότερο κανόνα',
    tableRestore: 'Επαναφορά από το αντίγραφό της',
    whoOwner: 'Οι δικές σας εκτελέσεις',
    whoEveryone: 'Όλοι όσοι τη χρησιμοποιούν',
    answerYes: 'Ναι',
    answerNo: 'Όχι',
  },
  hu: {
    kindWrite: 'Jóváhagyandó alkalmazásmódosítás',
    kindGrant: 'Jóváhagyandó alkalmazáshozzáférés',
    kindBusiness: 'Alkalmazás jóváhagyása',
    approve: 'Jóváhagyás',
    reject: 'Elutasítás',
    working: 'Folyamatban…',
    checkStatus: 'Állapot ellenőrzése',
    statusStillPending:
      'A Poneva ezt még nem zárta le. Lehet, hogy még feldolgozás alatt van, ezért addig nem lehet másképp dönteni.',
    statusStillUnknown:
      'Nem kaptunk választ. Amíg ez nem rendeződik, nem lehet másképp dönteni. Próbáld újra egy pillanat múlva.',
    loading: 'Betöltés…',
    unavailable: 'Ez jelenleg nem érhető el.',
    loadFailed: 'Nem sikerült betölteni. Próbáld újra egy pillanat múlva.',
    requestedBy: 'Kérte: {name}',
    originApp: 'Az alkalmazásból',
    originAgent: 'Egy ügynöktől',
    originClient: 'Ezen keresztül: {client}',
    originFor: 'ennek: {name}',
    changedUnderYou:
      'Ez megváltozott, miközben olvastad. Most az aktuális verziót látod. Nem született döntés.',
    notAllowed: 'Ezt nem döntheted el.',
    cannotDecideNow: 'Erről most nem lehet dönteni. Semmi nem lett elküldve.',
    expired: 'Ez a kérés lejárt. Semmi nem változott.',
    stale: 'Az adatok megváltoztak a kérés óta. Kérd újra a friss kérést.',
    approved: 'Jóváhagyva',
    rejected: 'Elutasítva',
    needsYou: 'Jóváhagyás szükséges',
    waitingOn: '{name} döntésére vár',
    unknownTitle: 'Nem tudjuk megerősíteni, mi történt',
    unknownBody:
      'Lehet, hogy a módosítás megtörtént, de az is lehet, hogy nem. Ne ismételd meg. Ellenőrizd az állapotot, vagy kérd meg az alkalmazás tulajdonosát, hogy ellenőrizze.',
    blockedApproval: 'A folytatás ehhez a jóváhagyásra vár.',
    blockedBudget: 'Szüneteltetve: elfogyott az alkalmazás AI-kerete.',
    blockedProvider: 'Szüneteltetve: egy csatlakoztatott szolgáltatás nem válaszol.',
    blockedViewer:
      'Szüneteltetve: ezt bejelentkezett megtekintőként kell futtatni, de nincs csatolt megtekintő. Ehelyett nem fut le a tulajdonos hozzáférésével.',
    writeTitle: 'Módosítások jóváhagyása ennél: {target}',
    writeSummaryAll: '{count} módosítás itt: {app}',
    rowCreate: 'Új',
    rowUpdate: 'Módosítva',
    rowDelete: 'Eltávolítva',
    rowInclude: 'Módosítás belefoglalása',
    rowExcluded: 'Kihagyva',
    fieldBefore: 'Előtte',
    fieldAfter: 'Utána',
    fieldEmpty: 'Üres',
    approveSome: '{included} jóváhagyása a(z) {total} közül',
    approveAll: 'Mind a(z) {total} jóváhagyása',
    allLeftOut:
      'Minden módosítás ki van hagyva. Az elutasítással leállíthatod a folyamatot, vagy foglalj bele egy módosítást a jóváhagyásához.',
    appliedSome: '{count} módosítás végrehajtva.',
    rejectedBody: 'Semmi nem változott.',
    noRows: 'Itt nincs mit jóváhagyni.',
    grantTitle: '{app} hozzáférésének engedélyezése ehhez: {source}',
    grantRead: 'Csak olvasás',
    grantWrite: 'Olvasás és módosítás',
    audienceOwner: 'Csak te fogod látni ennek az alkalmazásnak az adatait innen: {source}.',
    audiencePeople:
      'Az alkalmazással megosztott {count} személy látni fogja az adatokat innen: {source}.',
    audienceCompany:
      'A vállalat minden alkalmazást megnyitó munkatársa látni fogja az adatokat innen: {source}.',
    grantRouted: 'Ezt {owner} dönti el, ezért neki lett elküldve.',
    grantRoutedUnknown: 'Ezt a kapcsolat tulajdonosa dönti el, ezért neki lett elküldve.',
    grantWhat: 'Hozzáférés',
    businessStep: '{order}. lépés',
    stepWaiting: 'Várakozás',
    stepPending: 'Döntés szükséges',
    stepApproved: 'Jóváhagyva',
    stepRejected: 'Elutasítva',
    stepYou: 'Te',
    businessNote: 'Az itt megadott jóváhagyás nem engedélyez új módosítást a Poneván kívül.',
    businessYourTurn: 'Te következel a döntésben.',
    businessNotYourTurn: '{name} döntésére várunk.',
    policyTitle: 'Hogyan használják az alkalmazások ezt a kapcsolatot?',
    policyCreator: 'Csak a létrehozóé',
    policyCreatorBody:
      'Mindenki ugyanazokat az adatokat látja, az alkalmazás létrehozójának kapcsolatán keresztül.',
    policyViewer: 'Csak a megtekintő sajátja',
    policyViewerBody: 'Mindenki a saját adatait látja, a saját kapcsolatán keresztül.',
    policyBoth: 'Mindkettő',
    policyBothBody: 'Az alkalmazás létrehozója alkalmazásonként választ.',
    policyRevoked: 'Az alkalmazások nem használhatják ezt a kapcsolatot',
    policyRevokedBody:
      'Válassz egy lehetőséget az újbóli engedélyezéshez. Nincs előre kiválasztott lehetőség.',
    policySave: 'Mentés',
    policySaving: 'Mentés…',
    policySaved: 'Mentve.',
    policyReadOnly: 'Ezt csak vállalati rendszergazda módosíthatja.',
    policyConflict: 'Valaki más előbb módosította ezt. Ez az aktuális beállítás.',
    policyRefused: 'Nem sikerült menteni.',
    policyFailed:
      'Nem tudtuk megerősíteni, hogy a mentés sikerült. Töltsd újra az oldalt az aktuális beállítás megtekintéséhez, mielőtt újra próbálkozol.',
    policyWidened:
      'A már közzétett appok a jelenlegi módon csatlakoznak továbbra is. A készítőiknek újra közzé kell tenniük őket az új lehetőség használatához.',
    policyNarrowed: 'Az appok minden csatlakozáskor ellenőrzik az aktuális beállítást.',
    policyAffected: '{count} közzétett app használja ezt a kapcsolatot ({when} állapot szerint).',
    policyAffectedOne: '1 közzétett app használja ezt a kapcsolatot ({when} állapot szerint).',
    policyAffectsNames: '{count} közzétett appot érint: {names}.',
    policyAffectsOneName: '1 közzétett appot érint: {names}.',
    policyGenericWiden:
      'Az ezt a kapcsolatot használó, közzétett appok a jelenlegi módon csatlakoznak továbbra is. A készítőiknek újra közzé kell tenniük őket az új lehetőség használatához.',
    policyGenericNarrow:
      'Az ezt a kapcsolatot használó, közzétett appok minden csatlakozáskor ellenőrzik az aktuális beállítást.',
    policyAffectedGeneric:
      'Az ezt a kapcsolatot használó, közzétett appok nem változnak meg automatikusan. A készítőiknek újra közzé kell tenniük őket.',
    policyUnavailable: 'Ez a beállítás jelenleg nem érhető el.',
    outsideRead: 'Engedd, hogy a(z) {app} alkalmazás olvasson innen: {hosts}',
    outsideReadSend:
      'Engedd, hogy a(z) {app} alkalmazás olvasson innen, és adatokat küldjön ide: {hosts}',
    outsideNone: 'Tiltsd le a(z) {app} alkalmazás elérését: {hosts}',
    outsideReadDone: 'A(z) {app} alkalmazás olvashat innen: {hosts}',
    outsideReadSendDone: 'A(z) {app} alkalmazás olvashat innen, és adatokat küldhet ide: {hosts}',
    outsideNoneDone: 'A(z) {app} alkalmazás már nem éri el ezt: {hosts}',
    outsideMore: 'további {count}',
    eventWebhookOn:
      'Engedd, hogy a(z) {app} alkalmazás fogadja a(z) „{event}” eseményt a webhookján',
    eventWebhookOnDone: 'A(z) {app} alkalmazás fogadja a(z) „{event}” eseményt a webhookján',
    eventWebhookOff:
      'Tiltsd le, hogy a(z) {app} alkalmazás fogadja a(z) „{event}” eseményt a webhookján',
    eventWebhookOffDone: 'A(z) {app} alkalmazás már nem fogadja a(z) „{event}” eseményt',
    eventRowsOn:
      'Engedd, hogy a(z) {app} alkalmazás futtassa ezt: {runs}, amikor a(z) {collection} tábla sorai {changes}',
    eventRowsOnDone:
      'A(z) {app} alkalmazás futtatja ezt: {runs}, amikor a(z) {collection} tábla sorai {changes}',
    eventRowsOff:
      'Tiltsd le, hogy a(z) {app} alkalmazás futtassa ezt: {runs}, amikor a(z) {collection} tábla sorai változnak',
    eventRowsOffDone:
      'A(z) {app} alkalmazás már nem futtatja ezt: {runs}, amikor a(z) {collection} tábla sorai változnak',
    eventInsert: 'hozzáadódnak',
    eventUpdate: 'módosulnak',
    eventDelete: 'törlődnek',
    scheduleTurnOn:
      'A(z) „{job}” ütemezésének bekapcsolása a(z) {app} alkalmazásban. {cadence} ({timezone}).',
    scheduleTurnOnDone:
      'A(z) „{job}” ütemezése be van kapcsolva a(z) {app} alkalmazásban. {cadence} ({timezone}).',
    scheduleResume:
      'A(z) „{job}” ütemezésének folytatása a(z) {app} alkalmazásban. {cadence} ({timezone}).',
    scheduleResumeDone:
      'A(z) „{job}” ütemezése ismét be van kapcsolva a(z) {app} alkalmazásban. {cadence} ({timezone}).',
    scheduleReschedule:
      'A(z) „{job}” ütemezésének módosítása a(z) {app} alkalmazásban. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'A(z) „{job}” ütemezése módosult a(z) {app} alkalmazásban. {cadence} ({timezone}).',
    schedulePause: 'A(z) „{job}” ütemezésének szüneteltetése a(z) {app} alkalmazásban.',
    schedulePauseDone: 'A(z) „{job}” ütemezése szünetel a(z) {app} alkalmazásban.',
    scheduleNext: 'Következő futtatások: {runs}.',
    scheduleResumeSaved:
      'A(z) „{job}” ütemezésének folytatása a legutóbb mentett verzióval a(z) {app} alkalmazásban. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'A(z) „{job}” ütemezése ismét be van kapcsolva a(z) {app} alkalmazásban, és a legutóbb mentett verziót futtatja. {cadence} ({timezone}).',
    scheduleResumePublished:
      'A(z) „{job}” ütemezésének folytatása a közzétett verzióval a(z) {app} alkalmazásban. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'A(z) „{job}” ütemezése ismét be van kapcsolva a(z) {app} alkalmazásban, és a közzétett verziót futtatja. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'A(z) „{job}” ütemezésének módosítása a(z) {app} alkalmazásban. {cadence} ({timezone}). Ezután szünetel, amíg nem folytatod.',
    scheduleReschedulePausedDone:
      'A(z) „{job}” ütemezése módosult a(z) {app} alkalmazásban. {cadence} ({timezone}). Szünetel, amíg nem folytatod.',
    scheduleSwitchSaved:
      'A(z) „{job}” ütemezésének átállítása a legutóbb mentett verzióra a(z) {app} alkalmazásban. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'A(z) „{job}” ütemezése a(z) {app} alkalmazásban mostantól a legutóbb mentett verziót futtatja. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'A(z) „{job}” ütemezésének átállítása a legutóbb mentett verzióra a(z) {app} alkalmazásban. {cadence} ({timezone}). Ezután szünetel, amíg nem folytatod.',
    scheduleSwitchSavedPausedDone:
      'A(z) „{job}” ütemezése a(z) {app} alkalmazásban mostantól a legutóbb mentett verziót használja. {cadence} ({timezone}). Szünetel, amíg nem folytatod.',
    scheduleSwitchPublished:
      'A(z) „{job}” ütemezésének átállítása a közzétett verzióra a(z) {app} alkalmazásban. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'A(z) „{job}” ütemezése a(z) {app} alkalmazásban mostantól a közzétett verziót futtatja. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'A(z) „{job}” ütemezésének átállítása a közzétett verzióra a(z) {app} alkalmazásban. {cadence} ({timezone}). Ezután szünetel, amíg nem folytatod.',
    scheduleSwitchPublishedPausedDone:
      'A(z) „{job}” ütemezése a(z) {app} alkalmazásban mostantól a közzétett verziót használja. {cadence} ({timezone}). Szünetel, amíg nem folytatod.',
    dataWritesOn:
      'A(z) {app} „{function}” függvényének engedélyezése saját sorok kezelésére: {verbs}',
    dataWritesOnDone: 'A(z) {app} „{function}” függvénye jogosult saját sorokat {verbs}',
    dataWritesOff: 'A(z) {app} „{function}” függvényének tiltása a saját sorok módosításától',
    dataWritesOffDone: 'A(z) {app} „{function}” függvénye már nem módosítja a saját sorait',
    dataWriteInsert: 'hozzáadni',
    dataWriteUpdate: 'módosítani',
    dataWriteDelete: 'törölni',
    tableDropField:
      'Engedd, hogy a(z) {app} alkalmazás törölje a(z) „{field}” mezőt a(z) {table} táblából ({rows})',
    tableDropFieldDone:
      'A(z) {app} alkalmazás törölte a(z) „{field}” mezőt a(z) {table} táblából ({rows})',
    tableDropTable: 'Engedd, hogy a(z) {app} alkalmazás törölje a(z) {table} táblát ({rows})',
    tableDropTableDone: 'A(z) {app} alkalmazás törölte a(z) {table} táblát ({rows})',
    tableClear:
      'Engedd, hogy a(z) {app} alkalmazás kiürítse a(z) „{field}” mezőt a(z) {table} táblában ott, ahol az érték megsérti az új szabályt ({rows})',
    tableClearDone:
      'A(z) {app} alkalmazás kiürítette a(z) „{field}” mezőt a(z) {table} táblában ott, ahol az érték megsértette az új szabályt ({rows})',
    tableRestoreField:
      'Engedd, hogy a(z) {app} alkalmazás visszaállítsa a(z) „{field}” mezőt a(z) {table} táblában a másolatból ({rows})',
    tableRestoreFieldDone:
      'A(z) {app} alkalmazás visszaállította a(z) „{field}” mezőt a(z) {table} táblában ({rows})',
    tableRestoreTable:
      'Engedd, hogy a(z) {app} alkalmazás visszaállítsa a(z) {table} táblát a másolatból ({rows})',
    tableRestoreTableDone: 'A(z) {app} alkalmazás visszaállította a(z) {table} táblát ({rows})',
    tableRowsLoseOne: '1 sor elveszít egy értéket',
    tableRowsLoseMany: '{count} sor elveszít egy értéket',
    tableRowsLostOne: '1 sor elveszített egy értéket',
    tableRowsLostMany: '{count} sor elveszített egy értéket',
    tableRowsOne: '1 sor',
    tableRowsMany: '{count} sor',
    connectedWriteOn: 'Engedd a(z) {app} alkalmazásnak: {action} itt: {destination}',
    connectedWriteOnDone: 'A(z) {app} alkalmazás jogosult: {action} itt: {destination}',
    connectedWriteOff: 'Tiltsd le, hogy a(z) {app} alkalmazás ide írjon: {destination}',
    connectedWriteOffDone: 'A(z) {app} alkalmazás már nem ír ide: {destination}',
    codeConfirm:
      'Engedd, hogy a(z) {app} alkalmazás használja a jóváhagyásaidat az általuk végzett módosításokkal: {names}',
    codeConfirmDone:
      'A(z) {app} alkalmazás használja a jóváhagyásaidat az általuk végzett módosításokkal: {names}',
    codeConfirmCurrent:
      'Engedd, hogy a(z) {app} alkalmazás használja a jóváhagyásaidat a jelenlegi kódjával',
    codeConfirmCurrentDone:
      'A(z) {app} alkalmazás a jelenlegi kódjával használja a jóváhagyásaidat',
    runChanges: 'A(z) „{function}” függvény futtatása most a(z) {app} alkalmazásban',
    runChangesDone: 'A(z) „{function}” függvény lefutott a(z) {app} alkalmazásban',
    continueApproved: 'Jóváhagyva. Folytasd: futtasd le, és ellenőrizd, hogy működik.',
    continueDeclined: 'Elutasítva. Folytasd nélküle.',
    continueMixed: 'A kérésekről döntöttem. Folytasd.',
    typeOutsideAccess: 'Engedélyezi, hogy egy alkalmazás külső szolgáltatásokat érjen el',
    typeEvent: 'Egy alkalmazásesemény be- vagy kikapcsolása',
    typeSchedule: 'Egy alkalmazás ütemezésének bekapcsolása, szüneteltetése vagy módosítása',
    typeDataWrites: 'Engedélyezi, hogy egy alkalmazásfüggvény módosítsa a saját sorait',
    typeTableChange: 'Adatok törlése vagy visszaállítása egy alkalmazás tábláiban',
    typeConnectedWrite: 'Engedélyezi, hogy egy alkalmazás írjon egy vállalati rendszerbe',
    typeConfirmCode: 'A módosított alkalmazáskód használhassa a jóváhagyásaidat',
    typeRunChanges: 'Adatokat módosító alkalmazásfüggvény futtatása',
    typePublicLink: 'Egy alkalmazás nyilvános hivatkozásának módosítása',
    typeUnpublish: 'Egy alkalmazás nyilvános hivatkozásának eltávolítása',
    labelHosts: 'Szolgáltatások',
    labelAccess: 'Hozzáférés',
    labelEvent: 'Esemény',
    labelKind: 'Ekkor indul',
    labelCollection: 'Gyűjtemény',
    labelChanges: 'Sorváltozások',
    labelRuns: 'Futtatások',
    labelFunction: 'Függvény',
    labelCollections: 'Táblák',
    labelWho: 'Kinek',
    labelAllowed: 'Engedélyezett',
    labelEnabled: 'Állapot',
    labelJob: 'Ütemezett feladat',
    labelSchedule: 'Ütemezés',
    labelTimezone: 'Időzóna',
    labelChange: 'Módosítás',
    labelTable: 'Tábla',
    labelField: 'Mező',
    labelRows: 'Sorok',
    labelKeptDays: 'A másolat megőrzése (nap)',
    labelRestores: 'Visszaállít',
    labelAction: 'Művelet',
    labelDestination: 'Cél',
    labelChangedBy: 'Módosította',
    labelInput: 'Bemenet',
    labelPublicTables: 'Másolt táblák',
    labelPublicActions: 'A látogatók küldhetnek',
    labelPublicRuns: 'Minden beküldés elindít',
    labelPublicRunsOff: 'Bekapcsolás után ez is elindulna',
    labelEmbedSites: 'Webhelyek, amelyek beágyazhatják',
    labelPublicLink: 'Nyilvános hivatkozás',
    labelPublishedBy: 'Közzétette',
    wordRead: 'Csak olvasás',
    wordReadSend: 'Olvasás és adatküldés',
    wordNoAccess: 'Nincs hozzáférés',
    publicPublish: 'Közzététel nyilvános hivatkozással',
    publicRefresh: 'Adatainak újbóli másolása',
    publicUnpublish: 'Közzététel visszavonása',
    eventWebhook: 'A webhookja',
    eventRows: 'A sorainak módosításai',
    switchOn: 'Be',
    switchOff: 'Ki',
    switchPaused: 'Szüneteltetve',
    changeInsert: 'Hozzáadva',
    changeUpdate: 'Módosítva',
    changeDelete: 'Törölve',
    wordDropField: 'Egy mező értékeinek törlése',
    wordDropTable: 'Egy tábla törlése',
    tableClearValues: 'Szigorúbb szabályt sértő értékek törlése',
    tableRestore: 'Visszaállítás a másolatból',
    whoOwner: 'A saját futtatásaid',
    whoEveryone: 'Mindenki, aki használja',
    answerYes: 'Igen',
    answerNo: 'Nem',
  },
  ga: {
    kindWrite: 'Athrú aip le ceadú',
    kindGrant: 'Rochtain aip le ceadú',
    kindBusiness: 'Ceadú d’aip',
    approve: 'Ceadaigh',
    reject: 'Diúltaigh',
    working: 'Ag obair…',
    checkStatus: 'Seiceáil an stádas',
    statusStillPending:
      'Níl Poneva tar éis é seo a shocrú fós. Seans go bhfuil sé fós á phróiseáil, mar sin ní féidir aon rud eile a chinneadh go dtí go réitítear é.',
    statusStillUnknown:
      'Ní rabhamar in ann freagra a fháil. Ní féidir aon rud eile a chinneadh go dtí go réitítear é seo. Bain triail eile as ar ball beag.',
    loading: 'Á lódáil…',
    unavailable: 'Níl sé seo ar fáil faoi láthair.',
    loadFailed: 'Ní rabhamar in ann é seo a lódáil. Bain triail eile as ar ball beag.',
    requestedBy: 'D’iarr {name} é',
    originApp: 'Ón aip',
    originAgent: 'Ó Ghníomhaire',
    originClient: 'Trí {client}',
    originFor: 'do {name}',
    changedUnderYou:
      'Athraíodh é seo agus tú á léamh. Is é an leagan reatha atá le feiceáil agat anois. Ní dhearnadh aon chinneadh.',
    notAllowed: 'Ní féidir leat cinneadh a dhéanamh faoi seo.',
    cannotDecideNow: 'Ní féidir cinneadh a dhéanamh faoi seo faoi láthair. Níor seoladh aon rud.',
    expired: 'Tá an t-iarratas seo imithe in éag. Níor athraíodh aon rud.',
    stale: 'Athraíodh na sonraí ó rinneadh an t-iarratas seo. Iarr arís chun iarratas úr a fháil.',
    approved: 'Ceadaithe',
    rejected: 'Diúltaithe',
    needsYou: 'Teastaíonn do cheadú',
    waitingOn: 'Ag fanacht le {name}',
    unknownTitle: 'Ní féidir linn a dheimhniú cad a tharla',
    unknownBody:
      'Seans go ndearnadh an t-athrú nó seans nár deineadh é. Ná déan arís é. Seiceáil an stádas, nó iarr ar úinéir na haipe é a sheiceáil.',
    blockedApproval: 'Ag fanacht leis an gceadú seo sula leanfaidh sé ar aghaidh.',
    blockedBudget: 'Ar sos: tá buiséad AI na haipe seo ídithe.',
    blockedProvider: 'Ar sos: níl seirbhís nasctha ag freagairt.',
    blockedViewer:
      'Ar sos: ní mór é seo a rith mar amharcóir atá sínithe isteach, agus níl aon amharcóir ceangailte. Ní ritheann sé le rochtain an úinéara ina ionad sin.',
    writeTitle: 'Ceadaigh athruithe ar {target}',
    writeSummaryAll: '{count} athrú in {app}',
    rowCreate: 'Nua',
    rowUpdate: 'Athraithe',
    rowDelete: 'Bainte',
    rowInclude: 'Cuir an t-athrú seo san áireamh',
    rowExcluded: 'Fágtha ar lár',
    fieldBefore: 'Roimhe seo',
    fieldAfter: 'Ina dhiaidh sin',
    fieldEmpty: 'Folamh',
    approveSome: 'Ceadaigh {included} as {total}',
    approveAll: 'Ceadaigh na {total} ar fad',
    allLeftOut:
      'Fágtar gach athrú ar lár. Diúltaigh chun stop a chur leis, nó cuir athrú san áireamh chun é a cheadú.',
    appliedSome: 'Rinneadh {count} athrú.',
    rejectedBody: 'Níor athraíodh aon rud.',
    noRows: 'Níl aon rud le ceadú anseo.',
    grantTitle: 'Lig do {app} {source} a úsáid',
    grantRead: 'Léamh amháin',
    grantWrite: 'Léamh agus athrú',
    audienceOwner: 'Ní fheicfidh ach tusa sonraí na haipe seo ó {source}.',
    audiencePeople: 'Feicfidh an {count} duine a roinneann an aip seo leo sonraí ó {source}.',
    audienceCompany: 'Feicfidh gach duine sa chuideachta a osclaíonn an aip sonraí ó {source}.',
    grantRouted: 'Is é {owner} a dhéanann an cinneadh seo, mar sin seoladh chuige é.',
    grantRoutedUnknown:
      'Is é úinéir an naisc seo a dhéanann an cinneadh, mar sin seoladh chuige é.',
    grantWhat: 'Rochtain',
    businessStep: 'Céim {order}',
    stepWaiting: 'Ag fanacht',
    stepPending: 'Teastaíonn cinneadh',
    stepApproved: 'Ceadaithe',
    stepRejected: 'Diúltaithe',
    stepYou: 'Tusa',
    businessNote: 'Ní cheadaíonn ceadú anseo aon athrú nua lasmuigh de Poneva.',
    businessYourTurn: 'Is é do sheal cinneadh a dhéanamh.',
    businessNotYourTurn: 'Ag fanacht le {name} cinneadh a dhéanamh ar dtús.',
    policyTitle: 'An chaoi a n-úsáideann aipeanna an nasc seo',
    policyCreator: 'An cruthaitheoir amháin',
    policyCreatorBody: 'Feiceann gach duine na sonraí céanna, trí nasc chruthaitheoir na haipe.',
    policyViewer: 'Amharcóir amháin',
    policyViewerBody: 'Feiceann gach duine a shonraí féin, trína nasc féin.',
    policyBoth: 'An dá cheann',
    policyBothBody: 'Roghnaíonn cruthaitheoir na haipe do gach aip.',
    policyRevoked: 'Ní féidir le haipeanna an nasc seo a úsáid',
    policyRevokedBody: 'Roghnaigh rogha chun é a cheadú arís. Ní roghnófar aon rud ar do shon.',
    policySave: 'Sábháil',
    policySaving: 'Á shábháil…',
    policySaved: 'Sábháilte.',
    policyReadOnly: 'Ní féidir ach le riarthóir cuideachta é seo a athrú.',
    policyConflict: 'D’athraigh duine eile é seo ar dtús. Seo é an socrú reatha.',
    policyRefused: 'Níorbh fhéidir é seo a shábháil.',
    policyFailed:
      'Níorbh fhéidir linn a dheimhniú gur sábháladh é. Athlódáil chun an socrú reatha a fheiceáil sula ndéanfaidh tú iarracht eile.',
    policyWidened:
      'Coinníonn aipeanna atá foilsithe cheana an bealach ina nascann siad inniu. Iarrtar ar a gcruthaitheoirí iad a athfhoilsiú chun an rogha nua a úsáid.',
    policyNarrowed: 'Seiceálann aipeanna an socrú reatha gach uair a nascann siad.',
    policyAffected: 'Úsáideann {count} aip fhoilsithe an nasc seo (amhail {when}).',
    policyAffectedOne: 'Úsáideann 1 aip fhoilsithe an nasc seo (amhail {when}).',
    policyAffectsNames: 'Baineann sé seo le {count} aip fhoilsithe: {names}.',
    policyAffectsOneName: 'Baineann sé seo le 1 aip fhoilsithe: {names}.',
    policyGenericWiden:
      'Coinníonn aipeanna foilsithe a úsáideann an nasc seo an bealach ina nascann siad inniu. Iarrtar ar a gcruthaitheoirí iad a athfhoilsiú chun an rogha nua a úsáid.',
    policyGenericNarrow:
      'Seiceálann aipeanna foilsithe a úsáideann an nasc seo an socrú reatha gach uair a nascann siad.',
    policyAffectedGeneric:
      'Ní athraítear aipeanna foilsithe a úsáideann an nasc seo go huathoibríoch. Iarrtar ar a gcruthaitheoirí iad a athfhoilsiú.',
    policyUnavailable: 'Níl an socrú seo ar fáil faoi láthair.',
    outsideRead: 'Lig don aip {app} léamh ó {hosts}',
    outsideReadSend: 'Lig don aip {app} léamh ó {hosts} agus sonraí a sheoladh chucu',
    outsideNone: 'Cuir cosc ar an aip {app} {hosts} a shroicheadh',
    outsideReadDone: 'Is féidir leis an aip {app} léamh ó {hosts}',
    outsideReadSendDone: 'Is féidir leis an aip {app} léamh ó {hosts} agus sonraí a sheoladh chucu',
    outsideNoneDone: 'Ní shroicheann an aip {app} {hosts} a thuilleadh',
    outsideMore: '{count} eile',
    eventWebhookOn: 'Lig don aip {app} “{event}” a fháil ar a webhook',
    eventWebhookOnDone: 'Faigheann an aip {app} “{event}” ar a webhook',
    eventWebhookOff: 'Cuir cosc ar an aip {app} “{event}” a fháil ar a webhook',
    eventWebhookOffDone: 'Ní fhaigheann an aip {app} “{event}” a thuilleadh',
    eventRowsOn:
      'Lig don aip {app} {runs} a rith nuair a bhíonn sraitheanna sa tábla {collection} {changes}',
    eventRowsOnDone:
      'Ritheann an aip {app} {runs} nuair a bhíonn sraitheanna sa tábla {collection} {changes}',
    eventRowsOff:
      'Cuir cosc ar an aip {app} {runs} a rith nuair a athraítear sraitheanna sa tábla {collection}',
    eventRowsOffDone:
      'Ní ritheann an aip {app} {runs} a thuilleadh nuair a athraítear sraitheanna sa tábla {collection}',
    eventInsert: 'curtha leis',
    eventUpdate: 'athraithe',
    eventDelete: 'scriosta',
    scheduleTurnOn: 'Cas air sceideal “{job}” san aip {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Tá sceideal “{job}” san aip {app} casta air. {cadence} ({timezone}).',
    scheduleResume: 'Atosaigh sceideal “{job}” san aip {app}. {cadence} ({timezone}).',
    scheduleResumeDone: 'Tá sceideal “{job}” san aip {app} casta air arís. {cadence} ({timezone}).',
    scheduleReschedule: 'Athraigh sceideal “{job}” san aip {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone: 'Athraíodh sceideal “{job}” san aip {app}. {cadence} ({timezone}).',
    schedulePause: 'Cuir sceideal “{job}” san aip {app} ar sos.',
    schedulePauseDone: 'Tá sceideal “{job}” san aip {app} curtha ar sos.',
    scheduleNext: 'Na chéad rití eile: {runs}.',
    scheduleResumeSaved:
      'Atosaigh sceideal “{job}” san aip {app} leis an leagan is déanaí a sábháladh. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Tá sceideal “{job}” san aip {app} casta air arís agus ritheann sé an leagan is déanaí a sábháladh. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Atosaigh sceideal “{job}” san aip {app} leis an leagan foilsithe. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Tá sceideal “{job}” san aip {app} casta air arís agus ritheann sé an leagan foilsithe. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Athraigh sceideal “{job}” san aip {app}. {cadence} ({timezone}). Beidh sé ar sos ansin go dtí go n-atosaíonn tú é.',
    scheduleReschedulePausedDone:
      'Athraíodh sceideal “{job}” san aip {app}. {cadence} ({timezone}). Tá sé ar sos go dtí go n-atosaíonn tú é.',
    scheduleSwitchSaved:
      'Athraigh sceideal “{job}” san aip {app} chuig an leagan is déanaí a sábháladh. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Ritheann sceideal “{job}” san aip {app} an leagan is déanaí a sábháladh anois. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Athraigh sceideal “{job}” san aip {app} chuig an leagan is déanaí a sábháladh. {cadence} ({timezone}). Beidh sé ar sos ansin go dtí go n-atosaíonn tú é.',
    scheduleSwitchSavedPausedDone:
      'Úsáideann sceideal “{job}” san aip {app} an leagan is déanaí a sábháladh anois. {cadence} ({timezone}). Tá sé ar sos go dtí go n-atosaíonn tú é.',
    scheduleSwitchPublished:
      'Athraigh sceideal “{job}” san aip {app} chuig an leagan foilsithe. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Ritheann sceideal “{job}” san aip {app} an leagan foilsithe anois. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Athraigh sceideal “{job}” san aip {app} chuig an leagan foilsithe. {cadence} ({timezone}). Beidh sé ar sos ansin go dtí go n-atosaíonn tú é.',
    scheduleSwitchPublishedPausedDone:
      'Úsáideann sceideal “{job}” san aip {app} an leagan foilsithe anois. {cadence} ({timezone}). Tá sé ar sos go dtí go n-atosaíonn tú é.',
    dataWritesOn: 'Lig d’fheidhm “{function}” na haipe {app} sraitheanna féin na haipe {verbs}',
    dataWritesOnDone:
      'Is féidir le feidhm “{function}” na haipe {app} sraitheanna féin na haipe {verbs}',
    dataWritesOff:
      'Cuir cosc ar fheidhm “{function}” na haipe {app} sraitheanna féin na haipe a athrú',
    dataWritesOffDone:
      'Ní athraíonn feidhm “{function}” na haipe {app} sraitheanna féin na haipe a thuilleadh',
    dataWriteInsert: 'a chur leo',
    dataWriteUpdate: 'a athrú',
    dataWriteDelete: 'a scriosadh',
    tableDropField: 'Lig don aip {app} an réimse “{field}” a bhaint den tábla {table} ({rows})',
    tableDropFieldDone: 'Bhain an aip {app} an réimse “{field}” den tábla {table} ({rows})',
    tableDropTable: 'Lig don aip {app} an tábla {table} a scriosadh ({rows})',
    tableDropTableDone: 'Scrios an aip {app} an tábla {table} ({rows})',
    tableClear:
      'Lig don aip {app} “{field}” a ghlanadh sa tábla {table} nuair a bhriseann sé an riail nua ({rows})',
    tableClearDone:
      'Ghlan an aip {app} “{field}” sa tábla {table} nuair a bhris sé an riail nua ({rows})',
    tableRestoreField:
      'Lig don aip {app} “{field}” a chur ar ais sa tábla {table} ón gcóip ({rows})',
    tableRestoreFieldDone: 'Chuir an aip {app} “{field}” ar ais sa tábla {table} ({rows})',
    tableRestoreTable: 'Lig don aip {app} an tábla {table} a athchóiriú ón gcóip ({rows})',
    tableRestoreTableDone: 'D’athchóirigh an aip {app} an tábla {table} ({rows})',
    tableRowsLoseOne: 'Cailleann 1 sraith luach',
    tableRowsLoseMany: 'Cailleann {count} sraith luach',
    tableRowsLostOne: 'Chaill 1 sraith luach',
    tableRowsLostMany: 'Chaill {count} sraith luach',
    tableRowsOne: '1 sraith',
    tableRowsMany: '{count} sraith',
    connectedWriteOn: 'Lig don aip {app} {action} i {destination}',
    connectedWriteOnDone: 'Is féidir leis an aip {app} {action} i {destination}',
    connectedWriteOff: 'Cuir cosc ar an aip {app} scríobh chuig {destination}',
    connectedWriteOffDone: 'Ní scríobhann an aip {app} chuig {destination} a thuilleadh',
    codeConfirm:
      'Lig don aip {app} úsáid a bhaint as do cheadaithe leis na hathruithe a rinne {names}',
    codeConfirmDone:
      'Baineann an aip {app} úsáid as do cheadaithe leis na hathruithe a rinne {names}',
    codeConfirmCurrent: 'Lig don aip {app} úsáid a bhaint as do cheadaithe lena cód reatha',
    codeConfirmCurrentDone: 'Úsáideann {app} do cheadaithe lena cód reatha',
    runChanges: 'Rith feidhm “{function}” san aip {app} anois',
    runChangesDone: 'Rith feidhm “{function}” san aip {app}',
    continueApproved: 'Ceadaithe. Lean ar aghaidh: rith é agus seiceáil go n-oibríonn sé.',
    continueDeclined: 'Diúltaithe. Lean ar aghaidh gan é.',
    continueMixed: 'Rinne mé cinneadh faoi na hiarrataí. Lean ar aghaidh.',
    typeOutsideAccess: 'Lig d’aip rochtain a bheith aici ar sheirbhísí seachtracha',
    typeEvent: 'Cas imeacht aipe air nó as',
    typeSchedule: 'Cas sceideal aipe air, cuir ar sos é nó athraigh é',
    typeDataWrites: 'Lig d’fheidhm aipe a sraitheanna féin a athrú',
    typeTableChange: 'Scrios nó athchóirigh sonraí i dtáblaí aipe',
    typeConnectedWrite: 'Lig d’aip scríobh chuig córas cuideachta',
    typeConfirmCode: 'Lig do chód athraithe aipe do cheadaithe a úsáid',
    typeRunChanges: 'Rith feidhm aipe a athraíonn sonraí',
    typePublicLink: 'Athraigh nasc poiblí aipe',
    typeUnpublish: 'Tóg anuas nasc poiblí aipe',
    labelHosts: 'Seirbhísí',
    labelAccess: 'Rochtain',
    labelEvent: 'Imeacht',
    labelKind: 'Tosaíonn sé ar',
    labelCollection: 'Bailiúchán',
    labelChanges: 'Athruithe sraithe',
    labelRuns: 'Rith',
    labelFunction: 'Feidhm',
    labelCollections: 'Táblaí',
    labelWho: 'Do',
    labelAllowed: 'Ceadaithe',
    labelEnabled: 'Stádas',
    labelJob: 'Jab sceidealaithe',
    labelSchedule: 'Sceideal',
    labelTimezone: 'Crios ama',
    labelChange: 'Athrú',
    labelTable: 'Tábla',
    labelField: 'Réimse',
    labelRows: 'Sraitheanna',
    labelKeptDays: 'Coinnítear an chóip (laethanta)',
    labelRestores: 'Athchóiríonn',
    labelAction: 'Gníomh',
    labelDestination: 'Ceann scríbe',
    labelChangedBy: 'Athraithe ag',
    labelInput: 'Ionchur',
    labelPublicTables: 'Táblaí cóipeáilte',
    labelPublicActions: 'Is féidir le cuairteoirí a sheoladh',
    labelPublicRuns: 'Cuireann gach aighneacht tús le',
    labelPublicRunsOff: 'Thosófaí freisin nuair a chuirtear air é',
    labelEmbedSites: 'Suíomhanna ar féidir leo í a leabú',
    labelPublicLink: 'Nasc poiblí',
    labelPublishedBy: 'Foilsithe ag',
    wordRead: 'Léamh amháin',
    wordReadSend: 'Léigh agus seol sonraí',
    wordNoAccess: 'Gan rochtain',
    publicPublish: 'Foilsigh chuig nasc poiblí',
    publicRefresh: 'Cóipeáil a sonraí arís',
    publicUnpublish: 'Dífhoilsigh',
    eventWebhook: 'A webhook',
    eventRows: 'Athruithe ar a sraitheanna',
    switchOn: 'Air',
    switchOff: 'As',
    switchPaused: 'Curtha ar sos',
    changeInsert: 'Curtha leis',
    changeUpdate: 'Athraithe',
    changeDelete: 'Scriosta',
    wordDropField: 'Scrios luachanna réimse',
    wordDropTable: 'Scrios tábla',
    tableClearValues: 'Glan luachanna a sháraíonn riail níos déine',
    tableRestore: 'Athchóirigh óna cóip',
    whoOwner: 'Do rith féin',
    whoEveryone: 'Gach duine a úsáideann é',
    answerYes: 'Tá',
    answerNo: 'Níl',
  },
  it: {
    kindWrite: "Modifica dell'app da approvare",
    kindGrant: "Accesso all'app da approvare",
    kindBusiness: "Approvazione per un'app",
    approve: 'Approva',
    reject: 'Rifiuta',
    working: 'Operazione in corso…',
    checkStatus: 'Controlla lo stato',
    statusStillPending:
      'Poneva non ha ancora completato questa operazione. Potrebbe essere ancora in corso, quindi non è possibile prendere altre decisioni finché non sarà completata.',
    statusStillUnknown:
      'Non abbiamo ricevuto una risposta. Non è possibile prendere altre decisioni finché non sarà completata. Riprova tra poco.',
    loading: 'Caricamento…',
    unavailable: 'Al momento non è disponibile.',
    loadFailed: 'Non è stato possibile caricare questo elemento. Riprova tra poco.',
    requestedBy: 'Richiesto da {name}',
    originApp: "Dall'app",
    originAgent: 'Da un Agent',
    originClient: 'Tramite {client}',
    originFor: 'per {name}',
    changedUnderYou:
      'Questo elemento è cambiato mentre lo stavi leggendo. Quella che vedi ora è la versione attuale. Non è stata presa alcuna decisione.',
    notAllowed: 'Non puoi decidere su questo elemento.',
    cannotDecideNow:
      'Non è possibile decidere su questo elemento in questo momento. Non è stato inviato nulla.',
    expired: 'Questa richiesta è scaduta. Non è cambiato nulla.',
    stale:
      'I dati sono cambiati da quando è stata fatta questa richiesta. Chiedi di nuovo per ricevere una richiesta aggiornata.',
    approved: 'Approvato',
    rejected: 'Rifiutato',
    needsYou: 'Richiede la tua approvazione',
    waitingOn: 'In attesa di {name}',
    unknownTitle: 'Non possiamo confermare cosa è successo',
    unknownBody:
      "La modifica potrebbe essere stata applicata oppure no. Non ripeterla. Controlla lo stato oppure chiedi al proprietario dell'app di verificare.",
    blockedApproval: 'In attesa di questa approvazione prima di continuare.',
    blockedBudget: 'In pausa: il budget AI per questa app è esaurito.',
    blockedProvider: 'In pausa: un servizio collegato non risponde.',
    blockedViewer:
      "In pausa: deve essere eseguito da un visualizzatore con accesso effettuato, ma non è associato alcun visualizzatore. Non viene eseguito usando l'accesso del proprietario.",
    writeTitle: 'Approva le modifiche a {target}',
    writeSummaryAll: '{count} modifiche in {app}',
    rowCreate: 'Nuova',
    rowUpdate: 'Modificata',
    rowDelete: 'Rimossa',
    rowInclude: 'Includi questa modifica',
    rowExcluded: 'Esclusa',
    fieldBefore: 'Prima',
    fieldAfter: 'Dopo',
    fieldEmpty: 'Vuoto',
    approveSome: 'Approva {included} di {total}',
    approveAll: 'Approva tutte le {total}',
    allLeftOut:
      'Tutte le modifiche sono escluse. Rifiuta per interrompere oppure includi una modifica per approvarla.',
    appliedSome: '{count} modifiche applicate.',
    rejectedBody: 'Non è stato modificato nulla.',
    noRows: "Non c'è nulla da approvare qui.",
    grantTitle: 'Consenti a {app} di usare {source}',
    grantRead: 'Sola lettura',
    grantWrite: 'Lettura e modifica',
    audienceOwner: 'Solo tu vedrai i dati di questa app da {source}.',
    audiencePeople:
      'Le {count} persone con cui questa app è condivisa vedranno i dati da {source}.',
    audienceCompany: "Tutte le persone dell'azienda che aprono l'app vedranno i dati da {source}.",
    grantRouted: 'Decide {owner}, quindi è stata inviata a questa persona.',
    grantRoutedUnknown:
      'È il proprietario di questa connessione a decidere, quindi è stata inviata a lui.',
    grantWhat: 'Accesso',
    businessStep: 'Passaggio {order}',
    stepWaiting: 'In attesa',
    stepPending: 'Richiede una decisione',
    stepApproved: 'Approvato',
    stepRejected: 'Rifiutato',
    stepYou: 'Tu',
    businessNote: "L'approvazione qui non consente nuove modifiche al di fuori di Poneva.",
    businessYourTurn: 'Tocca a te decidere.',
    businessNotYourTurn: 'In attesa che {name} decida per primo.',
    policyTitle: 'Come le app usano questa connessione',
    policyCreator: 'Solo quella del creatore',
    policyCreatorBody:
      "Tutti vedono gli stessi dati, tramite la connessione del creatore dell'app.",
    policyViewer: 'Solo quella personale',
    policyViewerBody: 'Ogni persona vede i propri dati, tramite la propria connessione.',
    policyBoth: 'Entrambe',
    policyBothBody: "Il creatore dell'app sceglie per ogni app.",
    policyRevoked: 'Le app non possono usare questa connessione',
    policyRevokedBody:
      "Scegli un'opzione per consentirne nuovamente l'uso. Non viene scelto nulla automaticamente.",
    policySave: 'Salva',
    policySaving: 'Salvataggio…',
    policySaved: 'Salvato.',
    policyReadOnly: "Solo un amministratore dell'azienda può modificarlo.",
    policyConflict: "Qualcun altro lo ha modificato prima di te. Questa è l'impostazione attuale.",
    policyRefused: 'Non è stato possibile salvare.',
    policyFailed:
      "Non abbiamo potuto confermare che il salvataggio sia riuscito. Ricarica la pagina per vedere l'impostazione attuale prima di riprovare.",
    policyWidened:
      'Le app già pubblicate mantengono le modalità di connessione attuali. Ai loro creatori viene chiesto di ripubblicarle per usare la nuova opzione.',
    policyNarrowed: "Le app controllano l'impostazione attuale ogni volta che si connettono.",
    policyAffected: '{count} app pubblicate usano questa connessione (al {when}).',
    policyAffectedOne: '1 app pubblicata usa questa connessione (al {when}).',
    policyAffectsNames: 'Riguarda {count} app pubblicate: {names}.',
    policyAffectsOneName: 'Riguarda 1 app pubblicata: {names}.',
    policyGenericWiden:
      'Le app pubblicate che usano questa connessione mantengono le modalità di connessione attuali. Ai loro creatori viene chiesto di ripubblicarle per usare la nuova opzione.',
    policyGenericNarrow:
      "Le app pubblicate che usano questa connessione controllano l'impostazione attuale ogni volta che si connettono.",
    policyAffectedGeneric:
      'Le app pubblicate che usano questa connessione non vengono modificate automaticamente. Ai loro creatori viene chiesto di ripubblicarle.',
    policyUnavailable: 'Questa impostazione non è al momento disponibile.',
    outsideRead: 'Consenti a {app} di leggere da {hosts}',
    outsideReadSend: 'Consenti a {app} di leggere da {hosts} e di inviarvi dati',
    outsideNone: 'Impedisci a {app} di raggiungere {hosts}',
    outsideReadDone: '{app} può leggere da {hosts}',
    outsideReadSendDone: '{app} può leggere da {hosts} e inviarvi dati',
    outsideNoneDone: '{app} non raggiunge più {hosts}',
    outsideMore: 'altri {count}',
    eventWebhookOn: 'Consenti a {app} di ricevere “{event}” sul suo webhook',
    eventWebhookOnDone: '{app} riceve “{event}” sul suo webhook',
    eventWebhookOff: 'Impedisci a {app} di ricevere “{event}” sul suo webhook',
    eventWebhookOffDone: '{app} non riceve più “{event}”',
    eventRowsOn:
      'Consenti a {app} di eseguire {runs} quando vengono {changes} righe in {collection}',
    eventRowsOnDone: '{app} esegue {runs} quando vengono {changes} righe in {collection}',
    eventRowsOff: 'Impedisci a {app} di eseguire {runs} quando cambiano righe in {collection}',
    eventRowsOffDone: '{app} non esegue più {runs} quando cambiano righe in {collection}',
    eventInsert: 'aggiunte',
    eventUpdate: 'modificate',
    eventDelete: 'eliminate',
    scheduleTurnOn: 'Attiva la pianificazione di “{job}” in {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'La pianificazione di “{job}” in {app} è attiva. {cadence} ({timezone}).',
    scheduleResume: 'Riprendi la pianificazione di “{job}” in {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'La pianificazione di “{job}” in {app} è di nuovo attiva. {cadence} ({timezone}).',
    scheduleReschedule: 'Modifica la pianificazione di “{job}” in {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'La pianificazione di “{job}” in {app} è stata modificata. {cadence} ({timezone}).',
    schedulePause: 'Metti in pausa la pianificazione di “{job}” in {app}.',
    schedulePauseDone: 'La pianificazione di “{job}” in {app} è in pausa.',
    scheduleNext: 'Prossime esecuzioni: {runs}.',
    scheduleResumeSaved:
      'Riprendi la pianificazione di “{job}” in {app} con l’ultima versione salvata. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'La pianificazione di “{job}” in {app} è di nuovo attiva ed esegue l’ultima versione salvata. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Riprendi la pianificazione di “{job}” in {app} con la versione pubblicata. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'La pianificazione di “{job}” in {app} è di nuovo attiva ed esegue la versione pubblicata. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Modifica la pianificazione di “{job}” in {app}. {cadence} ({timezone}). Sarà in pausa finché non la riprendi.',
    scheduleReschedulePausedDone:
      'La pianificazione di “{job}” in {app} è stata modificata. {cadence} ({timezone}). È in pausa finché non la riprendi.',
    scheduleSwitchSaved:
      'Passa la pianificazione di “{job}” in {app} all’ultima versione salvata. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'La pianificazione di “{job}” in {app} ora esegue l’ultima versione salvata. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Passa la pianificazione di “{job}” in {app} all’ultima versione salvata. {cadence} ({timezone}). Sarà in pausa finché non la riprendi.',
    scheduleSwitchSavedPausedDone:
      'La pianificazione di “{job}” in {app} ora usa l’ultima versione salvata. {cadence} ({timezone}). È in pausa finché non la riprendi.',
    scheduleSwitchPublished:
      'Passa la pianificazione di “{job}” in {app} alla versione pubblicata. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'La pianificazione di “{job}” in {app} ora esegue la versione pubblicata. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Passa la pianificazione di “{job}” in {app} alla versione pubblicata. {cadence} ({timezone}). Sarà in pausa finché non la riprendi.',
    scheduleSwitchPublishedPausedDone:
      'La pianificazione di “{job}” in {app} ora usa la versione pubblicata. {cadence} ({timezone}). È in pausa finché non la riprendi.',
    dataWritesOn: 'Consenti a “{function}” di {app} di {verbs} le sue righe',
    dataWritesOnDone: '“{function}” di {app} può {verbs} le sue righe',
    dataWritesOff: 'Impedisci a “{function}” di {app} di modificare le sue righe',
    dataWritesOffDone: '“{function}” di {app} non modifica più le sue righe',
    dataWriteInsert: 'aggiungere',
    dataWriteUpdate: 'modificare',
    dataWriteDelete: 'eliminare',
    tableDropField: 'Consenti a {app} di eliminare il campo “{field}” da {table} ({rows})',
    tableDropFieldDone: '{app} ha eliminato il campo “{field}” da {table} ({rows})',
    tableDropTable: 'Consenti a {app} di eliminare la tabella {table} ({rows})',
    tableDropTableDone: '{app} ha eliminato la tabella {table} ({rows})',
    tableClear:
      'Consenti a {app} di svuotare “{field}” in {table} dove non rispetta la nuova regola ({rows})',
    tableClearDone:
      '{app} ha svuotato “{field}” in {table} dove non rispettava la nuova regola ({rows})',
    tableRestoreField:
      'Consenti a {app} di ripristinare “{field}” in {table} dalla sua copia ({rows})',
    tableRestoreFieldDone: '{app} ha ripristinato “{field}” in {table} ({rows})',
    tableRestoreTable:
      'Consenti a {app} di ripristinare la tabella {table} dalla sua copia ({rows})',
    tableRestoreTableDone: '{app} ha ripristinato la tabella {table} ({rows})',
    tableRowsLoseOne: '1 riga perde un valore',
    tableRowsLoseMany: '{count} righe perdono un valore',
    tableRowsLostOne: '1 riga ha perso un valore',
    tableRowsLostMany: '{count} righe hanno perso un valore',
    tableRowsOne: '1 riga',
    tableRowsMany: '{count} righe',
    connectedWriteOn: 'Consenti a {app} di {action} in {destination}',
    connectedWriteOnDone: '{app} può {action} in {destination}',
    connectedWriteOff: 'Impedisci a {app} di scrivere in {destination}',
    connectedWriteOffDone: '{app} non scrive più in {destination}',
    codeConfirm: 'Consenti a {app} di usare le tue approvazioni con le modifiche di {names}',
    codeConfirmDone: '{app} usa le tue approvazioni con le modifiche di {names}',
    codeConfirmCurrent: 'Consenti a {app} di usare le tue approvazioni con il suo codice attuale',
    codeConfirmCurrentDone: '{app} usa le tue approvazioni con il suo codice attuale',
    runChanges: 'Esegui ora “{function}” in {app}',
    runChangesDone: '“{function}” è stata eseguita in {app}',
    continueApproved: 'Approvato. Continua: eseguilo e verifica che funzioni.',
    continueDeclined: 'Rifiutato. Continua senza.',
    continueMixed: 'Ho deciso sulle richieste. Continua.',
    typeOutsideAccess: 'Consentire a un’app di raggiungere servizi esterni',
    typeEvent: 'Attivare o disattivare un evento di un’app',
    typeSchedule: 'Attivare, mettere in pausa o modificare la pianificazione di un’app',
    typeDataWrites: 'Consentire a una funzione di un’app di modificare le proprie righe',
    typeTableChange: 'Eliminare o ripristinare dati nelle tabelle di un’app',
    typeConnectedWrite: 'Consentire a un’app di scrivere in un sistema aziendale',
    typeConfirmCode: 'Lasciare che il codice modificato di un’app usi le tue approvazioni',
    typeRunChanges: 'Eseguire una funzione di un’app che modifica dati',
    typePublicLink: 'Modificare il link pubblico di un’app',
    typeUnpublish: 'Rimuovere il link pubblico di un’app',
    labelHosts: 'Servizi',
    labelAccess: 'Accesso',
    labelEvent: 'Evento',
    labelKind: 'Si avvia con',
    labelCollection: 'Raccolta',
    labelChanges: 'Modifiche alle righe',
    labelRuns: 'Esecuzioni',
    labelFunction: 'Funzione',
    labelCollections: 'Tabelle',
    labelWho: 'Per',
    labelAllowed: 'Consentito',
    labelEnabled: 'Stato',
    labelJob: 'Attività pianificata',
    labelSchedule: 'Pianificazione',
    labelTimezone: 'Fuso orario',
    labelChange: 'Modifica',
    labelTable: 'Tabella',
    labelField: 'Campo',
    labelRows: 'Righe',
    labelKeptDays: 'Copia conservata (giorni)',
    labelRestores: 'Ripristina',
    labelAction: 'Azione',
    labelDestination: 'Destinazione',
    labelChangedBy: 'Modificato da',
    labelInput: 'Input',
    labelPublicTables: 'Tabelle copiate',
    labelPublicActions: 'I visitatori possono inviare',
    labelPublicRuns: 'Ogni invio avvia',
    labelPublicRunsOff: 'Si avvierebbe anche una volta attivato',
    labelEmbedSites: 'Siti che possono incorporarla',
    labelPublicLink: 'Link pubblico',
    labelPublishedBy: 'Pubblicato da',
    wordRead: 'Sola lettura',
    wordReadSend: 'Lettura e invio di dati',
    wordNoAccess: 'Nessun accesso',
    publicPublish: 'Pubblica su un link pubblico',
    publicRefresh: 'Copia di nuovo i suoi dati',
    publicUnpublish: 'Annulla pubblicazione',
    eventWebhook: 'Il suo webhook',
    eventRows: 'Modifiche alle sue righe',
    switchOn: 'Attivo',
    switchOff: 'Disattivato',
    switchPaused: 'In pausa',
    changeInsert: 'Aggiunta',
    changeUpdate: 'Modificata',
    changeDelete: 'Eliminata',
    wordDropField: 'Elimina i valori di un campo',
    wordDropTable: 'Elimina una tabella',
    tableClearValues: 'Cancella i valori che violano una regola più rigida',
    tableRestore: 'Ripristina dalla sua copia',
    whoOwner: 'Le tue esecuzioni',
    whoEveryone: 'Tutte le persone che la usano',
    answerYes: 'Sì',
    answerNo: 'No',
  },
  lv: {
    kindWrite: 'Apstiprināmas lietotnes izmaiņas',
    kindGrant: 'Apstiprināma piekļuve lietotnei',
    kindBusiness: 'Lietotnes apstiprinājums',
    approve: 'Apstiprināt',
    reject: 'Noraidīt',
    working: 'Notiek darbība…',
    checkStatus: 'Pārbaudīt statusu',
    statusStillPending:
      'Poneva to vēl nav pabeidzis. Process, iespējams, joprojām notiek, tāpēc, kamēr tas nav pabeigts, neko citu nevar izlemt.',
    statusStillUnknown:
      'Neizdevās saņemt atbildi. Kamēr tas nav pabeigts, neko citu nevar izlemt. Pēc brīža mēģiniet vēlreiz.',
    loading: 'Notiek ielāde…',
    unavailable: 'Šobrīd tas nav pieejams.',
    loadFailed: 'Neizdevās to ielādēt. Pēc brīža mēģiniet vēlreiz.',
    requestedBy: 'Pieprasīja {name}',
    originApp: 'No lietotnes',
    originAgent: 'No aģenta',
    originClient: 'Izmantojot {client}',
    originFor: 'lietotājam {name}',
    changedUnderYou:
      'Tas mainījās, kamēr jūs to lasījāt. Tagad redzamā ir pašreizējā versija. Nekas netika izlemts.',
    notAllowed: 'Jūs nevarat par šo lemt.',
    cannotDecideNow: 'Par šo šobrīd nevar lemt. Nekas netika nosūtīts.',
    expired: 'Šim pieprasījumam beidzies derīguma termiņš. Nekas nemainījās.',
    stale:
      'Dati kopš pieprasījuma izveides ir mainījušies. Pieprasiet vēlreiz, lai saņemtu jaunu pieprasījumu.',
    approved: 'Apstiprināts',
    rejected: 'Noraidīts',
    needsYou: 'Nepieciešams apstiprinājums',
    waitingOn: 'Gaida {name}',
    unknownTitle: 'Nevaram apstiprināt, kas notika',
    unknownBody:
      'Izmaiņas varēja tikt veiktas vai arī ne. Neatkārtojiet darbību. Pārbaudiet statusu vai lūdziet lietotnes īpašniekam to pārbaudīt.',
    blockedApproval: 'Gaida šo apstiprinājumu, pirms turpināt.',
    blockedBudget: 'Apturēts: šai lietotnei pieejamais MI budžets ir izlietots.',
    blockedProvider: 'Apturēts: savienotais pakalpojums neatbild.',
    blockedViewer:
      'Apturēts: tas jāpalaiž kā pierakstītam skatītājam, taču skatītājs nav pievienots. Tā vietā netiks izmantota īpašnieka piekļuve.',
    writeTitle: 'Apstiprināt izmaiņas: {target}',
    writeSummaryAll: '{count} izmaiņas lietotnē {app}',
    rowCreate: 'Jauns',
    rowUpdate: 'Mainīts',
    rowDelete: 'Noņemts',
    rowInclude: 'Iekļaut šīs izmaiņas',
    rowExcluded: 'Izlaists',
    fieldBefore: 'Pirms',
    fieldAfter: 'Pēc',
    fieldEmpty: 'Tukšs',
    approveSome: 'Apstiprināt {included} no {total}',
    approveAll: 'Apstiprināt visas {total}',
    allLeftOut:
      'Visas izmaiņas ir izlaistas. Noraidiet, lai pārtrauktu, vai iekļaujiet kādu izmaiņu, lai to apstiprinātu.',
    appliedSome: 'Veiktas {count} izmaiņas.',
    rejectedBody: 'Nekas netika mainīts.',
    noRows: 'Šeit nav nekā apstiprināma.',
    grantTitle: 'Ļaut lietotnei {app} izmantot {source}',
    grantRead: 'Tikai lasīšana',
    grantWrite: 'Lasīšana un izmaiņu veikšana',
    audienceOwner: 'Tikai jūs redzēsiet šīs lietotnes datus no {source}.',
    audiencePeople: 'Šīs lietotnes koplietotāji — {count} cilvēki — redzēs datus no {source}.',
    audienceCompany: 'Ikviens uzņēmuma darbinieks, kurš atver lietotni, redzēs datus no {source}.',
    grantRouted: 'Par šo lemj {owner}, tāpēc tas tika nosūtīts viņam.',
    grantRoutedUnknown: 'Par šo lemj šī savienojuma īpašnieks, tāpēc tas tika nosūtīts viņam.',
    grantWhat: 'Piekļuve',
    businessStep: '{order}. darbība',
    stepWaiting: 'Gaida',
    stepPending: 'Jāpieņem lēmums',
    stepApproved: 'Apstiprināts',
    stepRejected: 'Noraidīts',
    stepYou: 'Jūs',
    businessNote: 'Apstiprināšana šeit neļauj veikt jaunas izmaiņas ārpus Poneva.',
    businessYourTurn: 'Tagad jālemj jums.',
    businessNotYourTurn: 'Vispirms jāgaida, līdz lēmumu pieņems {name}.',
    policyTitle: 'Kā lietotnes izmanto šo savienojumu',
    policyCreator: 'Tikai veidotāja',
    policyCreatorBody:
      'Visi redz vienus un tos pašus datus, izmantojot lietotnes veidotāja savienojumu.',
    policyViewer: 'Tikai skatītāja paša',
    policyViewerBody: 'Katrs redz savus datus, izmantojot savu savienojumu.',
    policyBoth: 'Abi',
    policyBothBody: 'Lietotnes veidotājs izvēlas katrai lietotnei.',
    policyRevoked: 'Lietotnes nevar izmantot šo savienojumu',
    policyRevokedBody:
      'Izvēlieties opciju, lai to atkal atļautu. Jūsu vietā nekas netiks izvēlēts.',
    policySave: 'Saglabāt',
    policySaving: 'Notiek saglabāšana…',
    policySaved: 'Saglabāts.',
    policyReadOnly: 'To var mainīt tikai uzņēmuma administrators.',
    policyConflict: 'Kāds cits to mainīja pirmais. Šis ir pašreizējais iestatījums.',
    policyRefused: 'Neizdevās saglabāt.',
    policyFailed:
      'Nevarējām apstiprināt, ka iestatījums ir saglabāts. Pirms mēģināt vēlreiz, atkārtoti ielādējiet lapu, lai skatītu pašreizējo iestatījumu.',
    policyWidened:
      'Jau publicētās lietotnes saglabā pašreizējo savienojuma veidu. To veidotājiem tiek lūgts tās publicēt atkārtoti, lai izmantotu jauno opciju.',
    policyNarrowed:
      'Lietotnes katru reizi, kad izveido savienojumu, pārbauda pašreizējo iestatījumu.',
    policyAffected: '{count} publicētās lietotnes izmanto šo savienojumu ({when} datumā).',
    policyAffectedOne: '1 publicēta lietotne izmanto šo savienojumu ({when} datumā).',
    policyAffectsNames: 'Ietekmē {count} publicētās lietotnes: {names}.',
    policyAffectsOneName: 'Ietekmē 1 publicētu lietotni: {names}.',
    policyGenericWiden:
      'Publicētās lietotnes, kas izmanto šo savienojumu, saglabā pašreizējo savienojuma veidu. To veidotājiem tiek lūgts tās publicēt atkārtoti, lai izmantotu jauno opciju.',
    policyGenericNarrow:
      'Publicētās lietotnes, kas izmanto šo savienojumu, katru reizi, kad izveido savienojumu, pārbauda pašreizējo iestatījumu.',
    policyAffectedGeneric:
      'Publicētās lietotnes, kas izmanto šo savienojumu, netiek automātiski mainītas. To veidotājiem tiek lūgts tās publicēt atkārtoti.',
    policyUnavailable: 'Šis iestatījums pašlaik nav pieejams.',
    outsideRead: 'Ļaut lietotnei {app} lasīt no {hosts}',
    outsideReadSend: 'Ļaut lietotnei {app} lasīt no {hosts} un sūtīt tiem datus',
    outsideNone: 'Neļaut lietotnei {app} piekļūt {hosts}',
    outsideReadDone: 'Lietotne {app} var lasīt no {hosts}',
    outsideReadSendDone: 'Lietotne {app} var lasīt no {hosts} un sūtīt tiem datus',
    outsideNoneDone: 'Lietotne {app} vairs nepiekļūst {hosts}',
    outsideMore: 'vēl {count}',
    eventWebhookOn: 'Ļaut lietotnei {app} saņemt “{event}” savā webhook',
    eventWebhookOnDone: 'Lietotne {app} saņem “{event}” savā webhook',
    eventWebhookOff: 'Neļaut lietotnei {app} saņemt “{event}” savā webhook',
    eventWebhookOffDone: 'Lietotne {app} vairs nesaņem “{event}”',
    eventRowsOn:
      'Ļaut lietotnei {app} palaist {runs}, kad rindas tabulā {collection} tiek {changes}',
    eventRowsOnDone: 'Lietotne {app} palaiž {runs}, kad rindas tabulā {collection} tiek {changes}',
    eventRowsOff: 'Neļaut lietotnei {app} palaist {runs}, kad rindas tabulā {collection} mainās',
    eventRowsOffDone: 'Lietotne {app} vairs nepalaiž {runs}, kad rindas tabulā {collection} mainās',
    eventInsert: 'pievienotas',
    eventUpdate: 'mainītas',
    eventDelete: 'dzēstas',
    scheduleTurnOn: 'Ieslēgt darba “{job}” grafiku lietotnē {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Darba “{job}” grafiks lietotnē {app} ir ieslēgts. {cadence} ({timezone}).',
    scheduleResume: 'Atsākt darba “{job}” grafiku lietotnē {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Darba “{job}” grafiks lietotnē {app} atkal ir ieslēgts. {cadence} ({timezone}).',
    scheduleReschedule: 'Mainīt darba “{job}” grafiku lietotnē {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Darba “{job}” grafiks lietotnē {app} ir mainīts. {cadence} ({timezone}).',
    schedulePause: 'Apturēt darba “{job}” grafiku lietotnē {app}.',
    schedulePauseDone: 'Darba “{job}” grafiks lietotnē {app} ir apturēts.',
    scheduleNext: 'Nākamās palaišanas reizes: {runs}.',
    scheduleResumeSaved:
      'Atsākt darba “{job}” grafiku lietotnē {app} ar pēdējo saglabāto versiju. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Darba “{job}” grafiks lietotnē {app} atkal ir ieslēgts un palaiž pēdējo saglabāto versiju. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Atsākt darba “{job}” grafiku lietotnē {app} ar publicēto versiju. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Darba “{job}” grafiks lietotnē {app} atkal ir ieslēgts un palaiž publicēto versiju. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Mainīt darba “{job}” grafiku lietotnē {app}. {cadence} ({timezone}). Tas būs apturēts, līdz jūs to atsāksiet.',
    scheduleReschedulePausedDone:
      'Darba “{job}” grafiks lietotnē {app} ir mainīts. {cadence} ({timezone}). Tas ir apturēts, līdz jūs to atsāksiet.',
    scheduleSwitchSaved:
      'Pārslēgt darba “{job}” grafiku lietotnē {app} uz pēdējo saglabāto versiju. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Darba “{job}” grafiks lietotnē {app} tagad palaiž pēdējo saglabāto versiju. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Pārslēgt darba “{job}” grafiku lietotnē {app} uz pēdējo saglabāto versiju. {cadence} ({timezone}). Tas būs apturēts, līdz jūs to atsāksiet.',
    scheduleSwitchSavedPausedDone:
      'Darba “{job}” grafiks lietotnē {app} tagad izmanto pēdējo saglabāto versiju. {cadence} ({timezone}). Tas ir apturēts, līdz jūs to atsāksiet.',
    scheduleSwitchPublished:
      'Pārslēgt darba “{job}” grafiku lietotnē {app} uz publicēto versiju. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Darba “{job}” grafiks lietotnē {app} tagad palaiž publicēto versiju. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Pārslēgt darba “{job}” grafiku lietotnē {app} uz publicēto versiju. {cadence} ({timezone}). Tas būs apturēts, līdz jūs to atsāksiet.',
    scheduleSwitchPublishedPausedDone:
      'Darba “{job}” grafiks lietotnē {app} tagad izmanto publicēto versiju. {cadence} ({timezone}). Tas ir apturēts, līdz jūs to atsāksiet.',
    dataWritesOn: 'Ļaut lietotnes {app} funkcijai “{function}” {verbs} lietotnes pašas rindas',
    dataWritesOnDone: 'Lietotnes {app} funkcija “{function}” var {verbs} lietotnes pašas rindas',
    dataWritesOff: 'Neļaut lietotnes {app} funkcijai “{function}” mainīt lietotnes pašas rindas',
    dataWritesOffDone: 'Lietotnes {app} funkcija “{function}” vairs nemaina lietotnes pašas rindas',
    dataWriteInsert: 'pievienot',
    dataWriteUpdate: 'mainīt',
    dataWriteDelete: 'dzēst',
    tableDropField: 'Ļaut lietotnei {app} dzēst lauku “{field}” no tabulas {table} ({rows})',
    tableDropFieldDone: 'Lietotne {app} izdzēsa lauku “{field}” no tabulas {table} ({rows})',
    tableDropTable: 'Ļaut lietotnei {app} dzēst tabulu {table} ({rows})',
    tableDropTableDone: 'Lietotne {app} izdzēsa tabulu {table} ({rows})',
    tableClear:
      'Ļaut lietotnei {app} notīrīt “{field}” tabulā {table}, kur tas pārkāpj jauno noteikumu ({rows})',
    tableClearDone:
      'Lietotne {app} notīrīja “{field}” tabulā {table}, kur tas pārkāpa jauno noteikumu ({rows})',
    tableRestoreField: 'Ļaut lietotnei {app} atjaunot “{field}” tabulā {table} no kopijas ({rows})',
    tableRestoreFieldDone: 'Lietotne {app} atjaunoja “{field}” tabulā {table} ({rows})',
    tableRestoreTable: 'Ļaut lietotnei {app} atjaunot tabulu {table} no kopijas ({rows})',
    tableRestoreTableDone: 'Lietotne {app} atjaunoja tabulu {table} ({rows})',
    tableRowsLoseOne: '1 rinda zaudē vērtību',
    tableRowsLoseMany: 'Rindas, kurās vērtība tiks noņemta: {count}',
    tableRowsLostOne: '1 rinda zaudēja vērtību',
    tableRowsLostMany: 'Rindas, kurās vērtība tika noņemta: {count}',
    tableRowsOne: '1 rinda',
    tableRowsMany: 'Rindas: {count}',
    connectedWriteOn: 'Ļaut lietotnei {app} {action} sistēmā {destination}',
    connectedWriteOnDone: 'Lietotne {app} var {action} sistēmā {destination}',
    connectedWriteOff: 'Neļaut lietotnei {app} rakstīt sistēmā {destination}',
    connectedWriteOffDone: 'Lietotne {app} vairs neraksta sistēmā {destination}',
    codeConfirm: 'Ļaut lietotnei {app} izmantot jūsu apstiprinājumus ar izmaiņām, ko veica {names}',
    codeConfirmDone: 'Lietotne {app} izmanto jūsu apstiprinājumus ar izmaiņām, ko veica {names}',
    codeConfirmCurrent: 'Ļaut lietotnei {app} izmantot jūsu apstiprinājumus ar tās pašreizējo kodu',
    codeConfirmCurrentDone: 'Lietotne {app} izmanto jūsu apstiprinājumus ar savu pašreizējo kodu',
    runChanges: 'Palaist funkciju “{function}” lietotnē {app} tagad',
    runChangesDone: 'Funkcija “{function}” tika palaista lietotnē {app}',
    continueApproved: 'Apstiprināts. Turpini: palaid to un pārbaudi, vai tas darbojas.',
    continueDeclined: 'Noraidīts. Turpini bez tā.',
    continueMixed: 'Lēmumi par pieprasījumiem ir pieņemti. Turpini.',
    typeOutsideAccess: 'Atļaut lietotnei piekļūt ārējiem pakalpojumiem',
    typeEvent: 'Ieslēgt vai izslēgt lietotnes notikumu',
    typeSchedule: 'Ieslēgt, apturēt vai mainīt lietotnes grafiku',
    typeDataWrites: 'Atļaut lietotnes funkcijai mainīt savas rindas',
    typeTableChange: 'Dzēst vai atjaunot datus lietotnes tabulās',
    typeConnectedWrite: 'Atļaut lietotnei rakstīt uzņēmuma sistēmā',
    typeConfirmCode: 'Ļaut mainītajam lietotnes kodam izmantot jūsu apstiprinājumus',
    typeRunChanges: 'Palaist lietotnes funkciju, kas maina datus',
    typePublicLink: 'Mainīt lietotnes publisko saiti',
    typeUnpublish: 'Noņemt lietotnes publisko saiti',
    labelHosts: 'Pakalpojumi',
    labelAccess: 'Piekļuve',
    labelEvent: 'Notikums',
    labelKind: 'Sākas, kad',
    labelCollection: 'Kolekcija',
    labelChanges: 'Rindu izmaiņas',
    labelRuns: 'Palaišanas',
    labelFunction: 'Funkcija',
    labelCollections: 'Tabulas',
    labelWho: 'Kam',
    labelAllowed: 'Atļauts',
    labelEnabled: 'Statuss',
    labelJob: 'Ieplānotais darbs',
    labelSchedule: 'Grafiks',
    labelTimezone: 'Laika josla',
    labelChange: 'Izmaiņa',
    labelTable: 'Tabula',
    labelField: 'Lauks',
    labelRows: 'Rindas',
    labelKeptDays: 'Kopija tiek glabāta (dienas)',
    labelRestores: 'Atjauno',
    labelAction: 'Darbība',
    labelDestination: 'Galamērķis',
    labelChangedBy: 'Mainīja',
    labelInput: 'Ievade',
    labelPublicTables: 'Nokopētās tabulas',
    labelPublicActions: 'Apmeklētāji var sūtīt',
    labelPublicRuns: 'Katra iesniegšana palaiž',
    labelPublicRunsOff: 'Palaistu arī pēc ieslēgšanas',
    labelEmbedSites: 'Vietnes, kurās to drīkst iegult',
    labelPublicLink: 'Publiska saite',
    labelPublishedBy: 'Publicēja',
    wordRead: 'Tikai lasīšana',
    wordReadSend: 'Lasīt un sūtīt datus',
    wordNoAccess: 'Nav piekļuves',
    publicPublish: 'Publicēt publiskā saitē',
    publicRefresh: 'Vēlreiz nokopēt tās datus',
    publicUnpublish: 'Atcelt publicēšanu',
    eventWebhook: 'Tās webhook',
    eventRows: 'Izmaiņas tās rindās',
    switchOn: 'Ieslēgts',
    switchOff: 'Izslēgts',
    switchPaused: 'Apturēts',
    changeInsert: 'Pievienots',
    changeUpdate: 'Mainīts',
    changeDelete: 'Dzēsts',
    wordDropField: 'Dzēst lauka vērtības',
    wordDropTable: 'Dzēst tabulu',
    tableClearValues: 'Notīrīt vērtības, kas pārkāpj stingrāku noteikumu',
    tableRestore: 'Atjaunot no tās kopijas',
    whoOwner: 'Jūsu pašu palaišanas',
    whoEveryone: 'Visi, kas to izmanto',
    answerYes: 'Jā',
    answerNo: 'Nē',
  },
  lt: {
    kindWrite: 'Programos pakeitimas, kurį reikia patvirtinti',
    kindGrant: 'Programos prieiga, kurią reikia patvirtinti',
    kindBusiness: 'Programos patvirtinimas',
    approve: 'Patvirtinti',
    reject: 'Atmesti',
    working: 'Vykdoma…',
    checkStatus: 'Patikrinti būseną',
    statusStillPending:
      'Poneva dar neužbaigė šio veiksmo. Jis gali būti vis dar vykdomas, todėl kol kas negalima priimti jokio kito sprendimo.',
    statusStillUnknown:
      'Nepavyko gauti atsakymo. Kol tai nebus išspręsta, negalima priimti jokio kito sprendimo. Po akimirkos bandykite dar kartą.',
    loading: 'Įkeliama…',
    unavailable: 'Šiuo metu tai nepasiekiama.',
    loadFailed: 'Nepavyko to įkelti. Po akimirkos bandykite dar kartą.',
    requestedBy: 'Paprašė {name}',
    originApp: 'Iš programos',
    originAgent: 'Iš agento',
    originClient: 'Per {client}',
    originFor: 'skirta {name}',
    changedUnderYou:
      'Tai pasikeitė, kol skaitėte. Dabar matote dabartinę versiją. Sprendimas nebuvo priimtas.',
    notAllowed: 'Negalite dėl to priimti sprendimo.',
    cannotDecideNow: 'Šiuo metu dėl to negalima priimti sprendimo. Nieko neišsiųsta.',
    expired: 'Šios užklausos galiojimo laikas baigėsi. Nieko nepakeista.',
    stale:
      'Nuo užklausos pateikimo duomenys pasikeitė. Paprašykite dar kartą, kad gautumėte naują užklausą.',
    approved: 'Patvirtinta',
    rejected: 'Atmesta',
    needsYou: 'Reikia patvirtinimo',
    waitingOn: 'Laukiama, kol spręs {name}',
    unknownTitle: 'Negalime patvirtinti, kas įvyko',
    unknownBody:
      'Pakeitimas galėjo būti atliktas arba ne. Nekartokite veiksmo. Patikrinkite būseną arba paprašykite programos savininko tai patikrinti.',
    blockedApproval: 'Laukiama šio patvirtinimo, kad būtų galima tęsti.',
    blockedBudget: 'Pristabdyta: šiai programai skirtas DI biudžetas išnaudotas.',
    blockedProvider: 'Pristabdyta: prijungta paslauga neatsako.',
    blockedViewer:
      'Pristabdyta: tai turi būti vykdoma prisijungusio žiūrinčiojo teisėmis, tačiau žiūrinčiojo nėra. Vietoj savininko prieigos niekas nevykdoma.',
    writeTitle: 'Patvirtinti {target} pakeitimus',
    writeSummaryAll: '{count} pakeitimų programoje {app}',
    rowCreate: 'Nauja',
    rowUpdate: 'Pakeista',
    rowDelete: 'Pašalinta',
    rowInclude: 'Įtraukti šį pakeitimą',
    rowExcluded: 'Neįtraukta',
    fieldBefore: 'Prieš',
    fieldAfter: 'Po',
    fieldEmpty: 'Tuščia',
    approveSome: 'Patvirtinti {included} iš {total}',
    approveAll: 'Patvirtinti visus {total}',
    allLeftOut:
      'Visi pakeitimai neįtraukti. Atmeskite, kad sustabdytumėte, arba įtraukite pakeitimą, kad jį patvirtintumėte.',
    appliedSome: 'Atlikta pakeitimų: {count}.',
    rejectedBody: 'Nieko nepakeista.',
    noRows: 'Čia nėra nieko, ką reikėtų patvirtinti.',
    grantTitle: 'Leisti programai {app} naudoti {source}',
    grantRead: 'Tik skaityti',
    grantWrite: 'Skaityti ir keisti',
    audienceOwner: 'Šios programos duomenis iš {source} matysite tik jūs.',
    audiencePeople: 'Duomenis iš {source} matys {count} žmonių, su kuriais bendrinama ši programa.',
    audienceCompany: 'Duomenis iš {source} matys visi įmonės darbuotojai, kurie atidarys programą.',
    grantRouted: 'Tai sprendžia {owner}, todėl užklausa išsiųsta jiems.',
    grantRoutedUnknown: 'Tai sprendžia šio ryšio savininkas, todėl užklausa išsiųsta jam.',
    grantWhat: 'Prieiga',
    businessStep: '{order} veiksmas',
    stepWaiting: 'Laukiama',
    stepPending: 'Reikia sprendimo',
    stepApproved: 'Patvirtinta',
    stepRejected: 'Atmesta',
    stepYou: 'Jūs',
    businessNote:
      'Patvirtinimas čia nesuteikia teisės atlikti jokių naujų pakeitimų už Poneva ribų.',
    businessYourTurn: 'Dabar sprendžiate jūs.',
    businessNotYourTurn: 'Pirmiausia laukiama, kol spręs {name}.',
    policyTitle: 'Kaip programos naudoja šį ryšį',
    policyCreator: 'Tik kūrėjo',
    policyCreatorBody: 'Visi mato tuos pačius duomenis per programos kūrėjo ryšį.',
    policyViewer: 'Tik paties žiūrinčiojo',
    policyViewerBody: 'Kiekvienas mato savo duomenis per savo ryšį.',
    policyBoth: 'Abu',
    policyBothBody: 'Programos kūrėjas pasirenka kiekvienai programai.',
    policyRevoked: 'Programos negali naudoti šio ryšio',
    policyRevokedBody:
      'Pasirinkite parinktį, kad vėl leistumėte jį naudoti. Už jus niekas nepasirinkta.',
    policySave: 'Išsaugoti',
    policySaving: 'Išsaugoma…',
    policySaved: 'Išsaugota.',
    policyReadOnly: 'Tai pakeisti gali tik įmonės administratorius.',
    policyConflict: 'Kažkas kitas tai pakeitė anksčiau. Tai yra dabartinis nustatymas.',
    policyRefused: 'Nepavyko išsaugoti.',
    policyFailed:
      'Nepavyko patvirtinti, kad nustatymas išsaugotas. Prieš bandydami dar kartą įkelkite puslapį, kad pamatytumėte dabartinį nustatymą.',
    policyWidened:
      'Jau paskelbtos programėlės ir toliau jungsis taip, kaip jungiasi dabar. Jų kūrėjų bus paprašyta paskelbti jas iš naujo, kad galėtų naudoti naują parinktį.',
    policyNarrowed: 'Programėlės kaskart jungdamosi tikrina dabartinį nustatymą.',
    policyAffected: '{count} paskelbtų programėlių naudoja šį ryšį (duomenys {when}).',
    policyAffectedOne: '1 paskelbta programėlė naudoja šį ryšį (duomenys {when}).',
    policyAffectsNames: 'Paveikiamos {count} paskelbtos programėlės: {names}.',
    policyAffectsOneName: 'Paveikiama 1 paskelbta programėlė: {names}.',
    policyGenericWiden:
      'Paskelbtos programėlės, naudojančios šį ryšį, ir toliau jungsis taip, kaip jungiasi dabar. Jų kūrėjų bus paprašyta paskelbti jas iš naujo, kad galėtų naudoti naują parinktį.',
    policyGenericNarrow:
      'Paskelbtos programėlės, naudojančios šį ryšį, kaskart jungdamosi tikrina dabartinį nustatymą.',
    policyAffectedGeneric:
      'Paskelbtos programėlės, naudojančios šį ryšį, automatiškai nepakeičiamos. Jų kūrėjų bus paprašyta paskelbti jas iš naujo.',
    policyUnavailable: 'Šis nustatymas šiuo metu nepasiekiamas.',
    outsideRead: 'Leisti programai {app} skaityti iš {hosts}',
    outsideReadSend: 'Leisti programai {app} skaityti iš {hosts} ir siųsti jiems duomenis',
    outsideNone: 'Neleisti programai {app} pasiekti {hosts}',
    outsideReadDone: 'Programa {app} gali skaityti iš {hosts}',
    outsideReadSendDone: 'Programa {app} gali skaityti iš {hosts} ir siųsti jiems duomenis',
    outsideNoneDone: 'Programa {app} nebepasiekia {hosts}',
    outsideMore: 'dar {count}',
    eventWebhookOn: 'Leisti programai {app} gauti „{event}“ savo webhook',
    eventWebhookOnDone: 'Programa {app} gauna „{event}“ savo webhook',
    eventWebhookOff: 'Neleisti programai {app} gauti „{event}“ savo webhook',
    eventWebhookOffDone: 'Programa {app} nebegauna „{event}“',
    eventRowsOn:
      'Leisti programai {app} vykdyti {runs}, kai eilutės lentelėje {collection} būna {changes}',
    eventRowsOnDone:
      'Programa {app} vykdo {runs}, kai eilutės lentelėje {collection} būna {changes}',
    eventRowsOff:
      'Neleisti programai {app} vykdyti {runs}, kai keičiasi eilutės lentelėje {collection}',
    eventRowsOffDone:
      'Programa {app} nebevykdo {runs}, kai keičiasi eilutės lentelėje {collection}',
    eventInsert: 'pridėtos',
    eventUpdate: 'pakeistos',
    eventDelete: 'ištrintos',
    scheduleTurnOn:
      'Įjungti užduoties „{job}“ tvarkaraštį programoje {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone:
      'Užduoties „{job}“ tvarkaraštis programoje {app} įjungtas. {cadence} ({timezone}).',
    scheduleResume: 'Tęsti užduoties „{job}“ tvarkaraštį programoje {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Užduoties „{job}“ tvarkaraštis programoje {app} vėl įjungtas. {cadence} ({timezone}).',
    scheduleReschedule:
      'Keisti užduoties „{job}“ tvarkaraštį programoje {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Užduoties „{job}“ tvarkaraštis programoje {app} pakeistas. {cadence} ({timezone}).',
    schedulePause: 'Pristabdyti užduoties „{job}“ tvarkaraštį programoje {app}.',
    schedulePauseDone: 'Užduoties „{job}“ tvarkaraštis programoje {app} pristabdytas.',
    scheduleNext: 'Kiti vykdymai: {runs}.',
    scheduleResumeSaved:
      'Tęsti užduoties „{job}“ tvarkaraštį programoje {app} su naujausia išsaugota versija. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Užduoties „{job}“ tvarkaraštis programoje {app} vėl įjungtas ir vykdo naujausią išsaugotą versiją. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Tęsti užduoties „{job}“ tvarkaraštį programoje {app} su paskelbta versija. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Užduoties „{job}“ tvarkaraštis programoje {app} vėl įjungtas ir vykdo paskelbtą versiją. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Keisti užduoties „{job}“ tvarkaraštį programoje {app}. {cadence} ({timezone}). Jis bus pristabdytas, kol vėl jį įjungsite.',
    scheduleReschedulePausedDone:
      'Užduoties „{job}“ tvarkaraštis programoje {app} pakeistas. {cadence} ({timezone}). Jis pristabdytas, kol vėl jį įjungsite.',
    scheduleSwitchSaved:
      'Perjungti užduoties „{job}“ tvarkaraštį programoje {app} į naujausią išsaugotą versiją. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Užduoties „{job}“ tvarkaraštis programoje {app} dabar vykdo naujausią išsaugotą versiją. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Perjungti užduoties „{job}“ tvarkaraštį programoje {app} į naujausią išsaugotą versiją. {cadence} ({timezone}). Jis bus pristabdytas, kol vėl jį įjungsite.',
    scheduleSwitchSavedPausedDone:
      'Užduoties „{job}“ tvarkaraštis programoje {app} dabar naudoja naujausią išsaugotą versiją. {cadence} ({timezone}). Jis pristabdytas, kol vėl jį įjungsite.',
    scheduleSwitchPublished:
      'Perjungti užduoties „{job}“ tvarkaraštį programoje {app} į paskelbtą versiją. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Užduoties „{job}“ tvarkaraštis programoje {app} dabar vykdo paskelbtą versiją. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Perjungti užduoties „{job}“ tvarkaraštį programoje {app} į paskelbtą versiją. {cadence} ({timezone}). Jis bus pristabdytas, kol vėl jį įjungsite.',
    scheduleSwitchPublishedPausedDone:
      'Užduoties „{job}“ tvarkaraštis programoje {app} dabar naudoja paskelbtą versiją. {cadence} ({timezone}). Jis pristabdytas, kol vėl jį įjungsite.',
    dataWritesOn: 'Leisti programos {app} funkcijai „{function}“ {verbs} savo eilutes',
    dataWritesOnDone: 'Programos {app} funkcija „{function}“ gali {verbs} savo eilutes',
    dataWritesOff: 'Neleisti programos {app} funkcijai „{function}“ keisti savo eilučių',
    dataWritesOffDone: 'Programos {app} funkcija „{function}“ nebekeičia savo eilučių',
    dataWriteInsert: 'pridėti',
    dataWriteUpdate: 'keisti',
    dataWriteDelete: 'trinti',
    tableDropField: 'Leisti programai {app} pašalinti lauką „{field}“ iš lentelės {table} ({rows})',
    tableDropFieldDone: 'Programa {app} pašalino lauką „{field}“ iš lentelės {table} ({rows})',
    tableDropTable: 'Leisti programai {app} pašalinti lentelę {table} ({rows})',
    tableDropTableDone: 'Programa {app} pašalino lentelę {table} ({rows})',
    tableClear:
      'Leisti programai {app} išvalyti „{field}“ lentelėje {table}, kur tai pažeidžia naują taisyklę ({rows})',
    tableClearDone:
      'Programa {app} išvalė „{field}“ lentelėje {table}, kur tai pažeidė naują taisyklę ({rows})',
    tableRestoreField:
      'Leisti programai {app} grąžinti „{field}“ lentelėje {table} iš kopijos ({rows})',
    tableRestoreFieldDone: 'Programa {app} grąžino „{field}“ lentelėje {table} ({rows})',
    tableRestoreTable: 'Leisti programai {app} atkurti lentelę {table} iš kopijos ({rows})',
    tableRestoreTableDone: 'Programa {app} atkūrė lentelę {table} ({rows})',
    tableRowsLoseOne: '1 eilutė praranda reikšmę',
    tableRowsLoseMany: 'Eilutės, kuriose reikšmė bus pašalinta: {count}',
    tableRowsLostOne: '1 eilutė prarado reikšmę',
    tableRowsLostMany: 'Eilutės, kuriose reikšmė buvo pašalinta: {count}',
    tableRowsOne: '1 eilutė',
    tableRowsMany: 'Eilutės: {count}',
    connectedWriteOn: 'Leisti programai {app} {action} sistemoje {destination}',
    connectedWriteOnDone: 'Programa {app} gali {action} sistemoje {destination}',
    connectedWriteOff: 'Neleisti programai {app} rašyti į {destination}',
    connectedWriteOffDone: 'Programa {app} nebeįrašo į {destination}',
    codeConfirm:
      'Leisti programai {app} naudoti jūsų patvirtinimus su pakeitimais, kuriuos atliko {names}',
    codeConfirmDone:
      'Programa {app} naudoja jūsų patvirtinimus su pakeitimais, kuriuos atliko {names}',
    codeConfirmCurrent: 'Leisti programai {app} naudoti jūsų patvirtinimus su dabartiniu jos kodu',
    codeConfirmCurrentDone: 'Programa {app} naudoja jūsų patvirtinimus su dabartiniu savo kodu',
    runChanges: 'Dabar paleisti funkciją „{function}“ programoje {app}',
    runChangesDone: 'Funkcija „{function}“ paleista programoje {app}',
    continueApproved: 'Patvirtinta. Tęsk: paleisk tai ir patikrink, ar veikia.',
    continueDeclined: 'Atmesta. Tęsk be to.',
    continueMixed: 'Dėl užklausų sprendimai priimti. Tęsk.',
    typeOutsideAccess: 'Leisti programai pasiekti išorines paslaugas',
    typeEvent: 'Įjungti arba išjungti programos įvykį',
    typeSchedule: 'Įjungti, pristabdyti arba keisti programos tvarkaraštį',
    typeDataWrites: 'Leisti programos funkcijai keisti savo eilutes',
    typeTableChange: 'Ištrinti arba atkurti programos lentelių duomenis',
    typeConnectedWrite: 'Leisti programai rašyti į įmonės sistemą',
    typeConfirmCode: 'Leisti pakeistam programos kodui naudoti jūsų patvirtinimus',
    typeRunChanges: 'Paleisti programos funkciją, kuri keičia duomenis',
    typePublicLink: 'Pakeisti programos viešą nuorodą',
    typeUnpublish: 'Pašalinti programos viešą nuorodą',
    labelHosts: 'Paslaugos',
    labelAccess: 'Prieiga',
    labelEvent: 'Įvykis',
    labelKind: 'Pasileidžia, kai',
    labelCollection: 'Rinkinys',
    labelChanges: 'Eilučių pakeitimai',
    labelRuns: 'Paleidimai',
    labelFunction: 'Funkcija',
    labelCollections: 'Lentelės',
    labelWho: 'Kam',
    labelAllowed: 'Leidžiama',
    labelEnabled: 'Būsena',
    labelJob: 'Suplanuota užduotis',
    labelSchedule: 'Tvarkaraštis',
    labelTimezone: 'Laiko juosta',
    labelChange: 'Pakeitimas',
    labelTable: 'Lentelė',
    labelField: 'Laukas',
    labelRows: 'Eilutės',
    labelKeptDays: 'Kopija saugoma (dienų)',
    labelRestores: 'Atkuria',
    labelAction: 'Veiksmas',
    labelDestination: 'Paskirtis',
    labelChangedBy: 'Pakeitė',
    labelInput: 'Įvestis',
    labelPublicTables: 'Nukopijuotos lentelės',
    labelPublicActions: 'Lankytojai gali siųsti',
    labelPublicRuns: 'Kiekvienas pateikimas paleidžia',
    labelPublicRunsOff: 'Pasileistų ir įjungus',
    labelEmbedSites: 'Svetainės, kuriose galima įterpti',
    labelPublicLink: 'Vieša nuoroda',
    labelPublishedBy: 'Paskelbė',
    wordRead: 'Tik skaityti',
    wordReadSend: 'Skaityti ir siųsti duomenis',
    wordNoAccess: 'Nėra prieigos',
    publicPublish: 'Paskelbti viešoje nuorodoje',
    publicRefresh: 'Dar kartą nukopijuoti jos duomenis',
    publicUnpublish: 'Nebeskelbti',
    eventWebhook: 'Jos webhook',
    eventRows: 'Jos eilučių pakeitimai',
    switchOn: 'Įjungta',
    switchOff: 'Išjungta',
    switchPaused: 'Pristabdyta',
    changeInsert: 'Pridėta',
    changeUpdate: 'Pakeista',
    changeDelete: 'Ištrinta',
    wordDropField: 'Ištrinti lauko reikšmes',
    wordDropTable: 'Ištrinti lentelę',
    tableClearValues: 'Išvalyti reikšmes, pažeidžiančias griežtesnę taisyklę',
    tableRestore: 'Atkurti iš jos kopijos',
    whoOwner: 'Jūsų pačių paleidimai',
    whoEveryone: 'Visi, kurie ją naudoja',
    answerYes: 'Taip',
    answerNo: 'Ne',
  },
  mt: {
    kindWrite: 'Bidla fl-app għall-approvazzjoni',
    kindGrant: 'Aċċess għall-app għall-approvazzjoni',
    kindBusiness: 'Approvazzjoni għal app',
    approve: 'Approva',
    reject: 'Irrifjuta',
    working: 'Qed naħdmu…',
    checkStatus: 'Iċċekkja l-istatus',
    statusStillPending:
      'Poneva għadu ma ddeċidiex dwar dan. Jista’ jkun għadu għaddej, għalhekk ma jista’ jiġi deċiż xejn ieħor qabel ma jintemm.',
    statusStillUnknown:
      'Ma stajniex niksbu tweġiba. Ma jista’ jiġi deċiż xejn ieħor qabel ma jintemm dan. Erġa’ pprova wara ftit.',
    loading: 'Qed jitgħabba…',
    unavailable: 'Dan mhux disponibbli bħalissa.',
    loadFailed: 'Ma stajniex nitgħabbew dan. Erġa’ pprova wara ftit.',
    requestedBy: 'Mitlub minn {name}',
    originApp: 'Mill-app',
    originAgent: 'Minn Aġent',
    originClient: 'Permezz ta’ {client}',
    originFor: 'għal {name}',
    changedUnderYou:
      'Dan inbidel waqt li kont qed taqrah. Dak li qed tara issa huwa l-verżjoni attwali. Ma ttieħdet l-ebda deċiżjoni.',
    notAllowed: 'Ma tistax tiddeċiedi dwar dan.',
    cannotDecideNow: 'Dan ma jistax jiġi deċiż bħalissa. Ma ntbagħat xejn.',
    expired: 'Din it-talba skadiet. Ma nbidel xejn.',
    stale: 'Id-dejta nbidlet minn meta saret din it-talba. Erġa’ staqsi biex tikseb talba ġdida.',
    approved: 'Approvat',
    rejected: 'Irrifjutat',
    needsYou: 'Teħtieġ l-approvazzjoni',
    waitingOn: 'Qed tistenna lil {name}',
    unknownTitle: 'Ma nistgħux nikkonfermaw x’ġara',
    unknownBody:
      'Il-bidla setgħet saret jew ma setgħetx issir. Tirrepetihiex. Iċċekkja l-istatus, jew staqsi lis-sid tal-app biex jiċċekkja.',
    blockedApproval: 'Qed tistenna din l-approvazzjoni qabel tkompli.',
    blockedBudget: 'Imwaqqfa: il-baġit tal-AI għal din l-app intuża kollu.',
    blockedProvider: 'Imwaqqfa: servizz konness mhux qed jirrispondi.',
    blockedViewer:
      'Imwaqqfa: dan irid jaħdem bħala viewer illoggjat, u m’hemm l-ebda viewer mehmuż. Xejn ma jaħdem bl-aċċess tas-sid minflok.',
    writeTitle: 'Approva l-bidliet għal {target}',
    writeSummaryAll: '{count} bidliet f’{app}',
    rowCreate: 'Ġdid',
    rowUpdate: 'Inbidel',
    rowDelete: 'Tneħħa',
    rowInclude: 'Inkludi din il-bidla',
    rowExcluded: 'Tħalla barra',
    fieldBefore: 'Qabel',
    fieldAfter: 'Wara',
    fieldEmpty: 'Vojt',
    approveSome: 'Approva {included} minn {total}',
    approveAll: 'Approva l-{total} kollha',
    allLeftOut:
      'Il-bidliet kollha tħallew barra. Irrifjuta biex twaqqaf, jew inkludi bidla biex tapprovaha.',
    appliedSome: 'Saru {count} bidliet.',
    rejectedBody: 'Ma nbidel xejn.',
    noRows: 'M’hemm xejn x’approva hawn.',
    grantTitle: 'Ħalli lil {app} juża {source}',
    grantRead: 'Qari biss',
    grantWrite: 'Qari u bidliet',
    audienceOwner: 'Int biss se tara d-dejta ta’ din l-app minn {source}.',
    audiencePeople:
      'Il-{count} persuni li magħhom tinqasam din l-app se jaraw id-dejta minn {source}.',
    audienceCompany: 'Kull min fil-kumpanija jiftaħ l-app se jara d-dejta minn {source}.',
    grantRouted: '{owner} jiddeċiedi dwar dan, għalhekk intbagħat lilu.',
    grantRoutedUnknown:
      'Is-sid ta’ din il-konnessjoni jiddeċiedi dwar dan, għalhekk intbagħat lilu.',
    grantWhat: 'Aċċess',
    businessStep: 'Pass {order}',
    stepWaiting: 'Qed tistenna',
    stepPending: 'Jeħtieġ deċiżjoni',
    stepApproved: 'Approvat',
    stepRejected: 'Irrifjutat',
    stepYou: 'Int',
    businessNote: 'L-approvazzjoni hawn ma tippermettix bidla ġdida barra minn Poneva.',
    businessYourTurn: 'Issa jmissek tiddeċiedi.',
    businessNotYourTurn: 'Qed tistenna lil {name} jiddeċiedi l-ewwel.',
    policyTitle: 'Kif l-apps jużaw din il-konnessjoni',
    policyCreator: 'Tal-kreatur biss',
    policyCreatorBody: 'Kulħadd jara l-istess dejta, permezz tal-konnessjoni tal-kreatur tal-app.',
    policyViewer: 'Tal-viewer biss',
    policyViewerBody:
      'Kull persuna tara d-dejta tagħha stess, permezz tal-konnessjoni tagħha stess.',
    policyBoth: 'It-tnejn',
    policyBothBody: 'Il-kreatur tal-app jagħżel għal kull app.',
    policyRevoked: 'L-apps ma jistgħux jużaw din il-konnessjoni',
    policyRevokedBody: 'Agħżel għażla biex terġa’ tippermettiha. Ma ntgħażel xejn għalik.',
    policySave: 'Issejvja',
    policySaving: 'Qed jiġi ssejvjat…',
    policySaved: 'Issejvjat.',
    policyReadOnly: 'Amministratur tal-kumpanija biss jista’ jibdel dan.',
    policyConflict: 'Xi ħadd ieħor biddel dan l-ewwel. Dan huwa l-issettjar attwali.',
    policyRefused: 'Dan ma setax jiġi ssejvjat.',
    policyFailed:
      'Ma stajniex nikkonfermaw li dan ġie ssejvjat. Erġa’ kkarga biex tara s-setting attwali qabel ma terġa’ tipprova.',
    policyWidened:
      'L-apps li diġà ġew ippubblikati se jżommu l-mod kif jikkonnettjaw illum. Il-ħallieqa tagħhom se jintalbu jerġgħu jippubblikawhom biex jużaw l-għażla l-ġdida.',
    policyNarrowed: 'L-apps jiċċekkjaw is-setting attwali kull darba li jikkonnettjaw.',
    policyAffected: '{count} apps ippubblikati jużaw din il-konnessjoni (fit-{when}).',
    policyAffectedOne: 'App ippubblikata waħda tuża din il-konnessjoni (fit-{when}).',
    policyAffectsNames: 'Taffettwa {count} apps ippubblikati: {names}.',
    policyAffectsOneName: 'Taffettwa app ippubblikata waħda: {names}.',
    policyGenericWiden:
      'L-apps ippubblikati li jużaw din il-konnessjoni se jżommu l-mod kif jikkonnettjaw illum. Il-ħallieqa tagħhom se jintalbu jerġgħu jippubblikawhom biex jużaw l-għażla l-ġdida.',
    policyGenericNarrow:
      'L-apps ippubblikati li jużaw din il-konnessjoni jiċċekkjaw is-setting attwali kull darba li jikkonnettjaw.',
    policyAffectedGeneric:
      'L-apps ippubblikati li jużaw din il-konnessjoni ma jinbidlux awtomatikament. Il-ħallieqa tagħhom se jintalbu jerġgħu jippubblikawhom.',
    policyUnavailable: 'Dan is-setting mhux disponibbli bħalissa.',
    outsideRead: 'Ħalli {app} taqra minn {hosts}',
    outsideReadSend: 'Ħalli {app} taqra minn {hosts} u tibgħat dejta lilu',
    outsideNone: 'Waqqaf lil {app} milli tilħaq {hosts}',
    outsideReadDone: '{app} tista’ taqra minn {hosts}',
    outsideReadSendDone: '{app} tista’ taqra minn {hosts} u tibgħat dejta lilu',
    outsideNoneDone: '{app} m’għadhiex tilħaq {hosts}',
    outsideMore: '{count} oħra',
    eventWebhookOn: 'Ħalli {app} tirċievi “{event}” fil-webhook tagħha',
    eventWebhookOnDone: '{app} tirċievi “{event}” fil-webhook tagħha',
    eventWebhookOff: 'Waqqaf lil {app} milli tirċievi “{event}” fil-webhook tagħha',
    eventWebhookOffDone: '{app} m’għadhiex tirċievi “{event}”',
    eventRowsOn: 'Ħalli {app} tħaddem {runs} meta r-ringieli f’{collection} jkunu {changes}',
    eventRowsOnDone: '{app} tħaddem {runs} meta r-ringieli f’{collection} ikunu {changes}',
    eventRowsOff: 'Waqqaf lil {app} milli tħaddem {runs} meta jinbidlu r-ringieli f’{collection}',
    eventRowsOffDone: '{app} m’għadhiex tħaddem {runs} meta jinbidlu r-ringieli f’{collection}',
    eventInsert: 'miżjuda',
    eventUpdate: 'mibdula',
    eventDelete: 'imneħħija',
    scheduleTurnOn: 'Ixgħel l-iskeda ta’ “{job}” f’{app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'L-iskeda ta’ “{job}” f’{app} hija mixgħula. {cadence} ({timezone}).',
    scheduleResume: 'Kompli l-iskeda ta’ “{job}” f’{app}. {cadence} ({timezone}).',
    scheduleResumeDone: 'L-iskeda ta’ “{job}” f’{app} reġgħet mixgħula. {cadence} ({timezone}).',
    scheduleReschedule: 'Biddel l-iskeda ta’ “{job}” f’{app}. {cadence} ({timezone}).',
    scheduleRescheduleDone: 'L-iskeda ta’ “{job}” f’{app} inbidlet. {cadence} ({timezone}).',
    schedulePause: 'Waqqaf temporanjament l-iskeda ta’ “{job}” f’{app}.',
    schedulePauseDone: 'L-iskeda ta’ “{job}” f’{app} hija mwaqqfa temporanjament.',
    scheduleNext: 'It-tħaddim li jmiss: {runs}.',
    scheduleResumeSaved:
      'Kompli l-iskeda ta’ “{job}” f’{app} bl-aħħar verżjoni salvata. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'L-iskeda ta’ “{job}” f’{app} reġgħet mixgħula u tħaddem l-aħħar verżjoni salvata. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Kompli l-iskeda ta’ “{job}” f’{app} bil-verżjoni ppubblikata. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'L-iskeda ta’ “{job}” f’{app} reġgħet mixgħula u tħaddem il-verżjoni ppubblikata. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Biddel l-iskeda ta’ “{job}” f’{app}. {cadence} ({timezone}). Se tkun mwaqqfa temporanjament sakemm terġa’ tkompliha.',
    scheduleReschedulePausedDone:
      'L-iskeda ta’ “{job}” f’{app} inbidlet. {cadence} ({timezone}). Hija mwaqqfa temporanjament sakemm terġa’ tkompliha.',
    scheduleSwitchSaved:
      'Aqleb l-iskeda ta’ “{job}” f’{app} għall-aħħar verżjoni salvata. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'L-iskeda ta’ “{job}” f’{app} issa tħaddem l-aħħar verżjoni salvata. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Aqleb l-iskeda ta’ “{job}” f’{app} għall-aħħar verżjoni salvata. {cadence} ({timezone}). Se tkun mwaqqfa temporanjament sakemm terġa’ tkompliha.',
    scheduleSwitchSavedPausedDone:
      'L-iskeda ta’ “{job}” f’{app} issa tuża l-aħħar verżjoni salvata. {cadence} ({timezone}). Hija mwaqqfa temporanjament sakemm terġa’ tkompliha.',
    scheduleSwitchPublished:
      'Aqleb l-iskeda ta’ “{job}” f’{app} għall-verżjoni ppubblikata. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'L-iskeda ta’ “{job}” f’{app} issa tħaddem il-verżjoni ppubblikata. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Aqleb l-iskeda ta’ “{job}” f’{app} għall-verżjoni ppubblikata. {cadence} ({timezone}). Se tkun mwaqqfa temporanjament sakemm terġa’ tkompliha.',
    scheduleSwitchPublishedPausedDone:
      'L-iskeda ta’ “{job}” f’{app} issa tuża l-verżjoni ppubblikata. {cadence} ({timezone}). Hija mwaqqfa temporanjament sakemm terġa’ tkompliha.',
    dataWritesOn: 'Ħalli “{function}” ta’ {app} {verbs} ir-ringieli tagħha stess',
    dataWritesOnDone: '“{function}” ta’ {app} tista’ {verbs} ir-ringieli tagħha stess',
    dataWritesOff: 'Waqqaf lil “{function}” ta’ {app} milli tbiddel ir-ringieli tagħha stess',
    dataWritesOffDone: '“{function}” ta’ {app} m’għadhiex tbiddel ir-ringieli tagħha stess',
    dataWriteInsert: 'iżżid',
    dataWriteUpdate: 'tbiddel',
    dataWriteDelete: 'tneħħi',
    tableDropField: 'Ħalli {app} tneħħi l-qasam “{field}” minn {table} ({rows})',
    tableDropFieldDone: '{app} neħħiet il-qasam “{field}” minn {table} ({rows})',
    tableDropTable: 'Ħalli {app} tneħħi t-tabella {table} ({rows})',
    tableDropTableDone: '{app} neħħiet it-tabella {table} ({rows})',
    tableClear: 'Ħalli {app} tbattal “{field}” f’{table} fejn jikser ir-regola l-ġdida ({rows})',
    tableClearDone: '{app} battlet “{field}” f’{table} fejn kien jikser ir-regola l-ġdida ({rows})',
    tableRestoreField: 'Ħalli {app} terġa’ tpoġġi “{field}” f’{table} mill-kopja tagħha ({rows})',
    tableRestoreFieldDone: '{app} reġgħet poġġiet “{field}” f’{table} ({rows})',
    tableRestoreTable: 'Ħalli {app} terġa’ tiġbed it-tabella {table} mill-kopja tagħha ({rows})',
    tableRestoreTableDone: '{app} reġgħet ġibdet it-tabella {table} ({rows})',
    tableRowsLoseOne: 'Ringiela 1 titlef valur',
    tableRowsLoseMany: 'Ringieli fejn il-valur se jitneħħa: {count}',
    tableRowsLostOne: 'Ringiela 1 tilfet valur',
    tableRowsLostMany: 'Ringieli fejn il-valur tneħħa: {count}',
    tableRowsOne: 'Ringiela 1',
    tableRowsMany: 'Ringieli: {count}',
    connectedWriteOn: 'Ħalli {app} {action} f’{destination}',
    connectedWriteOnDone: '{app} tista’ {action} f’{destination}',
    connectedWriteOff: 'Waqqaf lil {app} milli tikteb f’{destination}',
    connectedWriteOffDone: '{app} m’għadhiex tikteb f’{destination}',
    codeConfirm: 'Ħalli {app} tuża l-approvazzjonijiet tiegħek mal-bidliet ta’ {names}',
    codeConfirmDone: '{app} tuża l-approvazzjonijiet tiegħek mal-bidliet ta’ {names}',
    codeConfirmCurrent: 'Ħalli {app} tuża l-approvazzjonijiet tiegħek bil-kodiċi attwali tagħha',
    codeConfirmCurrentDone: '{app} tuża l-approvazzjonijiet tiegħek bil-kodiċi attwali tagħha',
    runChanges: 'Ħaddem “{function}” f’{app} issa',
    runChangesDone: '“{function}” ħadmet f’{app}',
    continueApproved: 'Approvat. Kompli: ħaddmu u ċċekkja li jaħdem.',
    continueDeclined: 'Irrifjutat. Kompli mingħajru.',
    continueMixed: 'Iddeċidejt dwar it-talbiet. Kompli.',
    typeOutsideAccess: 'Ħalli app tilħaq servizzi esterni',
    typeEvent: 'Ixgħel jew itfi avveniment ta’ app',
    typeSchedule: 'Ixgħel, waqqaf temporanjament jew biddel l-iskeda ta’ app',
    typeDataWrites: 'Ħalli funzjoni ta’ app tbiddel ir-ringieli tagħha stess',
    typeTableChange: 'Neħħi jew irkupra d-dejta fit-tabelli ta’ app',
    typeConnectedWrite: 'Ħalli app tikteb f’sistema tal-kumpanija',
    typeConfirmCode: 'Ħalli l-kodiċi mibdul ta’ app juża l-approvazzjonijiet tiegħek',
    typeRunChanges: 'Mexxi funzjoni ta’ app li tbiddel id-dejta',
    typePublicLink: 'Biddel il-link pubbliku ta’ app',
    typeUnpublish: 'Neħħi l-link pubbliku ta’ app',
    labelHosts: 'Servizzi',
    labelAccess: 'Aċċess',
    labelEvent: 'Avveniment',
    labelKind: 'Jibda meta',
    labelCollection: 'Kollezzjoni',
    labelChanges: 'Bidliet fir-ringieli',
    labelRuns: 'Eżekuzzjonijiet',
    labelFunction: 'Funzjoni',
    labelCollections: 'Tabelli',
    labelWho: 'Għal',
    labelAllowed: 'Permess',
    labelEnabled: 'Stat',
    labelJob: 'Xogħol skedat',
    labelSchedule: 'Skeda',
    labelTimezone: 'Żona tal-ħin',
    labelChange: 'Bidla',
    labelTable: 'Tabella',
    labelField: 'Qasam',
    labelRows: 'Ringieli',
    labelKeptDays: 'Il-kopja tinżamm (jiem)',
    labelRestores: 'Jirkupra',
    labelAction: 'Azzjoni',
    labelDestination: 'Destinazzjoni',
    labelChangedBy: 'Mibdul minn',
    labelInput: 'Input',
    labelPublicTables: 'Tabelli kkupjati',
    labelPublicActions: 'Il-viżitaturi jistgħu jibagħtu',
    labelPublicRuns: 'Kull sottomissjoni tibda',
    labelPublicRunsOff: 'Kien jibda wkoll malli jinxtegħel',
    labelEmbedSites: 'Siti li jistgħu jdaħħluha',
    labelPublicLink: 'Link pubbliku',
    labelPublishedBy: 'Ippubblikat minn',
    wordRead: 'Qari biss',
    wordReadSend: 'Aqra u ibgħat dejta',
    wordNoAccess: 'L-ebda aċċess',
    publicPublish: 'Ippubblika f’link pubbliku',
    publicRefresh: 'Ikkopja d-dejta tagħha mill-ġdid',
    publicUnpublish: 'Neħħi l-pubblikazzjoni',
    eventWebhook: 'Il-webhook tagħha',
    eventRows: 'Bidliet fir-ringieli tagħha',
    switchOn: 'Mixgħul',
    switchOff: 'Mitfi',
    switchPaused: 'Imwaqqaf temporanjament',
    changeInsert: 'Miżjud',
    changeUpdate: 'Mibdul',
    changeDelete: 'Imħassar',
    wordDropField: 'Ħassar il-valuri ta’ qasam',
    wordDropTable: 'Ħassar tabella',
    tableClearValues: 'Naddaf il-valuri li jiksru regola aktar stretta',
    tableRestore: 'Irkupra mill-kopja tagħha',
    whoOwner: 'L-eżekuzzjonijiet tiegħek stess',
    whoEveryone: 'Kull min jużaha',
    answerYes: 'Iva',
    answerNo: 'Le',
  },
  pl: {
    kindWrite: 'Zmiana w aplikacji do zatwierdzenia',
    kindGrant: 'Dostęp aplikacji do zatwierdzenia',
    kindBusiness: 'Zatwierdzenie aplikacji',
    approve: 'Zatwierdź',
    reject: 'Odrzuć',
    working: 'Przetwarzanie…',
    checkStatus: 'Sprawdź status',
    statusStillPending:
      'Poneva jeszcze tego nie rozstrzygnęła. Proces może nadal trwać, więc do tego czasu nie można podjąć żadnej innej decyzji.',
    statusStillUnknown:
      'Nie udało się uzyskać odpowiedzi. Do czasu rozstrzygnięcia nie można podjąć żadnej innej decyzji. Spróbuj ponownie za chwilę.',
    loading: 'Ładowanie…',
    unavailable: 'To jest teraz niedostępne.',
    loadFailed: 'Nie udało się tego załadować. Spróbuj ponownie za chwilę.',
    requestedBy: 'Poprosił(a) {name}',
    originApp: 'Z aplikacji',
    originAgent: 'Od agenta',
    originClient: 'Przez {client}',
    originFor: 'dla {name}',
    changedUnderYou:
      'To zmieniło się podczas przeglądania. Obecna wersja jest aktualna. Nie podjęto żadnej decyzji.',
    notAllowed: 'Nie możesz o tym decydować.',
    cannotDecideNow: 'Nie można teraz o tym zdecydować. Nic nie wysłano.',
    expired: 'To żądanie wygasło. Nic się nie zmieniło.',
    stale:
      'Dane zmieniły się od czasu wysłania tego żądania. Poproś ponownie, aby otrzymać nowe żądanie.',
    approved: 'Zatwierdzone',
    rejected: 'Odrzucone',
    needsYou: 'Wymaga zatwierdzenia',
    waitingOn: 'Oczekiwanie na {name}',
    unknownTitle: 'Nie możemy potwierdzić, co się stało',
    unknownBody:
      'Zmiana mogła zostać wprowadzona, ale nie musiała. Nie powtarzaj tej czynności. Sprawdź status lub poproś właściciela aplikacji o sprawdzenie.',
    blockedApproval: 'Oczekiwanie na to zatwierdzenie przed kontynuowaniem.',
    blockedBudget: 'Wstrzymano: budżet AI dla tej aplikacji został wyczerpany.',
    blockedProvider: 'Wstrzymano: połączona usługa nie odpowiada.',
    blockedViewer:
      'Wstrzymano: to musi zostać uruchomione jako zalogowany użytkownik z dostępem, ale żaden taki użytkownik nie jest przypisany. Nie zostanie to uruchomione z uprawnieniami właściciela.',
    writeTitle: 'Zatwierdź zmiany w {target}',
    writeSummaryAll: '{count} zmian w {app}',
    rowCreate: 'Nowa',
    rowUpdate: 'Zmieniona',
    rowDelete: 'Usunięta',
    rowInclude: 'Uwzględnij tę zmianę',
    rowExcluded: 'Pominięta',
    fieldBefore: 'Przed',
    fieldAfter: 'Po',
    fieldEmpty: 'Puste',
    approveSome: 'Zatwierdź {included} z {total}',
    approveAll: 'Zatwierdź wszystkie: {total}',
    allLeftOut:
      'Wszystkie zmiany pominięto. Odrzuć je, aby zatrzymać proces, lub uwzględnij zmianę, aby ją zatwierdzić.',
    appliedSome: 'Wprowadzono {count} zmian.',
    rejectedBody: 'Nic nie zmieniono.',
    noRows: 'Nie ma tu nic do zatwierdzenia.',
    grantTitle: 'Zezwól aplikacji {app} używać {source}',
    grantRead: 'Tylko odczyt',
    grantWrite: 'Odczyt i zmiany',
    audienceOwner: 'Tylko Ty zobaczysz dane tej aplikacji z {source}.',
    audiencePeople: '{count} osób, którym udostępniono tę aplikację, zobaczy dane z {source}.',
    audienceCompany: 'Każda osoba w firmie, która otworzy aplikację, zobaczy dane z {source}.',
    grantRouted: 'Decyduje o tym {owner}, więc żądanie zostało do niego wysłane.',
    grantRoutedUnknown:
      'Decyduje o tym właściciel tego połączenia, więc żądanie zostało do niego wysłane.',
    grantWhat: 'Dostęp',
    businessStep: 'Krok {order}',
    stepWaiting: 'Oczekiwanie',
    stepPending: 'Wymaga decyzji',
    stepApproved: 'Zatwierdzone',
    stepRejected: 'Odrzucone',
    stepYou: 'Ty',
    businessNote: 'Zatwierdzenie tutaj nie zezwala na żadną nową zmianę poza Poneva.',
    businessYourTurn: 'Teraz Twoja kolej na podjęcie decyzji.',
    businessNotYourTurn: 'Oczekiwanie, aż najpierw zdecyduje {name}.',
    policyTitle: 'Jak aplikacje używają tego połączenia',
    policyCreator: 'Tylko połączenie twórcy',
    policyCreatorBody: 'Wszyscy widzą te same dane za pośrednictwem połączenia twórcy aplikacji.',
    policyViewer: 'Tylko własne połączenie użytkownika',
    policyViewerBody: 'Każda osoba widzi własne dane za pośrednictwem własnego połączenia.',
    policyBoth: 'Oba',
    policyBothBody: 'Twórca aplikacji wybiera ustawienie dla każdej aplikacji.',
    policyRevoked: 'Aplikacje nie mogą używać tego połączenia',
    policyRevokedBody:
      'Wybierz opcję, aby ponownie zezwolić na używanie połączenia. Nic nie zostanie wybrane za Ciebie.',
    policySave: 'Zapisz',
    policySaving: 'Zapisywanie…',
    policySaved: 'Zapisano.',
    policyReadOnly: 'Tylko administrator firmy może to zmienić.',
    policyConflict: 'Ktoś inny zmienił to wcześniej. To jest bieżące ustawienie.',
    policyRefused: 'Nie udało się zapisać.',
    policyFailed:
      'Nie udało się potwierdzić zapisu. Odśwież stronę, aby zobaczyć bieżące ustawienie, zanim spróbujesz ponownie.',
    policyWidened:
      'Opublikowane aplikacje zachowują obecny sposób łączenia. Ich twórcy zostaną poproszeni o ponowną publikację, aby mogli korzystać z nowej opcji.',
    policyNarrowed: 'Aplikacje sprawdzają bieżące ustawienie przy każdym połączeniu.',
    policyAffected: 'Z tego połączenia korzysta {count} opublikowanych aplikacji (stan na {when}).',
    policyAffectedOne: 'Z tego połączenia korzysta 1 opublikowana aplikacja (stan na {when}).',
    policyAffectsNames: 'Dotyczy {count} opublikowanych aplikacji: {names}.',
    policyAffectsOneName: 'Dotyczy 1 opublikowanej aplikacji: {names}.',
    policyGenericWiden:
      'Opublikowane aplikacje korzystające z tego połączenia zachowują obecny sposób łączenia. Ich twórcy zostaną poproszeni o ponowną publikację, aby mogli korzystać z nowej opcji.',
    policyGenericNarrow:
      'Opublikowane aplikacje korzystające z tego połączenia sprawdzają bieżące ustawienie przy każdym połączeniu.',
    policyAffectedGeneric:
      'Opublikowane aplikacje korzystające z tego połączenia nie zostaną automatycznie zmienione. Ich twórcy zostaną poproszeni o ponowną publikację.',
    policyUnavailable: 'To ustawienie jest obecnie niedostępne.',
    outsideRead: 'Pozwól aplikacji {app} odczytywać dane z: {hosts}',
    outsideReadSend: 'Pozwól aplikacji {app} odczytywać dane z i wysyłać dane do: {hosts}',
    outsideNone: 'Zablokuj aplikacji {app} dostęp do: {hosts}',
    outsideReadDone: 'Aplikacja {app} może odczytywać dane z: {hosts}',
    outsideReadSendDone: 'Aplikacja {app} może odczytywać dane z i wysyłać dane do: {hosts}',
    outsideNoneDone: 'Aplikacja {app} nie ma już dostępu do: {hosts}',
    outsideMore: 'jeszcze {count}',
    eventWebhookOn: 'Pozwól aplikacji {app} odbierać „{event}” na jej webhooku',
    eventWebhookOnDone: 'Aplikacja {app} odbiera „{event}” na swoim webhooku',
    eventWebhookOff: 'Zablokuj aplikacji {app} odbieranie „{event}” na jej webhooku',
    eventWebhookOffDone: 'Aplikacja {app} nie odbiera już „{event}”',
    eventRowsOn:
      'Pozwól aplikacji {app} uruchamiać: {runs}, gdy wiersze w tabeli {collection} są {changes}',
    eventRowsOnDone:
      'Aplikacja {app} uruchamia: {runs}, gdy wiersze w tabeli {collection} są {changes}',
    eventRowsOff:
      'Zablokuj aplikacji {app} uruchamianie: {runs}, gdy wiersze w tabeli {collection} się zmieniają',
    eventRowsOffDone:
      'Aplikacja {app} nie uruchamia już: {runs}, gdy wiersze w tabeli {collection} się zmieniają',
    eventInsert: 'dodane',
    eventUpdate: 'zmienione',
    eventDelete: 'usunięte',
    scheduleTurnOn: 'Włącz harmonogram zadania „{job}” w aplikacji {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone:
      'Harmonogram zadania „{job}” w aplikacji {app} jest włączony. {cadence} ({timezone}).',
    scheduleResume: 'Wznów harmonogram zadania „{job}” w aplikacji {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Harmonogram zadania „{job}” w aplikacji {app} jest znowu włączony. {cadence} ({timezone}).',
    scheduleReschedule:
      'Zmień harmonogram zadania „{job}” w aplikacji {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Harmonogram zadania „{job}” w aplikacji {app} został zmieniony. {cadence} ({timezone}).',
    schedulePause: 'Wstrzymaj harmonogram zadania „{job}” w aplikacji {app}.',
    schedulePauseDone: 'Harmonogram zadania „{job}” w aplikacji {app} jest wstrzymany.',
    scheduleNext: 'Następne uruchomienia: {runs}.',
    scheduleResumeSaved:
      'Wznów harmonogram zadania „{job}” w aplikacji {app} z ostatnio zapisaną wersją. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Harmonogram zadania „{job}” w aplikacji {app} jest znowu włączony i uruchamia ostatnio zapisaną wersję. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Wznów harmonogram zadania „{job}” w aplikacji {app} z opublikowaną wersją. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Harmonogram zadania „{job}” w aplikacji {app} jest znowu włączony i uruchamia opublikowaną wersję. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Zmień harmonogram zadania „{job}” w aplikacji {app}. {cadence} ({timezone}). Będzie wstrzymany, dopóki go nie wznowisz.',
    scheduleReschedulePausedDone:
      'Harmonogram zadania „{job}” w aplikacji {app} został zmieniony. {cadence} ({timezone}). Jest wstrzymany, dopóki go nie wznowisz.',
    scheduleSwitchSaved:
      'Przełącz harmonogram zadania „{job}” w aplikacji {app} na ostatnio zapisaną wersję. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Harmonogram zadania „{job}” w aplikacji {app} uruchamia teraz ostatnio zapisaną wersję. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Przełącz harmonogram zadania „{job}” w aplikacji {app} na ostatnio zapisaną wersję. {cadence} ({timezone}). Będzie wstrzymany, dopóki go nie wznowisz.',
    scheduleSwitchSavedPausedDone:
      'Harmonogram zadania „{job}” w aplikacji {app} używa teraz ostatnio zapisanej wersji. {cadence} ({timezone}). Jest wstrzymany, dopóki go nie wznowisz.',
    scheduleSwitchPublished:
      'Przełącz harmonogram zadania „{job}” w aplikacji {app} na opublikowaną wersję. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Harmonogram zadania „{job}” w aplikacji {app} uruchamia teraz opublikowaną wersję. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Przełącz harmonogram zadania „{job}” w aplikacji {app} na opublikowaną wersję. {cadence} ({timezone}). Będzie wstrzymany, dopóki go nie wznowisz.',
    scheduleSwitchPublishedPausedDone:
      'Harmonogram zadania „{job}” w aplikacji {app} używa teraz opublikowanej wersji. {cadence} ({timezone}). Jest wstrzymany, dopóki go nie wznowisz.',
    dataWritesOn: 'Pozwól funkcji „{function}” aplikacji {app} {verbs} własne wiersze',
    dataWritesOnDone: 'Funkcja „{function}” aplikacji {app} może {verbs} własne wiersze',
    dataWritesOff: 'Zablokuj funkcji „{function}” aplikacji {app} zmienianie własnych wierszy',
    dataWritesOffDone: 'Funkcja „{function}” aplikacji {app} nie zmienia już własnych wierszy',
    dataWriteInsert: 'dodawać',
    dataWriteUpdate: 'zmieniać',
    dataWriteDelete: 'usuwać',
    tableDropField: 'Pozwól aplikacji {app} usunąć pole „{field}” z tabeli {table} ({rows})',
    tableDropFieldDone: 'Aplikacja {app} usunęła pole „{field}” z tabeli {table} ({rows})',
    tableDropTable: 'Pozwól aplikacji {app} usunąć tabelę {table} ({rows})',
    tableDropTableDone: 'Aplikacja {app} usunęła tabelę {table} ({rows})',
    tableClear:
      'Pozwól aplikacji {app} wyczyścić „{field}” w tabeli {table} tam, gdzie wartość łamie nową regułę ({rows})',
    tableClearDone:
      'Aplikacja {app} wyczyściła „{field}” w tabeli {table} tam, gdzie wartość łamała nową regułę ({rows})',
    tableRestoreField:
      'Pozwól aplikacji {app} przywrócić „{field}” w tabeli {table} z kopii ({rows})',
    tableRestoreFieldDone: 'Aplikacja {app} przywróciła „{field}” w tabeli {table} ({rows})',
    tableRestoreTable: 'Pozwól aplikacji {app} przywrócić tabelę {table} z kopii ({rows})',
    tableRestoreTableDone: 'Aplikacja {app} przywróciła tabelę {table} ({rows})',
    tableRowsLoseOne: '1 wiersz traci wartość',
    tableRowsLoseMany: 'Wiersze, w których wartość zostanie usunięta: {count}',
    tableRowsLostOne: '1 wiersz stracił wartość',
    tableRowsLostMany: 'Wiersze, w których wartość została usunięta: {count}',
    tableRowsOne: '1 wiersz',
    tableRowsMany: 'Wiersze: {count}',
    connectedWriteOn: 'Pozwól aplikacji {app} {action} w: {destination}',
    connectedWriteOnDone: 'Aplikacja {app} może {action} w: {destination}',
    connectedWriteOff: 'Zablokuj aplikacji {app} zapisywanie w: {destination}',
    connectedWriteOffDone: 'Aplikacja {app} nie zapisuje już w: {destination}',
    codeConfirm:
      'Pozwól aplikacji {app} używać Twoich zatwierdzeń ze zmianami wprowadzonymi przez: {names}',
    codeConfirmDone:
      'Aplikacja {app} używa Twoich zatwierdzeń ze zmianami wprowadzonymi przez: {names}',
    codeConfirmCurrent: 'Pozwól aplikacji {app} używać Twoich zatwierdzeń z jej bieżącym kodem',
    codeConfirmCurrentDone: 'Aplikacja {app} używa Twoich zatwierdzeń ze swoim bieżącym kodem',
    runChanges: 'Uruchom teraz funkcję „{function}” w aplikacji {app}',
    runChangesDone: 'Funkcja „{function}” została uruchomiona w aplikacji {app}',
    continueApproved: 'Zatwierdzono. Kontynuuj: uruchom to i sprawdź, czy działa.',
    continueDeclined: 'Odrzucono. Kontynuuj bez tego.',
    continueMixed: 'Decyzje w sprawie żądań zapadły. Kontynuuj.',
    typeOutsideAccess: 'Zezwól aplikacji na dostęp do usług zewnętrznych',
    typeEvent: 'Włącz lub wyłącz zdarzenie aplikacji',
    typeSchedule: 'Włącz, wstrzymaj lub zmień harmonogram aplikacji',
    typeDataWrites: 'Zezwól funkcji aplikacji na zmianę własnych wierszy',
    typeTableChange: 'Usuń lub przywróć dane w tabelach aplikacji',
    typeConnectedWrite: 'Zezwól aplikacji na zapis w systemie firmy',
    typeConfirmCode: 'Pozwól zmienionemu kodowi aplikacji korzystać z Twoich zatwierdzeń',
    typeRunChanges: 'Uruchom funkcję aplikacji, która zmienia dane',
    typePublicLink: 'Zmień link publiczny aplikacji',
    typeUnpublish: 'Wyłącz link publiczny aplikacji',
    labelHosts: 'Usługi',
    labelAccess: 'Dostęp',
    labelEvent: 'Zdarzenie',
    labelKind: 'Uruchamia się przy',
    labelCollection: 'Kolekcja',
    labelChanges: 'Zmiany wierszy',
    labelRuns: 'Uruchomienia',
    labelFunction: 'Funkcja',
    labelCollections: 'Tabele',
    labelWho: 'Dla',
    labelAllowed: 'Dozwolone',
    labelEnabled: 'Stan',
    labelJob: 'Zaplanowane zadanie',
    labelSchedule: 'Harmonogram',
    labelTimezone: 'Strefa czasowa',
    labelChange: 'Zmiana',
    labelTable: 'Tabela',
    labelField: 'Pole',
    labelRows: 'Wiersze',
    labelKeptDays: 'Kopia przechowywana (dni)',
    labelRestores: 'Przywraca',
    labelAction: 'Akcja',
    labelDestination: 'Miejsce docelowe',
    labelChangedBy: 'Zmienił(a)',
    labelInput: 'Dane wejściowe',
    labelPublicTables: 'Skopiowane tabele',
    labelPublicActions: 'Odwiedzający mogą wysyłać',
    labelPublicRuns: 'Każde przesłanie uruchamia',
    labelPublicRunsOff: 'Uruchomiłoby się też po włączeniu',
    labelEmbedSites: 'Witryny, które mogą ją osadzić',
    labelPublicLink: 'Link publiczny',
    labelPublishedBy: 'Opublikował(a)',
    wordRead: 'Tylko do odczytu',
    wordReadSend: 'Odczyt i wysyłanie danych',
    wordNoAccess: 'Brak dostępu',
    publicPublish: 'Opublikuj pod linkiem publicznym',
    publicRefresh: 'Skopiuj jej dane ponownie',
    publicUnpublish: 'Cofnij publikację',
    eventWebhook: 'Jej webhook',
    eventRows: 'Zmiany jej wierszy',
    switchOn: 'Włączone',
    switchOff: 'Wyłączone',
    switchPaused: 'Wstrzymano',
    changeInsert: 'Dodano',
    changeUpdate: 'Zmieniono',
    changeDelete: 'Usunięto',
    wordDropField: 'Usuń wartości pola',
    wordDropTable: 'Usuń tabelę',
    tableClearValues: 'Wyczyść wartości naruszające surowszą regułę',
    tableRestore: 'Przywróć z jej kopii',
    whoOwner: 'Twoje własne uruchomienia',
    whoEveryone: 'Wszyscy, którzy z niej korzystają',
    answerYes: 'Tak',
    answerNo: 'Nie',
  },
  pt: {
    kindWrite: 'Alteração no aplicativo para aprovar',
    kindGrant: 'Acesso ao aplicativo para aprovar',
    kindBusiness: 'Aprovação de um aplicativo',
    approve: 'Aprovar',
    reject: 'Rejeitar',
    working: 'Em andamento…',
    checkStatus: 'Verificar status',
    statusStillPending:
      'A Poneva ainda não concluiu isso. Talvez ainda esteja em processamento, então nada mais poderá ser decidido até que isso aconteça.',
    statusStillUnknown:
      'Não foi possível obter uma resposta. Nada mais poderá ser decidido até que isso seja concluído. Tente novamente em instantes.',
    loading: 'Carregando…',
    unavailable: 'Isso não está disponível no momento.',
    loadFailed: 'Não foi possível carregar isso. Tente novamente em instantes.',
    requestedBy: 'Solicitado por {name}',
    originApp: 'Do aplicativo',
    originAgent: 'De um Agente',
    originClient: 'Via {client}',
    originFor: 'para {name}',
    changedUnderYou:
      'Isso mudou enquanto você lia. O que você vê agora é a versão atual. Nada foi decidido.',
    notAllowed: 'Você não pode decidir isso.',
    cannotDecideNow: 'Isso não pode ser decidido agora. Nada foi enviado.',
    expired: 'Esta solicitação expirou. Nada mudou.',
    stale:
      'Os dados mudaram desde que isso foi solicitado. Solicite novamente para obter uma nova solicitação.',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
    needsYou: 'Precisa da sua aprovação',
    waitingOn: 'Aguardando {name}',
    unknownTitle: 'Não foi possível confirmar o que aconteceu',
    unknownBody:
      'A alteração pode ter sido feita ou não. Não a repita. Verifique o status ou peça ao proprietário do aplicativo para verificar.',
    blockedApproval: 'Aguardando esta aprovação para continuar.',
    blockedBudget: 'Pausado: o orçamento de IA deste aplicativo foi esgotado.',
    blockedProvider: 'Pausado: um serviço conectado não está respondendo.',
    blockedViewer:
      'Pausado: isso precisa ser executado como um visualizador conectado, mas nenhum visualizador está associado. Nada é executado usando o acesso do proprietário.',
    writeTitle: 'Aprovar alterações em {target}',
    writeSummaryAll: '{count} alterações em {app}',
    rowCreate: 'Novo',
    rowUpdate: 'Alterado',
    rowDelete: 'Removido',
    rowInclude: 'Incluir esta alteração',
    rowExcluded: 'Excluída',
    fieldBefore: 'Antes',
    fieldAfter: 'Depois',
    fieldEmpty: 'Vazio',
    approveSome: 'Aprovar {included} de {total}',
    approveAll: 'Aprovar todas as {total}',
    allLeftOut:
      'Todas as alterações foram excluídas. Rejeite para interromper ou inclua uma alteração para aprová-la.',
    appliedSome: '{count} alterações feitas.',
    rejectedBody: 'Nada foi alterado.',
    noRows: 'Não há nada para aprovar aqui.',
    grantTitle: 'Permitir que {app} use {source}',
    grantRead: 'Somente leitura',
    grantWrite: 'Ler e alterar',
    audienceOwner: 'Somente você verá os dados deste aplicativo de {source}.',
    audiencePeople:
      'As {count} pessoas com quem este aplicativo é compartilhado verão dados de {source}.',
    audienceCompany:
      'Todas as pessoas da empresa que abrirem o aplicativo verão dados de {source}.',
    grantRouted: '{owner} decide isso, então foi enviado a essa pessoa.',
    grantRoutedUnknown:
      'O proprietário desta conexão decide isso, então foi enviado a essa pessoa.',
    grantWhat: 'Acesso',
    businessStep: 'Etapa {order}',
    stepWaiting: 'Aguardando',
    stepPending: 'Precisa de uma decisão',
    stepApproved: 'Aprovado',
    stepRejected: 'Rejeitado',
    stepYou: 'Você',
    businessNote: 'A aprovação aqui não permite nenhuma nova alteração fora da Poneva.',
    businessYourTurn: 'É sua vez de decidir.',
    businessNotYourTurn: 'Aguardando {name} decidir primeiro.',
    policyTitle: 'Como os aplicativos usam esta conexão',
    policyCreator: 'Somente do criador',
    policyCreatorBody:
      'Todas as pessoas veem os mesmos dados, por meio da conexão do criador do aplicativo.',
    policyViewer: 'Somente do próprio visualizador',
    policyViewerBody: 'Cada pessoa vê seus próprios dados, por meio da própria conexão.',
    policyBoth: 'Ambos',
    policyBothBody: 'O criador do aplicativo escolhe para cada aplicativo.',
    policyRevoked: 'Os aplicativos não podem usar esta conexão',
    policyRevokedBody:
      'Escolha uma opção para permitir o uso novamente. Nada será escolhido por você.',
    policySave: 'Salvar',
    policySaving: 'Salvando…',
    policySaved: 'Salvo.',
    policyReadOnly: 'Somente um administrador da empresa pode alterar isso.',
    policyConflict: 'Outra pessoa alterou isso primeiro. Esta é a configuração atual.',
    policyRefused: 'Não foi possível salvar.',
    policyFailed:
      'Não foi possível confirmar que foi salvo. Recarregue para ver a configuração atual antes de tentar novamente.',
    policyWidened:
      'Os aplicativos que já estão publicados mantêm a forma como se conectam hoje. Os criadores serão solicitados a republicá-los para usar a nova opção.',
    policyNarrowed: 'Os aplicativos verificam a configuração atual sempre que se conectam.',
    policyAffected: '{count} aplicativos publicados usam esta conexão (em {when}).',
    policyAffectedOne: '1 aplicativo publicado usa esta conexão (em {when}).',
    policyAffectsNames: 'Afeta {count} aplicativos publicados: {names}.',
    policyAffectsOneName: 'Afeta 1 aplicativo publicado: {names}.',
    policyGenericWiden:
      'Os aplicativos publicados que usam esta conexão mantêm a forma como se conectam hoje. Os criadores serão solicitados a republicá-los para usar a nova opção.',
    policyGenericNarrow:
      'Os aplicativos publicados que usam esta conexão verificam a configuração atual sempre que se conectam.',
    policyAffectedGeneric:
      'Os aplicativos publicados que usam esta conexão não são alterados automaticamente. Os criadores serão solicitados a republicá-los.',
    policyUnavailable: 'Esta configuração não está disponível no momento.',
    outsideRead: 'Permitir que {app} leia de {hosts}',
    outsideReadSend: 'Permitir que {app} leia de {hosts} e envie dados para ele',
    outsideNone: 'Impedir que {app} acesse {hosts}',
    outsideReadDone: '{app} pode ler de {hosts}',
    outsideReadSendDone: '{app} pode ler de {hosts} e enviar dados para ele',
    outsideNoneDone: '{app} não acessa mais {hosts}',
    outsideMore: 'mais {count}',
    eventWebhookOn: 'Permitir que {app} receba “{event}” no seu webhook',
    eventWebhookOnDone: '{app} recebe “{event}” no seu webhook',
    eventWebhookOff: 'Impedir que {app} receba “{event}” no seu webhook',
    eventWebhookOffDone: '{app} não recebe mais “{event}”',
    eventRowsOn: 'Permitir que {app} execute {runs} quando linhas em {collection} forem {changes}',
    eventRowsOnDone: '{app} executa {runs} quando linhas em {collection} são {changes}',
    eventRowsOff: 'Impedir que {app} execute {runs} quando linhas em {collection} mudarem',
    eventRowsOffDone: '{app} não executa mais {runs} quando linhas em {collection} mudam',
    eventInsert: 'adicionadas',
    eventUpdate: 'alteradas',
    eventDelete: 'excluídas',
    scheduleTurnOn: 'Ativar o agendamento de “{job}” em {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'O agendamento de “{job}” em {app} está ativado. {cadence} ({timezone}).',
    scheduleResume: 'Retomar o agendamento de “{job}” em {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'O agendamento de “{job}” em {app} está ativado novamente. {cadence} ({timezone}).',
    scheduleReschedule: 'Alterar o agendamento de “{job}” em {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'O agendamento de “{job}” em {app} foi alterado. {cadence} ({timezone}).',
    schedulePause: 'Pausar o agendamento de “{job}” em {app}.',
    schedulePauseDone: 'O agendamento de “{job}” em {app} está em pausa.',
    scheduleNext: 'Próximas execuções: {runs}.',
    scheduleResumeSaved:
      'Retomar o agendamento de “{job}” em {app} com a versão salva mais recente. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'O agendamento de “{job}” em {app} está ativado novamente e executa a versão salva mais recente. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Retomar o agendamento de “{job}” em {app} com a versão publicada. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'O agendamento de “{job}” em {app} está ativado novamente e executa a versão publicada. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Alterar o agendamento de “{job}” em {app}. {cadence} ({timezone}). Ele ficará em pausa até você retomá-lo.',
    scheduleReschedulePausedDone:
      'O agendamento de “{job}” em {app} foi alterado. {cadence} ({timezone}). Ele está em pausa até você retomá-lo.',
    scheduleSwitchSaved:
      'Mudar o agendamento de “{job}” em {app} para a versão salva mais recente. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'O agendamento de “{job}” em {app} agora executa a versão salva mais recente. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Mudar o agendamento de “{job}” em {app} para a versão salva mais recente. {cadence} ({timezone}). Ele ficará em pausa até você retomá-lo.',
    scheduleSwitchSavedPausedDone:
      'O agendamento de “{job}” em {app} agora usa a versão salva mais recente. {cadence} ({timezone}). Ele está em pausa até você retomá-lo.',
    scheduleSwitchPublished:
      'Mudar o agendamento de “{job}” em {app} para a versão publicada. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'O agendamento de “{job}” em {app} agora executa a versão publicada. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Mudar o agendamento de “{job}” em {app} para a versão publicada. {cadence} ({timezone}). Ele ficará em pausa até você retomá-lo.',
    scheduleSwitchPublishedPausedDone:
      'O agendamento de “{job}” em {app} agora usa a versão publicada. {cadence} ({timezone}). Ele está em pausa até você retomá-lo.',
    dataWritesOn: 'Permitir que “{function}” de {app} possa {verbs} suas próprias linhas',
    dataWritesOnDone: '“{function}” de {app} pode {verbs} suas próprias linhas',
    dataWritesOff: 'Impedir que “{function}” de {app} altere suas próprias linhas',
    dataWritesOffDone: '“{function}” de {app} não altera mais suas próprias linhas',
    dataWriteInsert: 'adicionar',
    dataWriteUpdate: 'alterar',
    dataWriteDelete: 'excluir',
    tableDropField: 'Permitir que {app} exclua o campo “{field}” de {table} ({rows})',
    tableDropFieldDone: '{app} excluiu o campo “{field}” de {table} ({rows})',
    tableDropTable: 'Permitir que {app} exclua a tabela {table} ({rows})',
    tableDropTableDone: '{app} excluiu a tabela {table} ({rows})',
    tableClear: 'Permitir que {app} limpe “{field}” em {table} onde viola a nova regra ({rows})',
    tableClearDone: '{app} limpou “{field}” em {table} onde violava a nova regra ({rows})',
    tableRestoreField:
      'Permitir que {app} restaure “{field}” em {table} a partir da cópia ({rows})',
    tableRestoreFieldDone: '{app} restaurou “{field}” em {table} ({rows})',
    tableRestoreTable: 'Permitir que {app} restaure a tabela {table} a partir da cópia ({rows})',
    tableRestoreTableDone: '{app} restaurou a tabela {table} ({rows})',
    tableRowsLoseOne: '1 linha perde um valor',
    tableRowsLoseMany: '{count} linhas perdem um valor',
    tableRowsLostOne: '1 linha perdeu um valor',
    tableRowsLostMany: '{count} linhas perderam um valor',
    tableRowsOne: '1 linha',
    tableRowsMany: '{count} linhas',
    connectedWriteOn: 'Permitir que {app} {action} em {destination}',
    connectedWriteOnDone: '{app} pode {action} em {destination}',
    connectedWriteOff: 'Impedir que {app} grave em {destination}',
    connectedWriteOffDone: '{app} não grava mais em {destination}',
    codeConfirm: 'Permitir que {app} use suas aprovações com as alterações de {names}',
    codeConfirmDone: '{app} usa suas aprovações com as alterações de {names}',
    codeConfirmCurrent: 'Permitir que {app} use suas aprovações com o código atual',
    codeConfirmCurrentDone: '{app} usa suas aprovações com o código atual',
    runChanges: 'Executar agora “{function}” em {app}',
    runChangesDone: '“{function}” foi executada em {app}',
    continueApproved: 'Aprovado. Continue: execute e verifique se funciona.',
    continueDeclined: 'Recusado. Continue sem isso.',
    continueMixed: 'Já decidi sobre as solicitações. Continue.',
    typeOutsideAccess: 'Permitir que um aplicativo acesse serviços externos',
    typeEvent: 'Ativar ou desativar um evento de um aplicativo',
    typeSchedule: 'Ativar, pausar ou alterar o agendamento de um aplicativo',
    typeDataWrites: 'Permitir que uma função de um aplicativo altere as próprias linhas',
    typeTableChange: 'Excluir ou restaurar dados nas tabelas de um aplicativo',
    typeConnectedWrite: 'Permitir que um aplicativo grave em um sistema da empresa',
    typeConfirmCode: 'Deixar que o código alterado de um aplicativo use suas aprovações',
    typeRunChanges: 'Executar uma função de um aplicativo que altera dados',
    typePublicLink: 'Alterar o link público de um aplicativo',
    typeUnpublish: 'Retirar o link público de um aplicativo',
    labelHosts: 'Serviços',
    labelAccess: 'Acesso',
    labelEvent: 'Evento',
    labelKind: 'Começa com',
    labelCollection: 'Coleção',
    labelChanges: 'Alterações de linhas',
    labelRuns: 'Execuções',
    labelFunction: 'Função',
    labelCollections: 'Tabelas',
    labelWho: 'Para',
    labelAllowed: 'Permitido',
    labelEnabled: 'Estado',
    labelJob: 'Tarefa agendada',
    labelSchedule: 'Agenda',
    labelTimezone: 'Fuso horário',
    labelChange: 'Alteração',
    labelTable: 'Tabela',
    labelField: 'Campo',
    labelRows: 'Linhas',
    labelKeptDays: 'Cópia guardada (dias)',
    labelRestores: 'Restaura',
    labelAction: 'Ação',
    labelDestination: 'Destino',
    labelChangedBy: 'Alterado por',
    labelInput: 'Entrada',
    labelPublicTables: 'Tabelas copiadas',
    labelPublicActions: 'Os visitantes podem enviar',
    labelPublicRuns: 'Cada envio inicia',
    labelPublicRunsOff: 'Também iniciaria depois de ativado',
    labelEmbedSites: 'Sites que podem incorporá-lo',
    labelPublicLink: 'Link público',
    labelPublishedBy: 'Publicado por',
    wordRead: 'Somente leitura',
    wordReadSend: 'Ler e enviar dados',
    wordNoAccess: 'Sem acesso',
    publicPublish: 'Publicar em um link público',
    publicRefresh: 'Copiar novamente os dados dele',
    publicUnpublish: 'Cancelar publicação',
    eventWebhook: 'O webhook do aplicativo',
    eventRows: 'Alterações nas linhas do aplicativo',
    switchOn: 'Ativado',
    switchOff: 'Desativado',
    switchPaused: 'Pausado',
    changeInsert: 'Adicionada',
    changeUpdate: 'Alterada',
    changeDelete: 'Excluída',
    wordDropField: 'Excluir os valores de um campo',
    wordDropTable: 'Excluir uma tabela',
    tableClearValues: 'Limpar os valores que violam uma regra mais rígida',
    tableRestore: 'Restaurar a partir da cópia',
    whoOwner: 'Suas próprias execuções',
    whoEveryone: 'Todas as pessoas que o usam',
    answerYes: 'Sim',
    answerNo: 'Não',
  },
  ro: {
    kindWrite: 'Modificare a aplicației de aprobat',
    kindGrant: 'Acces la aplicație de aprobat',
    kindBusiness: 'Aprobare pentru o aplicație',
    approve: 'Aprobă',
    reject: 'Respinge',
    working: 'Se lucrează…',
    checkStatus: 'Verifică starea',
    statusStillPending:
      'Poneva nu a soluționat încă această solicitare. Este posibil să fie încă în curs, așa că nu se poate lua nicio altă decizie până atunci.',
    statusStillUnknown:
      'Nu am putut obține un răspuns. Nu se poate lua nicio altă decizie până la soluționarea acestei situații. Încearcă din nou peste puțin timp.',
    loading: 'Se încarcă…',
    unavailable: 'Această opțiune nu este disponibilă momentan.',
    loadFailed: 'Nu am putut încărca acest element. Încearcă din nou peste puțin timp.',
    requestedBy: 'Solicitat de {name}',
    originApp: 'Din aplicație',
    originAgent: 'De la un Agent',
    originClient: 'Prin {client}',
    originFor: 'pentru {name}',
    changedUnderYou:
      'Acest element s-a modificat în timp ce îl citeai. Ceea ce vezi acum este versiunea actuală. Nu s-a luat nicio decizie.',
    notAllowed: 'Nu poți lua o decizie în acest caz.',
    cannotDecideNow: 'Nu se poate lua o decizie în acest moment. Nu s-a trimis nimic.',
    expired: 'Solicitarea a expirat. Nu s-a schimbat nimic.',
    stale:
      'Datele s-au modificat de la trimiterea acestei solicitări. Solicită din nou pentru a primi o solicitare actualizată.',
    approved: 'Aprobat',
    rejected: 'Respins',
    needsYou: 'Necesită aprobare',
    waitingOn: 'Se așteaptă {name}',
    unknownTitle: 'Nu putem confirma ce s-a întâmplat',
    unknownBody:
      'Este posibil ca modificarea să fi fost făcută sau nu. Nu repeta acțiunea. Verifică starea sau roagă proprietarul aplicației să verifice.',
    blockedApproval: 'Se așteaptă această aprobare înainte de a continua.',
    blockedBudget: 'Întrerupt: bugetul AI pentru această aplicație s-a epuizat.',
    blockedProvider: 'Întrerupt: un serviciu conectat nu răspunde.',
    blockedViewer:
      'Întrerupt: această acțiune trebuie executată de un vizualizator autentificat, dar nu este asociat niciunul. Nu se execută folosind accesul proprietarului.',
    writeTitle: 'Aprobă modificările pentru {target}',
    writeSummaryAll: '{count} modificări în {app}',
    rowCreate: 'Nou',
    rowUpdate: 'Modificat',
    rowDelete: 'Eliminat',
    rowInclude: 'Include această modificare',
    rowExcluded: 'Exclusă',
    fieldBefore: 'Înainte',
    fieldAfter: 'După',
    fieldEmpty: 'Gol',
    approveSome: 'Aprobă {included} din {total}',
    approveAll: 'Aprobă toate cele {total}',
    allLeftOut:
      'Toate modificările sunt excluse. Respinge pentru a opri procesul sau include o modificare pentru a o aproba.',
    appliedSome: 'S-au făcut {count} modificări.',
    rejectedBody: 'Nu s-a modificat nimic.',
    noRows: 'Nu există nimic de aprobat aici.',
    grantTitle: 'Permite-i aplicației {app} să folosească {source}',
    grantRead: 'Doar citire',
    grantWrite: 'Citire și modificare',
    audienceOwner: 'Doar tu vei vedea datele acestei aplicații din {source}.',
    audiencePeople:
      'Cele {count} persoane cu care este partajată această aplicație vor vedea datele din {source}.',
    audienceCompany: 'Toți membrii companiei care deschid aplicația vor vedea datele din {source}.',
    grantRouted: '{owner} ia această decizie, așa că solicitarea i-a fost trimisă.',
    grantRoutedUnknown:
      'Proprietarul acestei conexiuni ia această decizie, așa că solicitarea i-a fost trimisă.',
    grantWhat: 'Acces',
    businessStep: 'Pasul {order}',
    stepWaiting: 'În așteptare',
    stepPending: 'Necesită o decizie',
    stepApproved: 'Aprobat',
    stepRejected: 'Respins',
    stepYou: 'Tu',
    businessNote: 'Aprobarea aici nu permite nicio modificare nouă în afara Poneva.',
    businessYourTurn: 'Este rândul tău să iei o decizie.',
    businessNotYourTurn: 'Se așteaptă mai întâi decizia lui {name}.',
    policyTitle: 'Cum folosesc aplicațiile această conexiune',
    policyCreator: 'Doar conexiunea creatorului',
    policyCreatorBody: 'Toți văd aceleași date, prin conexiunea creatorului aplicației.',
    policyViewer: 'Doar conexiunea proprie a vizualizatorului',
    policyViewerBody: 'Fiecare persoană își vede propriile date, prin propria conexiune.',
    policyBoth: 'Ambele',
    policyBothBody: 'Creatorul aplicației alege pentru fiecare aplicație.',
    policyRevoked: 'Aplicațiile nu pot folosi această conexiune',
    policyRevokedBody:
      'Alege o opțiune pentru a permite din nou accesul. Nu se alege nimic în locul tău.',
    policySave: 'Salvează',
    policySaving: 'Se salvează…',
    policySaved: 'Salvat.',
    policyReadOnly: 'Doar un administrator al companiei poate modifica această setare.',
    policyConflict:
      'Altcineva a modificat această setare înaintea ta. Aceasta este setarea actuală.',
    policyRefused: 'Nu s-a putut salva.',
    policyFailed:
      'Nu am putut confirma că s-a salvat. Reîncărcați pentru a vedea setarea actuală înainte de a încerca din nou.',
    policyWidened:
      'Aplicațiile deja publicate păstrează modul actual de conectare. Creatorii lor trebuie să le republice pentru a folosi noua opțiune.',
    policyNarrowed: 'Aplicațiile verifică setarea actuală de fiecare dată când se conectează.',
    policyAffected: '{count} aplicații publicate folosesc această conexiune (la {when}).',
    policyAffectedOne: '1 aplicație publicată folosește această conexiune (la {when}).',
    policyAffectsNames: 'Afectează {count} aplicații publicate: {names}.',
    policyAffectsOneName: 'Afectează 1 aplicație publicată: {names}.',
    policyGenericWiden:
      'Aplicațiile publicate care folosesc această conexiune păstrează modul actual de conectare. Creatorii lor trebuie să le republice pentru a folosi noua opțiune.',
    policyGenericNarrow:
      'Aplicațiile publicate care folosesc această conexiune verifică setarea actuală de fiecare dată când se conectează.',
    policyAffectedGeneric:
      'Aplicațiile publicate care folosesc această conexiune nu sunt modificate automat. Creatorii lor trebuie să le republice.',
    policyUnavailable: 'Această setare nu este disponibilă momentan.',
    outsideRead: 'Permite ca {app} să citească din {hosts}',
    outsideReadSend: 'Permite ca {app} să citească din {hosts} și să trimită date acolo',
    outsideNone: 'Oprește accesul {app} la {hosts}',
    outsideReadDone: '{app} poate citi din {hosts}',
    outsideReadSendDone: '{app} poate citi din {hosts} și poate trimite date acolo',
    outsideNoneDone: '{app} nu mai accesează {hosts}',
    outsideMore: 'încă {count}',
    eventWebhookOn: 'Permite ca {app} să primească „{event}” la webhook-ul său',
    eventWebhookOnDone: '{app} primește „{event}” la webhook-ul său',
    eventWebhookOff: 'Oprește primirea „{event}” de către {app} la webhook-ul său',
    eventWebhookOffDone: '{app} nu mai primește „{event}”',
    eventRowsOn: 'Permite ca {app} să ruleze {runs} când rândurile din {collection} sunt {changes}',
    eventRowsOnDone: '{app} rulează {runs} când rândurile din {collection} sunt {changes}',
    eventRowsOff:
      'Oprește rularea {runs} de către {app} când se schimbă rândurile din {collection}',
    eventRowsOffDone: '{app} nu mai rulează {runs} când se schimbă rândurile din {collection}',
    eventInsert: 'adăugate',
    eventUpdate: 'modificate',
    eventDelete: 'șterse',
    scheduleTurnOn: 'Activează programarea pentru „{job}” în {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone:
      'Programarea pentru „{job}” în {app} este activată. {cadence} ({timezone}).',
    scheduleResume: 'Reia programarea pentru „{job}” în {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Programarea pentru „{job}” în {app} este din nou activată. {cadence} ({timezone}).',
    scheduleReschedule: 'Modifică programarea pentru „{job}” în {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Programarea pentru „{job}” în {app} a fost modificată. {cadence} ({timezone}).',
    schedulePause: 'Pune pe pauză programarea pentru „{job}” în {app}.',
    schedulePauseDone: 'Programarea pentru „{job}” în {app} este în pauză.',
    scheduleNext: 'Următoarele rulări: {runs}.',
    scheduleResumeSaved:
      'Reia programarea pentru „{job}” în {app} cu cea mai recentă versiune salvată. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Programarea pentru „{job}” în {app} este din nou activată și rulează cea mai recentă versiune salvată. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Reia programarea pentru „{job}” în {app} cu versiunea publicată. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Programarea pentru „{job}” în {app} este din nou activată și rulează versiunea publicată. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Modifică programarea pentru „{job}” în {app}. {cadence} ({timezone}). Va fi în pauză până când o reiei.',
    scheduleReschedulePausedDone:
      'Programarea pentru „{job}” în {app} a fost modificată. {cadence} ({timezone}). Este în pauză până când o reiei.',
    scheduleSwitchSaved:
      'Comută programarea pentru „{job}” în {app} la cea mai recentă versiune salvată. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Programarea pentru „{job}” în {app} rulează acum cea mai recentă versiune salvată. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Comută programarea pentru „{job}” în {app} la cea mai recentă versiune salvată. {cadence} ({timezone}). Va fi în pauză până când o reiei.',
    scheduleSwitchSavedPausedDone:
      'Programarea pentru „{job}” în {app} folosește acum cea mai recentă versiune salvată. {cadence} ({timezone}). Este în pauză până când o reiei.',
    scheduleSwitchPublished:
      'Comută programarea pentru „{job}” în {app} la versiunea publicată. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Programarea pentru „{job}” în {app} rulează acum versiunea publicată. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Comută programarea pentru „{job}” în {app} la versiunea publicată. {cadence} ({timezone}). Va fi în pauză până când o reiei.',
    scheduleSwitchPublishedPausedDone:
      'Programarea pentru „{job}” în {app} folosește acum versiunea publicată. {cadence} ({timezone}). Este în pauză până când o reiei.',
    dataWritesOn: 'Permite ca „{function}” din {app} să {verbs} propriile rânduri',
    dataWritesOnDone: '„{function}” din {app} poate {verbs} propriile rânduri',
    dataWritesOff: 'Oprește modificarea propriilor rânduri de către „{function}” din {app}',
    dataWritesOffDone: '„{function}” din {app} nu își mai modifică propriile rânduri',
    dataWriteInsert: 'adauge',
    dataWriteUpdate: 'modifice',
    dataWriteDelete: 'șteargă',
    tableDropField: 'Permite ca {app} să elimine câmpul „{field}” din {table} ({rows})',
    tableDropFieldDone: '{app} a eliminat câmpul „{field}” din {table} ({rows})',
    tableDropTable: 'Permite ca {app} să elimine tabelul {table} ({rows})',
    tableDropTableDone: '{app} a eliminat tabelul {table} ({rows})',
    tableClear:
      'Permite ca {app} să golească „{field}” în {table} unde încalcă noua regulă ({rows})',
    tableClearDone: '{app} a golit „{field}” în {table} unde încălca noua regulă ({rows})',
    tableRestoreField: 'Permite ca {app} să readucă „{field}” în {table} din copia sa ({rows})',
    tableRestoreFieldDone: '{app} a readus „{field}” în {table} ({rows})',
    tableRestoreTable: 'Permite ca {app} să restaureze tabelul {table} din copia sa ({rows})',
    tableRestoreTableDone: '{app} a restaurat tabelul {table} ({rows})',
    tableRowsLoseOne: '1 rând pierde o valoare',
    tableRowsLoseMany: 'Rânduri în care valoarea va fi eliminată: {count}',
    tableRowsLostOne: '1 rând a pierdut o valoare',
    tableRowsLostMany: 'Rânduri în care valoarea a fost eliminată: {count}',
    tableRowsOne: '1 rând',
    tableRowsMany: 'Rânduri: {count}',
    connectedWriteOn: 'Permite ca {app} să {action} în {destination}',
    connectedWriteOnDone: '{app} poate {action} în {destination}',
    connectedWriteOff: 'Oprește scrierea de către {app} în {destination}',
    connectedWriteOffDone: '{app} nu mai scrie în {destination}',
    codeConfirm:
      'Permite ca {app} să folosească aprobările tale pentru modificările făcute de {names}',
    codeConfirmDone: '{app} folosește aprobările tale pentru modificările făcute de {names}',
    codeConfirmCurrent: 'Permite ca {app} să folosească aprobările tale cu codul ei actual',
    codeConfirmCurrentDone: '{app} folosește aprobările tale cu codul ei actual',
    runChanges: 'Rulează acum „{function}” în {app}',
    runChangesDone: '„{function}” a rulat în {app}',
    continueApproved: 'Aprobat. Continuă: rulează-l și verifică dacă funcționează.',
    continueDeclined: 'Refuzat. Continuă fără.',
    continueMixed: 'Am decis asupra solicitărilor. Continuă.',
    typeOutsideAccess: 'Permite unei aplicații să acceseze servicii externe',
    typeEvent: 'Activează sau dezactivează un eveniment al unei aplicații',
    typeSchedule: 'Activează, pune pe pauză sau modifică programarea unei aplicații',
    typeDataWrites: 'Permite unei funcții a aplicației să își modifice propriile rânduri',
    typeTableChange: 'Șterge sau restaurează date în tabelele unei aplicații',
    typeConnectedWrite: 'Permite unei aplicații să scrie într-un sistem al companiei',
    typeConfirmCode: 'Lasă codul modificat al aplicației să folosească aprobările tale',
    typeRunChanges: 'Rulează o funcție a aplicației care modifică date',
    typePublicLink: 'Modifică linkul public al unei aplicații',
    typeUnpublish: 'Elimină linkul public al unei aplicații',
    labelHosts: 'Servicii',
    labelAccess: 'Acces',
    labelEvent: 'Eveniment',
    labelKind: 'Pornește la',
    labelCollection: 'Colecție',
    labelChanges: 'Modificări de rânduri',
    labelRuns: 'Rulări',
    labelFunction: 'Funcție',
    labelCollections: 'Tabele',
    labelWho: 'Pentru',
    labelAllowed: 'Permis',
    labelEnabled: 'Stare',
    labelJob: 'Sarcină programată',
    labelSchedule: 'Program',
    labelTimezone: 'Fus orar',
    labelChange: 'Modificare',
    labelTable: 'Tabel',
    labelField: 'Câmp',
    labelRows: 'Rânduri',
    labelKeptDays: 'Copie păstrată (zile)',
    labelRestores: 'Restaurează',
    labelAction: 'Acțiune',
    labelDestination: 'Destinație',
    labelChangedBy: 'Modificat de',
    labelInput: 'Intrare',
    labelPublicTables: 'Tabele copiate',
    labelPublicActions: 'Vizitatorii pot trimite',
    labelPublicRuns: 'Fiecare trimitere pornește',
    labelPublicRunsOff: 'Ar porni și după activare',
    labelEmbedSites: 'Site-uri care o pot încorpora',
    labelPublicLink: 'Link public',
    labelPublishedBy: 'Publicat de',
    wordRead: 'Doar citire',
    wordReadSend: 'Citire și trimitere de date',
    wordNoAccess: 'Fără acces',
    publicPublish: 'Publică la un link public',
    publicRefresh: 'Copiază din nou datele ei',
    publicUnpublish: 'Anulează publicarea',
    eventWebhook: 'Webhookul ei',
    eventRows: 'Modificări ale rândurilor ei',
    switchOn: 'Activat',
    switchOff: 'Dezactivat',
    switchPaused: 'În pauză',
    changeInsert: 'Adăugat',
    changeUpdate: 'Modificat',
    changeDelete: 'Șters',
    wordDropField: 'Șterge valorile unui câmp',
    wordDropTable: 'Șterge un tabel',
    tableClearValues: 'Golește valorile care încalcă o regulă mai strictă',
    tableRestore: 'Restaurează din copia ei',
    whoOwner: 'Rulările tale',
    whoEveryone: 'Toți cei care o folosesc',
    answerYes: 'Da',
    answerNo: 'Nu',
  },
  sk: {
    kindWrite: 'Schválenie zmeny v aplikácii',
    kindGrant: 'Schválenie prístupu aplikácie',
    kindBusiness: 'Schválenie aplikácie',
    approve: 'Schváliť',
    reject: 'Zamietnuť',
    working: 'Spracúva sa…',
    checkStatus: 'Skontrolovať stav',
    statusStillPending:
      'Poneva to zatiaľ nedokončila. Možno sa to ešte spracúva, takže kým sa to nedokončí, nemožno rozhodnúť o ničom ďalšom.',
    statusStillUnknown:
      'Nepodarilo sa získať odpoveď. Kým sa to nedokončí, nemožno rozhodnúť o ničom ďalšom. Skúste to znova o chvíľu.',
    loading: 'Načítava sa…',
    unavailable: 'Momentálne to nie je k dispozícii.',
    loadFailed: 'Nepodarilo sa to načítať. Skúste to znova o chvíľu.',
    requestedBy: 'Požiadal(a) {name}',
    originApp: 'Z aplikácie',
    originAgent: 'Od agenta',
    originClient: 'Cez {client}',
    originFor: 'pre {name}',
    changedUnderYou:
      'Počas čítania sa to zmenilo. Teraz vidíte aktuálnu verziu. O ničom sa nerozhodlo.',
    notAllowed: 'O tomto nemôžete rozhodnúť.',
    cannotDecideNow: 'Momentálne o tom nemožno rozhodnúť. Nič sa neodoslalo.',
    expired: 'Platnosť tejto požiadavky vypršala. Nič sa nezmenilo.',
    stale: 'Údaje sa odvtedy zmenili. Požiadajte znova o aktuálnu požiadavku.',
    approved: 'Schválené',
    rejected: 'Zamietnuté',
    needsYou: 'Vyžaduje schválenie',
    waitingOn: 'Čaká sa na {name}',
    unknownTitle: 'Nemôžeme potvrdiť, čo sa stalo',
    unknownBody:
      'Zmena sa mohla, ale nemusela vykonať. Neopakujte ju. Skontrolujte stav alebo požiadajte vlastníka aplikácie, aby to overil.',
    blockedApproval: 'Čaká sa na toto schválenie, kým bude možné pokračovať.',
    blockedBudget: 'Pozastavené: rozpočet AI pre túto aplikáciu sa vyčerpal.',
    blockedProvider: 'Pozastavené: pripojená služba neodpovedá.',
    blockedViewer:
      'Pozastavené: musí sa to spustiť ako prihlásený používateľ a žiadny používateľ nie je pripojený. Nespustí sa to s prístupom vlastníka.',
    writeTitle: 'Schváliť zmeny v {target}',
    writeSummaryAll: '{count} zmien v {app}',
    rowCreate: 'Nové',
    rowUpdate: 'Zmenené',
    rowDelete: 'Odstránené',
    rowInclude: 'Zahrnúť túto zmenu',
    rowExcluded: 'Vynechané',
    fieldBefore: 'Predtým',
    fieldAfter: 'Potom',
    fieldEmpty: 'Prázdne',
    approveSome: 'Schváliť {included} z {total}',
    approveAll: 'Schváliť všetkých {total}',
    allLeftOut:
      'Všetky zmeny sú vynechané. Ak ich chcete zastaviť, zamietnite ich, alebo zahrňte zmenu na schválenie.',
    appliedSome: 'Vykonalo sa {count} zmien.',
    rejectedBody: 'Nič sa nezmenilo.',
    noRows: 'Nie je tu nič na schválenie.',
    grantTitle: 'Povoliť aplikácii {app} používať {source}',
    grantRead: 'Len na čítanie',
    grantWrite: 'Čítanie a zmeny',
    audienceOwner: 'Údaje tejto aplikácie zo zdroja {source} uvidíte iba vy.',
    audiencePeople:
      'Údaje zo zdroja {source} uvidí {count} ľudí, s ktorými je táto aplikácia zdieľaná.',
    audienceCompany: 'Údaje zo zdroja {source} uvidí každý vo firme, kto otvorí túto aplikáciu.',
    grantRouted: 'Rozhoduje o tom {owner}, preto sa to odoslalo tejto osobe.',
    grantRoutedUnknown: 'Rozhoduje o tom vlastník tohto pripojenia, preto sa to odoslalo jemu.',
    grantWhat: 'Prístup',
    businessStep: 'Krok {order}',
    stepWaiting: 'Čaká sa',
    stepPending: 'Vyžaduje rozhodnutie',
    stepApproved: 'Schválené',
    stepRejected: 'Zamietnuté',
    stepYou: 'Vy',
    businessNote: 'Schválenie tu nepovoľuje žiadnu novú zmenu mimo Ponevy.',
    businessYourTurn: 'Teraz ste na rade vy.',
    businessNotYourTurn: 'Najprv sa čaká na rozhodnutie osoby {name}.',
    policyTitle: 'Ako aplikácie používajú toto pripojenie',
    policyCreator: 'Iba pripojenie tvorcu',
    policyCreatorBody: 'Všetci vidia rovnaké údaje prostredníctvom pripojenia tvorcu aplikácie.',
    policyViewer: 'Iba vlastné pripojenie používateľa',
    policyViewerBody: 'Každý vidí svoje vlastné údaje prostredníctvom vlastného pripojenia.',
    policyBoth: 'Oboje',
    policyBothBody: 'Tvorca aplikácie vyberá pre každú aplikáciu.',
    policyRevoked: 'Aplikácie nemôžu používať toto pripojenie',
    policyRevokedBody: 'Vyberte možnosť a znova ho povoľte. Nič sa nevyberie za vás.',
    policySave: 'Uložiť',
    policySaving: 'Ukladá sa…',
    policySaved: 'Uložené.',
    policyReadOnly: 'Meniť to môže iba správca spoločnosti.',
    policyConflict: 'Niekto iný to zmenil skôr. Toto je aktuálne nastavenie.',
    policyRefused: 'Toto sa nepodarilo uložiť.',
    policyFailed:
      'Nepodarilo sa potvrdiť, že nastavenie bolo uložené. Pred ďalším pokusom znova načítajte stránku a skontrolujte aktuálne nastavenie.',
    policyWidened:
      'Aplikácie, ktoré už boli publikované, si zachovajú spôsob dnešného pripájania. Ich tvorcovia budú požiadaní, aby ich znova publikovali a mohli používať novú možnosť.',
    policyNarrowed: 'Aplikácie pri každom pripojení skontrolujú aktuálne nastavenie.',
    policyAffected: 'Toto pripojenie používa {count} publikovaných aplikácií (k {when}).',
    policyAffectedOne: 'Toto pripojenie používa 1 publikovaná aplikácia (k {when}).',
    policyAffectsNames: 'Ovplyvní {count} publikovaných aplikácií: {names}.',
    policyAffectsOneName: 'Ovplyvní 1 publikovanú aplikáciu: {names}.',
    policyGenericWiden:
      'Publikované aplikácie, ktoré používajú toto pripojenie, si zachovajú spôsob dnešného pripájania. Ich tvorcovia budú požiadaní, aby ich znova publikovali a mohli používať novú možnosť.',
    policyGenericNarrow:
      'Publikované aplikácie, ktoré používajú toto pripojenie, pri každom pripojení skontrolujú aktuálne nastavenie.',
    policyAffectedGeneric:
      'Publikované aplikácie, ktoré používajú toto pripojenie, sa automaticky nezmenia. Ich tvorcovia budú požiadaní, aby ich znova publikovali.',
    policyUnavailable: 'Toto nastavenie momentálne nie je k dispozícii.',
    outsideRead: 'Povoliť aplikácii {app} čítať z: {hosts}',
    outsideReadSend: 'Povoliť aplikácii {app} čítať z a odosielať údaje na: {hosts}',
    outsideNone: 'Zabrániť aplikácii {app} v prístupe k: {hosts}',
    outsideReadDone: 'Aplikácia {app} môže čítať z: {hosts}',
    outsideReadSendDone: 'Aplikácia {app} môže čítať z a odosielať údaje na: {hosts}',
    outsideNoneDone: 'Aplikácia {app} už nemá prístup k: {hosts}',
    outsideMore: 'ešte {count}',
    eventWebhookOn: 'Povoliť aplikácii {app} prijímať „{event}“ na svojom webhooku',
    eventWebhookOnDone: 'Aplikácia {app} prijíma „{event}“ na svojom webhooku',
    eventWebhookOff: 'Zabrániť aplikácii {app} v prijímaní „{event}“ na svojom webhooku',
    eventWebhookOffDone: 'Aplikácia {app} už neprijíma „{event}“',
    eventRowsOn:
      'Povoliť aplikácii {app} spúšťať: {runs}, keď sú riadky v tabuľke {collection} {changes}',
    eventRowsOnDone:
      'Aplikácia {app} spúšťa: {runs}, keď sú riadky v tabuľke {collection} {changes}',
    eventRowsOff:
      'Zabrániť aplikácii {app} v spúšťaní: {runs}, keď sa riadky v tabuľke {collection} menia',
    eventRowsOffDone:
      'Aplikácia {app} už nespúšťa: {runs}, keď sa riadky v tabuľke {collection} menia',
    eventInsert: 'pridané',
    eventUpdate: 'zmenené',
    eventDelete: 'odstránené',
    scheduleTurnOn: 'Zapnúť plán pre „{job}“ v aplikácii {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Plán pre „{job}“ v aplikácii {app} je zapnutý. {cadence} ({timezone}).',
    scheduleResume: 'Obnoviť plán pre „{job}“ v aplikácii {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Plán pre „{job}“ v aplikácii {app} je znova zapnutý. {cadence} ({timezone}).',
    scheduleReschedule: 'Zmeniť plán pre „{job}“ v aplikácii {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Plán pre „{job}“ v aplikácii {app} bol zmenený. {cadence} ({timezone}).',
    schedulePause: 'Pozastaviť plán pre „{job}“ v aplikácii {app}.',
    schedulePauseDone: 'Plán pre „{job}“ v aplikácii {app} je pozastavený.',
    scheduleNext: 'Ďalšie spustenia: {runs}.',
    scheduleResumeSaved:
      'Obnoviť plán pre „{job}“ v aplikácii {app} s najnovšou uloženou verziou. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Plán pre „{job}“ v aplikácii {app} je znova zapnutý a spúšťa najnovšiu uloženú verziu. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Obnoviť plán pre „{job}“ v aplikácii {app} so zverejnenou verziou. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Plán pre „{job}“ v aplikácii {app} je znova zapnutý a spúšťa zverejnenú verziu. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Zmeniť plán pre „{job}“ v aplikácii {app}. {cadence} ({timezone}). Bude pozastavený, kým ho neobnovíte.',
    scheduleReschedulePausedDone:
      'Plán pre „{job}“ v aplikácii {app} bol zmenený. {cadence} ({timezone}). Je pozastavený, kým ho neobnovíte.',
    scheduleSwitchSaved:
      'Prepnúť plán pre „{job}“ v aplikácii {app} na najnovšiu uloženú verziu. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Plán pre „{job}“ v aplikácii {app} teraz spúšťa najnovšiu uloženú verziu. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Prepnúť plán pre „{job}“ v aplikácii {app} na najnovšiu uloženú verziu. {cadence} ({timezone}). Bude pozastavený, kým ho neobnovíte.',
    scheduleSwitchSavedPausedDone:
      'Plán pre „{job}“ v aplikácii {app} teraz používa najnovšiu uloženú verziu. {cadence} ({timezone}). Je pozastavený, kým ho neobnovíte.',
    scheduleSwitchPublished:
      'Prepnúť plán pre „{job}“ v aplikácii {app} na zverejnenú verziu. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Plán pre „{job}“ v aplikácii {app} teraz spúšťa zverejnenú verziu. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Prepnúť plán pre „{job}“ v aplikácii {app} na zverejnenú verziu. {cadence} ({timezone}). Bude pozastavený, kým ho neobnovíte.',
    scheduleSwitchPublishedPausedDone:
      'Plán pre „{job}“ v aplikácii {app} teraz používa zverejnenú verziu. {cadence} ({timezone}). Je pozastavený, kým ho neobnovíte.',
    dataWritesOn: 'Povoliť funkcii „{function}“ aplikácie {app} {verbs} vlastné riadky',
    dataWritesOnDone: 'Funkcia „{function}“ aplikácie {app} môže {verbs} vlastné riadky',
    dataWritesOff: 'Zabrániť funkcii „{function}“ aplikácie {app} v zmenách vlastných riadkov',
    dataWritesOffDone: 'Funkcia „{function}“ aplikácie {app} už nemení vlastné riadky',
    dataWriteInsert: 'pridávať',
    dataWriteUpdate: 'meniť',
    dataWriteDelete: 'mazať',
    tableDropField: 'Povoliť aplikácii {app} odstrániť pole „{field}“ z tabuľky {table} ({rows})',
    tableDropFieldDone: 'Aplikácia {app} odstránila pole „{field}“ z tabuľky {table} ({rows})',
    tableDropTable: 'Povoliť aplikácii {app} odstrániť tabuľku {table} ({rows})',
    tableDropTableDone: 'Aplikácia {app} odstránila tabuľku {table} ({rows})',
    tableClear:
      'Povoliť aplikácii {app} vymazať „{field}“ v tabuľke {table} tam, kde hodnota porušuje nové pravidlo ({rows})',
    tableClearDone:
      'Aplikácia {app} vymazala „{field}“ v tabuľke {table} tam, kde hodnota porušovala nové pravidlo ({rows})',
    tableRestoreField:
      'Povoliť aplikácii {app} obnoviť „{field}“ v tabuľke {table} z kópie ({rows})',
    tableRestoreFieldDone: 'Aplikácia {app} obnovila „{field}“ v tabuľke {table} ({rows})',
    tableRestoreTable: 'Povoliť aplikácii {app} obnoviť tabuľku {table} z kópie ({rows})',
    tableRestoreTableDone: 'Aplikácia {app} obnovila tabuľku {table} ({rows})',
    tableRowsLoseOne: '1 riadok stratí hodnotu',
    tableRowsLoseMany: 'Riadky, v ktorých sa hodnota odstráni: {count}',
    tableRowsLostOne: '1 riadok prišiel o hodnotu',
    tableRowsLostMany: 'Riadky, v ktorých sa hodnota odstránila: {count}',
    tableRowsOne: '1 riadok',
    tableRowsMany: 'Riadky: {count}',
    connectedWriteOn: 'Povoliť aplikácii {app} {action} v: {destination}',
    connectedWriteOnDone: 'Aplikácia {app} môže {action} v: {destination}',
    connectedWriteOff: 'Zabrániť aplikácii {app} v zápise do: {destination}',
    connectedWriteOffDone: 'Aplikácia {app} už nezapisuje do: {destination}',
    codeConfirm: 'Povoliť aplikácii {app} používať vaše schválenia so zmenami od: {names}',
    codeConfirmDone: 'Aplikácia {app} používa vaše schválenia so zmenami od: {names}',
    codeConfirmCurrent: 'Povoliť aplikácii {app} používať vaše schválenia s jej aktuálnym kódom',
    codeConfirmCurrentDone: 'Aplikácia {app} používa vaše schválenia so svojím aktuálnym kódom',
    runChanges: 'Spustiť teraz funkciu „{function}“ v aplikácii {app}',
    runChangesDone: 'Funkcia „{function}“ bola spustená v aplikácii {app}',
    continueApproved: 'Schválené. Pokračuj: spusti to a skontroluj, že to funguje.',
    continueDeclined: 'Zamietnuté. Pokračuj bez toho.',
    continueMixed: 'O žiadostiach je rozhodnuté. Pokračuj.',
    typeOutsideAccess: 'Povoliť aplikácii prístup k externým službám',
    typeEvent: 'Zapnúť alebo vypnúť udalosť aplikácie',
    typeSchedule: 'Zapnúť, pozastaviť alebo zmeniť plán aplikácie',
    typeDataWrites: 'Povoliť funkcii aplikácie meniť vlastné riadky',
    typeTableChange: 'Odstrániť alebo obnoviť dáta v tabuľkách aplikácie',
    typeConnectedWrite: 'Povoliť aplikácii zapisovať do firemného systému',
    typeConfirmCode: 'Nechať zmenený kód aplikácie používať vaše schválenia',
    typeRunChanges: 'Spustiť funkciu aplikácie, ktorá mení dáta',
    typePublicLink: 'Zmeniť verejný odkaz aplikácie',
    typeUnpublish: 'Stiahnuť verejný odkaz aplikácie',
    labelHosts: 'Služby',
    labelAccess: 'Prístup',
    labelEvent: 'Udalosť',
    labelKind: 'Spustí sa pri',
    labelCollection: 'Kolekcia',
    labelChanges: 'Zmeny riadkov',
    labelRuns: 'Spustenia',
    labelFunction: 'Funkcia',
    labelCollections: 'Tabuľky',
    labelWho: 'Pre',
    labelAllowed: 'Povolené',
    labelEnabled: 'Stav',
    labelJob: 'Naplánovaná úloha',
    labelSchedule: 'Plán',
    labelTimezone: 'Časové pásmo',
    labelChange: 'Zmena',
    labelTable: 'Tabuľka',
    labelField: 'Pole',
    labelRows: 'Riadky',
    labelKeptDays: 'Kópia sa uchováva (dni)',
    labelRestores: 'Obnoví',
    labelAction: 'Akcia',
    labelDestination: 'Cieľ',
    labelChangedBy: 'Zmenil(a)',
    labelInput: 'Vstup',
    labelPublicTables: 'Skopírované tabuľky',
    labelPublicActions: 'Návštevníci môžu odoslať',
    labelPublicRuns: 'Každé odoslanie spustí',
    labelPublicRunsOff: 'Spustilo by sa aj po zapnutí',
    labelEmbedSites: 'Weby, ktoré ju smú vložiť',
    labelPublicLink: 'Verejný odkaz',
    labelPublishedBy: 'Publikoval(a)',
    wordRead: 'Iba na čítanie',
    wordReadSend: 'Čítanie a odosielanie dát',
    wordNoAccess: 'Bez prístupu',
    publicPublish: 'Publikovať na verejný odkaz',
    publicRefresh: 'Znova skopírovať jej dáta',
    publicUnpublish: 'Zrušiť publikovanie',
    eventWebhook: 'Jej webhook',
    eventRows: 'Zmeny jej riadkov',
    switchOn: 'Zapnuté',
    switchOff: 'Vypnuté',
    switchPaused: 'Pozastavené',
    changeInsert: 'Pridané',
    changeUpdate: 'Zmenené',
    changeDelete: 'Odstránené',
    wordDropField: 'Odstrániť hodnoty poľa',
    wordDropTable: 'Odstrániť tabuľku',
    tableClearValues: 'Vymazať hodnoty, ktoré porušujú prísnejšie pravidlo',
    tableRestore: 'Obnoviť z jej kópie',
    whoOwner: 'Vaše vlastné spustenia',
    whoEveryone: 'Všetci, ktorí ju používajú',
    answerYes: 'Áno',
    answerNo: 'Nie',
  },
  sl: {
    kindWrite: 'Sprememba aplikacije za odobritev',
    kindGrant: 'Dostop aplikacije za odobritev',
    kindBusiness: 'Odobritev za aplikacijo',
    approve: 'Odobri',
    reject: 'Zavrni',
    working: 'Poteka…',
    checkStatus: 'Preveri stanje',
    statusStillPending:
      'Poneva tega še ni zaključila. Morda je še v obdelavi, zato se do zaključka ne da odločiti o ničemer drugem.',
    statusStillUnknown:
      'Odgovora nismo mogli pridobiti. Dokler se to ne razreši, se ne da odločiti o ničemer drugem. Poskusite znova čez trenutek.',
    loading: 'Nalaganje…',
    unavailable: 'To trenutno ni na voljo.',
    loadFailed: 'Tega nismo mogli naložiti. Poskusite znova čez trenutek.',
    requestedBy: 'Zahteval/-a {name}',
    originApp: 'Iz aplikacije',
    originAgent: 'Od agenta',
    originClient: 'Prek {client}',
    originFor: 'za {name}',
    changedUnderYou:
      'To se je spremenilo, medtem ko ste brali. Zdaj vidite trenutno različico. Nič ni bilo odločeno.',
    notAllowed: 'O tem ne morete odločati.',
    cannotDecideNow: 'O tem trenutno ni mogoče odločati. Nič ni bilo poslano.',
    expired: 'Ta zahteva je potekla. Nič se ni spremenilo.',
    stale: 'Podatki so se od oddaje te zahteve spremenili. Znova vprašajte za novo zahtevo.',
    approved: 'Odobreno',
    rejected: 'Zavrnjeno',
    needsYou: 'Potrebna je odobritev',
    waitingOn: 'Čakanje na {name}',
    unknownTitle: 'Ne moremo potrditi, kaj se je zgodilo',
    unknownBody:
      'Sprememba je morda bila izvedena ali pa ne. Ne ponavljajte je. Preverite stanje ali prosite lastnika aplikacije, naj preveri.',
    blockedApproval: 'Pred nadaljevanjem čaka na to odobritev.',
    blockedBudget: 'Začasno ustavljeno: proračun umetne inteligence za to aplikacijo je porabljen.',
    blockedProvider: 'Začasno ustavljeno: povezana storitev se ne odziva.',
    blockedViewer:
      'Začasno ustavljeno: to se mora izvajati kot prijavljen pregledovalec, vendar noben pregledovalec ni povezan. Namesto dostopa lastnika se nič ne bo izvajalo.',
    writeTitle: 'Odobri spremembe za {target}',
    writeSummaryAll: '{count} sprememb v aplikaciji {app}',
    rowCreate: 'Novo',
    rowUpdate: 'Spremenjeno',
    rowDelete: 'Odstranjeno',
    rowInclude: 'Vključi to spremembo',
    rowExcluded: 'Izpuščeno',
    fieldBefore: 'Prej',
    fieldAfter: 'Potem',
    fieldEmpty: 'Prazno',
    approveSome: 'Odobri {included} od {total}',
    approveAll: 'Odobri vseh {total}',
    allLeftOut:
      'Vse spremembe so izpuščene. Zavrni jih, da ustaviš postopek, ali vključi spremembo, da jo odobriš.',
    appliedSome: 'Uveljavljenih sprememb: {count}.',
    rejectedBody: 'Nič se ni spremenilo.',
    noRows: 'Tukaj ni ničesar za odobritev.',
    grantTitle: 'Dovoli aplikaciji {app} uporabo vira {source}',
    grantRead: 'Samo za branje',
    grantWrite: 'Branje in spreminjanje',
    audienceOwner: 'Podatke te aplikacije iz vira {source} boste videli samo vi.',
    audiencePeople:
      'Podatke iz vira {source} bodo videli ljudje, s katerimi je aplikacija v skupni rabi ({count}).',
    audienceCompany: 'Podatke iz vira {source} bodo videli vsi v podjetju, ki odprejo aplikacijo.',
    grantRouted: 'O tem odloča {owner}, zato je bilo poslano njemu/njej.',
    grantRoutedUnknown: 'O tem odloča lastnik te povezave, zato je bilo poslano njemu/njej.',
    grantWhat: 'Dostop',
    businessStep: 'Korak {order}',
    stepWaiting: 'Čakanje',
    stepPending: 'Potrebna je odločitev',
    stepApproved: 'Odobreno',
    stepRejected: 'Zavrnjeno',
    stepYou: 'Vi',
    businessNote: 'Odobritev tukaj ne omogoča nobene nove spremembe zunaj Poneve.',
    businessYourTurn: 'Na vrsti ste za odločitev.',
    businessNotYourTurn: 'Čakanje, da se najprej odloči {name}.',
    policyTitle: 'Kako aplikacije uporabljajo to povezavo',
    policyCreator: 'Samo povezava ustvarjalca',
    policyCreatorBody: 'Vsi vidijo iste podatke prek povezave ustvarjalca aplikacije.',
    policyViewer: 'Samo lastna povezava pregledovalca',
    policyViewerBody: 'Vsak vidi svoje podatke prek svoje povezave.',
    policyBoth: 'Oboje',
    policyBothBody: 'Ustvarjalec aplikacije izbere možnost za vsako aplikacijo.',
    policyRevoked: 'Aplikacije ne morejo uporabljati te povezave',
    policyRevokedBody: 'Izberite možnost, da jo znova omogočite. Nič ne bo izbrano namesto vas.',
    policySave: 'Shrani',
    policySaving: 'Shranjevanje…',
    policySaved: 'Shranjeno.',
    policyReadOnly: 'To lahko spremeni samo skrbnik podjetja.',
    policyConflict: 'Nekdo drug je to najprej spremenil. To je trenutna nastavitev.',
    policyRefused: 'Tega ni bilo mogoče shraniti.',
    policyFailed:
      'Nismo mogli potrditi, da je bilo shranjeno. Znova naložite stran, da preverite trenutno nastavitev, preden poskusite znova.',
    policyWidened:
      'Aplikacije, ki so že objavljene, ohranijo trenutni način povezovanja. Njihove ustvarjalce prosimo, naj jih znova objavijo, če želijo uporabiti novo možnost.',
    policyNarrowed: 'Aplikacije ob vsaki povezavi preverijo trenutno nastavitev.',
    policyAffected: '{count} objavljenih aplikacij uporablja to povezavo (na dan {when}).',
    policyAffectedOne: '1 objavljena aplikacija uporablja to povezavo (na dan {when}).',
    policyAffectsNames: 'Vpliva na {count} objavljenih aplikacij: {names}.',
    policyAffectsOneName: 'Vpliva na 1 objavljeno aplikacijo: {names}.',
    policyGenericWiden:
      'Objavljene aplikacije, ki uporabljajo to povezavo, ohranijo trenutni način povezovanja. Njihove ustvarjalce prosimo, naj jih znova objavijo, če želijo uporabiti novo možnost.',
    policyGenericNarrow:
      'Objavljene aplikacije, ki uporabljajo to povezavo, ob vsaki povezavi preverijo trenutno nastavitev.',
    policyAffectedGeneric:
      'Objavljene aplikacije, ki uporabljajo to povezavo, se ne spremenijo samodejno. Njihove ustvarjalce prosimo, naj jih znova objavijo.',
    policyUnavailable: 'Ta nastavitev trenutno ni na voljo.',
    outsideRead: 'Dovoli aplikaciji {app} branje iz: {hosts}',
    outsideReadSend: 'Dovoli aplikaciji {app} branje iz in pošiljanje podatkov na: {hosts}',
    outsideNone: 'Prepreči aplikaciji {app} dostop do: {hosts}',
    outsideReadDone: 'Aplikacija {app} lahko bere iz: {hosts}',
    outsideReadSendDone: 'Aplikacija {app} lahko bere iz in pošilja podatke na: {hosts}',
    outsideNoneDone: 'Aplikacija {app} nima več dostopa do: {hosts}',
    outsideMore: 'še {count}',
    eventWebhookOn: 'Dovoli aplikaciji {app} prejemanje „{event}“ na njenem webhooku',
    eventWebhookOnDone: 'Aplikacija {app} prejema „{event}“ na svojem webhooku',
    eventWebhookOff: 'Prepreči aplikaciji {app} prejemanje „{event}“ na njenem webhooku',
    eventWebhookOffDone: 'Aplikacija {app} ne prejema več „{event}“',
    eventRowsOn:
      'Dovoli aplikaciji {app} zagon: {runs}, ko so vrstice v tabeli {collection} {changes}',
    eventRowsOnDone:
      'Aplikacija {app} zažene: {runs}, ko so vrstice v tabeli {collection} {changes}',
    eventRowsOff:
      'Prepreči aplikaciji {app} zagon: {runs}, ko se vrstice v tabeli {collection} spremenijo',
    eventRowsOffDone:
      'Aplikacija {app} ne zažene več: {runs}, ko se vrstice v tabeli {collection} spremenijo',
    eventInsert: 'dodane',
    eventUpdate: 'spremenjene',
    eventDelete: 'izbrisane',
    scheduleTurnOn: 'Vklopi razpored za „{job}“ v aplikaciji {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone:
      'Razpored za „{job}“ v aplikaciji {app} je vklopljen. {cadence} ({timezone}).',
    scheduleResume: 'Nadaljuj razpored za „{job}“ v aplikaciji {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'Razpored za „{job}“ v aplikaciji {app} je znova vklopljen. {cadence} ({timezone}).',
    scheduleReschedule: 'Spremeni razpored za „{job}“ v aplikaciji {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'Razpored za „{job}“ v aplikaciji {app} je spremenjen. {cadence} ({timezone}).',
    schedulePause: 'Začasno ustavi razpored za „{job}“ v aplikaciji {app}.',
    schedulePauseDone: 'Razpored za „{job}“ v aplikaciji {app} je začasno ustavljen.',
    scheduleNext: 'Naslednji zagoni: {runs}.',
    scheduleResumeSaved:
      'Nadaljuj razpored za „{job}“ v aplikaciji {app} z najnovejšo shranjeno različico. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Razpored za „{job}“ v aplikaciji {app} je znova vklopljen in zaganja najnovejšo shranjeno različico. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Nadaljuj razpored za „{job}“ v aplikaciji {app} z objavljeno različico. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Razpored za „{job}“ v aplikaciji {app} je znova vklopljen in zaganja objavljeno različico. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Spremeni razpored za „{job}“ v aplikaciji {app}. {cadence} ({timezone}). Začasno bo ustavljen, dokler ga ne nadaljujete.',
    scheduleReschedulePausedDone:
      'Razpored za „{job}“ v aplikaciji {app} je spremenjen. {cadence} ({timezone}). Začasno je ustavljen, dokler ga ne nadaljujete.',
    scheduleSwitchSaved:
      'Preklopi razpored za „{job}“ v aplikaciji {app} na najnovejšo shranjeno različico. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Razpored za „{job}“ v aplikaciji {app} zdaj zaganja najnovejšo shranjeno različico. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Preklopi razpored za „{job}“ v aplikaciji {app} na najnovejšo shranjeno različico. {cadence} ({timezone}). Začasno bo ustavljen, dokler ga ne nadaljujete.',
    scheduleSwitchSavedPausedDone:
      'Razpored za „{job}“ v aplikaciji {app} zdaj uporablja najnovejšo shranjeno različico. {cadence} ({timezone}). Začasno je ustavljen, dokler ga ne nadaljujete.',
    scheduleSwitchPublished:
      'Preklopi razpored za „{job}“ v aplikaciji {app} na objavljeno različico. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Razpored za „{job}“ v aplikaciji {app} zdaj zaganja objavljeno različico. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Preklopi razpored za „{job}“ v aplikaciji {app} na objavljeno različico. {cadence} ({timezone}). Začasno bo ustavljen, dokler ga ne nadaljujete.',
    scheduleSwitchPublishedPausedDone:
      'Razpored za „{job}“ v aplikaciji {app} zdaj uporablja objavljeno različico. {cadence} ({timezone}). Začasno je ustavljen, dokler ga ne nadaljujete.',
    dataWritesOn: 'Dovoli funkciji „{function}“ aplikacije {app} {verbs} lastne vrstice',
    dataWritesOnDone: 'Funkcija „{function}“ aplikacije {app} lahko {verbs} lastne vrstice',
    dataWritesOff: 'Prepreči funkciji „{function}“ aplikacije {app} spreminjanje lastnih vrstic',
    dataWritesOffDone: 'Funkcija „{function}“ aplikacije {app} ne spreminja več lastnih vrstic',
    dataWriteInsert: 'dodajati',
    dataWriteUpdate: 'spreminjati',
    dataWriteDelete: 'brisati',
    tableDropField:
      'Dovoli aplikaciji {app} odstranitev polja „{field}“ iz tabele {table} ({rows})',
    tableDropFieldDone: 'Aplikacija {app} je odstranila polje „{field}“ iz tabele {table} ({rows})',
    tableDropTable: 'Dovoli aplikaciji {app} odstranitev tabele {table} ({rows})',
    tableDropTableDone: 'Aplikacija {app} je odstranila tabelo {table} ({rows})',
    tableClear:
      'Dovoli aplikaciji {app} izpraznitev polja „{field}“ v tabeli {table}, kjer vrednost krši novo pravilo ({rows})',
    tableClearDone:
      'Aplikacija {app} je izpraznila polje „{field}“ v tabeli {table}, kjer je vrednost kršila novo pravilo ({rows})',
    tableRestoreField:
      'Dovoli aplikaciji {app} povrnitev polja „{field}“ v tabeli {table} iz kopije ({rows})',
    tableRestoreFieldDone: 'Aplikacija {app} je povrnila polje „{field}“ v tabeli {table} ({rows})',
    tableRestoreTable: 'Dovoli aplikaciji {app} obnovitev tabele {table} iz kopije ({rows})',
    tableRestoreTableDone: 'Aplikacija {app} je obnovila tabelo {table} ({rows})',
    tableRowsLoseOne: '1 vrstica izgubi vrednost',
    tableRowsLoseMany: 'Vrstice, v katerih se vrednost odstrani: {count}',
    tableRowsLostOne: '1 vrstica je izgubila vrednost',
    tableRowsLostMany: 'Vrstice, v katerih se je vrednost odstranila: {count}',
    tableRowsOne: '1 vrstica',
    tableRowsMany: 'Vrstice: {count}',
    connectedWriteOn: 'Dovoli aplikaciji {app} {action} v: {destination}',
    connectedWriteOnDone: 'Aplikacija {app} lahko {action} v: {destination}',
    connectedWriteOff: 'Prepreči aplikaciji {app} pisanje v: {destination}',
    connectedWriteOffDone: 'Aplikacija {app} ne piše več v: {destination}',
    codeConfirm:
      'Dovoli aplikaciji {app} uporabo vaših odobritev s spremembami, ki jih je naredil(a): {names}',
    codeConfirmDone:
      'Aplikacija {app} uporablja vaše odobritve s spremembami, ki jih je naredil(a): {names}',
    codeConfirmCurrent: 'Dovoli aplikaciji {app} uporabo vaših odobritev z njeno trenutno kodo',
    codeConfirmCurrentDone: 'Aplikacija {app} uporablja vaše odobritve z njeno trenutno kodo',
    runChanges: 'Zaženi zdaj funkcijo „{function}“ v aplikaciji {app}',
    runChangesDone: 'Funkcija „{function}“ je bila zagnana v aplikaciji {app}',
    continueApproved: 'Odobreno. Nadaljuj: zaženi in preveri, ali deluje.',
    continueDeclined: 'Zavrnjeno. Nadaljuj brez tega.',
    continueMixed: 'O zahtevah je odločeno. Nadaljuj.',
    typeOutsideAccess: 'Aplikaciji dovoli dostop do zunanjih storitev',
    typeEvent: 'Vklopi ali izklopi dogodek aplikacije',
    typeSchedule: 'Vklopi, začasno ustavi ali spremeni razpored aplikacije',
    typeDataWrites: 'Funkciji aplikacije dovoli spreminjanje lastnih vrstic',
    typeTableChange: 'Izbriši ali obnovi podatke v tabelah aplikacije',
    typeConnectedWrite: 'Aplikaciji dovoli pisanje v sistem podjetja',
    typeConfirmCode: 'Spremenjeni kodi aplikacije dovoli uporabo vaših odobritev',
    typeRunChanges: 'Zaženi funkcijo aplikacije, ki spreminja podatke',
    typePublicLink: 'Spremeni javno povezavo aplikacije',
    typeUnpublish: 'Odstrani javno povezavo aplikacije',
    labelHosts: 'Storitve',
    labelAccess: 'Dostop',
    labelEvent: 'Dogodek',
    labelKind: 'Zažene se ob',
    labelCollection: 'Zbirka',
    labelChanges: 'Spremembe vrstic',
    labelRuns: 'Zagoni',
    labelFunction: 'Funkcija',
    labelCollections: 'Tabele',
    labelWho: 'Za',
    labelAllowed: 'Dovoljeno',
    labelEnabled: 'Stanje',
    labelJob: 'Načrtovano opravilo',
    labelSchedule: 'Razpored',
    labelTimezone: 'Časovni pas',
    labelChange: 'Sprememba',
    labelTable: 'Tabela',
    labelField: 'Polje',
    labelRows: 'Vrstice',
    labelKeptDays: 'Kopija se hrani (dni)',
    labelRestores: 'Obnovi',
    labelAction: 'Dejanje',
    labelDestination: 'Cilj',
    labelChangedBy: 'Spremenil(a)',
    labelInput: 'Vnos',
    labelPublicTables: 'Kopirane tabele',
    labelPublicActions: 'Obiskovalci lahko pošljejo',
    labelPublicRuns: 'Vsaka oddaja zažene',
    labelPublicRunsOff: 'Zagnalo bi se tudi po vklopu',
    labelEmbedSites: 'Spletna mesta, ki jo smejo vdelati',
    labelPublicLink: 'Javna povezava',
    labelPublishedBy: 'Objavil(a)',
    wordRead: 'Samo branje',
    wordReadSend: 'Branje in pošiljanje podatkov',
    wordNoAccess: 'Brez dostopa',
    publicPublish: 'Objavi na javni povezavi',
    publicRefresh: 'Ponovno kopiraj njene podatke',
    publicUnpublish: 'Prekliči objavo',
    eventWebhook: 'Njen webhook',
    eventRows: 'Spremembe njenih vrstic',
    switchOn: 'Vklopljeno',
    switchOff: 'Izklopljeno',
    switchPaused: 'Začasno ustavljeno',
    changeInsert: 'Dodano',
    changeUpdate: 'Spremenjeno',
    changeDelete: 'Izbrisano',
    wordDropField: 'Izbriši vrednosti polja',
    wordDropTable: 'Izbriši tabelo',
    tableClearValues: 'Počisti vrednosti, ki kršijo strožje pravilo',
    tableRestore: 'Obnovi iz njene kopije',
    whoOwner: 'Vaši lastni zagoni',
    whoEveryone: 'Vsi, ki jo uporabljajo',
    answerYes: 'Da',
    answerNo: 'Ne',
  },
  es: {
    kindWrite: 'Cambio de la app que requiere aprobación',
    kindGrant: 'Acceso de la app que requiere aprobación',
    kindBusiness: 'Aprobación para una app',
    approve: 'Aprobar',
    reject: 'Rechazar',
    working: 'Procesando…',
    checkStatus: 'Comprobar estado',
    statusStillPending:
      'Poneva aún no ha resuelto esto. Puede que siga en proceso, así que no se puede tomar ninguna otra decisión hasta que se resuelva.',
    statusStillUnknown:
      'No pudimos obtener una respuesta. No se puede tomar ninguna otra decisión hasta que esto se resuelva. Inténtalo de nuevo en un momento.',
    loading: 'Cargando…',
    unavailable: 'Esto no está disponible en este momento.',
    loadFailed: 'No pudimos cargar esto. Inténtalo de nuevo en un momento.',
    requestedBy: 'Solicitado por {name}',
    originApp: 'Desde la app',
    originAgent: 'Desde un agente',
    originClient: 'A través de {client}',
    originFor: 'para {name}',
    changedUnderYou:
      'Esto cambió mientras lo leías. Lo que ves ahora es la versión actual. No se tomó ninguna decisión.',
    notAllowed: 'No puedes decidir sobre esto.',
    cannotDecideNow: 'Esto no se puede decidir en este momento. No se envió nada.',
    expired: 'Esta solicitud ha caducado. No se hizo ningún cambio.',
    stale:
      'Los datos cambiaron desde que se solicitó esto. Vuelve a solicitarlo para obtener una solicitud actualizada.',
    approved: 'Aprobado',
    rejected: 'Rechazado',
    needsYou: 'Requiere aprobación',
    waitingOn: 'Esperando a {name}',
    unknownTitle: 'No podemos confirmar qué ocurrió',
    unknownBody:
      'Puede que el cambio se haya realizado o no. No lo repitas. Comprueba el estado o pide al propietario de la app que lo revise.',
    blockedApproval: 'Esperando esta aprobación para continuar.',
    blockedBudget: 'En pausa: se agotó el presupuesto de IA de esta app.',
    blockedProvider: 'En pausa: un servicio conectado no responde.',
    blockedViewer:
      'En pausa: esto debe ejecutarse como usuario autenticado, pero no hay ninguno asociado. No se ejecuta usando el acceso del propietario.',
    writeTitle: 'Aprobar cambios en {target}',
    writeSummaryAll: '{count} cambios en {app}',
    rowCreate: 'Nuevo',
    rowUpdate: 'Modificado',
    rowDelete: 'Eliminado',
    rowInclude: 'Incluir este cambio',
    rowExcluded: 'Excluido',
    fieldBefore: 'Antes',
    fieldAfter: 'Después',
    fieldEmpty: 'Vacío',
    approveSome: 'Aprobar {included} de {total}',
    approveAll: 'Aprobar los {total}',
    allLeftOut:
      'Se han excluido todos los cambios. Recházalos para detener el proceso o incluye un cambio para aprobarlo.',
    appliedSome: 'Se realizaron {count} cambios.',
    rejectedBody: 'No se hizo ningún cambio.',
    noRows: 'No hay nada que aprobar aquí.',
    grantTitle: 'Permitir que {app} use {source}',
    grantRead: 'Solo lectura',
    grantWrite: 'Lectura y cambios',
    audienceOwner: 'Solo tú verás los datos de esta app de {source}.',
    audiencePeople:
      'Las {count} personas con quienes compartes esta app verán los datos de {source}.',
    audienceCompany:
      'Todas las personas de la empresa que abran la app verán los datos de {source}.',
    grantRouted: '{owner} decide sobre esto, así que se le envió.',
    grantRoutedUnknown: 'El propietario de esta conexión decide sobre esto, así que se le envió.',
    grantWhat: 'Acceso',
    businessStep: 'Paso {order}',
    stepWaiting: 'Esperando',
    stepPending: 'Requiere una decisión',
    stepApproved: 'Aprobado',
    stepRejected: 'Rechazado',
    stepYou: 'Tú',
    businessNote: 'Aprobar aquí no permite ningún cambio nuevo fuera de Poneva.',
    businessYourTurn: 'Te toca decidir.',
    businessNotYourTurn: 'Esperando a que {name} decida primero.',
    policyTitle: 'Cómo usan las apps esta conexión',
    policyCreator: 'Solo del creador',
    policyCreatorBody:
      'Todas las personas ven los mismos datos mediante la conexión del creador de la app.',
    policyViewer: 'Solo de cada usuario',
    policyViewerBody: 'Cada persona ve sus propios datos mediante su propia conexión.',
    policyBoth: 'Ambas',
    policyBothBody: 'El creador de la app elige para cada app.',
    policyRevoked: 'Las apps no pueden usar esta conexión',
    policyRevokedBody: 'Elige una opción para permitirlo de nuevo. No se elegirá nada por ti.',
    policySave: 'Guardar',
    policySaving: 'Guardando…',
    policySaved: 'Guardado.',
    policyReadOnly: 'Solo un administrador de la empresa puede cambiar esto.',
    policyConflict: 'Otra persona lo cambió antes. Esta es la configuración actual.',
    policyRefused: 'No se pudo guardar.',
    policyFailed:
      'No pudimos confirmar que se haya guardado. Recarga para ver la configuración actual antes de volver a intentarlo.',
    policyWidened:
      'Las aplicaciones que ya están publicadas mantienen su forma actual de conexión. Se pide a sus creadores que las vuelvan a publicar para usar la nueva opción.',
    policyNarrowed: 'Las aplicaciones comprueban la configuración actual cada vez que se conectan.',
    policyAffected: '{count} aplicaciones publicadas usan esta conexión (a fecha de {when}).',
    policyAffectedOne: '1 aplicación publicada usa esta conexión (a fecha de {when}).',
    policyAffectsNames: 'Afecta a {count} aplicaciones publicadas: {names}.',
    policyAffectsOneName: 'Afecta a 1 aplicación publicada: {names}.',
    policyGenericWiden:
      'Las aplicaciones publicadas que usan esta conexión mantienen su forma actual de conexión. Se pide a sus creadores que las vuelvan a publicar para usar la nueva opción.',
    policyGenericNarrow:
      'Las aplicaciones publicadas que usan esta conexión comprueban la configuración actual cada vez que se conectan.',
    policyAffectedGeneric:
      'Las aplicaciones publicadas que usan esta conexión no cambian automáticamente. Se pide a sus creadores que las vuelvan a publicar.',
    policyUnavailable: 'Esta configuración no está disponible en este momento.',
    outsideRead: 'Permitir que {app} lea de {hosts}',
    outsideReadSend: 'Permitir que {app} lea de {hosts} y le envíe datos',
    outsideNone: 'Impedir que {app} acceda a {hosts}',
    outsideReadDone: '{app} puede leer de {hosts}',
    outsideReadSendDone: '{app} puede leer de {hosts} y enviarle datos',
    outsideNoneDone: '{app} ya no accede a {hosts}',
    outsideMore: '{count} más',
    eventWebhookOn: 'Permitir que {app} reciba “{event}” en su webhook',
    eventWebhookOnDone: '{app} recibe “{event}” en su webhook',
    eventWebhookOff: 'Impedir que {app} reciba “{event}” en su webhook',
    eventWebhookOffDone: '{app} ya no recibe “{event}”',
    eventRowsOn: 'Permitir que {app} ejecute {runs} cuando se {changes} filas en {collection}',
    eventRowsOnDone: '{app} ejecuta {runs} cuando se {changes} filas en {collection}',
    eventRowsOff: 'Impedir que {app} ejecute {runs} cuando cambien filas en {collection}',
    eventRowsOffDone: '{app} ya no ejecuta {runs} cuando cambian filas en {collection}',
    eventInsert: 'añadan',
    eventUpdate: 'cambien',
    eventDelete: 'eliminen',
    scheduleTurnOn: 'Activar la programación de “{job}” en {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone:
      'La programación de “{job}” en {app} está activada. {cadence} ({timezone}).',
    scheduleResume: 'Reanudar la programación de “{job}” en {app}. {cadence} ({timezone}).',
    scheduleResumeDone:
      'La programación de “{job}” en {app} vuelve a estar activada. {cadence} ({timezone}).',
    scheduleReschedule: 'Cambiar la programación de “{job}” en {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone:
      'La programación de “{job}” en {app} ha cambiado. {cadence} ({timezone}).',
    schedulePause: 'Pausar la programación de “{job}” en {app}.',
    schedulePauseDone: 'La programación de “{job}” en {app} está en pausa.',
    scheduleNext: 'Próximas ejecuciones: {runs}.',
    scheduleResumeSaved:
      'Reanudar la programación de “{job}” en {app} con la última versión guardada. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'La programación de “{job}” en {app} vuelve a estar activada y ejecuta la última versión guardada. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Reanudar la programación de “{job}” en {app} con la versión publicada. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'La programación de “{job}” en {app} vuelve a estar activada y ejecuta la versión publicada. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Cambiar la programación de “{job}” en {app}. {cadence} ({timezone}). Quedará en pausa hasta que la reanudes.',
    scheduleReschedulePausedDone:
      'La programación de “{job}” en {app} ha cambiado. {cadence} ({timezone}). Está en pausa hasta que la reanudes.',
    scheduleSwitchSaved:
      'Cambiar la programación de “{job}” en {app} a la última versión guardada. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'La programación de “{job}” en {app} ahora ejecuta la última versión guardada. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Cambiar la programación de “{job}” en {app} a la última versión guardada. {cadence} ({timezone}). Quedará en pausa hasta que la reanudes.',
    scheduleSwitchSavedPausedDone:
      'La programación de “{job}” en {app} ahora usa la última versión guardada. {cadence} ({timezone}). Está en pausa hasta que la reanudes.',
    scheduleSwitchPublished:
      'Cambiar la programación de “{job}” en {app} a la versión publicada. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'La programación de “{job}” en {app} ahora ejecuta la versión publicada. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Cambiar la programación de “{job}” en {app} a la versión publicada. {cadence} ({timezone}). Quedará en pausa hasta que la reanudes.',
    scheduleSwitchPublishedPausedDone:
      'La programación de “{job}” en {app} ahora usa la versión publicada. {cadence} ({timezone}). Está en pausa hasta que la reanudes.',
    dataWritesOn: 'Permitir que “{function}” de {app} pueda {verbs} sus propias filas',
    dataWritesOnDone: '“{function}” de {app} puede {verbs} sus propias filas',
    dataWritesOff: 'Impedir que “{function}” de {app} cambie sus propias filas',
    dataWritesOffDone: '“{function}” de {app} ya no cambia sus propias filas',
    dataWriteInsert: 'añadir',
    dataWriteUpdate: 'cambiar',
    dataWriteDelete: 'eliminar',
    tableDropField: 'Permitir que {app} elimine el campo “{field}” de {table} ({rows})',
    tableDropFieldDone: '{app} eliminó el campo “{field}” de {table} ({rows})',
    tableDropTable: 'Permitir que {app} elimine la tabla {table} ({rows})',
    tableDropTableDone: '{app} eliminó la tabla {table} ({rows})',
    tableClear:
      'Permitir que {app} borre “{field}” en {table} donde incumple la nueva regla ({rows})',
    tableClearDone: '{app} borró “{field}” en {table} donde incumplía la nueva regla ({rows})',
    tableRestoreField: 'Permitir que {app} recupere “{field}” en {table} desde su copia ({rows})',
    tableRestoreFieldDone: '{app} recuperó “{field}” en {table} ({rows})',
    tableRestoreTable: 'Permitir que {app} restaure la tabla {table} desde su copia ({rows})',
    tableRestoreTableDone: '{app} restauró la tabla {table} ({rows})',
    tableRowsLoseOne: '1 fila pierde un valor',
    tableRowsLoseMany: '{count} filas pierden un valor',
    tableRowsLostOne: '1 fila perdió un valor',
    tableRowsLostMany: '{count} filas perdieron un valor',
    tableRowsOne: '1 fila',
    tableRowsMany: '{count} filas',
    connectedWriteOn: 'Permitir que {app} {action} en {destination}',
    connectedWriteOnDone: '{app} puede {action} en {destination}',
    connectedWriteOff: 'Impedir que {app} escriba en {destination}',
    connectedWriteOffDone: '{app} ya no escribe en {destination}',
    codeConfirm: 'Permitir que {app} use tus aprobaciones con los cambios de {names}',
    codeConfirmDone: '{app} usa tus aprobaciones con los cambios de {names}',
    codeConfirmCurrent: 'Permitir que {app} use tus aprobaciones con su código actual',
    codeConfirmCurrentDone: '{app} usa tus aprobaciones con su código actual',
    runChanges: 'Ejecutar ahora “{function}” en {app}',
    runChangesDone: '“{function}” se ejecutó en {app}',
    continueApproved: 'Aprobado. Continúa: ejecútalo y comprueba que funciona.',
    continueDeclined: 'Rechazado. Continúa sin ello.',
    continueMixed: 'He decidido sobre las solicitudes. Continúa.',
    typeOutsideAccess: 'Permitir que una app acceda a servicios externos',
    typeEvent: 'Activar o desactivar un evento de una app',
    typeSchedule: 'Activar, pausar o cambiar la programación de una app',
    typeDataWrites: 'Permitir que una función de una app cambie sus propias filas',
    typeTableChange: 'Eliminar o restaurar datos en las tablas de una app',
    typeConnectedWrite: 'Permitir que una app escriba en un sistema de la empresa',
    typeConfirmCode: 'Dejar que el código modificado de una app use tus aprobaciones',
    typeRunChanges: 'Ejecutar una función de una app que cambia datos',
    typePublicLink: 'Cambiar el enlace público de una app',
    typeUnpublish: 'Retirar el enlace público de una app',
    labelHosts: 'Servicios',
    labelAccess: 'Acceso',
    labelEvent: 'Evento',
    labelKind: 'Se inicia con',
    labelCollection: 'Colección',
    labelChanges: 'Cambios de filas',
    labelRuns: 'Ejecuciones',
    labelFunction: 'Función',
    labelCollections: 'Tablas',
    labelWho: 'Para',
    labelAllowed: 'Permitido',
    labelEnabled: 'Estado',
    labelJob: 'Tarea programada',
    labelSchedule: 'Programación',
    labelTimezone: 'Zona horaria',
    labelChange: 'Cambio',
    labelTable: 'Tabla',
    labelField: 'Campo',
    labelRows: 'Filas',
    labelKeptDays: 'Copia conservada (días)',
    labelRestores: 'Restaura',
    labelAction: 'Acción',
    labelDestination: 'Destino',
    labelChangedBy: 'Cambiado por',
    labelInput: 'Entrada',
    labelPublicTables: 'Tablas copiadas',
    labelPublicActions: 'Los visitantes pueden enviar',
    labelPublicRuns: 'Cada envío inicia',
    labelPublicRunsOff: 'También se iniciaría al activarlo',
    labelEmbedSites: 'Sitios que pueden insertarla',
    labelPublicLink: 'Enlace público',
    labelPublishedBy: 'Publicado por',
    wordRead: 'Solo lectura',
    wordReadSend: 'Leer y enviar datos',
    wordNoAccess: 'Sin acceso',
    publicPublish: 'Publicar en un enlace público',
    publicRefresh: 'Copiar de nuevo sus datos',
    publicUnpublish: 'Anular la publicación',
    eventWebhook: 'Su webhook',
    eventRows: 'Cambios en sus filas',
    switchOn: 'Activado',
    switchOff: 'Desactivado',
    switchPaused: 'En pausa',
    changeInsert: 'Añadida',
    changeUpdate: 'Cambiada',
    changeDelete: 'Eliminada',
    wordDropField: 'Eliminar los valores de un campo',
    wordDropTable: 'Eliminar una tabla',
    tableClearValues: 'Borrar los valores que incumplen una regla más estricta',
    tableRestore: 'Restaurar desde su copia',
    whoOwner: 'Tus propias ejecuciones',
    whoEveryone: 'Todas las personas que la usan',
    answerYes: 'Sí',
    answerNo: 'No',
  },
  sv: {
    kindWrite: 'Appändring att godkänna',
    kindGrant: 'Appåtkomst att godkänna',
    kindBusiness: 'Godkännande för en app',
    approve: 'Godkänn',
    reject: 'Avslå',
    working: 'Arbetar…',
    checkStatus: 'Kontrollera status',
    statusStillPending:
      'Poneva har inte avgjort detta än. Det kan fortfarande pågå, så inget annat kan beslutas innan det är klart.',
    statusStillUnknown:
      'Vi fick inget svar. Inget annat kan beslutas innan detta är avgjort. Försök igen om en stund.',
    loading: 'Läser in…',
    unavailable: 'Detta är inte tillgängligt just nu.',
    loadFailed: 'Vi kunde inte läsa in detta. Försök igen om en stund.',
    requestedBy: 'Begärt av {name}',
    originApp: 'Från appen',
    originAgent: 'Från en Agent',
    originClient: 'Via {client}',
    originFor: 'för {name}',
    changedUnderYou:
      'Detta ändrades medan du läste. Det du ser nu är den aktuella versionen. Inget har beslutats.',
    notAllowed: 'Du kan inte besluta om detta.',
    cannotDecideNow: 'Det går inte att besluta om detta just nu. Inget skickades.',
    expired: 'Begäran har gått ut. Inget ändrades.',
    stale: 'Datan har ändrats sedan detta begärdes. Be om en ny begäran.',
    approved: 'Godkänd',
    rejected: 'Avslagen',
    needsYou: 'Behöver godkännande',
    waitingOn: 'Väntar på {name}',
    unknownTitle: 'Vi kan inte bekräfta vad som hände',
    unknownBody:
      'Ändringen kan ha gjorts eller inte. Upprepa den inte. Kontrollera statusen, eller be appens ägare titta på det.',
    blockedApproval: 'Väntar på det här godkännandet innan det fortsätter.',
    blockedBudget: 'Pausad: AI-budgeten för den här appen är förbrukad.',
    blockedProvider: 'Pausad: en ansluten tjänst svarar inte.',
    blockedViewer:
      'Pausad: det här måste köras som en inloggad tittare, och ingen tittare är ansluten. Ingenting körs med ägarens åtkomst i stället.',
    writeTitle: 'Godkänn ändringar i {target}',
    writeSummaryAll: '{count} ändringar i {app}',
    rowCreate: 'Ny',
    rowUpdate: 'Ändrad',
    rowDelete: 'Borttagen',
    rowInclude: 'Ta med den här ändringen',
    rowExcluded: 'Utelämnad',
    fieldBefore: 'Före',
    fieldAfter: 'Efter',
    fieldEmpty: 'Tom',
    approveSome: 'Godkänn {included} av {total}',
    approveAll: 'Godkänn alla {total}',
    allLeftOut:
      'Alla ändringar är utelämnade. Avslå för att stoppa, eller ta med en ändring för att godkänna den.',
    appliedSome: '{count} ändringar gjordes.',
    rejectedBody: 'Ingenting ändrades.',
    noRows: 'Det finns inget att godkänna här.',
    grantTitle: 'Låt {app} använda {source}',
    grantRead: 'Endast läsning',
    grantWrite: 'Läsa och ändra',
    audienceOwner: 'Bara du kommer att se appens data från {source}.',
    audiencePeople: 'De {count} personer som appen delas med kommer att se data från {source}.',
    audienceCompany: 'Alla i företaget som öppnar appen kommer att se data från {source}.',
    grantRouted: '{owner} beslutar om detta, så begäran skickades dit.',
    grantRoutedUnknown:
      'Ägaren av den här anslutningen beslutar om detta, så begäran skickades dit.',
    grantWhat: 'Åtkomst',
    businessStep: 'Steg {order}',
    stepWaiting: 'Väntar',
    stepPending: 'Behöver ett beslut',
    stepApproved: 'Godkänt',
    stepRejected: 'Avslaget',
    stepYou: 'Du',
    businessNote: 'Ett godkännande här tillåter inga nya ändringar utanför Poneva.',
    businessYourTurn: 'Det är din tur att besluta.',
    businessNotYourTurn: 'Väntar på att {name} ska besluta först.',
    policyTitle: 'Hur appar använder den här anslutningen',
    policyCreator: 'Bara skaparens',
    policyCreatorBody: 'Alla ser samma data, via appskaparens anslutning.',
    policyViewer: 'Bara tittarens egen',
    policyViewerBody: 'Var och en ser sina egna data, via sin egen anslutning.',
    policyBoth: 'Båda',
    policyBothBody: 'Appens skapare väljer för varje app.',
    policyRevoked: 'Appar kan inte använda den här anslutningen',
    policyRevokedBody: 'Välj ett alternativ för att tillåta den igen. Inget väljs åt dig.',
    policySave: 'Spara',
    policySaving: 'Sparar…',
    policySaved: 'Sparat.',
    policyReadOnly: 'Bara en företagsadministratör kan ändra detta.',
    policyConflict: 'Någon annan ändrade detta först. Det här är den aktuella inställningen.',
    policyRefused: 'Det gick inte att spara detta.',
    policyFailed:
      'Vi kunde inte bekräfta att detta sparades. Ladda om för att se den aktuella inställningen innan du försöker igen.',
    policyWidened:
      'Appar som redan är publicerade behåller hur de ansluter i dag. Deras skapare ombeds publicera igen för att använda det nya alternativet.',
    policyNarrowed: 'Appar kontrollerar den aktuella inställningen varje gång de ansluter.',
    policyAffected: '{count} publicerade appar använder den här anslutningen (per {when}).',
    policyAffectedOne: '1 publicerad app använder den här anslutningen (per {when}).',
    policyAffectsNames: 'Påverkar {count} publicerade appar: {names}.',
    policyAffectsOneName: 'Påverkar 1 publicerad app: {names}.',
    policyGenericWiden:
      'Publicerade appar som använder den här anslutningen behåller hur de ansluter i dag. Deras skapare ombeds publicera om för att använda det nya alternativet.',
    policyGenericNarrow:
      'Publicerade appar som använder den här anslutningen kontrollerar den aktuella inställningen varje gång de ansluter.',
    policyAffectedGeneric:
      'Publicerade appar som använder den här anslutningen ändras inte automatiskt. Deras skapare ombeds publicera igen.',
    policyUnavailable: 'Den här inställningen är inte tillgänglig just nu.',
    outsideRead: 'Låt {app} läsa från {hosts}',
    outsideReadSend: 'Låt {app} läsa från och skicka data till {hosts}',
    outsideNone: 'Stoppa {app} från att nå {hosts}',
    outsideReadDone: '{app} kan läsa från {hosts}',
    outsideReadSendDone: '{app} kan läsa från och skicka data till {hosts}',
    outsideNoneDone: '{app} når inte längre {hosts}',
    outsideMore: '{count} till',
    eventWebhookOn: 'Låt {app} ta emot ”{event}” på sin webhook',
    eventWebhookOnDone: '{app} tar emot ”{event}” på sin webhook',
    eventWebhookOff: 'Stoppa {app} från att ta emot ”{event}” på sin webhook',
    eventWebhookOffDone: '{app} tar inte längre emot ”{event}”',
    eventRowsOn: 'Låt {app} köra {runs} när rader i {collection} {changes}',
    eventRowsOnDone: '{app} kör {runs} när rader i {collection} {changes}',
    eventRowsOff: 'Stoppa {app} från att köra {runs} när rader i {collection} ändras',
    eventRowsOffDone: '{app} kör inte längre {runs} när rader i {collection} ändras',
    eventInsert: 'läggs till',
    eventUpdate: 'ändras',
    eventDelete: 'tas bort',
    scheduleTurnOn: 'Slå på schemat för ”{job}” i {app}. {cadence} ({timezone}).',
    scheduleTurnOnDone: 'Schemat för ”{job}” i {app} är på. {cadence} ({timezone}).',
    scheduleResume: 'Återuppta schemat för ”{job}” i {app}. {cadence} ({timezone}).',
    scheduleResumeDone: 'Schemat för ”{job}” i {app} är på igen. {cadence} ({timezone}).',
    scheduleReschedule: 'Ändra schemat för ”{job}” i {app}. {cadence} ({timezone}).',
    scheduleRescheduleDone: 'Schemat för ”{job}” i {app} är ändrat. {cadence} ({timezone}).',
    schedulePause: 'Pausa schemat för ”{job}” i {app}.',
    schedulePauseDone: 'Schemat för ”{job}” i {app} är pausat.',
    scheduleNext: 'Nästa körningar: {runs}.',
    scheduleResumeSaved:
      'Återuppta schemat för ”{job}” i {app} med den senast sparade versionen. {cadence} ({timezone}).',
    scheduleResumeSavedDone:
      'Schemat för ”{job}” i {app} är på igen och kör den senast sparade versionen. {cadence} ({timezone}).',
    scheduleResumePublished:
      'Återuppta schemat för ”{job}” i {app} med den publicerade versionen. {cadence} ({timezone}).',
    scheduleResumePublishedDone:
      'Schemat för ”{job}” i {app} är på igen och kör den publicerade versionen. {cadence} ({timezone}).',
    scheduleReschedulePaused:
      'Ändra schemat för ”{job}” i {app}. {cadence} ({timezone}). Det blir pausat tills du återupptar det.',
    scheduleReschedulePausedDone:
      'Schemat för ”{job}” i {app} är ändrat. {cadence} ({timezone}). Det är pausat tills du återupptar det.',
    scheduleSwitchSaved:
      'Flytta schemat för ”{job}” i {app} till den senast sparade versionen. {cadence} ({timezone}).',
    scheduleSwitchSavedDone:
      'Schemat för ”{job}” i {app} kör nu den senast sparade versionen. {cadence} ({timezone}).',
    scheduleSwitchSavedPaused:
      'Flytta schemat för ”{job}” i {app} till den senast sparade versionen. {cadence} ({timezone}). Det blir pausat tills du återupptar det.',
    scheduleSwitchSavedPausedDone:
      'Schemat för ”{job}” i {app} använder nu den senast sparade versionen. {cadence} ({timezone}). Det är pausat tills du återupptar det.',
    scheduleSwitchPublished:
      'Flytta schemat för ”{job}” i {app} till den publicerade versionen. {cadence} ({timezone}).',
    scheduleSwitchPublishedDone:
      'Schemat för ”{job}” i {app} kör nu den publicerade versionen. {cadence} ({timezone}).',
    scheduleSwitchPublishedPaused:
      'Flytta schemat för ”{job}” i {app} till den publicerade versionen. {cadence} ({timezone}). Det blir pausat tills du återupptar det.',
    scheduleSwitchPublishedPausedDone:
      'Schemat för ”{job}” i {app} använder nu den publicerade versionen. {cadence} ({timezone}). Det är pausat tills du återupptar det.',
    dataWritesOn: 'Låt ”{function}” i {app} {verbs} appens egna rader',
    dataWritesOnDone: '”{function}” i {app} kan {verbs} appens egna rader',
    dataWritesOff: 'Stoppa ”{function}” i {app} från att ändra appens egna rader',
    dataWritesOffDone: '”{function}” i {app} ändrar inte längre appens egna rader',
    dataWriteInsert: 'lägga till',
    dataWriteUpdate: 'ändra',
    dataWriteDelete: 'ta bort',
    tableDropField: 'Låt {app} ta bort fältet ”{field}” från {table} ({rows})',
    tableDropFieldDone: '{app} tog bort fältet ”{field}” från {table} ({rows})',
    tableDropTable: 'Låt {app} ta bort tabellen {table} ({rows})',
    tableDropTableDone: '{app} tog bort tabellen {table} ({rows})',
    tableClear: 'Låt {app} tömma ”{field}” i {table} där värdet bryter mot den nya regeln ({rows})',
    tableClearDone: '{app} tömde ”{field}” i {table} där värdet bröt mot den nya regeln ({rows})',
    tableRestoreField: 'Låt {app} lägga tillbaka ”{field}” i {table} från kopian ({rows})',
    tableRestoreFieldDone: '{app} lade tillbaka ”{field}” i {table} ({rows})',
    tableRestoreTable: 'Låt {app} återställa tabellen {table} från kopian ({rows})',
    tableRestoreTableDone: '{app} återställde tabellen {table} ({rows})',
    tableRowsLoseOne: '1 rad förlorar ett värde',
    tableRowsLoseMany: '{count} rader förlorar ett värde',
    tableRowsLostOne: '1 rad förlorade ett värde',
    tableRowsLostMany: '{count} rader förlorade ett värde',
    tableRowsOne: '1 rad',
    tableRowsMany: '{count} rader',
    connectedWriteOn: 'Låt {app} {action} i {destination}',
    connectedWriteOnDone: '{app} kan {action} i {destination}',
    connectedWriteOff: 'Stoppa {app} från att skriva till {destination}',
    connectedWriteOffDone: '{app} skriver inte längre till {destination}',
    codeConfirm: 'Låt {app} använda dina godkännanden med ändringarna av {names}',
    codeConfirmDone: '{app} använder dina godkännanden med ändringarna av {names}',
    codeConfirmCurrent: 'Låt {app} använda dina godkännanden med sin nuvarande kod',
    codeConfirmCurrentDone: '{app} använder dina godkännanden med sin nuvarande kod',
    runChanges: 'Kör ”{function}” i {app} nu',
    runChangesDone: '”{function}” kördes i {app}',
    continueApproved: 'Godkänt. Fortsätt: kör det och kontrollera att det fungerar.',
    continueDeclined: 'Avböjt. Fortsätt utan det.',
    continueMixed: 'Jag har tagit ställning till förfrågningarna. Fortsätt.',
    typeOutsideAccess: 'Låt en app nå externa tjänster',
    typeEvent: 'Slå på eller av en apphändelse',
    typeSchedule: 'Slå på, pausa eller ändra en apps schema',
    typeDataWrites: 'Låt en appfunktion ändra sina egna rader',
    typeTableChange: 'Ta bort eller återställ data i en apps tabeller',
    typeConnectedWrite: 'Låt en app skriva till ett företagssystem',
    typeConfirmCode: 'Låt ändrad appkod använda dina godkännanden',
    typeRunChanges: 'Kör en appfunktion som ändrar data',
    typePublicLink: 'Ändra en apps offentliga länk',
    typeUnpublish: 'Ta ner en apps offentliga länk',
    labelHosts: 'Tjänster',
    labelAccess: 'Åtkomst',
    labelEvent: 'Händelse',
    labelKind: 'Startar vid',
    labelCollection: 'Samling',
    labelChanges: 'Radändringar',
    labelRuns: 'Körningar',
    labelFunction: 'Funktion',
    labelCollections: 'Tabeller',
    labelWho: 'För',
    labelAllowed: 'Tillåtet',
    labelEnabled: 'Status',
    labelJob: 'Schemalagt jobb',
    labelSchedule: 'Schema',
    labelTimezone: 'Tidszon',
    labelChange: 'Ändring',
    labelTable: 'Tabell',
    labelField: 'Fält',
    labelRows: 'Rader',
    labelKeptDays: 'Kopia sparas (dagar)',
    labelRestores: 'Återställer',
    labelAction: 'Åtgärd',
    labelDestination: 'Mål',
    labelChangedBy: 'Ändrad av',
    labelInput: 'Indata',
    labelPublicTables: 'Kopierade tabeller',
    labelPublicActions: 'Besökare kan skicka',
    labelPublicRuns: 'Varje inskickning startar',
    labelPublicRunsOff: 'Skulle också starta när den slås på',
    labelEmbedSites: 'Webbplatser som får bädda in den',
    labelPublicLink: 'Offentlig länk',
    labelPublishedBy: 'Publicerad av',
    wordRead: 'Endast läsning',
    wordReadSend: 'Läsa och skicka data',
    wordNoAccess: 'Ingen åtkomst',
    publicPublish: 'Publicera till en offentlig länk',
    publicRefresh: 'Kopiera dess data igen',
    publicUnpublish: 'Avpublicera',
    eventWebhook: 'Appens webhook',
    eventRows: 'Ändringar i appens rader',
    switchOn: 'På',
    switchOff: 'Av',
    switchPaused: 'Pausad',
    changeInsert: 'Tillagd',
    changeUpdate: 'Ändrad',
    changeDelete: 'Borttagen',
    wordDropField: 'Ta bort ett fälts värden',
    wordDropTable: 'Ta bort en tabell',
    tableClearValues: 'Rensa värden som bryter mot en striktare regel',
    tableRestore: 'Återställ från kopian',
    whoOwner: 'Dina egna körningar',
    whoEveryone: 'Alla som använder den',
    answerYes: 'Ja',
    answerNo: 'Nej',
  },
};
export default copy;
